import React, { useState, useEffect, useRef } from 'react';
import { PropertySelector } from './components/PropertySelector';
import { usePropertyContext } from './hooks/usePropertyContext';
import { PortfolioDashboard } from './components/portfolio/PortfolioDashboard';
import { PropertyProfile } from './components/portfolio/PropertyProfile';
import { Promotions as YatraPromotions } from './components/Promotions';
import { AnalyticsOperations } from './components/portfolio/AnalyticsOperations';
import { BookingXmlDetails } from './components/bookings/BookingXmlDetails';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import { Calendar } from './components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from './components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { Checkbox } from './components/ui/checkbox';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './components/ui/accordion';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';
import { CalendarDays, Hotel, Users, BarChart3, Settings, LogOut, Plus, ChevronLeft, ChevronRight, Tag, Upload, Download, Menu, X, TrendingUp, ChevronDown, ChevronUp, Info, Eye, EyeOff, Edit, Trash2, DollarSign, FileText, LayoutDashboard, Building2, CheckCircle, XCircle, AlertCircle, RefreshCcw, MoreVertical, Search, Check, ChevronsUpDown, Percent, Copy } from 'lucide-react';
import HotelOnboardingForm from './components/HotelOnboardingForm';
import { OtaCodeCatalogSection } from './components/OtaCodeCatalogSection';
import { BulkRateUpdateModal } from './components/BulkRateUpdateModal';
import { LinkRoomTypesSection } from './components/LinkRoomTypesSection';
import { PropertyMappingTab } from './components/PropertyMappingTab';
import { OtaSyncToggles, BulkOtaSyncToggles } from './components/OtaSyncToggles';
import { OtaPropertyOnboarding } from './components/OtaPropertyOnboarding';
import PropertiesPage from './pages/PropertiesPage';
import WebhookMonitor from './WebhookMonitor';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Agoda Content Push — Payload & Response Viewer.
// Collapsible accordion: one section per outbound request stage, collapsed by
// default. Each section shows endpoint, request XML, HTTP status, response body,
// timestamp, with copy buttons for request and response.
const STAGE_LABELS = {
  property_content: 'Property Content — OTA_HotelDescriptiveContentNotifRQ',
  rooms: 'Rooms — OTA_HotelInvNotifRQ',
  rate_plans: 'Rate Plans — OTA_HotelRatePlanNotifRQ',
  products: 'Products — OTA_HotelProductNotifRQ',
  verify: 'Verify — OTA_HotelDescriptiveInfoRQ',
  validate: 'Validate — local readiness',
};

function CopyBtn({ text, label, testid }) {
  return (
    <Button
      size="sm" variant="outline" className="h-6 px-2 text-[10px] gap-1"
      data-testid={testid}
      disabled={!text}
      onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(text || ''); toast.success(`${label} copied`); }}>
      <Copy className="w-3 h-3" />Copy {label}
    </Button>
  );
}

function AgodaPayloadViewer({ stages }) {
  const sendable = (stages || []).filter(s => s.request_xml != null || s.response_body != null);
  if (sendable.length === 0) return null;
  return (
    <div className="border-t pt-2 mt-2" data-testid="agoda-payload-viewer">
      <div className="text-xs font-semibold text-gray-700 mb-1">Payload &amp; Response Viewer</div>
      <Accordion type="multiple" className="w-full">
        {sendable.map((st, i) => {
          const ok = st.status === 'success';
          const statusColor = ok ? 'bg-green-100 text-green-700' : st.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800';
          return (
            <AccordionItem value={`${st.stage}-${i}`} key={`${st.stage}-${i}`} data-testid={`agoda-payload-accordion-${st.stage}`}>
              <AccordionTrigger className="py-2 text-xs" data-testid={`agoda-payload-accordion-trigger-${st.stage}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{STAGE_LABELS[st.stage] || st.stage}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${statusColor}`}>{st.status}</span>
                  {st.http_code != null && <span className="text-[10px] text-gray-500">HTTP {st.http_code}</span>}
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-0" data-testid={`agoda-payload-accordion-content-${st.stage}`}>
                <div className="space-y-2 text-[11px]">
                  <div className="grid grid-cols-1 gap-0.5 text-gray-600">
                    <div><strong>Endpoint:</strong> <span className="font-mono break-all">{st.endpoint || '—'}</span></div>
                    <div className="flex gap-4 flex-wrap">
                      <span><strong>Method:</strong> {st.method || 'POST'}</span>
                      <span><strong>Notif:</strong> {st.notif_type || '—'}</span>
                      <span><strong>HTTP Status:</strong> {st.http_code ?? '—'}</span>
                    </div>
                    <div><strong>Timestamp:</strong> {st.sent_at ? new Date(st.sent_at).toLocaleString() : '—'}</div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-700">Request Payload</span>
                      <CopyBtn text={st.request_xml} label="Request" testid={`agoda-payload-copy-req-${st.stage}`} />
                    </div>
                    <pre className="bg-gray-900 text-green-200 text-[10px] p-2 rounded overflow-auto max-h-64 whitespace-pre" data-testid={`agoda-payload-req-xml-${st.stage}`}>
{st.request_xml || '(none)'}
                    </pre>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-700">Response Body</span>
                      <CopyBtn text={st.response_body} label="Response" testid={`agoda-payload-copy-res-${st.stage}`} />
                    </div>
                    <pre className="bg-gray-900 text-blue-200 text-[10px] p-2 rounded overflow-auto max-h-64 whitespace-pre" data-testid={`agoda-payload-res-body-${st.stage}`}>
{st.response_body || '(empty)'}
                    </pre>
                  </div>
                  {st.error && <div className="text-red-600"><strong>Error:</strong> {st.error}</div>}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}


// Auth Context
const AuthContext = React.createContext();

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    setToken(token);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

const useAuth = () => React.useContext(AuthContext);

// Login Component
function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'staff'
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, formData);
      
      login(response.data.token, response.data.user);
      toast.success(isLogin ? 'Logged in successfully!' : 'Account created successfully!');
      
      // Redirect to dashboard after successful login
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center mb-4">
            <Hotel className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-800">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </CardTitle>
          <p className="text-gray-600 text-sm">
            {isLogin ? 'Sign in to your hotel dashboard' : 'Join the hotel management platform'}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Input
                  name="full_name"
                  type="text"
                  placeholder="Full Name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  required
                  className="h-11"
                  data-testid="register-fullname-input"
                />
              </div>
            )}
            <div>
              <Input
                name="email"
                type="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="h-11"
                data-testid={isLogin ? "login-email-input" : "register-email-input"}
              />
            </div>
            <div>
              <div className="relative">
                <Input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="h-11 pr-11"
                  data-testid={isLogin ? "login-password-input" : "register-password-input"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                  data-testid="toggle-password-visibility-btn"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            {!isLogin && (
              <div>
                <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                  <SelectTrigger className="h-11" data-testid="register-role-select">
                    <SelectValue placeholder="Select Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button 
              type="submit" 
              className="w-full h-11 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700"
              disabled={loading}
              data-testid={isLogin ? "login-submit-btn" : "register-submit-btn"}
            >
              {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-orange-600 hover:text-orange-700 text-sm font-medium"
              data-testid="auth-switch-btn"
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Accounting Form Component
function AccountingForm({ entry, onSave, onCancel }) {
  const [formData, setFormData] = useState(entry || {
    hotel_name: '',
    city: '',
    state: '',
    ota: '',
    booking_date: '',
    check_in_date: '',
    check_out_date: '',
    no_of_rooms: '',
    no_of_pax: '',
    guest_name: '',
    sell_rate: '',
    hotel_tax: '',
    ota_commission: '',
    gst_on_ota_commission: '',
    tds: '',
    tcs: '',
    pay_to_hotel: '',
    payment_received: '',
    payment_received_date: '',
    receive_to_grb: '',
    grb_commission: '',
    gst_on_grb_commission: '',
    setup_adjusted: false,
    booking_id: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Name *</label>
          <Input
            value={formData.hotel_name}
            onChange={(e) => setFormData({...formData, hotel_name: e.target.value})}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
          <Input
            value={formData.city}
            onChange={(e) => setFormData({...formData, city: e.target.value})}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
          <Input
            value={formData.state}
            onChange={(e) => setFormData({...formData, state: e.target.value})}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">OTA</label>
          <Input
            value={formData.ota}
            onChange={(e) => setFormData({...formData, ota: e.target.value})}
            placeholder="e.g., Booking.com, Agoda, Expedia"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Guest Name *</label>
          <Input
            value={formData.guest_name}
            onChange={(e) => setFormData({...formData, guest_name: e.target.value})}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Booking Date *</label>
          <Input
            type="date"
            value={formData.booking_date}
            onChange={(e) => setFormData({...formData, booking_date: e.target.value})}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Check-In Date *</label>
          <Input
            type="date"
            value={formData.check_in_date}
            onChange={(e) => setFormData({...formData, check_in_date: e.target.value})}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Check-Out Date *</label>
          <Input
            type="date"
            value={formData.check_out_date}
            onChange={(e) => setFormData({...formData, check_out_date: e.target.value})}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">No. of Rooms *</label>
          <Input
            type="number"
            value={formData.no_of_rooms}
            onChange={(e) => setFormData({...formData, no_of_rooms: parseInt(e.target.value)})}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">No. of Pax *</label>
          <Input
            type="number"
            value={formData.no_of_pax}
            onChange={(e) => setFormData({...formData, no_of_pax: parseInt(e.target.value)})}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sell Rate *</label>
          <Input
            type="number"
            value={formData.sell_rate}
            onChange={(e) => setFormData({...formData, sell_rate: parseFloat(e.target.value)})}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Tax</label>
          <Input
            type="number"
            value={formData.hotel_tax}
            onChange={(e) => setFormData({...formData, hotel_tax: parseFloat(e.target.value) || 0})}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">OTA Commission</label>
          <Input
            type="number"
            value={formData.ota_commission}
            onChange={(e) => setFormData({...formData, ota_commission: parseFloat(e.target.value) || 0})}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Pay to Hotel *</label>
          <Input
            type="number"
            value={formData.pay_to_hotel}
            onChange={(e) => setFormData({...formData, pay_to_hotel: parseFloat(e.target.value)})}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Received</label>
          <Input
            type="number"
            value={formData.payment_received}
            onChange={(e) => setFormData({...formData, payment_received: parseFloat(e.target.value) || 0})}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Received Date</label>
          <Input
            type="date"
            value={formData.payment_received_date}
            onChange={(e) => setFormData({...formData, payment_received_date: e.target.value})}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">GRB Commission</label>
          <Input
            type="number"
            value={formData.grb_commission}
            onChange={(e) => setFormData({...formData, grb_commission: parseFloat(e.target.value) || 0})}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Booking ID *</label>
          <Input
            value={formData.booking_id}
            onChange={(e) => setFormData({...formData, booking_id: e.target.value})}
            placeholder="BKG001"
            required
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.setup_adjusted}
            onChange={(e) => setFormData({...formData, setup_adjusted: e.target.checked})}
            className="w-4 h-4"
          />
          <label className="text-sm font-medium text-gray-700">Setup Adjusted</label>
        </div>
      </div>
      <div className="flex gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
        <Button type="submit" className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600">
          Save Entry
        </Button>
      </div>
    </form>
  );
}

// Main Dashboard
function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Feb 2026 — restore the last-visited tab across reloads. We seed with the
  // localStorage value if any; validation against the current property mode
  // happens once properties finish loading (see the effect further below),
  // and any invalid value is safely coerced to 'dashboard'.
  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem('activeTab') || 'dashboard'; }
    catch (_) { return 'dashboard'; }
  });
  const [properties, setProperties] = useState([]);
  const [propertiesLoaded, setPropertiesLoaded] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  // Always-current selected property id — used by fetch sequencing guards so a
  // slow response for a previously-selected property can't clobber the new one.
  const selectedPropertyIdRef = useRef(null);
  selectedPropertyIdRef.current = selectedProperty?.id || null;
  // Phase 1 redesign — global property context (localStorage-backed + master mode).
  const { allPropertiesMode, setAllPropertiesMode } = usePropertyContext(
    selectedProperty, setSelectedProperty, properties
  );
  const [calendarData, setCalendarData] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [googleSheetsBookings, setGoogleSheetsBookings] = useState([]);
  const [isLoadingGoogleSheets, setIsLoadingGoogleSheets] = useState(false);
  const [lastGoogleSheetsUpdate, setLastGoogleSheetsUpdate] = useState(null);
  const [googleSheetsError, setGoogleSheetsError] = useState(null);
  
  // Bookings pagination state
  const [bookingsPage, setBookingsPage] = useState(1);
  const [bookingsLimit, setBookingsLimit] = useState(20);
  const [bookingsTotalCount, setBookingsTotalCount] = useState(0);
  const [bookingsTotalPages, setBookingsTotalPages] = useState(0);
  
  // Bookings filter options (fetched once)
  const [bookingsFilterOptions, setBookingsFilterOptions] = useState({
    otas: [],
    hotels: [],
    statuses: []
  });
  
  // Bookings filters
  const [bookingsOTAFilter, setBookingsOTAFilter] = useState([]);  // Changed to array for multi-select
  const [bookingsStatusFilter, setBookingsStatusFilter] = useState([]);  // Changed to array for multi-select
  const [bookingsHotelFilter, setBookingsHotelFilter] = useState([]);  // Changed to array for multi-select
  const [bookingsDateRange, setBookingsDateRange] = useState({ start: '', end: '' });
  const [bookingsSearchQuery, setBookingsSearchQuery] = useState('');  // Global search
  const [bookingsSearchDebounced, setBookingsSearchDebounced] = useState('');  // Debounced search
  const [showPropertyBreakdown, setShowPropertyBreakdown] = useState(false);
  const [hotelFilterOpen, setHotelFilterOpen] = useState(false);  // For multi-select popover
  const [hotelFilterSearch, setHotelFilterSearch] = useState('');  // Search within hotel dropdown
  const [otaFilterOpen, setOtaFilterOpen] = useState(false);  // For OTA multi-select popover
  const [otaFilterSearch, setOtaFilterSearch] = useState('');  // Search within OTA dropdown
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);  // For Status multi-select popover
  const [statusFilterSearch, setStatusFilterSearch] = useState('');  // Search within Status dropdown
  const [selectedBookingDetail, setSelectedBookingDetail] = useState(null);  // For booking detail view
  const [showBookingDetailModal, setShowBookingDetailModal] = useState(false);
  const [dashboardMetrics, setDashboardMetrics] = useState(null);  // Dashboard metrics from backend
  
  const [roomTypes, setRoomTypes] = useState([]);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showRoomTypeModal, setShowRoomTypeModal] = useState(false);
  const [propertyFormData, setPropertyFormData] = useState({
    name: '',
    address: '',
    city: '',
    country: '',
    phone: '',
    email: '',
    total_rooms: '',
    description: '',
    number_of_rooms: '',
    grb_agreement_date: '',
    sales_person_name: '',
    ota_live_date: '',
    first_reservation_date: '',
    setup_agreed: '',
    token: '',
    grb_commission_agreed: '',
    setup_completion_status: '',
    property_status: ''
  });
  const [roomTypeFormData, setRoomTypeFormData] = useState({
    name: '',
    description: '',
    max_occupancy: '',
    base_rate: '',
    room_count: '',
    amenities: ''
  });
  const [auditLogs, setAuditLogs] = useState([]);
  const [updateLogs, setUpdateLogs] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showBulkSidebar, setShowBulkSidebar] = useState(false); // New right sidebar for bulk actions
  const [bulkActionType, setBulkActionType] = useState('');
  const [bulkActionsMenuOpen, setBulkActionsMenuOpen] = useState(false);
  const [showCalendarDatePicker, setShowCalendarDatePicker] = useState(false);
  const [calendarView, setCalendarView] = useState('7'); // '7' or '14' days
  const [inventoryView, setInventoryView] = useState('grb'); // 'grb' or 'competitor'
  const [ratesView, setRatesView] = useState('grb'); // 'grb' or 'competitor'
  const [competitorData, setCompetitorData] = useState({});
  const [showCompetitorUpload, setShowCompetitorUpload] = useState(false);
  const [competitorUploadData, setCompetitorUploadData] = useState({
    selectedHotel: '',
    sheetUrl: ''
  });
  const [competitorRefreshTrigger, setCompetitorRefreshTrigger] = useState(0);
  const [realCompetitorRates, setRealCompetitorRates] = useState([]);
  const [loadingCompetitorRates, setLoadingCompetitorRates] = useState(false);
  const [uploadedCompetitorData, setUploadedCompetitorData] = useState({}); // Store data per hotel: { hotelId: { competitors, dates, rates } }
  const [competitorLastUpdated, setCompetitorLastUpdated] = useState(null); // Last updated timestamp for current property
  const [competitorLastUpdatedBy, setCompetitorLastUpdatedBy] = useState(null); // Who updated last
  const [bulkFormData, setBulkFormData] = useState({
    checkInDate: '',
    checkOutDate: '',
    hotelId: '',
    roomTypeIds: [], // Changed from single to array for multi-select
    value: '',
    rateType: 'base', // 'base' or 'net'
    restrictionType: 'block', // 'block' or 'unblock'
    // MMT CHM V3 cert (Feb 2026): multi-DateRange + daysOfWeek + multi-occupancy
    dateRanges: [{ from_date: '', to_date: '' }],
    daysOfWeek: { sun: true, mon: true, tue: true, wed: true, thu: true, fri: true, sat: true },
  });
  // Sidebar visibility state - true means sidebar is OPEN/VISIBLE, false means HIDDEN
  const [sidebarOpen, setSidebarOpen] = useState(false); // Default: sidebar hidden, full-width content
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [hiddenTabs, setHiddenTabs] = useState([]);
  const [agxData, setAgxData] = useState([]);
  const [agxMetric, setAgxMetric] = useState('impression'); // Default metric for AGX Campaign
  const [expandedCompetitorRates, setExpandedCompetitorRates] = useState(false);
  const [expandedCompetitorInventory, setExpandedCompetitorInventory] = useState(false);
  const [showCreateBooking, setShowCreateBooking] = useState(false);
  const [competitorDateOffset, setCompetitorDateOffset] = useState(0);
  const [showEditProperty, setShowEditProperty] = useState(false);
  const [editPropertyData, setEditPropertyData] = useState({});
  const [selectedBookingForView, setSelectedBookingForView] = useState(null);
  const [selectedBookingForEdit, setSelectedBookingForEdit] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [newBookingData, setNewBookingData] = useState({
    bookingDate: '',
    bookingId: '',
    checkIn: '',
    checkOut: '',
    otaName: '',
    rate: '',
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    roomType: '',
    numberOfRooms: 1,
    status: 'confirmed'
  });
  const [competitorRatesData, setCompetitorRatesData] = useState({});
  const [competitorInventoryData, setCompetitorInventoryData] = useState({});
  
  // Payments and Accounting state with sample data
  const [paymentsData, setPaymentsData] = useState([
    {
      id: 'PAY001',
      hotelName: 'Grand Palace Hotel',
      hotelId: 'HOTEL001',
      totalAmount: 5000,
      bookingId: 'BKG001',
      payments: [
        { amount: 2000, date: '2025-11-12', invoiceUrl: '#' },
        { amount: 1000, date: '2025-11-15', invoiceUrl: '#' },
        { amount: 1000, date: '2025-11-19', invoiceUrl: '#' }
      ]
    },
    {
      id: 'PAY002',
      hotelName: 'Ocean View Resort',
      hotelId: 'HOTEL002',
      totalAmount: 8000,
      bookingId: 'BKG002',
      payments: [
        { amount: 4000, date: '2025-10-10', invoiceUrl: '#' },
        { amount: 2000, date: '2025-10-12', invoiceUrl: '#' }
      ]
    },
    {
      id: 'PAY003',
      hotelName: 'City Inn',
      hotelId: 'HOTEL003',
      totalAmount: 3000,
      bookingId: 'BKG003',
      payments: [
        { amount: 1500, date: '2025-10-01', invoiceUrl: '#' }
      ]
    }
  ]);
  const [expandedPayments, setExpandedPayments] = useState({});
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showEditPayment, setShowEditPayment] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentFormData, setPaymentFormData] = useState({
    hotelName: '',
    totalAmount: '',
    amount: '',
    date: '',
    bookingId: ''
  });
  
  const [accountingData, setAccountingData] = useState([
    {
      id: 'ACC001',
      hotel_name: 'Grand Palace Hotel',
      city: 'Jaipur',
      state: 'Rajasthan',
      ota: 'Booking.com',
      booking_date: '2025-11-05',
      check_in_date: '2025-11-10',
      check_out_date: '2025-11-12',
      no_of_rooms: 2,
      no_of_pax: 4,
      guest_name: 'Rajesh Sharma',
      sell_rate: 6000,
      hotel_tax: 500,
      ota_commission: 600,
      gst_on_ota_commission: 108,
      tds: 60,
      tcs: 30,
      pay_to_hotel: 5400,
      payment_received: 5000,
      payment_received_date: '2025-11-15',
      receive_to_grb: 5400,
      grb_commission: 300,
      gst_on_grb_commission: 54,
      setup_adjusted: true,
      to_be_paid_to_hotel: 400,
      booking_id: 'BKG001'
    },
    {
      id: 'ACC002',
      hotel_name: 'Ocean View Resort',
      city: 'Goa',
      state: 'Goa',
      ota: 'Agoda',
      booking_date: '2025-10-02',
      check_in_date: '2025-10-08',
      check_out_date: '2025-10-10',
      no_of_rooms: 3,
      no_of_pax: 6,
      guest_name: 'Meena Verma',
      sell_rate: 9000,
      hotel_tax: 700,
      ota_commission: 900,
      gst_on_ota_commission: 162,
      tds: 90,
      tcs: 45,
      pay_to_hotel: 8300,
      payment_received: 8000,
      payment_received_date: '2025-10-12',
      receive_to_grb: 8300,
      grb_commission: 400,
      gst_on_grb_commission: 72,
      setup_adjusted: false,
      to_be_paid_to_hotel: 300,
      booking_id: 'BKG002'
    },
    {
      id: 'ACC003',
      hotel_name: 'City Inn',
      city: 'Delhi',
      state: 'Delhi',
      ota: 'Expedia',
      booking_date: '2025-10-01',
      check_in_date: '2025-10-05',
      check_out_date: '2025-10-07',
      no_of_rooms: 1,
      no_of_pax: 2,
      guest_name: 'Ankit Singh',
      sell_rate: 3000,
      hotel_tax: 300,
      ota_commission: 300,
      gst_on_ota_commission: 54,
      tds: 30,
      tcs: 15,
      pay_to_hotel: 2700,
      payment_received: 2500,
      payment_received_date: '2025-10-07',
      receive_to_grb: 2700,
      grb_commission: 150,
      gst_on_grb_commission: 27,
      setup_adjusted: true,
      to_be_paid_to_hotel: 200,
      booking_id: 'BKG003'
    }
  ]);
  const [showAddAccounting, setShowAddAccounting] = useState(false);
  const [showEditAccounting, setShowEditAccounting] = useState(false);
  const [selectedAccounting, setSelectedAccounting] = useState(null);
  const [accountingFilters, setAccountingFilters] = useState({
    hotel: '',
    city: '',
    dateFrom: '',
    dateTo: ''
  });
  const [selectedPaymentHotel, setSelectedPaymentHotel] = useState('');
  const [selectedAccountingHotel, setSelectedAccountingHotel] = useState('');
  
  // Advanced Calendar State
  const [expandedRoomType, setExpandedRoomType] = useState(null);
  const [showRatesPopup, setShowRatesPopup] = useState(false);
  const [agodaDeviationRooms, setAgodaDeviationRooms] = useState([]);
  const [ratesFormData, setRatesFormData] = useState({
    checkinDate: '',
    checkoutDate: '',
    baseRate: '',
    updateMode: 'all',  // 'all' | 'room_types' | 'deviation'
    perRoomRates: {},   // { [room_type_id]: '2000' }  (legacy — kept for back-compat)
    // Feb 2026 (rev.5) — richer room_types mode. Every room type has its
    // own bucket with per-rate-plan rates, extra-guest pricing, and
    // per-rate-plan restrictions. Only room types the operator expands +
    // fills get pushed on save.
    rtDetails: {},      // { [rtId]: { ratePlans:{[rpId]:'2000'}, extras:{adult,child1,child2,child3}, restrictions:{[rpId]:{...}} } }
    rtExpanded: {},     // { [rtId]: true }
  });
  const [animatedCells, setAnimatedCells] = useState(new Set());
  // Deferred cell edits for the availability calendar (Feb 20 2026 (p)).
  // Every {rooms|rate|closed} input reads from here first and falls back
  // to the server value. Nothing hits the network until the operator
  // clicks the per-cell Save button below the inputs. Shape:
  //   { [`${roomTypeId}_${date}`]: { available_rooms?, rate?, is_closed? } }
  const [pendingCellEdits, setPendingCellEdits] = useState({});
  
  // Logs State
  const [logs, setLogs] = useState([]);
  const [logsFilter, setLogsFilter] = useState({
    actionType: '',
    property: '',
    dateFrom: '',
    dateTo: ''
  });
  
  // Settings State
  const [settings, setSettings] = useState({
    inclusionRate: 30,
    roomTypeIncrement: 500,
    rateType: 'unit' // 'unit' or 'percentage'
  });
  const [storageMetrics, setStorageMetrics] = useState(null);
  const [loadingStorageMetrics, setLoadingStorageMetrics] = useState(false);
  const [showClearStorageModal, setShowClearStorageModal] = useState(false);
  const [clearStorageOptions, setClearStorageOptions] = useState({
    competitor_data: false,
    audit_logs: false,
    old_uploads: false
  });
  
  // OTA API Integration State
  const [otaApis, setOtaApis] = useState([]);
  const [showAddOTAModal, setShowAddOTAModal] = useState(false);
  const [otaModalStep, setOtaModalStep] = useState(1); // 1: Master Info, 2: Endpoints
  const [otaFormData, setOtaFormData] = useState({
    ota_name: '',
    auth_type: 'API Key',
    api_key: '',
    username: '',
    password: '',
    oauth_token_url: '',
    bearer_token: '',
    description: '',
    endpoints: []
  });
  const [selectedOTA, setSelectedOTA] = useState(null);
  const [showOTADetailModal, setShowOTADetailModal] = useState(false);
  const [testingEndpoint, setTestingEndpoint] = useState(null);
  const [testResults, setTestResults] = useState({});
  
  // Link Property Modal State
  const [showLinkPropertyModal, setShowLinkPropertyModal] = useState(false);
  const [linkPropertyFormData, setLinkPropertyFormData] = useState({
    ota_id: '',
    ota_name: '',
    property_id: '',
    external_property_id: '',
    mapped_endpoints: []
  });
  const [isLinkingProperty, setIsLinkingProperty] = useState(false);
  
  // Hotel Owner Management State
  const [showCreateOwnerModal, setShowCreateOwnerModal] = useState(false);
  const [ownerFormData, setOwnerFormData] = useState({
    hotelName: '',
    hotelId: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [hotelOwners, setHotelOwners] = useState([]);

  // ==================== RATE PLAN STATE ====================
  const [showRatePlanModal, setShowRatePlanModal] = useState(false);
  const [ratePlanProperty, setRatePlanProperty] = useState(null);  // selected property obj
  const [ratePlanGrouped, setRatePlanGrouped] = useState([]);  // [{room_type_id, room_type_name, max_occupancy, rate_plans:[]}]
  const [ratePlanDrafts, setRatePlanDrafts] = useState({});  // {room_type_id: [{code, base_rate}], ...} — unsaved rows
  const [ratePlanSaving, setRatePlanSaving] = useState(false);
  // Calendar tab — rate plan calendar data & expansion state
  const [ratePlanCalendar, setRatePlanCalendar] = useState(null);  // response from /api/rate-plans/calendar
  const [expandedRatePlans, setExpandedRatePlans] = useState(new Set());  // set of rate_plan_ids that are expanded
  // ==================== ROOM TYPE LINKS STATE ====================
  // Link Room Types (Property Settings) — dependent rate cascade config.
  const [roomTypeLinks, setRoomTypeLinks] = useState([]);  // [{property_id, base_room_type_id, links:[...]}]
  // Pending Calendar rate edit awaiting the "update linked?" prompt.
  const [linkPrompt, setLinkPrompt] = useState(null);  // { roomTypeId, ratePlanId, ratePlanCode, occupancy, date, newRate }
  const [linkPromptBusy, setLinkPromptBusy] = useState(false);
  // Calendar occupancy-cell inline editor: explicit edit -> Save/Cancel.
  const [editingRateCell, setEditingRateCell] = useState(null);  // { key, value }
  const [savingRateCell, setSavingRateCell] = useState(null);    // key currently persisting
  // Bulk-rates sidebar — rate plan flow
  const [bulkRatePlanRoomTypeId, setBulkRatePlanRoomTypeId] = useState('');
  const [bulkExpandedRatePlans, setBulkExpandedRatePlans] = useState(new Set());
  const [bulkRatePlanInputs, setBulkRatePlanInputs] = useState({});  // {rate_plan_id: {1: '1200', 2: '1500'}}
  // Feb 28 2026 rev.3 — per-plan locator group override for MMT V3 Scenario 14.
  // {rate_plan_id: 2} → send this plan in <AvailRateUpdate locatorID="2">.
  // Absent / undefined → default locator=1 (grouped with other plans + inventory).
  const [bulkRatePlanLocatorGroups, setBulkRatePlanLocatorGroups] = useState({});
  // Feb 28 2026 rev.4 — per-plan Additional Guest Rate overrides.
  // Shape: {rate_plan_id: {adult_rate, child_rate_age1, child_rate_age2, child_rate_age3}}
  // Presence in this map = "Override" checkbox is TICKED for that plan.
  // Absent = plan inherits Room-Type defaults (bulkExtraGuestInputs[roomId]).
  const [bulkRatePlanExtrasOverrides, setBulkRatePlanExtrasOverrides] = useState({});
  const [bulkExtraGuestInputs, setBulkExtraGuestInputs] = useState({});  // {room_type_id: {extra_adult_rate, extra_child_rate_age1, extra_child_rate_age2}}
  // Per-rate-plan restrictions staged by the "⚙️ Set Rate Restrictions" sub-drawer.
  // Shape: {rate_plan_id: {closed?: bool, min_los?: number, min_advance_booking?: number}}
  const [bulkRatePlanRestrictions, setBulkRatePlanRestrictions] = useState({});
  const [showRestrictionsDrawer, setShowRestrictionsDrawer] = useState(false);
  const [restrictionsDrawerRoomTypeId, setRestrictionsDrawerRoomTypeId] = useState('');
  const [restrictionsDraft, setRestrictionsDraft] = useState({});  // scratch state used ONLY while the sub-drawer is open
  // Feb 28 2026 rev.3 — MMT V3 ARI XML preview modal.
  const [previewXmlModal, setPreviewXmlModal] = useState({ open: false, xml: '', hotel_code: '', warnings: [], locators: [] });
  const [bulkRateStartDate, setBulkRateStartDate] = useState('');
  const [bulkRateEndDate, setBulkRateEndDate] = useState('');

  // ==================== RATE CONFIG STATE (per-room-type increments for $ Rates auto-apply) ====================
  const [rateConfigs, setRateConfigs] = useState([]);  // [{room_type_id, room_type_name, max_occupancy, room_type_increment_*, rate_plan_increment_*, occupancy_increment_*}]
  const [rateConfigsLoading, setRateConfigsLoading] = useState(false);

  // ==================== HIERARCHY STATE (Room Type per-property + Global Rate Plan) ====================
  // roomTypeOrder: [{room_type_id, room_type_name, max_occupancy}] in LOWEST → HIGHEST order for the selected property
  const [roomTypeOrder, setRoomTypeOrder] = useState([]);
  const [roomTypeOrderLoading, setRoomTypeOrderLoading] = useState(false);
  const [roomTypeOrderSaving, setRoomTypeOrderSaving] = useState(false);
  // ratePlanOrder: ["EP","CP","MAP","AP"] global, LOWEST → HIGHEST
  const [ratePlanOrder, setRatePlanOrder] = useState([]);
  const [ratePlanOrderLoading, setRatePlanOrderLoading] = useState(false);
  const [ratePlanOrderSaving, setRatePlanOrderSaving] = useState(false);
  // Local buffer for adding a custom rate-plan code
  const [newRatePlanCode, setNewRatePlanCode] = useState('');

  // ==================== BULK PROPERTY IMPORT STATE ====================
  const [importFile, setImportFile] = useState(null);
  const [importUploading, setImportUploading] = useState(false);
  const [importOta, setImportOta] = useState('mmt');  // 'mmt' | 'agoda' — selected before upload
  const [importJobId, setImportJobId] = useState(null);
  const [importJobStatus, setImportJobStatus] = useState(null);  // { status, total_rows, processed_rows, rows: [...] }
  const importPollRef = useRef(null);
  // City/State bulk update (Settings → All Properties → Upload Excel)
  const [cityStateFile, setCityStateFile] = useState(null);
  const [cityStateUploading, setCityStateUploading] = useState(false);
  const [cityStateSummary, setCityStateSummary] = useState(null);

  // Base Rates import (Settings → All Properties → Import Base Rates)
  const [baseRateFile, setBaseRateFile] = useState(null);
  const [baseRateUploading, setBaseRateUploading] = useState(false);
  const [baseRateResult, setBaseRateResult] = useState(null);

  // Global OTA rate multipliers (Settings → All Properties)
  const [otaMultipliers, setOtaMultipliers] = useState({ mmt: 1, agoda: 1, yatra: 1 });
  const [otaMultSaving, setOtaMultSaving] = useState(false);



  // Property Filter & Notification State
  const [propertyFilter, setPropertyFilter] = useState('all'); // all, active, terminated
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showPropertySubMenu, setShowPropertySubMenu] = useState(false);
  const [showAllPropertiesModal, setShowAllPropertiesModal] = useState(false);
  const [allPropsTab, setAllPropsTab] = useState('properties');  // 'properties' | 'mapping'
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  // "See All Properties" enhancement — dependency-aware review + bulk delete
  const [allPropertiesWithDeps, setAllPropertiesWithDeps] = useState([]);
  const [loadingAllProperties, setLoadingAllProperties] = useState(false);
  const [propSearchQuery, setPropSearchQuery] = useState('');
  const [selectedPropertyIds, setSelectedPropertyIds] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteResults, setBulkDeleteResults] = useState(null);
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetStep, setResetStep] = useState(1); // 1: email, 2: code+password


  // Analytics State
  const [analyticsMetricTab, setAnalyticsMetricTab] = useState('room_nights'); // room_nights, revenue, asp, property_visits, conversion
  const [analyticsData, setAnalyticsData] = useState([]);
  const [allAnalyticsData, setAllAnalyticsData] = useState([]); // Store all data for filtering
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [showAnalyticsUpload, setShowAnalyticsUpload] = useState(false);
  const [showAnalyticsUploadModal, setShowAnalyticsUploadModal] = useState(false);
  const [analyticsViewMode, setAnalyticsViewMode] = useState('both'); // both, competitor, grb
  const [analyticsChartType, setAnalyticsChartType] = useState('line'); // line, bar
  const [analyticsDateFilter, setAnalyticsDateFilter] = useState('all'); // all, 1year, 9months, 6months, 3months, 1month, 15days, 7days
  const [analyticsOTAFilter, setAnalyticsOTAFilter] = useState('MMT'); // MMT, Expedia, Agoda, Booking.com, Airbnb
  const [analyticsUploadOTA, setAnalyticsUploadOTA] = useState('MMT'); // Selected OTA for upload, default MMT
  const [analyticsDataPage, setAnalyticsDataPage] = useState(0); // For pagination/navigation
  const [analyticsPageSize] = useState(10); // Number of records to show at once
  const [analyticsMobileMenuOpen, setAnalyticsMobileMenuOpen] = useState(false); // Mobile three-dot menu
  const [analyticsChartOptionsMenuOpen, setAnalyticsChartOptionsMenuOpen] = useState(false); // Mobile chart options menu
  
  // Dashboard KPIs State
  const [dashboardKPIs, setDashboardKPIs] = useState(null);
  const [dashboardDateRange, setDashboardDateRange] = useState('all');
  
  // Bookings Upload State
  const [showBookingsUploadModal, setShowBookingsUploadModal] = useState(false);
  
  // Promotions State
  const [promotions, setPromotions] = useState([]);
  const [showCreatePromotionModal, setShowCreatePromotionModal] = useState(false);
  const [selectedAGXForView, setSelectedAGXForView] = useState(null);
  const [showAGXDetailModal, setShowAGXDetailModal] = useState(false);
  const [promotionFormData, setPromotionFormData] = useState({
    property_id: '',
    promotion_name: '',
    discount_percentage: '',
    start_date: '',
    end_date: '',
    applicable_room_types: [],
    status: 'active',
    description: ''
  });
  const [showAGXUpload, setShowAGXUpload] = useState(false);

  useEffect(() => {
    fetchProperties();
    fetchNotifications();
  }, []);

  const prevCalendarPropIdRef = useRef(null);
  useEffect(() => {
    if (selectedProperty) {
      // On property CHANGE (not date nav), clear stale calendar/room data so the
      // previous property's grid never flashes while the new data loads.
      if (prevCalendarPropIdRef.current !== selectedProperty.id) {
        prevCalendarPropIdRef.current = selectedProperty.id;
        setCalendarData(null);
        setRoomTypes([]);
        setRatePlanCalendar(null);
      }
      fetchCalendarData();
      fetchBookings();
      fetchAnalytics();
      fetchRoomTypes();
      fetchUpdateLogs();
      fetchRoomTypeLinks(selectedProperty.id);
    }
  }, [selectedProperty, currentDate]);

  // Refetch analytics when OTA filter changes
  useEffect(() => {
    if (selectedProperty) {
      fetchAnalytics();
    }
  }, [analyticsOTAFilter]);

  // Fetch dashboard KPIs when property or date range changes
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchDashboardKPIs();
    }
  }, [selectedProperty, dashboardDateRange, activeTab]);

  useEffect(() => {
    fetchSystemHealth();
    fetchAuditLogs();
    
    // Set up periodic health checks
    const healthInterval = setInterval(() => {
      fetchSystemHealth();
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(healthInterval);
  }, []);

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarOpen', JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  // Bookings - Load on page open, when Bookings tab activates, and (Feb 2026)
  // whenever the user switches the selected property.
  useEffect(() => {
    if (activeTab === 'bookings') {
      fetchGoogleSheetsBookings(1, bookingsLimit);
      fetchBookingsFilterOptions();
      fetchDashboardMetrics();
    }
  }, [activeTab, selectedProperty?.id, allPropertiesMode]);

  // Google Sheets Bookings - Auto-refresh every 5 minutes
  useEffect(() => {
    if (activeTab === 'bookings') {
      const refreshInterval = setInterval(() => {
        fetchGoogleSheetsBookings(bookingsPage, bookingsLimit);
      }, 5 * 60 * 1000); // 5 minutes
      
      return () => clearInterval(refreshInterval);
    }
  }, [activeTab, bookingsPage, bookingsLimit]);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      
      // Close mobile sidebar if resizing from mobile to desktop
      if (width >= 768 && mobileSidebarOpen) {
        setMobileSidebarOpen(false);
      }
      
      // Force 7-day calendar view on mobile
      if (width < 768) {
        setCalendarView('7');
      }
    };

    // Initial check for mobile calendar view
    if (window.innerWidth < 768) {
      setCalendarView('7');
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileSidebarOpen]);

  // ESC key to close sidebar
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (mobileSidebarOpen) {
          setMobileSidebarOpen(false);
        }
        if (sidebarOpen) {
          setSidebarOpen(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [mobileSidebarOpen, sidebarOpen]);

  // Disable body scroll when sidebar is open on mobile/tablet
  useEffect(() => {
    const isMobileOrTablet = window.innerWidth < 1024;
    
    if ((mobileSidebarOpen || (sidebarOpen && isMobileOrTablet))) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileSidebarOpen, sidebarOpen]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  const closeMobileSidebar = () => {
    setMobileSidebarOpen(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Persist for reload restoration (Feb 2026).
    try { localStorage.setItem('activeTab', tab); } catch (_) { /* storage disabled */ }
    // Auto-close mobile sidebar when navigating
    if (window.innerWidth < 769) {
      closeMobileSidebar();
    }
  };

  // Feb 2026 — when the mode changes (portfolio ↔ single-property) the
  // current active tab may no longer be valid in the new mode. Redirect
  // to Dashboard, which is always allowed. This also handles the reload
  // restoration case where the stored tab is invalid for the mode we
  // ended up in.
  useEffect(() => {
    if (!isNavAllowedInMode(activeTab)) {
      setActiveTab('dashboard');
      try { localStorage.setItem('activeTab', 'dashboard'); } catch (_) { /* noop */ }
    }
  }, [allPropertiesMode]);

  // Load global OTA rate multipliers when the admin enters All Properties mode.
  useEffect(() => {
    if (allPropertiesMode && user?.role === 'admin') {
      loadOtaMultipliers();
    }
     
  }, [allPropertiesMode, user?.role]);


  // Fetch tab preferences
  const fetchTabPreferences = async () => {
    try {
      const response = await axios.get(`${API}/users/tab-preferences`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setHiddenTabs(response.data.hidden_tabs || []);
    } catch (error) {
      console.error('Failed to fetch tab preferences:', error);
    }
  };

  // Update tab preferences
  const updateTabPreferences = async (newHiddenTabs) => {
    try {
      await axios.post(`${API}/users/tab-preferences`, newHiddenTabs, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      setHiddenTabs(newHiddenTabs);
      toast.success('Tab preferences updated successfully');
    } catch (error) {
      console.error('Failed to update tab preferences:', error);
      toast.error('Failed to update tab preferences');
    }
  };

  // Toggle tab visibility
  const toggleTabVisibility = (tabId) => {
    let newHiddenTabs;
    if (hiddenTabs.includes(tabId)) {
      newHiddenTabs = hiddenTabs.filter(id => id !== tabId);
    } else {
      newHiddenTabs = [...hiddenTabs, tabId];
    }
    updateTabPreferences(newHiddenTabs);
  };

  // Clear analytics data for specific OTA
  const clearAnalyticsData = async (otaName) => {
    if (!window.confirm(`Are you sure you want to clear all ${otaName} analytics data? This action cannot be undone.`)) {
      return;
    }

    const loadingToast = toast.loading(`Clearing ${otaName} analytics...`);

    try {
      const response = await axios.delete(`${API}/analytics/clear`, {
        params: { ota_name: otaName },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.dismiss(loadingToast);
      toast.success(response.data.message);
      
      // Refresh analytics if currently viewing that OTA
      if (analyticsOTAFilter === otaName) {
        fetchAnalytics();
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Failed to clear analytics:', error);
      toast.error(error.response?.data?.detail || `Failed to clear ${otaName} analytics`);
    }
  };

  const fetchProperties = async () => {
    try {
      // Build URL with status filter if not 'all'
      let url = `${API}/properties`;
      if (propertyFilter !== 'all') {
        url += `?status=${propertyFilter}`;
      }
      
      const response = await axios.get(url);
      setProperties(response.data);
      
      // Set selected property, considering filter
      if (response.data.length > 0) {
        // If current property not in filtered list, select first from list
        const currentPropertyInList = response.data.find(p => p.id === selectedProperty?.id);
        if (!currentPropertyInList) {
          setSelectedProperty(response.data[0]);
        }
      } else {
        setSelectedProperty(null);
      }
    } catch (error) {
      toast.error('Failed to fetch properties');
    } finally {
      // Mark the initial load as complete so the "No Properties Found" empty
      // state only renders once we actually know the account has zero
      // properties (avoids the post-login flash).
      setPropertiesLoaded(true);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setNotifications(response.data || []);
      setUnreadNotifications(response.data?.filter(n => !n.is_read).length || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };


  // Upload Analytics Excel with OTA selection
  const handleAnalyticsUploadSubmit = async (file) => {
    if (!file || !analyticsUploadOTA) {
      toast.error('Please select OTA and file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('ota_name', analyticsUploadOTA);
    formData.append('property_id', selectedProperty.id);

    setLoadingAnalytics(true);
    const loadingToast = toast.loading('Uploading analytics data...');

    try {
      const response = await axios.post(`${API}/analytics/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.dismiss(loadingToast);
      toast.success(`✅ Analytics uploaded: ${response.data.total_records} records for ${analyticsUploadOTA}`);
      
      if (response.data.skipped_hotels && response.data.skipped_hotels.length > 0) {
        toast.warning(`Skipped hotels: ${response.data.skipped_hotels.join(', ')}`);
      }

      // Close modal and reset
      setShowAnalyticsUploadModal(false);
      setAnalyticsUploadOTA('');
      
      // Safe refresh - only fetch once
      await fetchAnalytics();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.response?.data?.detail || 'Failed to upload analytics');
      console.error('Analytics upload error:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Fetch Analytics Data
  const fetchAnalytics = async () => {
    if (!selectedProperty) return;
    
    setLoadingAnalytics(true);
    try {
      // If Agoda is selected, fetch AGX campaign data
      if (analyticsOTAFilter === 'Agoda') {
        await fetchAGXData();
        setAllAnalyticsData([]);
        setAnalyticsData([]);
      } else {
        // Fetch regular analytics for other OTAs
        const params = { ota_name: analyticsOTAFilter };
        
        const response = await axios.get(`${API}/analytics/${selectedProperty.id}`, {
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = response.data.data || [];
        setAllAnalyticsData(data);
        setAnalyticsData(data);
        setAnalyticsDataPage(0);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setAllAnalyticsData([]);
      setAnalyticsData([]);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Filter and paginate analytics data
  const getFilteredAnalyticsData = () => {
    if (!allAnalyticsData || allAnalyticsData.length === 0) return [];
    
    let filtered = [...allAnalyticsData];
    
    // Apply date filter
    if (analyticsDateFilter !== 'all') {
      const now = new Date();
      let startDate;
      
      switch (analyticsDateFilter) {
        case '7days':
          startDate = new Date(now.setDate(now.getDate() - 7));
          break;
        case '15days':
          startDate = new Date(now.setDate(now.getDate() - 15));
          break;
        case '1month':
          startDate = new Date(now.setMonth(now.getMonth() - 1));
          break;
        case '3months':
          startDate = new Date(now.setMonth(now.getMonth() - 3));
          break;
        case '6months':
          startDate = new Date(now.setMonth(now.getMonth() - 6));
          break;
        case '9months':
          startDate = new Date(now.setMonth(now.getMonth() - 9));
          break;
        case '1year':
          startDate = new Date(now.setFullYear(now.getFullYear() - 1));
          break;
        default:
          startDate = null;
      }
      
      if (startDate) {
        filtered = filtered.filter(item => {
          const itemDate = new Date(item.week_start);
          return itemDate >= startDate;
        });
      }
    }
    
    // Apply OTA filter (note: backend stores as 'ota' field regardless of OTA name)
    // This would require backend modification to store OTA name separately
    
    // Apply pagination
    const start = analyticsDataPage * analyticsPageSize;
    const end = start + analyticsPageSize;
    return filtered.slice(start, end);
  };

  // Get date range for dashboard KPIs
  const getDashboardDateRange = () => {
    if (dashboardDateRange === 'all') return null;
    
    const now = new Date();
    const ranges = {
      '7days': new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
      '15days': new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000),
      '30days': new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
      '60days': new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000),
      '90days': new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000),
      '6months': new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000),
      '1year': new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000),
    };
    
    return ranges[dashboardDateRange]?.toISOString().split('T')[0];
  };

  // Fetch Dashboard KPIs (uses grouped/deduplicated bookings metrics)
  const fetchDashboardKPIs = async () => {
    try {
      const fromDate = getDashboardDateRange();
      const params = {};
      if (fromDate) params.from_date = fromDate;
      // Optionally scope to selected property's hotel name(s) if known
      if (selectedProperty?.name) params.hotel_names = selectedProperty.name;
      
      const response = await axios.get(`${API}/bookings/dashboard-metrics`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // /bookings/dashboard-metrics returns { success, metrics: {...} }
      // Adapt to legacy fields used by the Dashboard cards
      const m = response.data?.metrics || {};
      setDashboardKPIs({
        total_bookings: m.total_bookings || 0,
        revenue: m.net_revenue || 0,
        net_revenue: m.net_revenue || 0,
        total_revenue: m.total_revenue || 0,
        avg_adr: m.avg_adr || 0,
        avg_length_of_stay: m.avg_length_of_stay || 0,
        avg_booking_window: m.avg_booking_window || 0,
        cancellation_rate: m.cancellation_rate || 0,
        cancellation_count: m.cancellation_count || 0,
      });
    } catch (error) {
      console.error('Failed to fetch dashboard KPIs:', error);
      setDashboardKPIs(null);
    }
  };

  // ==================== RATE PLAN HELPERS ====================

  // Internal rate-plan codes offered in Property Settings → Add Rate Plan.
  // Storage still uses the stable machine code (`NO_CANCELLATION`); the
  // human label ("No Cancellation") is resolved via RATE_PLAN_LABELS below.
  const RATE_PLAN_CODE_OPTIONS = ['CP', 'EP', 'MAP', 'AP', 'NO_CANCELLATION', 'CUSTOM'];
  const RATE_PLAN_LABELS = {
    EP: 'EP',
    CP: 'CP',
    MAP: 'MAP',
    AP: 'AP',
    NO_CANCELLATION: 'No Cancellation',
    CUSTOM: 'Custom',
  };
  const labelForRatePlanCode = (code) =>
    RATE_PLAN_LABELS[(code || '').toUpperCase()] || code;

  const openRatePlanManager = () => {
    // Pre-select the user's current property if available, else null
    setRatePlanProperty(selectedProperty || (properties && properties[0]) || null);
    setRatePlanDrafts({});
    setShowRatePlanModal(true);
  };

  // Fetch rate plans grouped by room_type for the modal + calendar
  const fetchRatePlansGrouped = async (propertyId) => {
    if (!propertyId) {
      setRatePlanGrouped([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/rate-plans`, {
        params: { property_id: propertyId },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setRatePlanGrouped(res.data?.grouped || []);
    } catch (err) {
      console.error('Failed to fetch rate plans:', err);
      setRatePlanGrouped([]);
      toast.error('Failed to fetch rate plans');
    }
  };

  // Add a draft row (in-memory) to a given room type in the Settings modal
  const addRatePlanDraft = (roomTypeId) => {
    setRatePlanDrafts(prev => ({
      ...prev,
      [roomTypeId]: [...(prev[roomTypeId] || []), { code: 'CP', base_rate: '' }],
    }));
  };

  const updateRatePlanDraft = (roomTypeId, idx, field, value) => {
    setRatePlanDrafts(prev => {
      const arr = [...(prev[roomTypeId] || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...prev, [roomTypeId]: arr };
    });
  };

  const removeRatePlanDraft = (roomTypeId, idx) => {
    setRatePlanDrafts(prev => {
      const arr = [...(prev[roomTypeId] || [])];
      arr.splice(idx, 1);
      return { ...prev, [roomTypeId]: arr };
    });
  };

  // Persist all drafts (new rate plans) for the selected property
  const saveRatePlanDrafts = async () => {
    if (!ratePlanProperty?.id) {
      toast.error('Please select a property');
      return;
    }
    setRatePlanSaving(true);
    const token = localStorage.getItem('token');
    let created = 0;
    let errors = 0;
    try {
      for (const [roomTypeId, drafts] of Object.entries(ratePlanDrafts)) {
        for (const d of drafts) {
          const code = String(d.code || '').trim().toUpperCase();
          const baseRate = parseFloat(d.base_rate || 0);
          if (!code || isNaN(baseRate) || baseRate <= 0) continue;
          // Feb 2026 — Custom rate plans require a display name in addition
          // to the code. Trim whitespace and reject empty/whitespace-only.
          const customName = String(d.custom_name || '').trim();
          if (code === 'CUSTOM' && !customName) {
            errors += 1;
            toast.error('Enter a Custom Rate Plan Name');
            continue;
          }
          try {
            await axios.post(`${API}/rate-plans`, {
              property_id: ratePlanProperty.id,
              room_type_id: roomTypeId,
              code,
              base_rate: baseRate,
              ...(code === 'CUSTOM' ? { name: customName } : {}),
            }, { headers: { Authorization: `Bearer ${token}` } });
            created += 1;
          } catch (err) {
            errors += 1;
            console.error('Rate plan create failed:', err?.response?.data || err);
          }
        }
      }
      if (created > 0) toast.success(`Created ${created} rate plan${created > 1 ? 's' : ''}`);
      if (errors > 0) toast.error(`${errors} rate plan${errors > 1 ? 's' : ''} failed (duplicates or invalid input)`);
      setRatePlanDrafts({});
      await fetchRatePlansGrouped(ratePlanProperty.id);
      // Refresh calendar rate-plan data if on calendar tab
      if (activeTab === 'calendar' && selectedProperty?.id === ratePlanProperty.id) {
        await fetchRatePlanCalendar();
      }
    } finally {
      setRatePlanSaving(false);
    }
  };

  // Update existing rate plan base_rate
  const updateRatePlanBaseRate = async (ratePlanId, newBaseRate) => {
    try {
      await axios.put(`${API}/rate-plans/${ratePlanId}`,
        { base_rate: parseFloat(newBaseRate) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Base rate updated');
      await fetchRatePlansGrouped(ratePlanProperty?.id);
    } catch (err) {
      toast.error('Failed to update base rate');
    }
  };

  // Delete a rate plan
  const deleteRatePlan = async (ratePlanId) => {
    if (!window.confirm('Delete this rate plan and all its date overrides?')) return;
    try {
      await axios.delete(`${API}/rate-plans/${ratePlanId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Rate plan deleted');
      await fetchRatePlansGrouped(ratePlanProperty?.id);
      if (activeTab === 'calendar') await fetchRatePlanCalendar();
    } catch (err) {
      toast.error('Failed to delete rate plan');
    }
  };

  // Fetch rate-plan calendar (used by Calendar tab)
  const fetchRatePlanCalendar = async () => {
    if (!selectedProperty?.id) {
      setRatePlanCalendar(null);
      return;
    }
    const reqPropertyId = selectedProperty.id;
    try {
      const startDate = currentDate.toISOString().split('T')[0];
      const days = parseInt(calendarView || '7', 10);
      const res = await axios.get(`${API}/rate-plans/calendar`, {
        params: { property_id: selectedProperty.id, start_date: startDate, days },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      // Ignore a response whose property is no longer the current selection.
      if (selectedPropertyIdRef.current !== reqPropertyId) return;
      setRatePlanCalendar(res.data);
    } catch (err) {
      console.error('Failed to fetch rate plan calendar:', err);
      if (selectedPropertyIdRef.current !== reqPropertyId) return;
      setRatePlanCalendar(null);
    }
  };

  // Toggle expansion of a rate plan in the Calendar tab
  const toggleRatePlanExpanded = (ratePlanId) => {
    setExpandedRatePlans(prev => {
      const next = new Set(prev);
      if (next.has(ratePlanId)) next.delete(ratePlanId);
      else next.add(ratePlanId);
      return next;
    });
  };

  // Bulk rates sidebar: toggle expand in sidebar context
  const toggleBulkRatePlanExpanded = (ratePlanId) => {
    setBulkExpandedRatePlans(prev => {
      const next = new Set(prev);
      if (next.has(ratePlanId)) next.delete(ratePlanId);
      else next.add(ratePlanId);
      return next;
    });
  };

  // Feb 28 2026 rev.3 — Preview XML: reuse the SAME bulk-action payload
  // shape but hit `/availability/bulk/preview` which returns the exact XML
  // without touching MMT.  Masking is enforced backend-side.
  const previewBulkAriXml = async () => {
    try {
      const ranges = (bulkFormData.dateRanges || []).filter(r => r.from_date && r.to_date);
      if (ranges.length === 0) {
        toast.error('Please add at least one date range');
        return;
      }
      const dow = bulkFormData.daysOfWeek || { sun:true,mon:true,tue:true,wed:true,thu:true,fri:true,sat:true };
      const rangesForApi = ranges.map(r => ({
        from_date: r.from_date, to_date: r.to_date,
        days_of_week: r.days_of_week || dow,
      }));

      const body = {
        property_id:  selectedProperty?.id,
        action:       bulkActionType === 'restrictions' ? 'restrictions' : 'rates',
        room_type_ids: bulkFormData.roomTypeIds || [],
        date_ranges:  rangesForApi,
        days_of_week: dow,
      };

      // Inventory rooms
      const invInputs = bulkFormData.inventory || {};
      const invByRoom = {};
      for (const rtId of (bulkFormData.roomTypeIds || [])) {
        const raw = invInputs[rtId];
        if (raw === undefined || raw === '' || raw === null) continue;
        const n = parseInt(raw, 10);
        if (Number.isFinite(n) && n >= 0) invByRoom[rtId] = n;
      }
      if (Object.keys(invByRoom).length) body.available_rooms_by_room = invByRoom;

      // Rate plans (multi-plan flat rate map from bulkRatePlanInputs)
      const rateMap = bulkRatePlanInputs || {};
      const rate_plans = [];
      for (const [rpId, occMap] of Object.entries(rateMap)) {
        const occupancy_rates = {};
        for (const [occ, rate] of Object.entries(occMap || {})) {
          if (rate === '' || rate == null) continue;
          const n = parseFloat(rate);
          if (Number.isFinite(n)) occupancy_rates[occ] = n;
        }
        if (Object.keys(occupancy_rates).length) rate_plans.push({ rate_plan_id: rpId, occupancy_rates });
      }
      if (rate_plans.length) body.rate_plans = rate_plans;

      // Top-level extras
      const rtId = bulkRatePlanRoomTypeId || (bulkFormData.roomTypeIds && bulkFormData.roomTypeIds[0]);
      const staged = (bulkExtraGuestInputs || {})[rtId] || {};
      const _pf = (v) => { const n = parseFloat(v); return Number.isFinite(n) && n >= 0 ? n : null; };
      if (_pf(staged.extra_adult_rate)      !== null) body.extra_adult_rate      = _pf(staged.extra_adult_rate);
      if (_pf(staged.extra_child_rate_age1) !== null) body.extra_child_rate_age1 = _pf(staged.extra_child_rate_age1);
      if (_pf(staged.extra_child_rate_age2) !== null) body.extra_child_rate_age2 = _pf(staged.extra_child_rate_age2);
      if (_pf(staged.extra_child_rate_age3) !== null) body.extra_child_rate_age3 = _pf(staged.extra_child_rate_age3);

      // Feb 28 2026 rev.4 — per-plan Additional Guest Rate overrides.
      const rate_plan_extras_overrides = [];
      for (const [rpId, ov] of Object.entries(bulkRatePlanExtrasOverrides || {})) {
        const entry = { rate_plan_id: rpId };
        if (_pf(ov.adult_rate)       !== null) entry.adult_rate      = _pf(ov.adult_rate);
        if (_pf(ov.child_rate_age1)  !== null) entry.child_rate_age1 = _pf(ov.child_rate_age1);
        if (_pf(ov.child_rate_age2)  !== null) entry.child_rate_age2 = _pf(ov.child_rate_age2);
        if (_pf(ov.child_rate_age3)  !== null) entry.child_rate_age3 = _pf(ov.child_rate_age3);
        if (Object.keys(entry).length > 1) rate_plan_extras_overrides.push(entry);
      }
      // Multi-room mode: hoist each room's staged extras onto every plan of
      // that room so the Preview reflects what the actual submit would send.
      if ((bulkFormData.roomTypeIds || []).length > 1) {
        for (const rtIdIter of bulkFormData.roomTypeIds) {
          const sg = (bulkExtraGuestInputs || {})[rtIdIter] || {};
          const roomExtras = {};
          if (_pf(sg.extra_adult_rate)      !== null) roomExtras.adult_rate      = _pf(sg.extra_adult_rate);
          if (_pf(sg.extra_child_rate_age1) !== null) roomExtras.child_rate_age1 = _pf(sg.extra_child_rate_age1);
          if (_pf(sg.extra_child_rate_age2) !== null) roomExtras.child_rate_age2 = _pf(sg.extra_child_rate_age2);
          if (_pf(sg.extra_child_rate_age3) !== null) roomExtras.child_rate_age3 = _pf(sg.extra_child_rate_age3);
          if (Object.keys(roomExtras).length === 0) continue;
          const rtRoom = (ratePlanCalendar?.room_types || []).find(r => r.room_type_id === rtIdIter);
          for (const p of (rtRoom?.rate_plans || [])) {
            if (rate_plan_extras_overrides.some(e => e.rate_plan_id === p.id)) continue; // explicit override wins
            rate_plan_extras_overrides.push({ rate_plan_id: p.id, ...roomExtras });
          }
        }
      }
      if (rate_plan_extras_overrides.length) body.rate_plan_extras_overrides = rate_plan_extras_overrides;

      // Staged restrictions
      const _RESTR_KEYS = ['closed','closed_to_arrival','closed_to_departure',
        'min_los','max_los','min_los_stay_based','max_los_stay_based',
        'min_los_arrival_based','max_los_arrival_based',
        'min_advance_booking','max_advance_booking',
        'min_advance_booking_offset','max_advance_booking_offset'];
      const rp_restr = Object.entries(bulkRatePlanRestrictions || {})
        .map(([rate_plan_id, r]) => {
          const out = { rate_plan_id };
          for (const k of _RESTR_KEYS) {
            if (r[k] === undefined || r[k] === '') continue;
            if (['closed','closed_to_arrival','closed_to_departure'].includes(k)) out[k] = !!r[k];
            else { const n = parseInt(r[k], 10); if (Number.isFinite(n)) out[k] = n; }
          }
          return out;
        }).filter(x => Object.keys(x).length > 1);
      if (rp_restr.length) body.rate_plan_restrictions = rp_restr;

      // Feb 28 2026 rev.3 — Scenario 14: per-plan locator override.
      const rate_plan_locators = Object.entries(bulkRatePlanLocatorGroups || {})
        .filter(([, v]) => v === 2)
        .map(([rate_plan_id]) => ({ rate_plan_id, locator_id: 2 }));
      if (rate_plan_locators.length) body.rate_plan_locators = rate_plan_locators;

      const { data } = await axios.post(`${API}/availability/bulk/preview`, body, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setPreviewXmlModal({
        open: true,
        xml: data?.xml || '',
        hotel_code: data?.hotel_code || '',
        warnings: data?.warnings || [],
        locators: data?.locators || [],
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate XML preview');
    }
  };

  // Unified Bulk Rates Apply — MMT CHM V3 cert (Feb 2026):
  // Gathers occupancy inputs across ALL rate plans of the selected room type,
  // plus shared extras (Adult, Child age-1, Child age-2), and submits them in
  // ONE POST /api/availability/bulk call. Backend fans out to per-plan MMT
  // ARI pushes with <AdditionalGuestRate> entries.
  const applyBulkRatesUnified = async () => {
    const ranges = (bulkFormData.dateRanges || []).filter(r => r.from_date && r.to_date);
    if (ranges.length === 0) {
      toast.error('Please add at least one date range');
      return;
    }
    const dow = bulkFormData.daysOfWeek || { sun:true,mon:true,tue:true,wed:true,thu:true,fri:true,sat:true };
    if (!['sun','mon','tue','wed','thu','fri','sat'].some(k => dow[k])) {
      toast.error('Select at least one day of week');
      return;
    }
    // Feb 28 2026 rev.3 — each range MAY carry its own days_of_week map.
    // Fall back to the global template when a range didn't customise its DOW.
    const rangesForApi = ranges.map((r) => ({
      from_date: r.from_date,
      to_date:   r.to_date,
      days_of_week: r.days_of_week || dow,
    }));
    const roomTypeId = bulkRatePlanRoomTypeId || (bulkFormData.roomTypeIds && bulkFormData.roomTypeIds[0]);
    if (!roomTypeId) {
      toast.error('Please select a room type');
      return;
    }
    const rpRoom = (ratePlanCalendar?.room_types || []).find(r => r.room_type_id === roomTypeId);
    const ratePlans = rpRoom?.rate_plans || [];

    // Feb 28 2026 rev.4 — aggregate rate plans across ALL selected room types.
    // Previously scoped to just `bulkRatePlanRoomTypeId`; now the drawer's
    // multi-room tab switches only the visible context, and Apply pulls every
    // plan the user has entered inputs for across every selected room.
    const selectedRoomIds = (bulkFormData.roomTypeIds || []).length
      ? bulkFormData.roomTypeIds
      : [roomTypeId];
    const rate_plans = [];
    const allPlanIdsByRoom = {};
    for (const rtId of selectedRoomIds) {
      const rtRoom = (ratePlanCalendar?.room_types || []).find(r => r.room_type_id === rtId);
      const plansForRoom = rtRoom?.rate_plans || [];
      allPlanIdsByRoom[rtId] = plansForRoom.map(p => p.id);
      for (const rp of plansForRoom) {
        const inputs = bulkRatePlanInputs[rp.id] || {};
        const occupancy_rates = {};
        Object.entries(inputs).forEach(([occ, val]) => {
          const num = parseFloat(val);
          if (!isNaN(num) && num >= 0) occupancy_rates[occ] = num;
        });
        if (Object.keys(occupancy_rates).length > 0) {
          rate_plans.push({ rate_plan_id: rp.id, occupancy_rates });
        }
      }
    }

    // Room-Type default extras — for MULTI-room submits, we fan the defaults
    // out into `rate_plan_extras_overrides` per plan so each MMT <Rate> gets
    // its parent room's defaults. For single-room submits we keep the legacy
    // top-level `extra_*_rate` fields (unchanged backend behaviour).
    const extras = {};
    const parseNum = (v) => {
      const n = parseFloat(v);
      return (!isNaN(n) && n >= 0) ? n : null;
    };
    if (selectedRoomIds.length === 1) {
      const staged = (bulkExtraGuestInputs || {})[roomTypeId] || {};
      const adultN = parseNum(staged.extra_adult_rate);
      const c1N    = parseNum(staged.extra_child_rate_age1);
      const c2N    = parseNum(staged.extra_child_rate_age2);
      const c3N    = parseNum(staged.extra_child_rate_age3);
      if (adultN !== null) extras.extra_adult_rate      = adultN;
      if (c1N    !== null) extras.extra_child_rate_age1 = c1N;
      if (c2N    !== null) extras.extra_child_rate_age2 = c2N;
      if (c3N    !== null) extras.extra_child_rate_age3 = c3N;
    }

    // Build rate_plan_extras_overrides. Sources (precedence):
    //   1. Explicit per-plan override from bulkRatePlanExtrasOverrides
    //      (Override checkbox ticked in the plan's expanded panel).
    //   2. Multi-room mode → hoist each room's staged extras onto every plan
    //      belonging to that room (so per-room defaults reach the correct XML).
    const rate_plan_extras_overrides = [];
    if (selectedRoomIds.length > 1) {
      for (const rtId of selectedRoomIds) {
        const staged = (bulkExtraGuestInputs || {})[rtId] || {};
        const adultN = parseNum(staged.extra_adult_rate);
        const c1N    = parseNum(staged.extra_child_rate_age1);
        const c2N    = parseNum(staged.extra_child_rate_age2);
        const c3N    = parseNum(staged.extra_child_rate_age3);
        if ([adultN, c1N, c2N, c3N].every(v => v === null)) continue;
        for (const planId of (allPlanIdsByRoom[rtId] || [])) {
          rate_plan_extras_overrides.push({
            rate_plan_id: planId,
            ...(adultN !== null ? { adult_rate: adultN } : {}),
            ...(c1N    !== null ? { child_rate_age1: c1N } : {}),
            ...(c2N    !== null ? { child_rate_age2: c2N } : {}),
            ...(c3N    !== null ? { child_rate_age3: c3N } : {}),
          });
        }
      }
    }
    for (const [rpId, ov] of Object.entries(bulkRatePlanExtrasOverrides || {})) {
      const adultN = parseNum(ov.adult_rate);
      const c1N    = parseNum(ov.child_rate_age1);
      const c2N    = parseNum(ov.child_rate_age2);
      const c3N    = parseNum(ov.child_rate_age3);
      if ([adultN, c1N, c2N, c3N].every(v => v === null)) continue;
      // Explicit override supersedes the multi-room default fan-out.
      const existing = rate_plan_extras_overrides.findIndex(e => e.rate_plan_id === rpId);
      const entry = {
        rate_plan_id: rpId,
        ...(adultN !== null ? { adult_rate: adultN } : {}),
        ...(c1N    !== null ? { child_rate_age1: c1N } : {}),
        ...(c2N    !== null ? { child_rate_age2: c2N } : {}),
        ...(c3N    !== null ? { child_rate_age3: c3N } : {}),
      };
      if (existing >= 0) rate_plan_extras_overrides[existing] = entry;
      else rate_plan_extras_overrides.push(entry);
    }


    // Rate + Inventory: collect inventory counts here so the guard below can
    // count them as valid content.
    const invInputsUp = bulkFormData.inventory || {};
    const anyInventoryEntered = (bulkFormData.roomTypeIds || []).some(rtId => {
      const raw = invInputsUp[rtId];
      if (raw === undefined || raw === '' || raw === null) return false;
      const n = parseInt(raw, 10);
      return Number.isFinite(n) && n >= 0;
    });

    // Feb 28 2026 rev.4.1 — count per-plan AdditionalGuestRates overrides as
    // a valid update category. Previously the guard only recognised
    // rate_plans / room-level extras / restrictions / inventory, so a user
    // updating ONLY a Rate Plan's Additional Guest Rate override (Adult=150,
    // Child1=125, Child2=125 without touching occupancy) was blocked at the
    // client with "Enter at least one occupancy rate, extra-guest rate,
    // restriction, or inventory count".
    const anyPerPlanExtraOverride = Object.values(bulkRatePlanExtrasOverrides || {}).some((ov) => {
      if (!ov) return false;
      return ['adult_rate', 'child_rate_age1', 'child_rate_age2', 'child_rate_age3']
        .some((k) => {
          const v = ov[k];
          if (v === undefined || v === null || v === '') return false;
          const n = parseFloat(v);
          return Number.isFinite(n) && n >= 0;
        });
    });

    if (rate_plans.length === 0
        && Object.keys(extras).length === 0
        && Object.keys(bulkRatePlanRestrictions || {}).length === 0
        && !anyInventoryEntered
        && !anyPerPlanExtraOverride) {
      toast.error('Enter at least one occupancy rate, extra-guest rate, restriction, or inventory count');
      return;
    }

    // Optimistic per-plan status pill so the drawer surfaces progress inline.
    rate_plans.forEach(rp => {
      setMmtSyncStatus(prev => ({ ...prev, [rp.rate_plan_id]: { state: 'syncing' } }));
    });

    try {
      // Restrictions payload — only include plans belonging to the selected room type.
      // Feb 28 2026 rev.3 — carry ALL restriction fields (tri-state booleans +
      // stay-based & arrival-based LOS + min/max advance-booking offsets).
      const _RESTR_KEYS = [
        'closed', 'closed_to_arrival', 'closed_to_departure',
        'min_los', 'max_los',
        'min_los_stay_based', 'max_los_stay_based',
        'min_los_arrival_based', 'max_los_arrival_based',
        'min_advance_booking', 'max_advance_booking',
        'min_advance_booking_offset', 'max_advance_booking_offset',
      ];
      // Feb 28 2026 rev.4.2 — Bug 2 fix: include restrictions for rate plans
      // belonging to ANY selected room type (not just the active tab's room).
      // Previously `roomRatePlanIds` was scoped to the active tab, so
      // restrictions configured on plans of the OTHER selected rooms were
      // silently dropped from the payload.
      const allSelectedRoomIds = (bulkFormData.roomTypeIds || []).length
        ? bulkFormData.roomTypeIds
        : [roomTypeId];
      const roomRatePlanIds = new Set();
      for (const rtId of allSelectedRoomIds) {
        const rtRoom = (ratePlanCalendar?.room_types || []).find(r => r.room_type_id === rtId);
        for (const rp of (rtRoom?.rate_plans || [])) roomRatePlanIds.add(rp.id);
      }
      const rate_plan_restrictions = Object.entries(bulkRatePlanRestrictions || {})
        .filter(([rpId, r]) => roomRatePlanIds.has(rpId)
                              && _RESTR_KEYS.some(k => r[k] !== undefined))
        .map(([rate_plan_id, r]) => {
          const out = { rate_plan_id };
          for (const k of _RESTR_KEYS) {
            if (r[k] === undefined || r[k] === '') continue;
            if (k === 'closed' || k === 'closed_to_arrival' || k === 'closed_to_departure') {
              out[k] = !!r[k];
            } else {
              const n = parseInt(r[k], 10);
              if (Number.isFinite(n)) out[k] = n;
            }
          }
          return out;
        });

      // Feb 28 2026 — Rate + Inventory combined submission: piggyback the
      // inventory counts entered per room onto this rates call so backend
      // persists inventory AND emits a single <AvailRateUpdate> block with
      // BOTH <Availability> and <Rate> children (MMT CHM V3 Extranet parity).
      const inv_by_room = {};
      if (bulkActionType === 'rate_inventory') {
        const invInputs = bulkFormData.inventory || {};
        for (const rtId of (bulkFormData.roomTypeIds || [])) {
          const raw = invInputs[rtId];
          if (raw === undefined || raw === '' || raw === null) continue;
          const n = parseInt(raw, 10);
          if (Number.isFinite(n) && n >= 0) inv_by_room[rtId] = n;
        }
      }

      // Jun 2026 — Yatra Extend Inventory (§5.5) separate EA/EC guest rates,
      // collected from the primary room's staged inputs. Only filled values.
      const _yEA = {}; const _yEC = {};
      const _stagedY = (bulkExtraGuestInputs || {})[roomTypeId] || {};
      [['yatra_ea1', 'EA1'], ['yatra_ea2', 'EA2'], ['yatra_ea3', 'EA3']].forEach(([k, t]) => {
        const n = parseNum(_stagedY[k]); if (n !== null) _yEA[t] = n;
      });
      [['yatra_ec1', 'EC1'], ['yatra_ec2', 'EC2'], ['yatra_ec3', 'EC3']].forEach(([k, t]) => {
        const n = parseNum(_stagedY[k]); if (n !== null) _yEC[t] = n;
      });

      const body = {
        property_id:   selectedProperty?.id,
        action:        'rates',
        // Feb 28 2026 rev.4 — for BOTH 'rates' and 'rate_inventory', include
        // every room the user checked so multi-room bulk submits reach all
        // selected rooms in one XML.
        room_type_ids: (bulkFormData.roomTypeIds && bulkFormData.roomTypeIds.length
          ? bulkFormData.roomTypeIds
          : [roomTypeId]),
        date_ranges:   rangesForApi,
        days_of_week:  dow,
        ...(rate_plans.length ? { rate_plans } : {}),
        ...extras,
        ...(rate_plan_restrictions.length ? { rate_plan_restrictions } : {}),
        ...(rate_plan_extras_overrides.length ? { rate_plan_extras_overrides } : {}),
        ...(Object.keys(inv_by_room).length ? { available_rooms_by_room: inv_by_room } : {}),
        ...(Object.keys(_yEA).length ? { yatra_extra_adult_rates: _yEA } : {}),
        ...(Object.keys(_yEC).length ? { yatra_extra_child_rates: _yEC } : {}),
        // Feb 28 2026 rev.3 — Scenario 14: per-plan locator override.
        ...((() => {
          const locators = Object.entries(bulkRatePlanLocatorGroups || {})
            .filter(([, v]) => v === 2)
            .map(([rate_plan_id]) => ({ rate_plan_id, locator_id: 2 }));
          return locators.length ? { rate_plan_locators: locators } : {};
        })()),
      };
      const { data: r } = await axios.post(`${API}/availability/bulk`, body, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success(`Applied: ${r?.writes || 0} rows across ${rate_plans.length} rate plan${rate_plans.length === 1 ? '' : 's'}`);
      // Agoda parallel push summary — completely decoupled from MMT toast.
      if (r?.agoda_summary && (r.agoda_summary.attempted_pushes || 0) > 0) {
        const s = r.agoda_summary;
        if (s.success_pushes > 0) {
          toast.success(
            `Agoda: ${s.success_pushes}/${s.attempted_pushes} pushes across `
            + `${s.success_date_count} date${s.success_date_count === 1 ? '' : 's'}, `
            + `${s.success_room_type_count} room type${s.success_room_type_count === 1 ? '' : 's'}`
          );
        }
        if (s.failed_pushes > 0) {
          toast.error(`Agoda: ${s.failed_pushes} push${s.failed_pushes === 1 ? '' : 'es'} failed — check Agoda audit for details`);
        }
      }
      // Feb 20 2026 (p): expose the full batch to the Agoda Payload viewer.
      if (Array.isArray(r?.agoda_sync_all) && r.agoda_sync_all.length > 0) {
        setAgodaBatchPayloads(r.agoda_sync_all);
        setAgodaLastPayload(r.agoda_sync_all[r.agoda_sync_all.length - 1]);
      }
      // Jun 2026 — Yatra / Travelguru push summary + payload viewer capture.
      captureYatraSync(r);
      if (r?.yatra_summary && (r.yatra_summary.attempted_pushes || 0) > 0) {
        const y = r.yatra_summary;
        if (y.success_pushes > 0) toast.success(`Yatra: ${y.success_pushes}/${y.attempted_pushes} update${y.attempted_pushes === 1 ? '' : 's'} sent — click Yatra Payload to inspect`);
        if (y.failed_pushes > 0) toast.error(`Yatra: ${y.failed_pushes} update${y.failed_pushes === 1 ? '' : 's'} failed — open Yatra Payload for the response`);
      }
      handleMmtSyncResult(r?.mmt_sync);

      // Fan the mmt_sync_all[] out into per-plan status pills for the drawer.
      const perPlan = r?.mmt_sync_all || [];
      const nowIso = new Date().toISOString();
      perPlan.forEach(entry => {
        const state = entry?.status === 'success'
          ? 'success'
          : (entry?.status === 'transport_error' ? 'failed' : 'failed');
        setMmtSyncStatus(prev => ({ ...prev, [entry.rate_plan_id]: {
          state,
          http_status: entry?.http_code,
          errors: entry?.error ? [{ code: '', description: entry.error, known: false, locator_id: null }] : [],
          warnings: [],
          per_locator: [],
          at: nowIso,
        }}));
      });

      // Clear staged extra inputs so re-opening the drawer feels fresh.
      setBulkExtraGuestInputs(prev => ({ ...prev, [roomTypeId]: {} }));
      // Feb 27 2026: refresh BOTH the rate-plan grid (occupancy rates +
      // per-plan restriction highlights) AND the main calendar grid (base
      // rate / inventory rows) so restrictions apply visually without a
      // page reload.
      try { await fetchRatePlanCalendar(); } catch { /* noop */ }
      try { await fetchRoomTypes(); } catch { /* noop */ }
      try { await fetchCalendarData(); } catch { /* noop */ }
      closeBulkSidebar();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to apply bulk rates';
      toast.error(detail);
      rate_plans.forEach(rp => {
        setMmtSyncStatus(prev => ({ ...prev, [rp.rate_plan_id]: {
          state: 'failed',
          errors: [{ code: '', description: detail, known: false, locator_id: null }],
          at: new Date().toISOString(),
        }}));
      });
    }
  };

  // Apply bulk rate update for an expanded rate plan.
  // MMT CHM V3 (Feb 2026): routes through /api/availability/bulk so the
  // MMT XML carries multi-<DateRange> (dow attrs) + multi-occupancy
  // <PerOccupancyRates> in a single call.
  const applyBulkRatePlanUpdate = async (ratePlanId) => {
    // Prefer multi-range + DOW from bulkFormData; fall back to legacy
    // single check-in/out when the drawer wasn't opened via handleBulkAction.
    const legacyStart = bulkRateStartDate || bulkFormData?.checkInDate;
    const legacyEnd = bulkRateEndDate || bulkFormData?.checkOutDate;
    let ranges = (bulkFormData.dateRanges || []).filter(r => r.from_date && r.to_date);
    if (ranges.length === 0 && legacyStart && legacyEnd) {
      ranges = [{ from_date: legacyStart, to_date: legacyEnd }];
    }
    if (ranges.length === 0) {
      toast.error('Please add at least one date range');
      return;
    }
    const dow = bulkFormData.daysOfWeek || { sun:true,mon:true,tue:true,wed:true,thu:true,fri:true,sat:true };
    if (!['sun','mon','tue','wed','thu','fri','sat'].some(k => dow[k])) {
      toast.error('Select at least one day of week');
      return;
    }
    const inputs = bulkRatePlanInputs[ratePlanId] || {};
    const occupancy_rates = {};
    Object.entries(inputs).forEach(([occ, val]) => {
      const num = parseFloat(val);
      if (!isNaN(num) && num >= 0) occupancy_rates[occ] = num;
    });
    if (Object.keys(occupancy_rates).length === 0) {
      toast.error('Enter at least one occupancy rate');
      return;
    }
    setMmtSyncStatus(prev => ({ ...prev, [ratePlanId]: { state: 'syncing' } }));
    try {
      const { data: r } = await axios.post(`${API}/availability/bulk`, {
        property_id: selectedProperty?.id,
        action: 'rates',
        rate_plan_id: ratePlanId,
        occupancy_rates,
        date_ranges: ranges,
        days_of_week: dow,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success(`Rates updated for ${r?.writes || 0} rows`);
      await fetchRatePlanCalendar();

      const sync = r?.mmt_sync;
      handleMmtSyncResult(sync);
      const state = !sync ? 'skipped'
        : sync.status === 'success' ? 'success'
        : sync.status === 'transport_error' ? 'failed' : 'failed';
      setMmtSyncStatus(prev => ({ ...prev, [ratePlanId]: {
        state,
        http_status: sync?.http_code,
        errors: sync?.error ? [{ code: '', description: sync.error, known: false, locator_id: null }] : [],
        warnings: [],
        per_locator: [],
        at: new Date().toISOString(),
      } }));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to apply bulk rates';
      toast.error(detail);
      setMmtSyncStatus(prev => ({ ...prev, [ratePlanId]: {
        state: 'failed',
        errors: [{ code: '', description: detail, known: false, locator_id: null }],
        at: new Date().toISOString(),
      } }));
    }
  };

  // Per-rate-plan MMT sync status. Shape: { <rate_plan_id>: { state, overall_status, http_status, errors[], warnings[], per_locator[], at } }
  const [mmtSyncStatus, setMmtSyncStatus] = useState({});

  // ---- MMT credential test (Property Settings) ----
  const [mmtTestResult, setMmtTestResult] = useState({});   // { [property_id]: {verdict,...} }
  const [mmtTestInputs, setMmtTestInputs] = useState({});   // { [property_id]: { bearer, channel, showManual } }
  const testMmtCredentials = async (propertyId) => {
    setPsSaving(prev => ({ ...prev, [`test-${propertyId}`]: true }));
    setMmtTestResult(prev => ({ ...prev, [propertyId]: null }));
    const inputs = mmtTestInputs[propertyId] || {};
    const payload = { property_id: propertyId };
    if (inputs.bearer && inputs.bearer.trim()) payload.bearer_token_override = inputs.bearer.trim();
    if (inputs.channel && inputs.channel.trim()) payload.channel_token_override = inputs.channel.trim();
    try {
      const { data: r } = await axios.post(`${API}/mmt/ari/test-credentials`, payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setMmtTestResult(prev => ({ ...prev, [propertyId]: r }));
      if (r.verdict === 'credentials_ok') toast.success(`MMT credentials OK (HTTP ${r.http_status}, ${r.duration_ms}ms)`);
      else if (r.verdict === 'credentials_rejected') toast.error(`MMT rejected credentials (HTTP ${r.http_status})`);
      else toast.warning(`MMT unreachable: ${r.transport_error || 'no response'}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Test failed';
      setMmtTestResult(prev => ({ ...prev, [propertyId]: { verdict: 'unreachable', errors: [{ description: detail }], note: '' } }));
      toast.error(`MMT test failed: ${detail}`);
    } finally {
      setPsSaving(prev => ({ ...prev, [`test-${propertyId}`]: false }));
    }
  };
  // ---- OTA Room Mapping (Property Settings → OTA Mapping section) ----
  // Reads /api/ota-mappings/{property_id} and lets the operator map
  // OTA room types (MMT + Agoda) to internal room types via a
  // dropdown. Only writes room_type_id — never touches ARI-facing
  // fields. Isolated from MMT / Agoda ARI code.
  const [otaMapData, setOtaMapData] = useState({});      // { [propertyId]: {internal_room_types, mmt_rooms, agoda_rooms} }
  const [otaMapLoading, setOtaMapLoading] = useState({});
  const [otaMapDrafts, setOtaMapDrafts] = useState({});  // { [propertyId]: { [ota_room_code]: internal_room_type_id } }
  const [otaMapSaving, setOtaMapSaving] = useState({});  // { [key]: bool }

  const fetchOtaMappings = async (propertyId) => {
    if (!propertyId) return;
    setOtaMapLoading(prev => ({ ...prev, [propertyId]: true }));
    try {
      const res = await axios.get(`${API}/ota-mappings/${propertyId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setOtaMapData(prev => ({ ...prev, [propertyId]: res.data }));
    } catch (err) {
      // Silent on non-admin / not-found so the section can hide gracefully.
      setOtaMapData(prev => ({ ...prev, [propertyId]: null }));
    } finally {
      setOtaMapLoading(prev => ({ ...prev, [propertyId]: false }));
    }
  };

  const saveOtaRoomMapping = async (propertyId, ota, otaRoomCode) => {
    const draftKey = `${ota}:${otaRoomCode}`;
    const draft = (otaMapDrafts[propertyId] || {})[draftKey];
    // undefined draft → user didn't change anything; use the currently-stored value.
    const bucket = ota === 'mmt' ? (otaMapData[propertyId]?.mmt_rooms || [])
                                 : (otaMapData[propertyId]?.agoda_rooms || []);
    const existing = bucket.find(r => r.ota_room_code === otaRoomCode);
    const internalRtId = draft === undefined ? (existing?.internal_room_type_id ?? '') : draft;

    const savingKey = `${propertyId}:${draftKey}`;
    setOtaMapSaving(prev => ({ ...prev, [savingKey]: true }));
    try {
      await axios.post(`${API}/ota-mappings/room`,
        {
          property_id:            propertyId,
          ota:                    ota,
          ota_room_code:          otaRoomCode,
          internal_room_type_id:  internalRtId || '',
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      toast.success(`${ota.toUpperCase()} room mapping saved`);
      await fetchOtaMappings(propertyId);
      // Clear the draft so the next render reads the fresh server value.
      setOtaMapDrafts(prev => {
        const next = { ...(prev[propertyId] || {}) };
        delete next[draftKey];
        return { ...prev, [propertyId]: next };
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to save ${ota.toUpperCase()} mapping`);
    } finally {
      setOtaMapSaving(prev => ({ ...prev, [savingKey]: false }));
    }
  };
  // ---- OTA Code Catalog (Property Settings → Room & Rate Codes) ----
  const [bulkRateOpen, setBulkRateOpen] = useState(false);
  // Additive, suggestion-only. Feeds the OTA Mapping dropdowns and never
  // mutates existing mapping rows.
  const [otaCodeCatalog, setOtaCodeCatalog] = useState({}); // { [pid]: { mmt:{room,rate_plan}, agoda:{...}, yatra:{...} } }
  const fetchOtaCodeCatalog = async (propertyId) => {
    try {
      const res = await axios.get(`${API}/ota-code-catalog/${propertyId}`, { headers: authHeaders() });
      setOtaCodeCatalog(prev => ({ ...prev, [propertyId]: res.data?.catalog || {} }));
    } catch (err) {
      // non-fatal — suggestions simply won't pre-populate
    }
  };
  const addOtaCode = async (propertyId, ota, kind, code, name) => {
    try {
      await axios.post(`${API}/ota-code-catalog`,
        { property_id: propertyId, ota, kind, code, name: name || '' }, { headers: authHeaders() });
      await fetchOtaCodeCatalog(propertyId);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to add code'); }
  };
  const updateOtaCode = async (propertyId, entryId, patch) => {
    try {
      await axios.put(`${API}/ota-code-catalog/${entryId}`, patch, { headers: authHeaders() });
      await fetchOtaCodeCatalog(propertyId);
      toast.success('Code updated');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to update code'); }
  };
  const deleteOtaCode = async (propertyId, entryId) => {
    try {
      await axios.delete(`${API}/ota-code-catalog/${entryId}`, { headers: authHeaders() });
      await fetchOtaCodeCatalog(propertyId);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to delete code'); }
  };
  // Catalog codes for a given (ota, kind) → [{id,code,name}].
  const catalogCodes = (propertyId, ota, kind) =>
    (((otaCodeCatalog[propertyId] || {})[ota] || {})[kind]) || [];



  // Feb 2026 — Central OTA Mapping section pre-fetches MMT (via property
  // settings load), Agoda, and Yatra data when Property Settings mounts so
  // the row-per-internal-item table always has fresh columns. Adding a new
  // OTA later just needs another entry in OTA_REGISTRY below.
  useEffect(() => {
    if (activeTab === 'property-settings' && selectedProperty?.id && user?.role === 'admin') {
      fetchOtaMappings(selectedProperty.id);
      if (!otaCodeCatalog[selectedProperty.id]) {
        fetchOtaCodeCatalog(selectedProperty.id);
      }
      if (!agodaData[selectedProperty.id] && !agodaLoading[selectedProperty.id]) {
        fetchAgodaMappings(selectedProperty.id);
      }
      if (!yatraData[selectedProperty.id] && !yatraLoading[selectedProperty.id]) {
        fetchYatraSettings(selectedProperty.id);
      }
    }
    // eslint-disable-next-line
  }, [activeTab, selectedProperty?.id, user?.role]);

  // OTA registry drives the central mapping table's columns. Adding a
  // future OTA (Expedia, Airbnb, Cleartrip...) is a pure additive change
  // here — the JSX iterates over this list.
  const OTA_REGISTRY = [
    { key: 'mmt',   label: 'MMT',   accent: 'text-green-700 bg-green-50 border-green-200' },
    { key: 'agoda', label: 'Agoda', accent: 'text-red-700   bg-red-50   border-red-200'   },
    { key: 'yatra', label: 'Yatra', accent: 'text-blue-700  bg-blue-50  border-blue-200'  },
  ];

  // Save extra-adult / extra-child surcharges on the room type. Uses the
  // existing PUT /room-types/{id} endpoint (extended Feb 2026) so it does NOT
  // touch any MMT / Agoda sync path.
  const applyExtraGuestRates = async (roomTypeId) => {
    const staged = (bulkExtraGuestInputs || {})[roomTypeId] || {};
    const patch = {};
    if (staged.extra_adult_rate !== undefined && staged.extra_adult_rate !== '')
      patch.extra_adult_rate = parseFloat(staged.extra_adult_rate);
    if (staged.extra_child_rate !== undefined && staged.extra_child_rate !== '')
      patch.extra_child_rate = parseFloat(staged.extra_child_rate);
    if (Object.keys(patch).length === 0) return;
    try {
      await axios.put(`${API}/room-types/${roomTypeId}`, patch, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success('Extra-guest rates saved');
      setBulkExtraGuestInputs(prev => ({ ...prev, [roomTypeId]: {} }));
      try { await fetchRoomTypes(); } catch { /* noop */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save extra-guest rates');
    }
  };

  const setMmtTestInput = (propertyId, field, value) => {
    setMmtTestInputs(prev => ({ ...prev, [propertyId]: { ...(prev[propertyId] || {}), [field]: value } }));
  };

  // ---- MMT egress IP check (for MMT whitelisting) ----
  // Global, not per-property, because the egress IP is a property of the backend server.
  const [mmtEgressChecking, setMmtEgressChecking] = useState(false);
  const [mmtEgressResult, setMmtEgressResult] = useState(null); // { egress_ip, environment_hint, checked_at, sources, note } or { error }
  const checkMmtEgressIp = async () => {
    setMmtEgressChecking(true);
    setMmtEgressResult(null);
    try {
      const { data } = await axios.get(`${API}/mmt/egress-check`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setMmtEgressResult(data);
      if (data?.egress_ip) {
        toast.success(`Egress IP: ${data.egress_ip}`);
        try { await navigator.clipboard.writeText(data.egress_ip); } catch { /* clipboard may be blocked */ }
      } else {
        toast.warning('Could not determine egress IP from any source');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Egress check failed';
      setMmtEgressResult({ error: detail });
      toast.error(`Egress check failed: ${detail}`);
    } finally {
      setMmtEgressChecking(false);
    }
  };

  // Re-fetch grouped rate plans whenever the modal opens or property changes
  useEffect(() => {
    if (showRatePlanModal && ratePlanProperty?.id) {
      fetchRatePlansGrouped(ratePlanProperty.id);
    }
  }, [showRatePlanModal, ratePlanProperty?.id]);

  // Auto-fetch rate plan calendar when entering Calendar tab
  useEffect(() => {
    if (activeTab === 'calendar' && selectedProperty?.id) {
      fetchRatePlanCalendar();
    }
  }, [activeTab, selectedProperty?.id, currentDate, calendarView]);

  // ==================== PROPERTY SETTINGS (OTA MAPPING) STATE ====================
  const [propertySettingsData, setPropertySettingsData] = useState({ properties: [] });
  const [psExpandedProperty, setPsExpandedProperty] = useState(null);  // property id currently open
  const [psExpandedRoomType, setPsExpandedRoomType] = useState(null);  // room type id currently open
  const [psRevealedTokens, setPsRevealedTokens] = useState({});  // { [property_id]: '<plaintext>' }
  // Local input buffers so admin can edit before Save without firing API per keystroke
  const [psPropertyInputs, setPsPropertyInputs] = useState({});  // { [property_id]: { ota_property_code, bearer_token } }
  const [psRoomInputs, setPsRoomInputs] = useState({});          // { [room_type_id]: { ota_room_code } }
  const [psRatePlanInputs, setPsRatePlanInputs] = useState({});  // { [rate_plan_id]: { ota_rate_plan_code } }
  const [psSaving, setPsSaving] = useState({});  // tracks per-entity save spinners

  // ==================== RATE CONFIG HELPERS ====================
  const fetchRateConfigs = async (propertyId) => {
    if (!propertyId) {
      setRateConfigs([]);
      return;
    }
    try {
      setRateConfigsLoading(true);
      const res = await axios.get(`${API}/rate-configs`, {
        params: { property_id: propertyId },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setRateConfigs(res.data?.configs || []);
    } catch (err) {
      console.error('Failed to fetch rate configs:', err);
      setRateConfigs([]);
    } finally {
      setRateConfigsLoading(false);
    }
  };

  const updateRateConfigLocal = (roomTypeId, field, value) => {
    setRateConfigs(prev => prev.map(c => c.room_type_id === roomTypeId ? { ...c, [field]: value } : c));
  };

  const saveRateConfig = async (roomTypeId) => {
    const cfg = rateConfigs.find(c => c.room_type_id === roomTypeId);
    if (!cfg || !selectedProperty?.id) return;
    try {
      await axios.put(`${API}/rate-configs`, {
        property_id: selectedProperty.id,
        room_type_id: roomTypeId,
        room_type_increment_type: cfg.room_type_increment_type || 'fixed',
        room_type_increment_value: parseFloat(cfg.room_type_increment_value || 0),
        rate_plan_increment_type: cfg.rate_plan_increment_type || 'fixed',
        rate_plan_increment_value: parseFloat(cfg.rate_plan_increment_value || 0),
        occupancy_increment_type: cfg.occupancy_increment_type || 'fixed',
        occupancy_increment_value: parseFloat(cfg.occupancy_increment_value || 0),
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success(`Rate config saved for ${cfg.room_type_name}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save rate config';
      toast.error(msg);
    }
  };

  // ==================== HIERARCHY HELPERS ====================
  const fetchRoomTypeOrder = async (propertyId) => {
    if (!propertyId) { setRoomTypeOrder([]); return; }
    try {
      setRoomTypeOrderLoading(true);
      const res = await axios.get(`${API}/properties/${propertyId}/room-type-order`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setRoomTypeOrder(res.data?.order || []);
    } catch (err) {
      console.error('Failed to fetch room type order:', err);
      setRoomTypeOrder([]);
    } finally {
      setRoomTypeOrderLoading(false);
    }
  };

  const moveRoomTypeOrder = (idx, direction) => {
    setRoomTypeOrder(prev => {
      const next = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const saveRoomTypeOrder = async () => {
    if (!selectedProperty?.id || roomTypeOrder.length === 0) return;
    try {
      setRoomTypeOrderSaving(true);
      await axios.put(`${API}/properties/${selectedProperty.id}/room-type-order`, {
        order: roomTypeOrder.map(r => r.room_type_id),
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Room type hierarchy saved (Lowest → Highest)');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save room type hierarchy');
    } finally {
      setRoomTypeOrderSaving(false);
    }
  };

  const fetchRatePlanOrder = async () => {
    try {
      setRatePlanOrderLoading(true);
      const res = await axios.get(`${API}/settings/rate-plan-order`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setRatePlanOrder(res.data?.order || []);
    } catch (err) {
      console.error('Failed to fetch rate plan order:', err);
      setRatePlanOrder([]);
    } finally {
      setRatePlanOrderLoading(false);
    }
  };

  const moveRatePlanOrder = (idx, direction) => {
    setRatePlanOrder(prev => {
      const next = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const removeRatePlanFromOrder = (code) => {
    setRatePlanOrder(prev => prev.filter(c => c !== code));
  };

  const addRatePlanToOrder = () => {
    const code = (newRatePlanCode || '').trim().toUpperCase();
    if (!code) return;
    setRatePlanOrder(prev => prev.includes(code) ? prev : [...prev, code]);
    setNewRatePlanCode('');
  };

  const saveRatePlanOrder = async () => {
    if (ratePlanOrder.length === 0) {
      toast.error('Add at least one rate plan code');
      return;
    }
    try {
      setRatePlanOrderSaving(true);
      await axios.put(`${API}/settings/rate-plan-order`, {
        order: ratePlanOrder,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Global rate plan hierarchy saved (Lowest → Highest)');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save rate plan hierarchy');
    } finally {
      setRatePlanOrderSaving(false);
    }
  };

  // ==================== BULK PROPERTY IMPORT HELPERS ====================
  const fetchImportJobStatus = async (jobId) => {
    try {
      const res = await axios.get(`${API}/import/properties/${jobId}/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setImportJobStatus(res.data);
      if (res.data?.status === 'completed' || res.data?.status === 'failed') {
        if (importPollRef.current) {
          clearInterval(importPollRef.current);
          importPollRef.current = null;
        }
        // Refresh property list so newly-imported properties appear in the dropdowns.
        try { fetchProperties(); } catch (_) { /* non-fatal */ }
      }
    } catch (err) {
      console.error('Failed to fetch import job status:', err);
    }
  };

  const startPollingImportJob = (jobId) => {
    // Clear any prior interval
    if (importPollRef.current) {
      clearInterval(importPollRef.current);
      importPollRef.current = null;
    }
    // Prime immediately, then every 3 seconds.
    fetchImportJobStatus(jobId);
    importPollRef.current = setInterval(() => fetchImportJobStatus(jobId), 3000);
  };

  const uploadImportExcel = async () => {
    if (!importFile) {
      toast.error('Choose an Excel file first');
      return;
    }
    try {
      setImportUploading(true);
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('ota', importOta);
      const res = await axios.post(`${API}/import/properties/excel`, fd, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          // Let axios set the multipart boundary automatically.
        },
      });
      const jobId = res.data?.job_id;
      if (!jobId) throw new Error('No job_id returned');
      setImportJobId(jobId);
      setImportJobStatus({ status: 'pending', total_rows: res.data?.total_rows || 0,
                           processed_rows: 0, rows: [] });
      toast.success(`Import queued (${res.data?.total_rows} rows). Polling status…`);
      startPollingImportJob(jobId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to upload Excel');
    } finally {
      setImportUploading(false);
    }
  };

  const uploadCityStateExcel = async () => {
    if (!cityStateFile) {
      toast.error('Choose an Excel file first');
      return;
    }
    try {
      setCityStateUploading(true);
      setCityStateSummary(null);
      const fd = new FormData();
      fd.append('file', cityStateFile);
      const res = await axios.post(`${API}/properties/bulk-update-location`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setCityStateSummary(res.data);
      toast.success(`${res.data.properties_updated} propert${res.data.properties_updated === 1 ? 'y' : 'ies'} updated`);
      if (fetchProperties) fetchProperties();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to upload Excel');
    } finally {
      setCityStateUploading(false);
    }
  };

  const loadOtaMultipliers = async () => {
    try {
      const res = await axios.get(`${API}/settings/ota-rate-multipliers`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.data?.multipliers) setOtaMultipliers(res.data.multipliers);
    } catch (e) { /* non-fatal */ }
  };

  const saveOtaMultipliers = async () => {
    try {
      setOtaMultSaving(true);
      const body = {
        mmt: Number(otaMultipliers.mmt) || 1,
        agoda: Number(otaMultipliers.agoda) || 1,
        yatra: Number(otaMultipliers.yatra) || 1,
      };
      const res = await axios.put(`${API}/settings/ota-rate-multipliers`, body, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setOtaMultipliers(res.data.multipliers);
      toast.success('OTA rate multipliers saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save multipliers');
    } finally { setOtaMultSaving(false); }
  };

  const uploadBaseRatesExcel = async () => {
    if (!baseRateFile) {
      toast.error('Choose an .xlsx file first');
      return;
    }
    try {
      setBaseRateUploading(true);
      setBaseRateResult(null);
      const fd = new FormData();
      fd.append('file', baseRateFile);
      const res = await axios.post(`${API}/rate-plans/import-base-rates-excel`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setBaseRateResult(res.data);
      const s = res.data?.summary || {};
      toast.success(`${s.matched_rows || 0} row(s) matched · ${s.total_dates_set || 0} date(s) set`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to import base rates');
    } finally {
      setBaseRateUploading(false);
    }
  };



  const downloadImportTemplate = async () => {
    try {
      const res = await axios.get(`${API}/import/properties/template.xlsx`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { ota: importOta },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const link = document.createElement('a');
      link.href = url;
      link.download = importOta === 'mmt' ? 'property_import_template.xlsx' : `property_import_template_${importOta}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to download template');
    }
  };

  const resetImportJob = () => {
    if (importPollRef.current) {
      clearInterval(importPollRef.current);
      importPollRef.current = null;
    }
    setImportFile(null);
    setImportJobId(null);
    setImportJobStatus(null);
  };

  // Cleanup poller on unmount
  useEffect(() => {
    return () => {
      if (importPollRef.current) {
        clearInterval(importPollRef.current);
        importPollRef.current = null;
      }
    };
  }, []);

  // Auto-fetch rate configs when entering Property Settings tab or changing
  // property. Rate Configuration lives under Property Settings (moved Feb
  // 2026 — it is property-scoped, not global). Room Type Hierarchy also
  // lives under Property Settings.
  useEffect(() => {
    if (user?.role === 'admin' && selectedProperty?.id) {
      if (activeTab === 'property-settings') {
        fetchRateConfigs(selectedProperty.id);
        fetchRoomTypeOrder(selectedProperty.id);
        fetchRatePlansGrouped(selectedProperty.id);
        fetchRoomTypeLinks(selectedProperty.id);
      }
    }
  }, [activeTab, selectedProperty?.id, user?.role]);

  // Global rate plan hierarchy — fetched once when entering Settings (not property-scoped)
  useEffect(() => {
    if (activeTab === 'settings' && user?.role === 'admin') {
      fetchRatePlanOrder();
    }
  }, [activeTab, user?.role]);


  // ==================== PROPERTY SETTINGS (OTA MAPPING) HELPERS ====================

  const fetchPropertySettings = async () => {
    try {
      const res = await axios.get(`${API}/property-settings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setPropertySettingsData(res.data || { properties: [] });
    } catch (err) {
      console.error('Failed to fetch property settings:', err);
      setPropertySettingsData({ properties: [] });
    }
  };

  useEffect(() => {
    if (activeTab === 'property-settings' && user?.role === 'admin') {
      fetchPropertySettings();
    }
  }, [activeTab, user?.role]);

  const psGetPropertyInput = (propertyId, ota = 'mmt', field) => {
    const local = psPropertyInputs[propertyId];
    if (local && local[field] !== undefined) return local[field];
    const prop = (propertySettingsData.properties || []).find(p => p.id === propertyId);
    if (field === 'ota_property_code') return prop?.ota_mappings?.[ota]?.ota_property_code || '';
    if (field === 'bearer_token') return '';
    return '';
  };

  const psSavePropertyMapping = async (propertyId, ota = 'mmt') => {
    const local = psPropertyInputs[propertyId] || {};
    const body = {
      property_id: propertyId,
      ota,
      ota_property_code: local.ota_property_code !== undefined ? local.ota_property_code : undefined,
    };
    if (local.bearer_token) body.bearer_token = local.bearer_token;
    setPsSaving(prev => ({ ...prev, [`prop-${propertyId}`]: true }));
    try {
      await axios.put(`${API}/property-settings/property`, body, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success('MMT property mapping saved');
      setPsPropertyInputs(prev => ({ ...prev, [propertyId]: { ...prev[propertyId], bearer_token: '' } }));
      setPsRevealedTokens(prev => ({ ...prev, [propertyId]: undefined }));
      await fetchPropertySettings();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save property mapping');
    } finally {
      setPsSaving(prev => ({ ...prev, [`prop-${propertyId}`]: false }));
    }
  };

  const psRevealToken = async (propertyId, ota = 'mmt') => {
    if (psRevealedTokens[propertyId]) {
      setPsRevealedTokens(prev => ({ ...prev, [propertyId]: undefined }));
      return;
    }
    try {
      const res = await axios.get(`${API}/property-settings/reveal-token`, {
        params: { property_id: propertyId, ota },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setPsRevealedTokens(prev => ({ ...prev, [propertyId]: res.data?.bearer_token || '' }));
    } catch (err) {
      toast.error('Failed to reveal token');
    }
  };

  const psSaveRoomMapping = async (roomTypeId, ota = 'mmt') => {
    const code = (psRoomInputs[roomTypeId] || {}).ota_room_code;
    setPsSaving(prev => ({ ...prev, [`room-${roomTypeId}`]: true }));
    try {
      await axios.put(`${API}/property-settings/room`,
        { room_type_id: roomTypeId, ota, ota_room_code: code },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('MMT room mapping saved');
      await fetchPropertySettings();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save room mapping');
    } finally {
      setPsSaving(prev => ({ ...prev, [`room-${roomTypeId}`]: false }));
    }
  };

  const psSaveRatePlanMapping = async (ratePlanId, ota = 'mmt') => {
    const code = (psRatePlanInputs[ratePlanId] || {}).ota_rate_plan_code;
    setPsSaving(prev => ({ ...prev, [`rp-${ratePlanId}`]: true }));
    try {
      await axios.put(`${API}/property-settings/rate-plan`,
        { rate_plan_id: ratePlanId, ota, ota_rate_plan_code: code },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('MMT rate-plan mapping saved');
      await fetchPropertySettings();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save rate-plan mapping');
    } finally {
      setPsSaving(prev => ({ ...prev, [`rp-${ratePlanId}`]: false }));
    }
  };

  // ==================== AGODA MAPPINGS (Property Settings → Agoda tab) ====================
  // Feb 2026: Isolated from MMT. Reads/writes only ota="agoda" rows via /api/agoda/*.
  const [psActiveOtaTab, setPsActiveOtaTab] = useState({}); // { [propertyId]: 'mmt' | 'agoda' | 'yatra' }
  const [agodaData, setAgodaData] = useState({});           // { [propertyId]: <response> }
  const [agodaLoading, setAgodaLoading] = useState({});
  const [agodaSaving, setAgodaSaving] = useState({});       // { [key]: bool }
  const [agodaInputs, setAgodaInputs] = useState({});       // per-property form buffers

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchAgodaMappings = async (propertyId) => {
    if (!propertyId) return;
    setAgodaLoading(prev => ({ ...prev, [propertyId]: true }));
    try {
      const res = await axios.get(`${API}/agoda/mappings/${propertyId}`, { headers: authHeaders() });
      setAgodaData(prev => ({ ...prev, [propertyId]: res.data }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load Agoda mappings');
    } finally {
      setAgodaLoading(prev => ({ ...prev, [propertyId]: false }));
    }
  };

  const saveAgodaPropertyCode = async (propertyId) => {
    const code = (agodaInputs[propertyId] || {}).agoda_property_code || '';
    const currency = (agodaInputs[propertyId] || {}).agoda_currency || (agodaData[propertyId]?.agoda_currency || 'INR');
    setAgodaSaving(prev => ({ ...prev, [`prop-code-${propertyId}`]: true }));
    try {
      await axios.post(`${API}/agoda/property-code`,
        { property_id: propertyId, agoda_property_code: code, agoda_currency: currency },
        { headers: authHeaders() });
      toast.success('Agoda Property ID saved');
      await fetchAgodaMappings(propertyId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save Agoda Property ID');
    } finally {
      setAgodaSaving(prev => ({ ...prev, [`prop-code-${propertyId}`]: false }));
    }
  };

  const saveAgodaRoomMapping = async (propertyId, form) => {
    const key = `room-${form.agoda_room_id || 'new'}`;
    setAgodaSaving(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post(`${API}/agoda/room-mapping`,
        { property_id: propertyId, ...form },
        { headers: authHeaders() });
      toast.success('Agoda room mapping saved');
      await fetchAgodaMappings(propertyId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save Agoda room mapping');
    } finally {
      setAgodaSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const saveAgodaRatePlanMapping = async (propertyId, form) => {
    const key = `rp-${form.agoda_rate_plan_id || 'new'}`;
    setAgodaSaving(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post(`${API}/agoda/rate-plan-mapping`,
        { property_id: propertyId, ...form },
        { headers: authHeaders() });
      toast.success('Agoda rate plan mapping saved');
      await fetchAgodaMappings(propertyId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save Agoda rate plan mapping');
    } finally {
      setAgodaSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const saveAgodaProductMapping = async (propertyId, form) => {
    const key = `prod-${form.agoda_room_id || ''}-${form.agoda_rate_plan_id || ''}`;
    setAgodaSaving(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post(`${API}/agoda/product-mapping`,
        { property_id: propertyId, ...form },
        { headers: authHeaders() });
      toast.success('Agoda product mapping saved');
      await fetchAgodaMappings(propertyId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save Agoda product mapping');
    } finally {
      setAgodaSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const toggleAgodaMappingActive = async (propertyId, kind, id, active) => {
    const path = kind === 'room' ? 'room-mapping'
               : kind === 'rp' ? 'rate-plan-mapping'
               : 'product-mapping';
    setAgodaSaving(prev => ({ ...prev, [`act-${id}`]: true }));
    try {
      await axios.patch(`${API}/agoda/${path}/${id}/activate`,
        { active },
        { headers: authHeaders() });
      await fetchAgodaMappings(propertyId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update status');
    } finally {
      setAgodaSaving(prev => ({ ...prev, [`act-${id}`]: false }));
    }
  };

  const deleteAgodaMapping = async (propertyId, kind, id) => {
    const path = kind === 'room' ? 'room-mapping'
               : kind === 'rp' ? 'rate-plan-mapping'
               : 'product-mapping';
    if (!window.confirm('Delete this Agoda mapping? This does not affect MMT mappings.')) return;
    try {
      await axios.delete(`${API}/agoda/${path}/${id}`, { headers: authHeaders() });
      toast.success('Agoda mapping deleted');
      await fetchAgodaMappings(propertyId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete mapping');
    }
  };

  const syncAgodaGetProduct = async (propertyId) => {
    setAgodaSaving(prev => ({ ...prev, [`sync-${propertyId}`]: true }));
    try {
      const res = await axios.post(`${API}/agoda/sync-getproduct/${propertyId}`, {}, { headers: authHeaders() });
      const d = res.data || {};
      toast.success(`Synced from Agoda: ${d.rooms_synced || 0} rooms · ${d.rate_plans_synced || 0} rate plans · ${d.products_synced || 0} products`);
      await fetchAgodaMappings(propertyId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Agoda sync failed');
    } finally {
      setAgodaSaving(prev => ({ ...prev, [`sync-${propertyId}`]: false }));
    }
  };

  // Refetch button (Property Settings): re-run the Add-Property-via-OTA fetch
  // for a chosen OTA, using credentials stored in that property's settings.
  const [refetchState, setRefetchState] = useState({});  // {propertyId: {ota, busy}}
  const refetchOtaNow = async (propertyId, ota) => {
    if (!propertyId || !ota) return;
    setRefetchState(prev => ({ ...prev, [propertyId]: { ota, busy: true } }));
    try {
      // Verify stored credentials exist before running the fetch.
      const st = await axios.get(`${API}/properties/${propertyId}/ota-creds-status`, { headers: authHeaders() });
      if (!st?.data?.[ota]) {
        toast.error('Please set OTA details first');
        return;
      }
      if (ota === 'agoda') {
        const res = await axios.post(`${API}/agoda/sync-getproduct/${propertyId}`, {}, { headers: authHeaders() });
        const d = res.data || {};
        toast.success(`Agoda refetch: ${d.rooms_synced || 0} rooms · ${d.rate_plans_synced || 0} rate plans · ${d.products_synced || 0} products`);
        await fetchAgodaMappings(propertyId);
      } else if (ota === 'mmt') {
        const res = await axios.post(`${API}/properties/${propertyId}/mmt/resync-listing`, {}, { headers: authHeaders() });
        const d = res.data || {};
        toast.success(`MMT refetch: ${d.rooms_synced || 0} rooms · ${d.rate_plans_synced || 0} rate plans (added ${d.rooms_added || 0} rooms · ${d.rate_plans_added || 0} rate plans)`);
        if (typeof fetchProperties === 'function') { try { await fetchProperties(); } catch (e) {} }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Refetch failed');
    } finally {
      setRefetchState(prev => ({ ...prev, [propertyId]: { ota, busy: false } }));
    }
  };

  // Whenever a property card enters the Agoda tab, load its Agoda data once.
  useEffect(() => {
    const propertyId = selectedProperty?.id;
    if (!propertyId) return;
    if ((psActiveOtaTab[propertyId] || 'mmt') === 'agoda' && !agodaData[propertyId] && !agodaLoading[propertyId]) {
      fetchAgodaMappings(propertyId);
    }
  }, [psActiveOtaTab, selectedProperty?.id]);

  const renderAgodaMappings = (property) => {
    const data = agodaData[property.id];
    const isLoading = agodaLoading[property.id];
    const inputs = agodaInputs[property.id] || {};
    const setPI = (patch) => setAgodaInputs(prev => ({ ...prev, [property.id]: { ...(prev[property.id] || {}), ...patch } }));
    const internalRTs = data?.internal_room_types || [];
    const internalRPs = data?.internal_rate_plans || [];

    return (
      <div className="space-y-4" data-testid={`agoda-section-${property.id}`}>
        {/* Agoda Property ID */}
        <div className="bg-red-50/40 border border-red-100 rounded-lg p-4">
          <div className="text-sm font-semibold text-gray-800 mb-2 flex items-center justify-between">
            <span>Agoda Property Configuration</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => syncAgodaGetProduct(property.id)}
              disabled={!!agodaSaving[`sync-${property.id}`] || !data?.agoda_property_code}
              data-testid={`agoda-sync-btn-${property.id}`}
              title={!data?.agoda_property_code ? 'Save Agoda Property ID first' : 'Pull rooms, rate plans and product mappings from Agoda GetProduct'}
            >
              {agodaSaving[`sync-${property.id}`] ? 'Syncing…' : 'Sync from Agoda'}
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Agoda Property ID</label>
              <div className="flex gap-2">
                <Input
                  value={inputs.agoda_property_code !== undefined ? inputs.agoda_property_code : (data?.agoda_property_code || '')}
                  onChange={(e) => setPI({ agoda_property_code: e.target.value })}
                  placeholder="e.g. 5950803"
                  className="text-sm"
                  data-testid={`agoda-property-code-input-${property.id}`}
                />
                <Button
                  size="sm"
                  onClick={() => saveAgodaPropertyCode(property.id)}
                  disabled={!!agodaSaving[`prop-code-${property.id}`]}
                  className="bg-gradient-to-r from-red-500 to-rose-600 text-white"
                  data-testid={`agoda-property-code-save-${property.id}`}
                >
                  {agodaSaving[`prop-code-${property.id}`] ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Agoda Currency</label>
              <Select
                value={inputs.agoda_currency !== undefined ? inputs.agoda_currency : (data?.agoda_currency || 'INR')}
                onValueChange={(v) => setPI({ agoda_currency: v })}
              >
                <SelectTrigger className="text-sm" data-testid={`agoda-currency-${property.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['INR','USD','EUR','GBP','AED','SGD','THB','JPY','AUD'].map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-[11px] text-gray-500 mt-2">
            Sensitive credentials (client secret, access token) live in the OTA Credentials screen and are not shown here. New Agoda mappings default to <strong>Inactive</strong>.
          </div>
        </div>

        {isLoading && <div className="text-sm text-gray-500">Loading Agoda mappings…</div>}

        {/* Room Type Mappings */}
        <div className="border rounded-lg p-4 bg-white" data-testid={`agoda-room-mappings-${property.id}`}>
          <div className="text-sm font-semibold text-gray-800 mb-3">Agoda Room Type Mappings</div>
          {(data?.room_mappings || []).length === 0 ? (
            <div className="text-xs text-gray-500 italic mb-3">No Agoda room mappings yet. Use "Sync from Agoda" or add manually below.</div>
          ) : (
            <div className="overflow-x-auto mb-3">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-2 py-1 font-medium">Agoda Room Name</th>
                    <th className="text-left px-2 py-1 font-medium">Agoda Room ID</th>
                    <th className="text-left px-2 py-1 font-medium">Internal Room Type</th>
                    <th className="text-left px-2 py-1 font-medium">Status</th>
                    <th className="text-left px-2 py-1 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.room_mappings || []).map(rm => (
                    <tr key={rm.id} className="border-t" data-testid={`agoda-room-row-${rm.id}`}>
                      <td className="px-2 py-2">{rm.agoda_room_name || <span className="text-gray-400">—</span>}</td>
                      <td className="px-2 py-2 font-mono">{rm.agoda_room_id}</td>
                      <td className="px-2 py-2">
                        <Select
                          value={rm.internal_room_type_id || ''}
                          onValueChange={(v) => saveAgodaRoomMapping(property.id, {
                            agoda_room_id: rm.agoda_room_id,
                            agoda_room_name: rm.agoda_room_name,
                            internal_room_type_id: v || null,
                            active: rm.active,
                          })}
                        >
                          <SelectTrigger className="h-8 text-xs w-52" data-testid={`agoda-room-internal-${rm.id}`}>
                            <SelectValue placeholder="Not mapped" />
                          </SelectTrigger>
                          <SelectContent>
                            {internalRTs.map(rt => (
                              <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => toggleAgodaMappingActive(property.id, 'room', rm.id, !rm.active)}
                          disabled={!!agodaSaving[`act-${rm.id}`]}
                          className={`px-2 py-0.5 rounded text-[11px] font-medium ${rm.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
                          data-testid={`agoda-room-active-toggle-${rm.id}`}
                        >
                          {rm.active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => deleteAgodaMapping(property.id, 'room', rm.id)}
                          className="text-red-600 hover:underline text-[11px]"
                          data-testid={`agoda-room-delete-${rm.id}`}
                        >Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Manual add */}
          <div className="border-t pt-3 mt-2">
            <div className="text-xs font-medium text-gray-700 mb-2">Add Agoda Room Mapping</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <Input
                placeholder="Agoda Room ID"
                value={inputs.new_room_id || ''}
                onChange={(e) => setPI({ new_room_id: e.target.value })}
                className="text-xs"
                data-testid={`agoda-new-room-id-${property.id}`}
              />
              <Input
                placeholder="Agoda Room Name"
                value={inputs.new_room_name || ''}
                onChange={(e) => setPI({ new_room_name: e.target.value })}
                className="text-xs"
                data-testid={`agoda-new-room-name-${property.id}`}
              />
              <Select
                value={inputs.new_room_internal || ''}
                onValueChange={(v) => setPI({ new_room_internal: v })}
              >
                <SelectTrigger className="h-9 text-xs" data-testid={`agoda-new-room-internal-${property.id}`}>
                  <SelectValue placeholder="Internal Room Type (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {internalRTs.map(rt => (
                    <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={async () => {
                  if (!inputs.new_room_id) return toast.error('Agoda Room ID is required');
                  await saveAgodaRoomMapping(property.id, {
                    agoda_room_id: inputs.new_room_id,
                    agoda_room_name: inputs.new_room_name || '',
                    internal_room_type_id: inputs.new_room_internal || null,
                    active: true,
                  });
                  setPI({ new_room_id: '', new_room_name: '', new_room_internal: '' });
                }}
                className="bg-red-500 hover:bg-red-600 text-white text-xs"
                data-testid={`agoda-new-room-add-${property.id}`}
              >Add</Button>
            </div>
          </div>
        </div>

        {/* Rate Plan Mappings */}
        <div className="border rounded-lg p-4 bg-white" data-testid={`agoda-rp-mappings-${property.id}`}>
          <div className="text-sm font-semibold text-gray-800 mb-3">Agoda Rate Plan Mappings</div>
          {(data?.rate_plan_mappings || []).length === 0 ? (
            <div className="text-xs text-gray-500 italic mb-3">No Agoda rate plan mappings yet.</div>
          ) : (
            <div className="overflow-x-auto mb-3">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-2 py-1 font-medium">Agoda Rate Plan Name</th>
                    <th className="text-left px-2 py-1 font-medium">Agoda Rate Plan ID</th>
                    <th className="text-left px-2 py-1 font-medium">Internal Rate Plan</th>
                    <th className="text-left px-2 py-1 font-medium">Status</th>
                    <th className="text-left px-2 py-1 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.rate_plan_mappings || []).map(rpm => (
                    <tr key={rpm.id} className="border-t" data-testid={`agoda-rp-row-${rpm.id}`}>
                      <td className="px-2 py-2">{rpm.agoda_rate_plan_name || <span className="text-gray-400">—</span>}</td>
                      <td className="px-2 py-2 font-mono">{rpm.agoda_rate_plan_id}</td>
                      <td className="px-2 py-2">
                        <Select
                          value={rpm.internal_rate_plan_id || ''}
                          onValueChange={(v) => saveAgodaRatePlanMapping(property.id, {
                            agoda_rate_plan_id: rpm.agoda_rate_plan_id,
                            agoda_rate_plan_name: rpm.agoda_rate_plan_name,
                            internal_rate_plan_id: v || null,
                            active: rpm.active,
                          })}
                        >
                          <SelectTrigger className="h-8 text-xs w-52" data-testid={`agoda-rp-internal-${rpm.id}`}>
                            <SelectValue placeholder="Not mapped" />
                          </SelectTrigger>
                          <SelectContent>
                            {internalRPs.map(rp => (
                              <SelectItem key={rp.id} value={rp.id}>{rp.code} — {rp.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => toggleAgodaMappingActive(property.id, 'rp', rpm.id, !rpm.active)}
                          className={`px-2 py-0.5 rounded text-[11px] font-medium ${rpm.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
                          data-testid={`agoda-rp-active-toggle-${rpm.id}`}
                        >
                          {rpm.active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => deleteAgodaMapping(property.id, 'rp', rpm.id)}
                          className="text-red-600 hover:underline text-[11px]"
                          data-testid={`agoda-rp-delete-${rpm.id}`}
                        >Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="border-t pt-3 mt-2">
            <div className="text-xs font-medium text-gray-700 mb-2">Add Agoda Rate Plan Mapping</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <Input
                placeholder="Agoda Rate Plan ID"
                value={inputs.new_rp_id || ''}
                onChange={(e) => setPI({ new_rp_id: e.target.value })}
                className="text-xs"
                data-testid={`agoda-new-rp-id-${property.id}`}
              />
              <Input
                placeholder="Agoda Rate Plan Name"
                value={inputs.new_rp_name || ''}
                onChange={(e) => setPI({ new_rp_name: e.target.value })}
                className="text-xs"
                data-testid={`agoda-new-rp-name-${property.id}`}
              />
              <Select
                value={inputs.new_rp_internal || ''}
                onValueChange={(v) => setPI({ new_rp_internal: v })}
              >
                <SelectTrigger className="h-9 text-xs" data-testid={`agoda-new-rp-internal-${property.id}`}>
                  <SelectValue placeholder="Internal Rate Plan (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {internalRPs.map(rp => (
                    <SelectItem key={rp.id} value={rp.id}>{rp.code} — {rp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={async () => {
                  if (!inputs.new_rp_id) return toast.error('Agoda Rate Plan ID is required');
                  await saveAgodaRatePlanMapping(property.id, {
                    agoda_rate_plan_id: inputs.new_rp_id,
                    agoda_rate_plan_name: inputs.new_rp_name || '',
                    internal_rate_plan_id: inputs.new_rp_internal || null,
                    active: true,
                  });
                  setPI({ new_rp_id: '', new_rp_name: '', new_rp_internal: '' });
                }}
                className="bg-red-500 hover:bg-red-600 text-white text-xs"
                data-testid={`agoda-new-rp-add-${property.id}`}
              >Add</Button>
            </div>
          </div>
        </div>

        {/* Product Mappings */}
        <div className="border rounded-lg p-4 bg-white" data-testid={`agoda-product-mappings-${property.id}`}>
          <div className="text-sm font-semibold text-gray-800 mb-3">Agoda Product Mappings (Room ↔ Rate Plan)</div>
          <div className="text-[11px] text-gray-500 mb-3">
            Defines which Agoda rate plans apply to which Agoda room. Required later by Agoda ARI.
          </div>
          {(data?.product_mappings || []).length === 0 ? (
            <div className="text-xs text-gray-500 italic mb-3">No product mappings yet.</div>
          ) : (
            <div className="overflow-x-auto mb-3">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-2 py-1 font-medium">Agoda Room ID</th>
                    <th className="text-left px-2 py-1 font-medium">Agoda Rate Plan ID</th>
                    <th className="text-left px-2 py-1 font-medium">Status</th>
                    <th className="text-left px-2 py-1 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.product_mappings || []).map(pm => (
                    <tr key={pm.id} className="border-t" data-testid={`agoda-prod-row-${pm.id}`}>
                      <td className="px-2 py-2 font-mono">{pm.agoda_room_id}</td>
                      <td className="px-2 py-2 font-mono">{pm.agoda_rate_plan_id}</td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => toggleAgodaMappingActive(property.id, 'prod', pm.id, !pm.active)}
                          className={`px-2 py-0.5 rounded text-[11px] font-medium ${pm.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
                          data-testid={`agoda-prod-active-toggle-${pm.id}`}
                        >
                          {pm.active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => deleteAgodaMapping(property.id, 'prod', pm.id)}
                          className="text-red-600 hover:underline text-[11px]"
                          data-testid={`agoda-prod-delete-${pm.id}`}
                        >Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="border-t pt-3 mt-2">
            <div className="text-xs font-medium text-gray-700 mb-2">Add Product Mapping</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <Select
                value={inputs.new_prod_room || ''}
                onValueChange={(v) => setPI({ new_prod_room: v })}
              >
                <SelectTrigger className="h-9 text-xs" data-testid={`agoda-new-prod-room-${property.id}`}>
                  <SelectValue placeholder="Select Agoda Room" />
                </SelectTrigger>
                <SelectContent>
                  {(data?.room_mappings || []).map(rm => (
                    <SelectItem key={rm.id} value={rm.agoda_room_id}>{rm.agoda_room_id} — {rm.agoda_room_name || '—'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={inputs.new_prod_rp || ''}
                onValueChange={(v) => setPI({ new_prod_rp: v })}
              >
                <SelectTrigger className="h-9 text-xs" data-testid={`agoda-new-prod-rp-${property.id}`}>
                  <SelectValue placeholder="Select Agoda Rate Plan" />
                </SelectTrigger>
                <SelectContent>
                  {(data?.rate_plan_mappings || []).map(rpm => (
                    <SelectItem key={rpm.id} value={rpm.agoda_rate_plan_id}>{rpm.agoda_rate_plan_id} — {rpm.agoda_rate_plan_name || '—'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={async () => {
                  if (!inputs.new_prod_room || !inputs.new_prod_rp) return toast.error('Both Room and Rate Plan are required');
                  await saveAgodaProductMapping(property.id, {
                    agoda_room_id: inputs.new_prod_room,
                    agoda_rate_plan_id: inputs.new_prod_rp,
                    active: true,
                  });
                  setPI({ new_prod_room: '', new_prod_rp: '' });
                }}
                className="bg-red-500 hover:bg-red-600 text-white text-xs"
                data-testid={`agoda-new-prod-add-${property.id}`}
              >Add</Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== YATRA / TRAVELGURU MAPPINGS (Property Settings → Yatra tab) ====================
  // Feb 2026: Strictly additive. Reads/writes only the new yatra_* collections
  // via /api/yatra/*. Never touches MMT or Agoda state or endpoints.
  const [yatraData, setYatraData] = useState({});
  const [yatraLoading, setYatraLoading] = useState({});
  const [yatraSaving, setYatraSaving] = useState({});
  const [yatraInputs, setYatraInputs] = useState({});
  // Global (backend-level) Yatra credentials — shared by ALL properties.
  const [yatraCreds, setYatraCreds] = useState(null);          // {has_credentials, username}
  const [yatraCredsInput, setYatraCredsInput] = useState({ username: '', password: '' });
  const [yatraCredsLoading, setYatraCredsLoading] = useState(false);
  const [yatraCredsSaving, setYatraCredsSaving] = useState(false);

  const fetchYatraSettings = async (propertyId) => {
    if (!propertyId) return;
    setYatraLoading(prev => ({ ...prev, [propertyId]: true }));
    try {
      const res = await axios.get(`${API}/yatra/property-settings`, {
        params: { property_id: propertyId },
        headers: authHeaders(),
      });
      setYatraData(prev => ({ ...prev, [propertyId]: res.data }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load Yatra mappings');
    } finally {
      setYatraLoading(prev => ({ ...prev, [propertyId]: false }));
    }
  };

  // Auto-fetch when the property card switches to the Yatra tab.
  useEffect(() => {
    const propertyId = selectedProperty?.id;
    if (!propertyId) return;
    if (psActiveOtaTab[propertyId] === 'yatra') {
      if (!yatraData[propertyId] && !yatraLoading[propertyId]) fetchYatraSettings(propertyId);
      if (yatraCreds === null && !yatraCredsLoading) fetchYatraCredentials();
    }
  }, [psActiveOtaTab, selectedProperty?.id]);

  const saveYatraProperty = async (propertyId) => {
    const buf = yatraInputs[propertyId] || {};
    if (!buf.yatra_hotel_code || !buf.yatra_hotel_code.trim()) {
      toast.error('Yatra Hotel Code is required');
      return;
    }
    setYatraSaving(prev => ({ ...prev, [`prop-${propertyId}`]: true }));
    try {
      await axios.put(`${API}/yatra/property`,
        {
          property_id:      propertyId,
          yatra_hotel_code: buf.yatra_hotel_code,
          active:           buf.active !== false,
        },
        { headers: authHeaders() });
      toast.success('Yatra property config saved');
      await fetchYatraSettings(propertyId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save Yatra property config');
    } finally {
      setYatraSaving(prev => ({ ...prev, [`prop-${propertyId}`]: false }));
    }
  };

  // ---- Global (backend-level) Yatra credentials — shared by ALL properties.
  const fetchYatraCredentials = async () => {
    setYatraCredsLoading(true);
    try {
      const res = await axios.get(`${API}/yatra/credentials`, { headers: authHeaders() });
      setYatraCreds(res.data);
      setYatraCredsInput({ username: res.data?.username || '', password: '' });
    } catch (err) {
      // Non-admins get 403 — silently ignore; the card is admin-only anyway.
    } finally {
      setYatraCredsLoading(false);
    }
  };

  const saveYatraCredentials = async () => {
    if (!yatraCredsInput.username || !yatraCredsInput.username.trim()) {
      toast.error('Yatra username is required');
      return;
    }
    if (!yatraCreds?.has_credentials && !yatraCredsInput.password?.trim()) {
      toast.error('Yatra password is required');
      return;
    }
    setYatraCredsSaving(true);
    try {
      await axios.put(`${API}/yatra/credentials`,
        {
          username: yatraCredsInput.username.trim(),
          ...(yatraCredsInput.password?.trim() ? { password: yatraCredsInput.password.trim() } : {}),
        },
        { headers: authHeaders() });
      toast.success('Yatra integration credentials saved');
      await fetchYatraCredentials();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save Yatra credentials');
    } finally {
      setYatraCredsSaving(false);
    }
  };

  const saveYatraRoomMapping = async (propertyId, form) => {
    const key = `room-${form.internal_room_type_id || 'new'}`;
    setYatraSaving(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post(`${API}/yatra/room`,
        { property_id: propertyId, ...form },
        { headers: authHeaders() });
      toast.success('Yatra room mapping saved');
      await fetchYatraSettings(propertyId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save Yatra room mapping');
    } finally {
      setYatraSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const deleteYatraRoomMapping = async (propertyId, mappingId) => {
    if (!window.confirm('Delete this Yatra room mapping?')) return;
    try {
      await axios.delete(`${API}/yatra/room/${mappingId}`, { headers: authHeaders() });
      toast.success('Yatra room mapping deleted');
      await fetchYatraSettings(propertyId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const saveYatraRatePlanMapping = async (propertyId, form) => {
    const key = `rp-${form.internal_rate_plan_id || 'new'}`;
    setYatraSaving(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post(`${API}/yatra/rate-plan`,
        { property_id: propertyId, ...form },
        { headers: authHeaders() });
      toast.success('Yatra rate-plan mapping saved');
      await fetchYatraSettings(propertyId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save Yatra rate-plan mapping');
    } finally {
      setYatraSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const deleteYatraRatePlanMapping = async (propertyId, mappingId) => {
    if (!window.confirm('Delete this Yatra rate-plan mapping?')) return;
    try {
      await axios.delete(`${API}/yatra/rate-plan/${mappingId}`, { headers: authHeaders() });
      toast.success('Yatra rate-plan mapping deleted');
      await fetchYatraSettings(propertyId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const renderYatraMappings = (property) => {
    // Feb 2026 reorganization — Yatra tab shows ONLY property-level
    // configuration (credentials + Hotel Code). Room-type and rate-plan
    // mappings live in the CENTRAL "OTA Mapping" section shared with
    // MMT and Agoda, so a future OTA plugs in as a new column there.
    const data = yatraData[property.id];
    const isLoading = yatraLoading[property.id];
    const inputs = yatraInputs[property.id] || {};

    // Onboarded Yatra hotel code lives in the shared ota_property_mappings
    // (ota="yatra"), surfaced by /property-settings as ota_mappings.yatra.
    // Use it as the default so the field auto-populates after onboarding.
    const onboardedYatraCode = ((propertySettingsData.properties || [])
      .find(p => p.id === property.id)?.ota_mappings?.yatra?.ota_property_code) || '';

    const propBuf = {
      yatra_hotel_code: inputs.yatra_hotel_code !== undefined ? inputs.yatra_hotel_code : (data?.property?.yatra_hotel_code || onboardedYatraCode || ''),
      active:           inputs.active !== undefined ? inputs.active : (data?.property?.active !== false),
    };

    return (
      <div className="space-y-4" data-testid={`yatra-section-${property.id}`}>
        {isLoading && <div className="text-xs text-gray-500">Loading Yatra config…</div>}

        {/* Global Yatra Integration Credentials — shared by every property */}
        <div className="bg-amber-50/60 border border-amber-200 rounded-lg p-4" data-testid="yatra-credentials-card">
          <div className="text-sm font-semibold text-gray-800 mb-1">Yatra / Travelguru Integration Credentials</div>
          <div className="text-[11px] text-gray-500 mb-3">
            Stored once on the backend and used for <strong>all</strong> properties. The password is encrypted at rest and never shown again after saving.
          </div>
          {yatraCredsLoading && <div className="text-xs text-gray-500 mb-2">Loading credentials…</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Yatra Username</label>
              <Input
                value={yatraCredsInput.username}
                onChange={(e) => setYatraCredsInput(prev => ({ ...prev, username: e.target.value }))}
                placeholder="Yatra web-service username"
                className="text-sm"
                data-testid="yatra-global-username"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Yatra Password</label>
              <Input
                type="password"
                value={yatraCredsInput.password}
                onChange={(e) => setYatraCredsInput(prev => ({ ...prev, password: e.target.value }))}
                placeholder={yatraCreds?.has_credentials ? '••••••••  (leave blank to keep)' : 'Enter password'}
                className="text-sm"
                data-testid="yatra-global-password"
              />
            </div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="text-[11px] text-gray-600" data-testid="yatra-credentials-status">
              {yatraCreds?.has_credentials
                ? `✓ Credentials configured (${yatraCreds.username})`
                : 'No credentials configured yet'}
            </span>
            <Button
              size="sm"
              onClick={saveYatraCredentials}
              disabled={yatraCredsSaving}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="yatra-save-credentials"
            >
              {yatraCredsSaving ? 'Saving…' : 'Save Credentials'}
            </Button>
          </div>
        </div>

        {/* Yatra Property Configuration — property-specific Hotel Code only */}
        <div className="bg-blue-50/40 border border-blue-100 rounded-lg p-4">
          <div className="text-sm font-semibold text-gray-800 mb-1">Yatra Property Configuration</div>
          <div className="text-[11px] text-gray-500 mb-3">
            The Yatra <strong>Hotel Code</strong> is specific to this property.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Yatra Hotel Code</label>
              <Input
                value={propBuf.yatra_hotel_code}
                onChange={(e) => { setYatraInputs(prev => ({ ...prev, [property.id]: { ...(prev[property.id] || {}), yatra_hotel_code: e.target.value } })); }}
                placeholder="e.g. 00225828"
                className="text-sm"
                data-testid={`yatra-hotel-code-${property.id}`}
              />
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={propBuf.active}
                  onChange={(e) => { setYatraInputs(prev => ({ ...prev, [property.id]: { ...(prev[property.id] || {}), active: e.target.checked } })); }}
                  data-testid={`yatra-active-${property.id}`}
                />
                Active
              </label>
              <Button
                size="sm"
                onClick={() => saveYatraProperty(property.id)}
                disabled={!!yatraSaving[`prop-${property.id}`]}
                className="bg-blue-600 hover:bg-blue-700 text-white ml-auto"
                data-testid={`yatra-save-property-${property.id}`}
              >
                {yatraSaving[`prop-${property.id}`] ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
          <div className="text-[11px] text-gray-500 mt-3 border-t border-blue-100 pt-2">
            <strong>Room Type &amp; Rate Plan mappings for Yatra</strong> (InvCode / RatePlanID) live in the central
            <em> OTA Mapping</em> section above — same table as MMT and Agoda.
          </div>
        </div>
      </div>
    );
  };

  const renderPropertySettings = () => {
    // Feb 2026 redesign — Property Settings must show ONLY the property
    // currently selected via the top PropertySelector. We filter the
    // existing propertySettingsData (fetched by fetchPropertySettings)
    // without changing that endpoint. Auto-expand the single visible
    // property so users don't have to click again.
    const allProps = propertySettingsData.properties || [];
    const scopedProps = selectedProperty?.id
      ? allProps.filter(p => p.id === selectedProperty.id)
      : [];

    return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600">
        Map <strong>{selectedProperty?.name || 'the selected property'}</strong>'s
        room types and rate plans to their corresponding OTA codes (MMT today,
        more later). Bearer tokens are encrypted at rest with Fernet
        (server-side) and masked in the UI.
      </div>

      {/* Add Room Type / Add Rate Plan — moved from Settings → Property
          Management (Feb 2026 redesign). Handlers unchanged. */}
      {user?.role === 'admin' && selectedProperty?.id && (
        <Card data-testid="ps-quick-actions-card">
          <CardHeader>
            <CardTitle className="text-base">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRoomTypeModal(true)}
                data-testid="add-room-type-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Room Type
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openRatePlanManager()}
                className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200 hover:border-orange-300"
                data-testid="add-rate-plan-btn"
              >
                <Tag className="w-4 h-4 mr-2" />
                Add Rate Plan
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Room Type Hierarchy — moved from Settings (Feb 2026 redesign).
          Order, drag/reorder, save, and cascade-rate handlers unchanged. */}
      {user?.role === 'admin' && selectedProperty?.id && (() => {
        const pid = selectedProperty.id;
        const data = otaMapData[pid];
        const loading = otaMapLoading[pid];
        const internalRTs = data?.internal_room_types || [];
        const drafts = otaMapDrafts[pid] || {};
        const rowsFor = (ota) => (ota === 'mmt' ? (data?.mmt_rooms || []) : (data?.agoda_rooms || []));
        const setDraft = (ota, code, val) => setOtaMapDrafts(prev => ({
          ...prev,
          [pid]: { ...(prev[pid] || {}), [`${ota}:${code}`]: val },
        }));
        const renderOtaSubsection = (ota, label, accent) => {
          const rows = rowsFor(ota);
          return (
            <div className="border rounded-lg overflow-hidden" data-testid={`ota-map-${ota}`}>
              <div className={`px-3 py-2 text-sm font-semibold ${accent}`}>
                {label} <span className="ml-2 text-xs opacity-80">({rows.length} room{rows.length === 1 ? '' : 's'})</span>
              </div>
              {rows.length === 0 ? (
                <div className="p-3 text-xs text-gray-500 bg-gray-50">
                  {ota === 'mmt'
                    ? 'No MMT rooms found yet. MMT rooms appear here after the MMT integration syncs — you don\'t need to add them manually.'
                    : 'No Agoda rooms yet. Sync from Agoda (Property Settings → Agoda tab → GetProduct) or key one in below.'}
                </div>
              ) : (
                <div className="divide-y">
                  {rows.map((row) => {
                    const draftKey = `${ota}:${row.ota_room_code}`;
                    const draftVal = drafts[draftKey];
                    const currentVal = draftVal === undefined
                      ? (row.internal_room_type_id ?? '')
                      : draftVal;
                    const savingKey = `${pid}:${draftKey}`;
                    return (
                      <div key={row.id || row.ota_room_code}
                        className="p-3 flex items-center gap-3 flex-wrap"
                        data-testid={`ota-map-row-${ota}-${row.ota_room_code}`}
                      >
                        <div className="flex-1 min-w-[180px]">
                          <div className="text-sm font-medium text-gray-800">
                            {row.ota_room_name || <span className="italic text-gray-500">(unnamed)</span>}
                          </div>
                          <div className="text-xs text-gray-500 font-mono">{row.ota_room_code}</div>
                        </div>
                        <div className="w-64">
                          <Select
                            value={currentVal || 'none'}
                            onValueChange={(v) => setDraft(ota, row.ota_room_code, v === 'none' ? '' : v)}
                          >
                            <SelectTrigger data-testid={`ota-map-select-${ota}-${row.ota_room_code}`}>
                              <SelectValue placeholder="— Not mapped —" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">— Not mapped —</SelectItem>
                              {internalRTs.map((rt) => (
                                <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => saveOtaRoomMapping(pid, ota, row.ota_room_code)}
                          disabled={!!otaMapSaving[savingKey]}
                          className="bg-gradient-to-r from-orange-500 to-amber-600 text-white"
                          data-testid={`ota-map-save-${ota}-${row.ota_room_code}`}
                        >
                          {otaMapSaving[savingKey] ? 'Saving…' : 'Save'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        };
        return (
          <Card data-testid="ota-mapping-card">
            <CardHeader>
              <CardTitle className="text-base">OTA Mapping</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-gray-600">
                Link each OTA room type to one of <strong>{selectedProperty.name}</strong>'s
                internal room types. Mapping is <em>optional</em> — a property may have MMT only,
                Agoda only, both, or neither. Saving one row updates its internal-room link only;
                the OTA-side codes and ARI push paths are untouched.
              </div>
              {loading ? (
                <div className="text-sm text-gray-500">Loading OTA rooms…</div>
              ) : !data ? (
                <div className="p-3 text-sm text-gray-500 bg-gray-50 rounded">
                  OTA mapping data unavailable for this property.
                </div>
              ) : (() => {
                // ---- Central Room Type Mapping (Feb 2026) --------------
                // Rows = internal room types. Columns = MMT | Agoda | Yatra
                // (driven by OTA_REGISTRY so future OTAs plug in trivially).
                // Each column reads from its own OTA data source and writes
                // via that OTA's existing endpoint — no cross-OTA writes.
                const pd = (propertySettingsData.properties || []).find(p => p.id === pid) || {};
                const roomTypes = pd.room_types || [];
                const agRoomMaps = (agodaData[pid]?.room_mappings || []);
                const agRpMaps   = (agodaData[pid]?.rate_plan_mappings || []);
                const yaRoomMaps = (yatraData[pid]?.room_mappings || []);
                const yaRpMaps   = (yatraData[pid]?.rate_plan_mappings || []);

                // Currently-mapped OTA-room-code per (internal_room_type, ota).
                const currentRoomCode = (rt, ota) => {
                  if (ota === 'mmt') return (rt.ota_mappings?.mmt?.ota_room_code) || '';
                  if (ota === 'agoda') return (agRoomMaps.find(m => m.internal_room_type_id === rt.id)?.agoda_room_id) || '';
                  if (ota === 'yatra') return (yaRoomMaps.find(m => m.internal_room_type_id === rt.id)?.yatra_inv_code) || (rt.ota_mappings?.yatra?.ota_room_code) || '';
                  return '';
                };
                const currentRoomName = (rt, ota) => {
                  if (ota === 'mmt') return ''; // MMT rows don't carry a separate room name here
                  if (ota === 'agoda') return (agRoomMaps.find(m => m.internal_room_type_id === rt.id)?.agoda_room_name) || '';
                  if (ota === 'yatra') return (yaRoomMaps.find(m => m.internal_room_type_id === rt.id)?.yatra_inv_type) || (rt.ota_mappings?.yatra?.ota_room_name) || '';
                  return '';
                };

                // Dropdown options: OTA rooms known for this property, unioned
                // with the Property Settings code catalog (dedup by code).
                const mergeCatalog = (base, ota, kind) => {
                  const out = [...base];
                  const seen = new Set(out.map(o => o.code));
                  for (const c of catalogCodes(pid, ota, kind)) {
                    if (c.code && !seen.has(c.code)) { out.push({ code: c.code, name: c.name || '' }); seen.add(c.code); }
                  }
                  return out;
                };
                const optionsFor = (ota) => {
                  if (ota === 'mmt') return mergeCatalog((data?.mmt_rooms || []).map(r => ({ code: r.ota_room_code, name: r.ota_room_name || '' })), 'mmt', 'room');
                  if (ota === 'agoda') return mergeCatalog((data?.agoda_rooms || []).map(r => ({ code: r.ota_room_code, name: r.ota_room_name || '' })), 'agoda', 'room');
                  if (ota === 'yatra') return mergeCatalog([], 'yatra', 'room');
                  return [];
                };
                // <datalist> options for free-text code inputs.
                const dataListId = (ota, kind) => `cat-${pid}-${ota}-${kind}`;
                const renderDataList = (ota, kind) => (
                  <datalist id={dataListId(ota, kind)}>
                    {catalogCodes(pid, ota, kind).map(c => (
                      <option key={c.id} value={c.code}>{c.name ? `${c.name} — ${c.code}` : c.code}</option>
                    ))}
                  </datalist>
                );


                // Central drafts store: cs[`${ota}:${internalRtId}`]: {code, name} for Yatra typed input
                const csKey = (ota, rtId) => `central:${ota}:${rtId}`;
                const setCentralDraft = (ota, rtId, patch) => setOtaMapDrafts(prev => ({
                  ...prev,
                  [pid]: { ...(prev[pid] || {}), [csKey(ota, rtId)]: { ...((prev[pid] || {})[csKey(ota, rtId)] || {}), ...patch } },
                }));
                const getCentralDraft = (ota, rtId) => (drafts[csKey(ota, rtId)] || {});

                const saveCentralRoom = async (ota, rt) => {
                  const draft = getCentralDraft(ota, rt.id);
                  if (ota === 'mmt' || ota === 'agoda') {
                    const code = draft.code !== undefined ? draft.code : currentRoomCode(rt, ota);
                    if (!code) { toast.error(`Pick a ${ota.toUpperCase()} room first`); return; }
                    await saveOtaRoomMapping(pid, ota, code);
                    // saveOtaRoomMapping refetches otaMappings; we also want
                    // agoda/yatra tables in sync post-save.
                    if (ota === 'agoda') fetchAgodaMappings(pid);
                  } else if (ota === 'yatra') {
                    const invCode = draft.code !== undefined ? draft.code : currentRoomCode(rt, 'yatra');
                    const invType = draft.name !== undefined ? draft.name : currentRoomName(rt, 'yatra');
                    if (!invCode) { toast.error('Enter Yatra InvCode'); return; }
                    await saveYatraRoomMapping(pid, {
                      internal_room_type_id: rt.id,
                      yatra_inv_code: invCode,
                      yatra_inv_type: invType,
                      active: true,
                    });
                  }
                };

                // ---- Central Rate Plan Mapping ---------------------
                // Rows = (internal_room_type, internal_rate_plan) tuples.
                const ratePlanRows = [];
                for (const rt of roomTypes) {
                  for (const rp of (rt.rate_plans || [])) {
                    ratePlanRows.push({ rt, rp });
                  }
                }
                const currentRpCode = (rp, ota) => {
                  if (ota === 'mmt') return (rp.ota_mappings?.mmt?.ota_rate_plan_code) || '';
                  if (ota === 'agoda') return (agRpMaps.find(m => m.internal_rate_plan_id === rp.id)?.agoda_rate_plan_id) || '';
                  if (ota === 'yatra') return (yaRpMaps.find(m => m.internal_rate_plan_id === rp.id)?.yatra_rate_plan_id) || (rp.ota_mappings?.yatra?.ota_rate_plan_code) || '';
                  return '';
                };
                const currentRpExtra = (rp, ota) => {
                  if (ota === 'yatra') {
                    const row = yaRpMaps.find(m => m.internal_rate_plan_id === rp.id);
                    return { name: row?.yatra_rate_plan_type || (rp.ota_mappings?.yatra?.ota_rate_plan_name) || '', pricing_occupancy: row?.pricing_occupancy || 2 };
                  }
                  return {};
                };

                const saveCentralRatePlan = async (ota, rt, rp) => {
                  const draft = getCentralDraft(`rp:${ota}`, rp.id);
                  if (ota === 'mmt') {
                    const code = draft.code !== undefined ? draft.code : currentRpCode(rp, 'mmt');
                    if (!code) { toast.error('Enter MMT rate-plan code'); return; }
                    try {
                      await axios.put(`${API}/property-settings/rate-plan`,
                        { property_id: pid, rate_plan_id: rp.id, ota: 'mmt', ota_rate_plan_code: code },
                        { headers: authHeaders() });
                      toast.success('MMT rate-plan mapping saved');
                      fetchPropertySettings();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Failed to save MMT rate-plan');
                    }
                  } else if (ota === 'agoda') {
                    const code = draft.code !== undefined ? draft.code : currentRpCode(rp, 'agoda');
                    if (!code) { toast.error('Enter Agoda rate-plan ID'); return; }
                    try {
                      await axios.post(`${API}/agoda/rate-plan-mapping`,
                        {
                          property_id: pid,
                          internal_room_type_id: rt.id,
                          internal_rate_plan_id: rp.id,
                          agoda_rate_plan_id: code,
                        },
                        { headers: authHeaders() });
                      toast.success('Agoda rate-plan mapping saved');
                      fetchAgodaMappings(pid);
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Failed to save Agoda rate-plan');
                    }
                  } else if (ota === 'yatra') {
                    const existing = currentRpExtra(rp, 'yatra');
                    const code = draft.code !== undefined ? draft.code : currentRpCode(rp, 'yatra');
                    const name = draft.name !== undefined ? draft.name : existing.name;
                    const po   = draft.pricing_occupancy !== undefined ? draft.pricing_occupancy : existing.pricing_occupancy;
                    if (!code) { toast.error('Enter Yatra RatePlanID'); return; }
                    await saveYatraRatePlanMapping(pid, {
                      internal_room_type_id: rt.id,
                      internal_rate_plan_id: rp.id,
                      yatra_rate_plan_id: code,
                      yatra_rate_plan_type: name,
                      pricing_occupancy: po,
                      active: true,
                    });
                  }
                };

                return (
                  <div className="space-y-6">
                    {/* ---- ROOM TYPE MAPPING ---- */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-800">Room Type Mapping</h4>
                        <div className="text-[10px] text-gray-500">Rows = internal room types · Columns = OTAs</div>
                      </div>
                      {roomTypes.length === 0 ? (
                        <div className="p-3 text-xs text-gray-500 bg-gray-50 rounded">
                          No internal room types configured yet. Use Quick Actions → Add Room Type above.
                        </div>
                      ) : (
                        <div className="overflow-x-auto border rounded-lg">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                              <tr>
                                <th className="px-3 py-2 text-left w-40">Internal Room Type</th>
                                {OTA_REGISTRY.map(ota => (
                                  <th key={ota.key} className="px-3 py-2 text-left">{ota.label}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {roomTypes.map(rt => (
                                <tr key={rt.id} data-testid={`central-room-row-${rt.id}`}>
                                  <td className="px-3 py-2 font-medium text-gray-800 align-top">{rt.name}</td>
                                  {OTA_REGISTRY.map(ota => {
                                    const draft = getCentralDraft(ota.key, rt.id);
                                    const currentCode = currentRoomCode(rt, ota.key);
                                    const draftCode = draft.code !== undefined ? draft.code : currentCode;
                                    const savingKey = `${pid}:central-room:${ota.key}:${rt.id}`;
                                    if (ota.key === 'mmt' || ota.key === 'agoda') {
                                      const opts = optionsFor(ota.key);
                                      return (
                                        <td key={ota.key} className={`px-3 py-2 align-top border-l ${ota.accent}`}>
                                          <div className="flex items-center gap-2">
                                            <Select
                                              value={draftCode || 'none'}
                                              onValueChange={v => setCentralDraft(ota.key, rt.id, { code: v === 'none' ? '' : v })}
                                            >
                                              <SelectTrigger className="min-w-[180px] text-xs" data-testid={`central-room-select-${ota.key}-${rt.id}`}>
                                                <SelectValue placeholder="— Not mapped —" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="none">— Not mapped —</SelectItem>
                                                {opts.map(o => (
                                                  <SelectItem key={o.code} value={o.code}>
                                                    {o.name ? `${o.name} — ${o.code}` : o.code}
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                            <Button
                                              size="sm" variant="outline"
                                              onClick={() => saveCentralRoom(ota.key, rt)}
                                              disabled={!!otaMapSaving[savingKey]}
                                              className="text-xs"
                                              data-testid={`central-room-save-${ota.key}-${rt.id}`}
                                            >Save</Button>
                                          </div>
                                        </td>
                                      );
                                    }
                                    // Yatra: free-text InvCode + InvType (no known-rooms list)
                                    const draftName = draft.name !== undefined ? draft.name : currentRoomName(rt, 'yatra');
                                    return (
                                      <td key={ota.key} className={`px-3 py-2 align-top border-l ${ota.accent}`}>
                                        <div className="flex flex-col gap-1">
                                          {renderDataList('yatra', 'room')}
                                          <Input
                                            value={draftCode}
                                            onChange={e => setCentralDraft('yatra', rt.id, { code: e.target.value })}
                                            placeholder="Yatra InvCode"
                                            list={dataListId('yatra', 'room')}
                                            className="text-xs"
                                            data-testid={`central-room-yatra-code-${rt.id}`}
                                          />
                                          <div className="flex items-center gap-2">
                                            <Input
                                              value={draftName}
                                              onChange={e => setCentralDraft('yatra', rt.id, { name: e.target.value })}
                                              placeholder="Yatra Room Name"
                                              className="text-xs"
                                              data-testid={`central-room-yatra-name-${rt.id}`}
                                            />
                                            <Button
                                              size="sm" variant="outline"
                                              onClick={() => saveCentralRoom('yatra', rt)}
                                              disabled={!!yatraSaving[`room-${rt.id}`]}
                                              className="text-xs whitespace-nowrap"
                                              data-testid={`central-room-save-yatra-${rt.id}`}
                                            >Save</Button>
                                          </div>
                                        </div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* ---- RATE PLAN MAPPING ---- */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-800">Rate Plan Mapping</h4>
                        <div className="text-[10px] text-gray-500">Rows = (internal room + rate plan) · Columns = OTAs</div>
                      </div>
                      {ratePlanRows.length === 0 ? (
                        <div className="p-3 text-xs text-gray-500 bg-gray-50 rounded">
                          No internal rate plans configured yet. Use Quick Actions → Add Rate Plan.
                        </div>
                      ) : (
                        <div className="overflow-x-auto border rounded-lg">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                              <tr>
                                <th className="px-3 py-2 text-left">Internal Room</th>
                                <th className="px-3 py-2 text-left">Internal Rate Plan</th>
                                {OTA_REGISTRY.map(ota => (
                                  <th key={ota.key} className="px-3 py-2 text-left">{ota.label}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {ratePlanRows.map(({ rt, rp }) => (
                                <tr key={rp.id} data-testid={`central-rp-row-${rp.id}`}>
                                  <td className="px-3 py-2 align-top text-gray-800">{rt.name}</td>
                                  <td className="px-3 py-2 align-top">
                                    <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-800 text-[10px] font-semibold rounded">{rp.code}</span>
                                    <div className="text-[10px] text-gray-500 mt-0.5">{rp.name}</div>
                                  </td>
                                  {OTA_REGISTRY.map(ota => {
                                    const draft = getCentralDraft(`rp:${ota.key}`, rp.id);
                                    const currentCode = currentRpCode(rp, ota.key);
                                    const draftCode = draft.code !== undefined ? draft.code : currentCode;
                                    if (ota.key === 'yatra') {
                                      const yExtra = currentRpExtra(rp, 'yatra');
                                      const draftName = draft.name !== undefined ? draft.name : yExtra.name;
                                      const draftPO   = draft.pricing_occupancy !== undefined ? draft.pricing_occupancy : yExtra.pricing_occupancy;
                                      const maxOcc = rt.max_occupancy || 1;
                                      const occOptions = Array.from({ length: Math.max(1, maxOcc) }, (_, i) => i + 1);
                                      return (
                                        <td key={ota.key} className={`px-3 py-2 align-top border-l ${ota.accent}`}>
                                          <div className="flex flex-col gap-1 min-w-[220px]">
                                            {renderDataList('yatra', 'rate_plan')}
                                            <Input
                                              value={draftCode}
                                              onChange={e => setCentralDraft('rp:yatra', rp.id, { code: e.target.value })}
                                              placeholder="Yatra RatePlanID"
                                              list={dataListId('yatra', 'rate_plan')}
                                              className="text-xs"
                                              data-testid={`central-rp-yatra-id-${rp.id}`}
                                            />
                                            <Input
                                              value={draftName}
                                              onChange={e => setCentralDraft('rp:yatra', rp.id, { name: e.target.value })}
                                              placeholder="Rate Plan Name"
                                              className="text-xs"
                                              data-testid={`central-rp-yatra-name-${rp.id}`}
                                            />
                                            <div className="flex items-center gap-1">
                                              <label className="text-[10px] text-gray-600 whitespace-nowrap">Pricing Occ</label>
                                              <select
                                                value={draftPO}
                                                onChange={e => setCentralDraft('rp:yatra', rp.id, { pricing_occupancy: parseInt(e.target.value, 10) })}
                                                className="text-xs border rounded px-1 py-0.5"
                                                data-testid={`central-rp-yatra-po-${rp.id}`}
                                              >
                                                {occOptions.map(o => <option key={o} value={o}>{o}</option>)}
                                              </select>
                                              <Button
                                                size="sm" variant="outline"
                                                onClick={() => saveCentralRatePlan('yatra', rt, rp)}
                                                disabled={!!yatraSaving[`rp-${rp.id}`]}
                                                className="text-xs ml-auto"
                                                data-testid={`central-rp-save-yatra-${rp.id}`}
                                              >Save</Button>
                                            </div>
                                          </div>
                                        </td>
                                      );
                                    }
                                    return (
                                      <td key={ota.key} className={`px-3 py-2 align-top border-l ${ota.accent}`}>
                                        <div className="flex items-center gap-2 min-w-[200px]">
                                          {renderDataList(ota.key, 'rate_plan')}
                                          <Input
                                            value={draftCode}
                                            onChange={e => setCentralDraft(`rp:${ota.key}`, rp.id, { code: e.target.value })}
                                            placeholder={ota.key === 'mmt' ? 'MMT Code' : 'Agoda ID'}
                                            list={dataListId(ota.key, 'rate_plan')}
                                            className="text-xs"
                                            data-testid={`central-rp-${ota.key}-code-${rp.id}`}
                                          />
                                          <Button
                                            size="sm" variant="outline"
                                            onClick={() => saveCentralRatePlan(ota.key, rt, rp)}
                                            className="text-xs"
                                            data-testid={`central-rp-save-${ota.key}-${rp.id}`}
                                          >Save</Button>
                                        </div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <div className="text-[10px] text-gray-500 mt-2">
                        <strong>Yatra Pricing Occupancy</strong>: when Calendar rates change,
                        Yatra receives the rate for the occupancy configured here — not the
                        occupancy that triggered the Calendar edit. Adding a future OTA is a
                        one-line change to <code>OTA_REGISTRY</code>.
                      </div>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        );
      })()}

      {/* Room Type Hierarchy — moved from Settings (Feb 2026 redesign).
          Order, drag/reorder, save, and cascade-rate handlers unchanged. */}
      {user?.role === 'admin' && selectedProperty?.id && (
        <Card data-testid="room-type-hierarchy-card">
          <CardHeader>
            <CardTitle className="text-base">Room Type Hierarchy (Property-Wise)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600">
              Order the room types for <strong>{selectedProperty?.name}</strong> from <strong>Lowest (Base)</strong> to <strong>Highest</strong>.
              When Calendar → <strong>$ Rates</strong> updates in mode <em>All</em>, the entered amount becomes the EP of the lowest room type
              and cascades upward using each room's <em>Room Type Increment</em>.
            </div>
            {roomTypeOrderLoading ? (
              <div className="text-sm text-gray-500">Loading…</div>
            ) : roomTypeOrder.length === 0 ? (
              <div className="p-3 text-sm text-gray-500 bg-gray-50 rounded">
                This property has no room types yet. Add a room type first.
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {roomTypeOrder.map((rt, idx) => (
                    <div
                      key={rt.room_type_id}
                      className="flex items-center gap-3 p-3 border rounded-lg bg-white"
                      data-testid={`room-hierarchy-row-${rt.room_type_id}`}
                    >
                      <div className="flex flex-col items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-700 text-xs font-bold">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-800">{rt.room_type_name}</div>
                        <div className="text-xs text-gray-500">
                          {idx === 0 ? 'LOWEST · Receives the base rate entered in $ Rates → All' :
                           idx === roomTypeOrder.length - 1 ? 'HIGHEST' : `Tier ${idx + 1}`}
                          {' · Max occupancy: '}{rt.max_occupancy}
                        </div>
                      </div>
                      <Button type="button" size="sm" variant="outline"
                        onClick={() => moveRoomTypeOrder(idx, -1)} disabled={idx === 0}
                        className="px-2" data-testid={`room-hierarchy-up-${rt.room_type_id}`}>↑</Button>
                      <Button type="button" size="sm" variant="outline"
                        onClick={() => moveRoomTypeOrder(idx, 1)} disabled={idx === roomTypeOrder.length - 1}
                        className="px-2" data-testid={`room-hierarchy-down-${rt.room_type_id}`}>↓</Button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={saveRoomTypeOrder}
                    disabled={roomTypeOrderSaving}
                    className="bg-gradient-to-r from-orange-500 to-amber-600 text-white"
                    data-testid="save-room-type-hierarchy-btn"
                  >
                    {roomTypeOrderSaving ? 'Saving…' : 'Save Room Hierarchy'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Link Room Types — dependent rate cascade config (Jun 2026) */}
      {user?.role === 'admin' && selectedProperty?.id && (
        <LinkRoomTypesSection
          apiBase={API}
          propertyId={selectedProperty.id}
          roomsData={ratePlanGrouped}
          links={roomTypeLinks}
          onSaved={() => fetchRoomTypeLinks(selectedProperty.id)}
        />
      )}

      {scopedProps.length === 0 ? (
        <div className="p-4 text-sm text-gray-500 bg-gray-50 rounded" data-testid="ps-no-selection">
          {selectedProperty?.id
            ? 'This property has no OTA mapping data yet.'
            : 'Select a property from the top selector to manage its OTA mappings.'}
        </div>
      ) : (
        scopedProps.map(p => {
          const isExpanded = psExpandedProperty === p.id || scopedProps.length === 1;
          const mmt = p.ota_mappings?.mmt || {};
          const tokenRevealed = psRevealedTokens[p.id];
          const bufCode = psGetPropertyInput(p.id, 'mmt', 'ota_property_code');
          const bufToken = (psPropertyInputs[p.id] || {}).bearer_token || '';
          return (
            <Card key={p.id} data-testid={`ps-property-${p.id}`}>
              <button
                type="button"
                onClick={() => setPsExpandedProperty(isExpanded ? null : p.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
                data-testid={`ps-toggle-property-${p.id}`}
              >
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-orange-500" />
                  <div className="text-left">
                    <div className="font-semibold text-gray-800">{p.name}</div>
                    <div className="text-xs text-gray-500">
                      MMT: {mmt.ota_property_code ? `${mmt.ota_property_code} • ${mmt.token?.has_token ? 'token set' : 'no token'}` : 'unmapped'}
                    </div>
                  </div>
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {isExpanded && (
                <div className="px-5 pb-5 space-y-5 border-t pt-4">
                  {/* Refetch from OTA — re-runs the Add-Property-via-OTA fetch
                      using credentials stored in this property's settings. */}
                  <div className="flex items-center gap-2 flex-wrap bg-gray-50 border rounded-lg p-2.5" data-testid={`ps-refetch-${p.id}`}>
                    <span className="text-xs font-medium text-gray-600">Refetch from OTA:</span>
                    <select
                      value={refetchState[p.id]?.ota || ''}
                      onChange={(e) => setRefetchState(prev => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), ota: e.target.value } }))}
                      className="text-xs border rounded px-2 py-1 bg-white"
                      data-testid={`ps-refetch-ota-select-${p.id}`}
                    >
                      <option value="">Select OTA…</option>
                      <option value="mmt">MMT</option>
                      <option value="agoda">Agoda</option>
                    </select>
                    <Button
                      size="sm"
                      disabled={!refetchState[p.id]?.ota || refetchState[p.id]?.busy}
                      onClick={() => refetchOtaNow(p.id, refetchState[p.id]?.ota)}
                      className="bg-gradient-to-r from-sky-500 to-indigo-600 text-white h-7 text-xs"
                      data-testid={`ps-refetch-btn-${p.id}`}
                    >{refetchState[p.id]?.busy ? 'Refetching…' : 'Refetch'}</Button>
                  </div>
                  {/* OTA Tab Switcher — Feb 2026. MMT is the default so
                      existing MMT UI behaviour is unchanged for users
                      who never touch the switcher. */}
                  <div className="flex items-center gap-2 flex-wrap" data-testid={`ps-ota-tabs-${p.id}`}>
                    <span className="text-xs font-medium text-gray-600 mr-1">OTA:</span>
                    <button
                      type="button"
                      onClick={() => setPsActiveOtaTab(prev => ({ ...prev, [p.id]: 'mmt' }))}
                      className={`px-3 py-1 rounded-full text-xs font-medium border ${(psActiveOtaTab[p.id] || 'mmt') === 'mmt' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-orange-50'}`}
                      data-testid={`ps-ota-tab-mmt-${p.id}`}
                    >MMT</button>
                    <button
                      type="button"
                      onClick={() => setPsActiveOtaTab(prev => ({ ...prev, [p.id]: 'agoda' }))}
                      className={`px-3 py-1 rounded-full text-xs font-medium border ${psActiveOtaTab[p.id] === 'agoda' ? 'bg-red-500 text-white border-red-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-red-50'}`}
                      data-testid={`ps-ota-tab-agoda-${p.id}`}
                    >Agoda</button>
                    <button
                      type="button"
                      onClick={() => setPsActiveOtaTab(prev => ({ ...prev, [p.id]: 'yatra' }))}
                      className={`px-3 py-1 rounded-full text-xs font-medium border ${psActiveOtaTab[p.id] === 'yatra' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50'}`}
                      data-testid={`ps-ota-tab-yatra-${p.id}`}
                    >Yatra</button>
                  </div>
                  {(psActiveOtaTab[p.id] || 'mmt') === 'mmt' && (<>
                  <div className="bg-orange-50/40 border border-orange-100 rounded-lg p-4">
                    <div className="text-sm font-semibold text-gray-800 mb-3">MakeMyTrip (MMT) Integration</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">MMT Property Code</label>
                        <Input
                          value={bufCode}
                          onChange={(e) => setPsPropertyInputs(prev => ({
                            ...prev,
                            [p.id]: { ...(prev[p.id] || {}), ota_property_code: e.target.value }
                          }))}
                          placeholder="e.g. 1000622126"
                          className="text-sm"
                          data-testid={`ps-property-code-${p.id}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Bearer Token {mmt.token?.has_token && <span className="ml-1 text-[10px] text-green-700">(stored — leave blank to keep)</span>}
                        </label>
                        <div className="flex gap-2">
                          <Input
                            type={tokenRevealed !== undefined ? 'text' : 'password'}
                            value={tokenRevealed !== undefined ? tokenRevealed : bufToken}
                            onChange={(e) => {
                              if (tokenRevealed !== undefined) return;
                              setPsPropertyInputs(prev => ({
                                ...prev,
                                [p.id]: { ...(prev[p.id] || {}), bearer_token: e.target.value }
                              }));
                            }}
                            placeholder={mmt.token?.has_token ? '••••••••' : 'Paste new bearer token'}
                            className="text-sm flex-1"
                            data-testid={`ps-bearer-input-${p.id}`}
                          />
                          {mmt.token?.has_token && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => psRevealToken(p.id)}
                              data-testid={`ps-token-toggle-${p.id}`}
                            >
                              {tokenRevealed !== undefined ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 border-t pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-semibold text-gray-700">Test with manual tokens (optional)</div>
                        <button
                          type="button"
                          className="text-[11px] text-orange-600 underline"
                          onClick={() => setMmtTestInput(p.id, 'showManual', !(mmtTestInputs[p.id]?.showManual))}
                          data-testid={`ps-test-mmt-toggle-manual-${p.id}`}
                        >
                          {mmtTestInputs[p.id]?.showManual ? 'Hide fields' : 'Show fields'}
                        </button>
                      </div>
                      {mmtTestInputs[p.id]?.showManual && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2 bg-orange-50 border border-orange-200 rounded p-2">
                          <div>
                            <label className="block text-[10px] font-medium text-gray-700 mb-0.5">Bearer token (override)</label>
                            <Input
                              type="password"
                              value={mmtTestInputs[p.id]?.bearer || ''}
                              onChange={(e) => setMmtTestInput(p.id, 'bearer', e.target.value)}
                              placeholder="Paste to try without saving"
                              className="text-xs h-8"
                              data-testid={`ps-test-mmt-bearer-${p.id}`}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium text-gray-700 mb-0.5">Channel token (override)</label>
                            <Input
                              type="password"
                              value={mmtTestInputs[p.id]?.channel || ''}
                              onChange={(e) => setMmtTestInput(p.id, 'channel', e.target.value)}
                              placeholder="Optional — falls back to MMT_CHANNEL_TOKEN env var"
                              className="text-xs h-8"
                              data-testid={`ps-test-mmt-channel-${p.id}`}
                            />
                          </div>
                          <div className="md:col-span-2 text-[10px] text-gray-600 italic">
                            Values entered here are used for this probe only. They are NEVER saved to the database or logs. Blank fields fall back to the saved / env-var values.
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-2 mt-3 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={checkMmtEgressIp}
                        disabled={mmtEgressChecking}
                        data-testid={`ps-check-egress-ip-${p.id}`}
                        title="Show this backend server's outbound IP — send to MMT support for whitelisting"
                      >
                        {mmtEgressChecking ? 'Checking IP…' : 'Check Egress IP'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => testMmtCredentials(p.id)}
                        disabled={!!psSaving[`test-${p.id}`]}
                        data-testid={`ps-test-mmt-${p.id}`}
                      >
                        {psSaving[`test-${p.id}`] ? 'Testing…' : 'Test MMT Credentials'}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => psSavePropertyMapping(p.id)}
                        disabled={!!psSaving[`prop-${p.id}`]}
                        className="bg-gradient-to-r from-orange-500 to-amber-600 text-white"
                        data-testid={`ps-save-property-${p.id}`}
                      >
                        {psSaving[`prop-${p.id}`] ? 'Saving…' : 'Save Property Mapping'}
                      </Button>
                    </div>
                    {mmtEgressResult && (
                      <div
                        className={`mt-2 border rounded p-2 text-xs ${mmtEgressResult.egress_ip ? 'bg-blue-50 border-blue-300 text-blue-900' : 'bg-red-50 border-red-300 text-red-800'}`}
                        data-testid="ps-egress-ip-result"
                      >
                        {mmtEgressResult.egress_ip ? (
                          <>
                            <div className="font-medium flex items-center gap-2 flex-wrap">
                              Backend egress IP:
                              <span className="font-mono text-sm px-1.5 py-0.5 bg-white border border-blue-300 rounded" data-testid="ps-egress-ip-value">
                                {mmtEgressResult.egress_ip}
                              </span>
                              <button
                                type="button"
                                className="text-[10px] underline opacity-70 hover:opacity-100"
                                onClick={async () => { try { await navigator.clipboard.writeText(mmtEgressResult.egress_ip); toast.success('IP copied'); } catch { toast.error('Copy blocked by browser'); } }}
                                data-testid="ps-egress-ip-copy"
                              >
                                copy
                              </button>
                            </div>
                            <div className="text-[10px] opacity-80 mt-1 break-all">
                              env: {mmtEgressResult.environment_hint || '—'} · checked: {mmtEgressResult.checked_at}
                            </div>
                            <div className="text-[10px] opacity-70 mt-1">{mmtEgressResult.note}</div>
                          </>
                        ) : (
                          <div className="font-medium">✗ Egress check failed: {mmtEgressResult.error || 'no sources responded'}</div>
                        )}
                      </div>
                    )}
                    {mmtTestResult[p.id] && (() => {
                      const r = mmtTestResult[p.id];
                      const bg = r.verdict === 'credentials_ok'       ? 'bg-green-50 border-green-300 text-green-800'
                               : r.verdict === 'credentials_rejected' ? 'bg-red-50 border-red-300 text-red-800'
                               :                                        'bg-amber-50 border-amber-300 text-amber-800';
                      const first = (r.errors && r.errors[0]) || null;
                      const bf = r.bearer_fingerprint || {};
                      const cf = r.channel_fingerprint || {};
                      return (
                        <div className={`mt-2 border rounded p-2 text-xs ${bg}`} data-testid={`ps-test-mmt-result-${p.id}`}>
                          <div className="font-medium">
                            {r.verdict === 'credentials_ok'       && `✓ Credentials OK  ·  HTTP ${r.http_status}  ·  ${r.duration_ms}ms`}
                            {r.verdict === 'credentials_rejected' && `✗ MMT rejected credentials  ·  HTTP ${r.http_status}  ·  ${r.duration_ms}ms`}
                            {r.verdict === 'unreachable'          && `⚠ MMT unreachable  ·  ${r.transport_error || 'no response'}`}
                          </div>
                          {first && (first.code || first.description) && (
                            <div className="mt-0.5"><span className="font-mono">{first.code ? `[${first.code}] ` : ''}</span>{first.description}</div>
                          )}
                          {(bf.length !== undefined || cf.length !== undefined) && (
                            <div className="mt-1.5 border-t pt-1.5 text-[10px] font-mono opacity-80" data-testid={`ps-test-mmt-fingerprint-${p.id}`}>
                              <div>bearer: len={bf.length} · sha256[:12]={bf.sha256_prefix || '—'} · &quot;{bf.first_4}…{bf.last_4}&quot;{(bf.has_leading_whitespace || bf.has_trailing_whitespace) ? ' ⚠ WS' : ''}{bf.has_tab_or_newline ? ' ⚠ TAB/NL' : ''}{bf.has_non_ascii ? ' ⚠ NON-ASCII' : ''}</div>
                              <div>channel: len={cf.length} · sha256[:12]={cf.sha256_prefix || '—'} · &quot;{cf.first_4}…{cf.last_4}&quot;{(cf.has_leading_whitespace || cf.has_trailing_whitespace) ? ' ⚠ WS' : ''}{cf.has_tab_or_newline ? ' ⚠ TAB/NL' : ''}{cf.has_non_ascii ? ' ⚠ NON-ASCII' : ''}</div>
                              <div>sent: {r.request_authorization_header_preview}</div>
                              <div>source: bearer={r.bearer_source} · channel={r.channel_source}</div>
                            </div>
                          )}
                          {r.response_excerpt && (
                            <details className="mt-1"><summary className="cursor-pointer text-[11px] opacity-70">Raw MMT response</summary>
                              <pre className="mt-1 text-[10px] whitespace-pre-wrap break-all">{r.response_excerpt}</pre>
                            </details>
                          )}
                          <div className="text-[10px] opacity-70 mt-1">{r.note}</div>
                        </div>
                      );
                    })()}
                  </div>

                  <div>
                    <div className="text-sm font-semibold text-gray-800 mb-2">Room Types</div>
                    {(p.room_types || []).length === 0 ? (
                      <div className="text-xs text-gray-500 italic">No room types defined for this property.</div>
                    ) : (
                      <div className="space-y-2">
                        {(p.room_types || []).map(rt => {
                          const rtExpanded = psExpandedRoomType === rt.id;
                          const rtMmt = rt.ota_mappings?.mmt || {};
                          const rtCodeBuf = (psRoomInputs[rt.id] || {}).ota_room_code !== undefined
                            ? psRoomInputs[rt.id].ota_room_code
                            : rtMmt.ota_room_code || '';
                          return (
                            <div key={rt.id} className="border rounded-lg" data-testid={`ps-room-${rt.id}`}>
                              <button
                                type="button"
                                onClick={() => setPsExpandedRoomType(rtExpanded ? null : rt.id)}
                                className="w-full flex items-center justify-between p-3 hover:bg-gray-50 text-left"
                                data-testid={`ps-toggle-room-${rt.id}`}
                              >
                                <div>
                                  <div className="text-sm font-medium">{rt.name}</div>
                                  <div className="text-xs text-gray-500">
                                    MMT room: {rtMmt.ota_room_code || 'unmapped'} • {(rt.rate_plans || []).length} rate plan(s) • max occ {rt.max_occupancy}
                                  </div>
                                </div>
                                {rtExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                              {rtExpanded && (
                                <div className="p-3 border-t space-y-3 bg-gray-50/50">
                                  <div className="flex items-end gap-2">
                                    <div className="flex-1">
                                      <label className="block text-xs font-medium text-gray-700 mb-1">MMT Room Code</label>
                                      <Input
                                        value={rtCodeBuf}
                                        onChange={(e) => setPsRoomInputs(prev => ({
                                          ...prev,
                                          [rt.id]: { ota_room_code: e.target.value }
                                        }))}
                                        placeholder="e.g. 45001654330"
                                        className="text-sm"
                                        data-testid={`ps-room-code-${rt.id}`}
                                      />
                                    </div>
                                    <Button
                                      size="sm"
                                      onClick={() => psSaveRoomMapping(rt.id)}
                                      disabled={!!psSaving[`room-${rt.id}`]}
                                      className="bg-gradient-to-r from-orange-500 to-amber-600 text-white"
                                      data-testid={`ps-save-room-${rt.id}`}
                                    >
                                      {psSaving[`room-${rt.id}`] ? 'Saving…' : 'Save Room'}
                                    </Button>
                                  </div>

                                  <div>
                                    <div className="text-xs font-semibold text-gray-700 mb-2">Rate Plans</div>
                                    {(rt.rate_plans || []).length === 0 ? (
                                      <div className="text-xs text-gray-500 italic">No rate plans for this room type. Add them via Settings → Add Rate Plan.</div>
                                    ) : (
                                      <div className="space-y-2">
                                        {(rt.rate_plans || []).map(rp => {
                                          const rpBuf = (psRatePlanInputs[rp.id] || {}).ota_rate_plan_code !== undefined
                                            ? psRatePlanInputs[rp.id].ota_rate_plan_code
                                            : (rp.ota_mappings?.mmt?.ota_rate_plan_code || '');
                                          return (
                                            <div key={rp.id} className="flex items-center gap-2 bg-white border rounded p-2" data-testid={`ps-rate-plan-${rp.id}`}>
                                              <div className="w-20 px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded text-center">
                                                {rp.code}
                                              </div>
                                              <Input
                                                value={rpBuf}
                                                onChange={(e) => setPsRatePlanInputs(prev => ({
                                                  ...prev,
                                                  [rp.id]: { ota_rate_plan_code: e.target.value }
                                                }))}
                                                placeholder="MMT Rate Plan Code (e.g. 990001679353)"
                                                className="flex-1 text-sm"
                                                data-testid={`ps-rate-plan-code-${rp.id}`}
                                              />
                                              <Button
                                                size="sm"
                                                onClick={() => psSaveRatePlanMapping(rp.id)}
                                                disabled={!!psSaving[`rp-${rp.id}`]}
                                                className="bg-gradient-to-r from-orange-500 to-amber-600 text-white"
                                                data-testid={`ps-save-rate-plan-${rp.id}`}
                                              >
                                                {psSaving[`rp-${rp.id}`] ? 'Saving…' : 'Save'}
                                              </Button>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <OtaCodeCatalogSection
                    ota="mmt" propertyId={p.id}
                    catalog={(otaCodeCatalog[p.id] || {}).mmt}
                    onAdd={(kind, code, name) => addOtaCode(p.id, 'mmt', kind, code, name)}
                    onUpdate={(id, patch) => updateOtaCode(p.id, id, patch)}
                    onDelete={(id) => deleteOtaCode(p.id, id)}
                  />
                  </>)}
                  {psActiveOtaTab[p.id] === 'agoda' && (<>
                    {renderAgodaMappings(p)}
                    <OtaCodeCatalogSection
                      ota="agoda" propertyId={p.id}
                      catalog={(otaCodeCatalog[p.id] || {}).agoda}
                      onAdd={(kind, code, name) => addOtaCode(p.id, 'agoda', kind, code, name)}
                      onUpdate={(id, patch) => updateOtaCode(p.id, id, patch)}
                      onDelete={(id) => deleteOtaCode(p.id, id)}
                    />
                  </>)}
                  {psActiveOtaTab[p.id] === 'yatra' && (<>
                    {renderYatraMappings(p)}
                    <OtaCodeCatalogSection
                      ota="yatra" propertyId={p.id}
                      catalog={(otaCodeCatalog[p.id] || {}).yatra}
                      onAdd={(kind, code, name) => addOtaCode(p.id, 'yatra', kind, code, name)}
                      onUpdate={(id, patch) => updateOtaCode(p.id, id, patch)}
                      onDelete={(id) => deleteOtaCode(p.id, id)}
                    />
                  </>)}
                </div>
              )}
            </Card>
          );
        })
      )}
      {/* Rate Configuration — per (property, room_type) increments for $ Rates auto-apply */}
      {user?.role === 'admin' && selectedProperty?.id && (
        <Card data-testid="rate-config-card">
          <CardHeader>
            <CardTitle className="text-base">Rate Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600">
              Configure per-room-type Rate Plan and Occupancy increments for <strong>{selectedProperty?.name}</strong>.
              When you use Calendar → <strong>$ Rates</strong> to update a base rate, the system auto-derives every Rate Plan × Occupancy
              rate using these increments.
            </div>
            {rateConfigsLoading ? (
              <div className="text-sm text-gray-500">Loading…</div>
            ) : rateConfigs.length === 0 ? (
              <div className="p-3 text-sm text-gray-500 bg-gray-50 rounded">
                This property has no room types yet. Add a room type first.
              </div>
            ) : (
              <div className="space-y-3">
                {[...rateConfigs]
                  .sort((a, b) => {
                    // Match the saved Room Type Hierarchy order; configs not in
                    // the saved order fall to the end, preserving default order.
                    const order = roomTypeOrder.map(r => r.room_type_id);
                    const ia = order.indexOf(a.room_type_id);
                    const ib = order.indexOf(b.room_type_id);
                    return (ia === -1 ? Infinity : ia) - (ib === -1 ? Infinity : ib);
                  })
                  .map((cfg) => (
                  <div key={cfg.room_type_id} className="border rounded-lg p-3 bg-white" data-testid={`rate-config-row-${cfg.room_type_id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-800">{cfg.room_type_name}</div>
                        <div className="text-xs text-gray-500">Max occupancy: {cfg.max_occupancy}</div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => saveRateConfig(cfg.room_type_id)}
                        className="bg-gradient-to-r from-orange-500 to-amber-600 text-white"
                        data-testid={`save-rate-config-${cfg.room_type_id}`}
                      >
                        Save
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Room Type Increment — cascades EP of THIS room to the NEXT (higher) room */}
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-700">Room Type Increment</label>
                        <div className="flex gap-2">
                          <Select
                            value={cfg.room_type_increment_type || 'fixed'}
                            onValueChange={(v) => updateRateConfigLocal(cfg.room_type_id, 'room_type_increment_type', v)}
                          >
                            <SelectTrigger className="w-28 text-sm" data-testid={`rt-inc-type-${cfg.room_type_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fixed">Fixed (₹)</SelectItem>
                              <SelectItem value="percentage">Percentage (%)</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            value={cfg.room_type_increment_value ?? ''}
                            onChange={(e) => updateRateConfigLocal(cfg.room_type_id, 'room_type_increment_value', e.target.value)}
                            placeholder={cfg.room_type_increment_type === 'percentage' ? '10' : '500'}
                            min="0"
                            className="flex-1 text-sm"
                            data-testid={`rt-inc-value-${cfg.room_type_id}`}
                          />
                        </div>
                        <p className="text-[10px] text-gray-500">This room's premium over the room directly below it</p>
                      </div>
                      {/* Rate Plan Increment */}
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-700">Rate Plan Increment</label>
                        <div className="flex gap-2">
                          <Select
                            value={cfg.rate_plan_increment_type || 'fixed'}
                            onValueChange={(v) => updateRateConfigLocal(cfg.room_type_id, 'rate_plan_increment_type', v)}
                          >
                            <SelectTrigger className="w-28 text-sm" data-testid={`rp-inc-type-${cfg.room_type_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fixed">Fixed (₹)</SelectItem>
                              <SelectItem value="percentage">Percentage (%)</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            value={cfg.rate_plan_increment_value ?? ''}
                            onChange={(e) => updateRateConfigLocal(cfg.room_type_id, 'rate_plan_increment_value', e.target.value)}
                            placeholder={cfg.rate_plan_increment_type === 'percentage' ? '5' : '30'}
                            min="0"
                            className="flex-1 text-sm"
                            data-testid={`rp-inc-value-${cfg.room_type_id}`}
                          />
                        </div>
                        <p className="text-[10px] text-gray-500">Applied progressively across Global Rate Plan order</p>
                      </div>
                      {/* Occupancy Increment */}
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-700">Occupancy Increment</label>
                        <div className="flex gap-2">
                          <Select
                            value={cfg.occupancy_increment_type || 'fixed'}
                            onValueChange={(v) => updateRateConfigLocal(cfg.room_type_id, 'occupancy_increment_type', v)}
                          >
                            <SelectTrigger className="w-28 text-sm" data-testid={`occ-inc-type-${cfg.room_type_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fixed">Fixed (₹)</SelectItem>
                              <SelectItem value="percentage">Percentage (%)</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            value={cfg.occupancy_increment_value ?? ''}
                            onChange={(e) => updateRateConfigLocal(cfg.room_type_id, 'occupancy_increment_value', e.target.value)}
                            placeholder={cfg.occupancy_increment_type === 'percentage' ? '10' : '50'}
                            min="0"
                            className="flex-1 text-sm"
                            data-testid={`occ-inc-value-${cfg.room_type_id}`}
                          />
                        </div>
                        <p className="text-[10px] text-gray-500">Applied progressively: Occ 1 → Occ 2 → Occ 3 …</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
    );
  };


  // ==================== LISTINGS (Master OTA Content) STATE & HELPERS ====================
  const [listingsList, setListingsList] = useState([]);  const [otasCatalog, setOtasCatalog] = useState([]);
  const [selectedListingId, setSelectedListingId] = useState(null);
  const [listingDetail, setListingDetail] = useState(null);
  const [listingTab, setListingTab] = useState('property_details');
  const [listingSectionInputs, setListingSectionInputs] = useState({});
  const [listingSaving, setListingSaving] = useState(false);
  // Feb 2026 — per-section Agoda sync status pill state ({[section]: {status, reason, log_id, http_code, notif_type, duration_ms, at}}).
  const [listingSyncStatus, setListingSyncStatus] = useState({});
  const [listingSyncHistory, setListingSyncHistory] = useState([]);
  const [listingSyncOpen, setListingSyncOpen] = useState(false);
  // Feb 2026 — Agoda Payload inspection panel state.
  const [agodaPayloadState, setAgodaPayloadState] = useState({
    section: 'full_property',
    loading: false,
    pushing: false,
    preview: null,
    pushResult: null,
    history: [],
  });
  const [agodaPayloadOpen, setAgodaPayloadOpen] = useState(false);
  // Phase 2 — staged Content Push (Rooms → Rate Plans → Products → Verify).
  const [contentPush, setContentPush] = useState({ loading: false, running: false, data: null });

  // Room-type photos — upload selection + dynamic Agoda ImageCaption catalogue.
  const [photoUploadCat, setPhotoUploadCat] = useState('Exterior');
  const [photoUploadRoom, setPhotoUploadRoom] = useState('');
  const [photoUploadCaption, setPhotoUploadCaption] = useState('');
  const [agodaCaptions, setAgodaCaptions] = useState({ loading: false, error: null, captions: [], default_code: '', vacation_rental: false });

  // Agoda Contract Management — Hotel + Host workflows (kept separate; never
  // auto-signed together). Host email is entered each time and NOT persisted.
  const [hotelContract, setHotelContract] = useState({ loading: false, signing: false, data: null, agreed: false });
  const [hostContract, setHostContract] = useState({ loading: false, signing: false, data: null, agreed: false, email: '' });

  const _authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchHotelContract = async () => {
    if (!selectedListingId) return;
    setHotelContract(s => ({ ...s, loading: true, agreed: false }));
    try {
      const res = await axios.get(`${API}/listings/${selectedListingId}/agoda-contract/hotel`, _authHeader());
      setHotelContract(s => ({ ...s, loading: false, data: res.data }));
    } catch (e) {
      setHotelContract(s => ({ ...s, loading: false, data: null }));
      toast.error(e.response?.data?.detail || 'Failed to retrieve hotel contract');
    }
  };

  const signHotelContract = async () => {
    if (!selectedListingId || !hotelContract.data?.contract) return;
    if (!hotelContract.agreed) { toast.error('Please review and agree before signing'); return; }
    if (!window.confirm('Sign the Agoda HOTEL contract on behalf of the property owner? This is a legally binding action.')) return;
    setHotelContract(s => ({ ...s, signing: true }));
    try {
      const res = await axios.post(`${API}/listings/${selectedListingId}/agoda-contract/hotel/sign`,
        { contract_code: hotelContract.data.contract.contract_code, user_agent: navigator.userAgent, approved: true },
        _authHeader());
      setHotelContract(s => ({ ...s, signing: false }));
      res.data.success ? toast.success(res.data.message || 'Hotel contract signed') : toast.error(res.data.message || 'Signing failed');
      fetchHotelContract();
    } catch (e) {
      setHotelContract(s => ({ ...s, signing: false }));
      toast.error(e.response?.data?.detail || 'Signing request failed');
    }
  };

  const fetchHostContract = async () => {
    if (!selectedListingId) return;
    if (!hostContract.email.trim()) { toast.error('Enter the Host Manage account email'); return; }
    setHostContract(s => ({ ...s, loading: true, agreed: false }));
    try {
      const res = await axios.post(`${API}/listings/${selectedListingId}/agoda-contract/host`,
        { email: hostContract.email.trim() }, _authHeader());
      setHostContract(s => ({ ...s, loading: false, data: res.data }));
    } catch (e) {
      setHostContract(s => ({ ...s, loading: false, data: null }));
      toast.error(e.response?.data?.detail || 'Failed to retrieve host contract');
    }
  };

  const signHostContract = async () => {
    if (!selectedListingId || !hostContract.data?.contract) return;
    if (!hostContract.agreed) { toast.error('Please review and agree before signing'); return; }
    if (!window.confirm('Sign the Agoda HOST contract? This applies to ALL properties under the Host Manage account and is legally binding.')) return;
    setHostContract(s => ({ ...s, signing: true }));
    try {
      const res = await axios.post(`${API}/listings/${selectedListingId}/agoda-contract/host/sign`,
        { email: hostContract.email.trim(), contract_code: hostContract.data.contract.contract_code, user_agent: navigator.userAgent, approved: true },
        _authHeader());
      setHostContract(s => ({ ...s, signing: false }));
      res.data.success ? toast.success(res.data.message || 'Host contract signed') : toast.error(res.data.message || 'Signing failed');
      fetchHostContract();
    } catch (e) {
      setHostContract(s => ({ ...s, signing: false }));
      toast.error(e.response?.data?.detail || 'Signing request failed');
    }
  };


  const runAgodaContentPush = async (execute = false) => {
    if (!selectedListingId) return;
    setContentPush(s => ({ ...s, loading: !execute, running: execute }));
    try {
      const res = await axios.post(
        `${API}/listings/${selectedListingId}/agoda-content/push?dry_run=${execute ? 'false' : 'true'}&execute=${execute ? 'true' : 'false'}`,
        {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setContentPush({ loading: false, running: false, data: res.data });
      if (execute) {
        res.data.success ? toast.success('Content push completed') : toast.error(`Content push failed at: ${res.data.failed_stage || 'a stage'}`);
      }
    } catch (e) {
      setContentPush(s => ({ ...s, loading: false, running: false }));
      toast.error(e.response?.data?.detail || 'Content push request failed');
    }
  };

  // Per-stage Agoda Content Push (each button calls ONLY its own backend stage).
  const AGODA_PUSH_STAGES = [
    { key: 'validate', label: 'Validate', readOnly: true },
    { key: 'property', label: 'Push Property Content', readOnly: false },
    { key: 'rooms', label: 'Push Rooms', readOnly: false },
    { key: 'rate_plans', label: 'Push Rate Plans', readOnly: false },
    { key: 'products', label: 'Push Products', readOnly: false },
    { key: 'verify', label: 'Verify', readOnly: true },
  ];
  const [stageResults, setStageResults] = useState({});

  const runAgodaContentStage = async (stageKey, label, readOnly) => {
    if (!selectedListingId) return;
    if (!readOnly) {
      const ok = window.confirm(`Run the LIVE "${label}" stage to Agoda now? This will create/update data on Agoda.`);
      if (!ok) return;
    }
    setStageResults(s => ({ ...s, [stageKey]: { ...(s[stageKey] || {}), loading: true } }));
    try {
      const res = await axios.post(
        `${API}/listings/${selectedListingId}/agoda-content/push?dry_run=false&execute=true&stage=${stageKey}`,
        {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const stages = res.data.stages || [];
      // Prefer the stage dict that actually carries the Agoda request/response;
      // fall back to a name match (property -> property_content), then the last.
      const payloadStages = stages.filter(x => x.request_xml || x.response_body);
      const nameFor = stageKey === 'property' ? 'property_content' : stageKey;
      const own = payloadStages[payloadStages.length - 1]
        || [...stages].reverse().find(x => x.stage === stageKey || x.stage === nameFor)
        || stages[stages.length - 1] || null;
      const ok = !!res.data.success;
      setStageResults(s => ({ ...s, [stageKey]: {
        loading: false, success: ok, data: res.data, stage: own, at: new Date().toLocaleTimeString() } }));
      ok ? toast.success(`${label}: success`)
         : toast.error(`${label} failed: ${res.data.failed_stage || own?.error || 'error'}`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'request failed';
      setStageResults(s => ({ ...s, [stageKey]: { loading: false, success: false, error: msg, at: new Date().toLocaleTimeString() } }));
      toast.error(`${label}: ${msg}`);
    }
  };


  const LISTING_TABS = [
    { id: 'property_details', label: 'Property Details' },
    { id: 'location',         label: 'Location' },
    { id: 'facilities',       label: 'Facilities' },
    { id: 'policies',         label: 'Policies' },
    { id: 'photos',           label: 'Photos' },
    { id: 'rooms',            label: 'Rooms' },
    { id: 'rate_plans',       label: 'Rate Plans' },
    { id: 'taxes',            label: 'Taxes' },
    { id: 'seo',              label: 'SEO' },
    { id: 'preview',          label: 'Preview' },
  ];
  // OTA-aligned categorised facilities master (Agoda/Booking.com/Expedia/Airbnb/MMT compatible)
  const LISTING_FACILITIES_CATEGORISED = {
    'General': ['Reception 24x7','Lift/Elevator','Air Conditioning','Heating','Power Backup','CCTV','24x7 Security','Fire Extinguisher','Smoke Detector','Non-Smoking Property','Garden','Terrace','Rooftop','Bonfire','Library','Newspaper'],
    'Internet': ['Free WiFi (Public Areas)','Free WiFi (In Rooms)','Paid WiFi','High-Speed Internet','Business Center Internet'],
    'Parking & Transport': ['Free Parking','Paid Parking','Valet Parking','EV Charging','Airport Shuttle (Free)','Airport Shuttle (Paid)','Car Rental','Bicycle Rental','Shuttle Service'],
    'Food & Drink': ['Restaurant','Bar','Coffee Shop','Lounge','Room Service','Breakfast Buffet','Vegetarian Options','Vegan Options','Halal Food','Kosher Food','Special Diet Meals','Minibar','Kitchenette','BBQ Facilities'],
    'Wellness & Recreation': ['Swimming Pool','Indoor Pool','Kids Pool','Spa','Sauna','Steam Room','Massage Services','Gym/Fitness Center','Yoga Studio','Tennis Court','Golf Course Access','Games Room','Kids Play Area','Water Sports'],
    'Business & Events': ['Business Centre','Conference Hall','Meeting Rooms','Banquet Hall','Wedding Hall','Audio/Visual Equipment','Fax/Photocopying','Printing Services','Coworking Space'],
    'Family & Kids': ['Family Rooms','Baby Cots','Babysitting Service','Kids Menu','Kids Club','High Chair'],
    'Accessibility': ['Wheelchair Accessible','Accessible Bathroom','Accessible Parking','Braille Signage','Elevator Access','Ground Floor Rooms'],
    'Services': ['Laundry','Dry Cleaning','Ironing Service','Concierge','Currency Exchange','ATM','Doctor on Call','Luggage Storage','Tours & Ticketing','Package Delivery','Wake-up Service','Multi-lingual Staff'],
    'Room Amenities': ['Safe/Locker','Slippers','Toiletries','Bottled Water','Balcony','Sea View','Mountain View'],
    'Rules-based': ['Pet Friendly','Couples Allowed','Bachelors Allowed','Unmarried Couples Allowed','Local IDs Accepted','Foreign Guests Allowed','Groups Allowed'],
  };
  const LISTING_FACILITIES_MASTER = Object.values(LISTING_FACILITIES_CATEGORISED).flat();

  const LISTING_PHOTO_CATEGORIES = [
    'Exterior','Lobby','Reception','Room','Bathroom','Suite','Dining','Restaurant','Bar','Kitchen',
    'Swimming Pool','Spa','Gym','Business Center','Meeting Room','Wedding Hall','View','Balcony',
    'Garden','Terrace','Kids Play Area','Entertainment','Amenities','Parking','Others'
  ];

  const LISTING_PROPERTY_TYPES = ['Hotel','Resort','Villa','Homestay','Apartment','Guesthouse','Hostel','Boutique Hotel','Serviced Apartment','Bed & Breakfast'];
  const LISTING_STAR_RATINGS = ['1','2','3','4','5'];
  const LISTING_PROPERTY_CLASSES = ['Budget','Mid-Range','Upscale','Luxury','Boutique'];
  const LISTING_CURRENCIES = ['INR','USD','EUR','GBP','AED','SGD','THB','JPY','AUD'];
  const LISTING_PAYMENT_METHODS = ['Cash','Credit Card','Debit Card','UPI','Bank Transfer','Digital Wallet','Cheque'];
  const LISTING_ACCEPTED_CARDS = ['Visa','MasterCard','American Express','RuPay','Diners Club','Discover','JCB'];
  const LISTING_BED_TYPES = ['King','Queen','Double','Twin','Single','Bunk','Sofa Bed'];
  const LISTING_VIEW_TYPES = ['City','Sea','Mountain','Garden','Pool','Courtyard','Street','No View'];
  const LISTING_MEAL_PLANS = ['Room Only (EP)','Breakfast (CP)','Half Board (MAP)','Full Board (AP)','All Inclusive'];
  const LISTING_CANCELLATION_TYPES = ['Flexible','Moderate','Strict','Non-Refundable','Free Cancellation 24h','Free Cancellation 48h','Free Cancellation 7 days'];

  // ---------- Agoda-aligned constants (Phase 1 P0) ----------
  const LISTING_LANGUAGE_CODES = [
    { code: 'en-US', label: 'English (United States)' },
    { code: 'en-GB', label: 'English (United Kingdom)' },
    { code: 'hi-IN', label: 'Hindi (India)' },
    { code: 'th-TH', label: 'Thai (Thailand)' },
    { code: 'id-ID', label: 'Indonesian (Indonesia)' },
    { code: 'ms-MY', label: 'Malay (Malaysia)' },
    { code: 'vi-VN', label: 'Vietnamese (Vietnam)' },
    { code: 'zh-CN', label: 'Chinese (Simplified)' },
    { code: 'zh-TW', label: 'Chinese (Traditional)' },
    { code: 'ja-JP', label: 'Japanese' },
    { code: 'ko-KR', label: 'Korean' },
    { code: 'ar-AE', label: 'Arabic (UAE)' },
    { code: 'fr-FR', label: 'French' },
    { code: 'de-DE', label: 'German' },
    { code: 'es-ES', label: 'Spanish' },
    { code: 'pt-PT', label: 'Portuguese' },
    { code: 'ru-RU', label: 'Russian' },
  ];
  const LISTING_ISO_COUNTRIES = [
    { iso2: 'IN', name: 'India' }, { iso2: 'TH', name: 'Thailand' }, { iso2: 'SG', name: 'Singapore' }, { iso2: 'MY', name: 'Malaysia' },
    { iso2: 'ID', name: 'Indonesia' }, { iso2: 'VN', name: 'Vietnam' }, { iso2: 'PH', name: 'Philippines' }, { iso2: 'LK', name: 'Sri Lanka' },
    { iso2: 'NP', name: 'Nepal' }, { iso2: 'BT', name: 'Bhutan' }, { iso2: 'MV', name: 'Maldives' }, { iso2: 'AE', name: 'United Arab Emirates' },
    { iso2: 'SA', name: 'Saudi Arabia' }, { iso2: 'OM', name: 'Oman' }, { iso2: 'QA', name: 'Qatar' }, { iso2: 'BH', name: 'Bahrain' },
    { iso2: 'KW', name: 'Kuwait' }, { iso2: 'TR', name: 'Türkiye' }, { iso2: 'US', name: 'United States' }, { iso2: 'CA', name: 'Canada' },
    { iso2: 'MX', name: 'Mexico' }, { iso2: 'GB', name: 'United Kingdom' }, { iso2: 'IE', name: 'Ireland' }, { iso2: 'FR', name: 'France' },
    { iso2: 'DE', name: 'Germany' }, { iso2: 'IT', name: 'Italy' }, { iso2: 'ES', name: 'Spain' }, { iso2: 'PT', name: 'Portugal' },
    { iso2: 'NL', name: 'Netherlands' }, { iso2: 'BE', name: 'Belgium' }, { iso2: 'CH', name: 'Switzerland' }, { iso2: 'AT', name: 'Austria' },
    { iso2: 'GR', name: 'Greece' }, { iso2: 'SE', name: 'Sweden' }, { iso2: 'NO', name: 'Norway' }, { iso2: 'DK', name: 'Denmark' },
    { iso2: 'FI', name: 'Finland' }, { iso2: 'PL', name: 'Poland' }, { iso2: 'CZ', name: 'Czechia' }, { iso2: 'HU', name: 'Hungary' },
    { iso2: 'RO', name: 'Romania' }, { iso2: 'RU', name: 'Russia' }, { iso2: 'AU', name: 'Australia' }, { iso2: 'NZ', name: 'New Zealand' },
    { iso2: 'JP', name: 'Japan' }, { iso2: 'KR', name: 'South Korea' }, { iso2: 'CN', name: 'China' }, { iso2: 'HK', name: 'Hong Kong SAR' },
    { iso2: 'TW', name: 'Taiwan' }, { iso2: 'MO', name: 'Macao SAR' }, { iso2: 'ZA', name: 'South Africa' }, { iso2: 'EG', name: 'Egypt' },
    { iso2: 'KE', name: 'Kenya' }, { iso2: 'TZ', name: 'Tanzania' }, { iso2: 'MA', name: 'Morocco' }, { iso2: 'BR', name: 'Brazil' },
    { iso2: 'AR', name: 'Argentina' }, { iso2: 'CL', name: 'Chile' },
  ];
  const LISTING_RATE_TYPES = [
    { agoda_id: 1, label: 'Net Excluding Tax',  desc: 'Supplier net price, taxes billed separately' },
    { agoda_id: 2, label: 'Net Including Tax',  desc: 'Supplier net price with taxes bundled' },
    { agoda_id: 3, label: 'Sell Excluding Tax', desc: 'Retail sell price, taxes billed separately' },
    { agoda_id: 4, label: 'Sell Including Tax', desc: 'Retail sell price with taxes bundled' },
  ];
  const LISTING_CANCEL_PENALTY_TYPES = ['nights', 'percent', 'fixed'];
  const LISTING_CANCEL_TIME_UNITS = ['hours', 'days'];

  const fetchListings = async () => {
    try {
      const res = await axios.get(`${API}/listings`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setListingsList(res.data?.listings || []);
      setOtasCatalog(res.data?.otas || []);
    } catch (err) { console.error('Failed to fetch listings:', err); }
  };

  // Feb 2026 — In single-property mode, Listings ALWAYS auto-opens the
  // editor for the selectedProperty. In All-Properties mode, the picker
  // list (`listingsList`) is used. Whenever the selected property changes
  // in single mode, close the current editor and reopen for the new one
  // so data never bleeds between properties.
  useEffect(() => {
    if (activeTab !== 'listings') return;
    if (allPropertiesMode) {
      if (!selectedListingId) fetchListings();
      return;
    }
    // Single-property mode: bind the editor strictly to selectedProperty.
    const pid = selectedProperty?.id;
    if (!pid) return;
    if (selectedListingId !== pid) {
      setListingDetail(null);
      setListingSectionInputs({});
      setListingSyncStatus({});
      openListingEditor(pid);
    }
    // eslint-disable-next-line
  }, [activeTab, allPropertiesMode, selectedProperty?.id, selectedListingId]);

  const fetchAgodaPayloadPreview = async (section = 'full_property') => {
    if (!selectedListingId) return;
    setAgodaPayloadState(s => ({ ...s, section, loading: true }));
    try {
      const res = await axios.get(
        `${API}/listings/${selectedListingId}/agoda-payload/preview`,
        { params: { section }, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      setAgodaPayloadState(s => ({ ...s, section, loading: false, preview: res.data }));
    } catch (err) {
      setAgodaPayloadState(s => ({ ...s, loading: false }));
      toast.error(err?.response?.data?.detail || 'Failed to load payload preview');
    }
  };

  const pushAgodaPayload = async (section) => {
    if (!selectedListingId) return;
    setAgodaPayloadState(s => ({ ...s, pushing: true }));
    try {
      const res = await axios.post(
        `${API}/listings/${selectedListingId}/agoda-payload/push`,
        {},
        { params: { section }, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      setAgodaPayloadState(s => ({ ...s, pushing: false, pushResult: res.data }));
      toast[res.data.success ? 'success' : 'error'](
        res.data.success ? 'Agoda Content Push accepted' : `Push failed: ${res.data.error || 'HTTP ' + (res.data.http_code || '?')}`,
      );
      fetchAgodaPayloadHistory();
    } catch (err) {
      setAgodaPayloadState(s => ({ ...s, pushing: false }));
      toast.error(err?.response?.data?.detail || 'Push failed');
    }
  };

  const fetchAgodaPayloadHistory = async () => {
    if (!selectedListingId) return;
    try {
      const res = await axios.get(
        `${API}/listings/${selectedListingId}/agoda-payload/history`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      setAgodaPayloadState(s => ({ ...s, history: res.data.logs || [] }));
    } catch (_e) { /* silent — panel refresh only */ }
  };

  const openAgodaPayloadHistoryEntry = async (log_id) => {
    if (!selectedListingId || !log_id) return;
    try {
      const res = await axios.get(
        `${API}/listings/${selectedListingId}/agoda-payload/history/${log_id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      setAgodaPayloadState(s => ({ ...s, pushResult: {
        success:              res.data.status === 'success',
        http_code:            res.data.http_code,
        duration_ms:          res.data.duration_ms,
        request_xml:          res.data.request_xml,
        response_xml:         res.data.response_xml,
        response_xml_pretty:  res.data.response_xml_pretty,
        request_ts:           res.data.request_ts,
        response_ts:          res.data.response_ts,
        error:                res.data.error,
      } }));
    } catch (_e) { /* silent */ }
  };

  useEffect(() => {
    if (activeTab === 'listings' && selectedListingId && agodaPayloadOpen) {
      fetchAgodaPayloadPreview(agodaPayloadState.section);
      fetchAgodaPayloadHistory();
    }
    // eslint-disable-next-line
  }, [activeTab, selectedListingId, agodaPayloadOpen]);

  useEffect(() => {
    if (activeTab === 'listings' && selectedListingId && listingTab === 'photos') {
      fetchAgodaCaptions();
    }
    // eslint-disable-next-line
  }, [activeTab, selectedListingId, listingTab]);

  const openListingEditor = async (propertyId) => {
    setSelectedListingId(propertyId);
    setListingTab('property_details');
    setListingSectionInputs({});
    try {
      const res = await axios.get(`${API}/listings/${propertyId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setListingDetail(res.data || null);
    } catch (err) { toast.error('Failed to open listing'); }
  };
  const closeListingEditor = () => {
    // In single-property mode there's no picker to go back to — closing
    // the editor just switches the nav to dashboard for that property so
    // listings can never orphan into an "empty picker" screen.
    if (!allPropertiesMode) {
      setActiveTab('dashboard');
      return;
    }
    setSelectedListingId(null);
    setListingDetail(null);
    setListingSectionInputs({});
    setListingSyncStatus({});
    fetchListings();
  };

  const getListingField = (section, field, fallback = '') => {
    const local = listingSectionInputs[section];
    if (local && local[field] !== undefined) return local[field];
    const saved = (listingDetail?.listing?.[section] || {})[field];
    return saved !== undefined && saved !== null ? saved : fallback;
  };
  const setListingField = (section, field, value) => {
    setListingSectionInputs(prev => ({ ...prev, [section]: { ...(prev[section] || {}), [field]: value } }));
  };

  const saveListingSection = async (sectionKey) => {
    if (!selectedListingId) return;
    const payload = {};
    if (['property_details','location','policies','taxes','seo','room_content'].includes(sectionKey)) {
      payload[sectionKey] = listingSectionInputs[sectionKey] || {};
    } else if (sectionKey === 'facilities') {
      payload.facilities = listingSectionInputs.facilities || (listingDetail?.listing?.facilities || []);
    } else if (sectionKey === 'rate_plans') {
      payload.rate_plans = listingSectionInputs.rate_plans || (listingDetail?.listing?.rate_plans || []);
    }
    setListingSaving(true);
    try {
      const res = await axios.put(`${API}/listings/${selectedListingId}`, payload, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setListingDetail(prev => ({ ...(prev || {}), listing: res.data.listing, validation: res.data.validation }));
      setListingSectionInputs(prev => { const n = { ...prev }; delete n[sectionKey]; return n; });
      toast.success('Section saved');
      // Save-only: PUT /listings never triggers Agoda sync. Agoda content is
      // pushed exclusively via the explicit "Agoda Payload" button.
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
    finally { setListingSaving(false); }
  };

  // Feb 2026 — small inline pill that summarizes an automatic Agoda Content Push
  // result next to each Save button. Reads from `listingSyncStatus[section]`.
  const renderListingSyncPill = (sectionKey) => {
    const s = listingSyncStatus[sectionKey];
    if (!s || !s.status) return null;
    const style = ({
      success: 'bg-green-100 text-green-700 border-green-300',
      failed:  'bg-red-100 text-red-700 border-red-300',
      pending: 'bg-amber-100 text-amber-800 border-amber-300',
      skipped: 'bg-gray-100 text-gray-600 border-gray-300',
    })[s.status] || 'bg-gray-100 text-gray-600 border-gray-300';
    const icon = ({ success: '✓', failed: '⚠', pending: '⏳', skipped: '↷' })[s.status] || '·';
    const label = ({
      success: 'Agoda Synced',
      failed:  'Agoda Sync Failed',
      pending: 'Agoda Sync Pending',
      skipped: 'Agoda Not Applicable',
    })[s.status] || s.status;
    const title = s.reason || (s.notif_type ? `NotifType=${s.notif_type}` : '');
    // Drop any redundant prefix from the reason so the pill doesn't read
    // "Agoda Sync Pending – Agoda Sync Pending – Property not yet linked".
    const rawReason = s.reason || '';
    const cleanedReason = rawReason
      .replace(/^\s*Agoda Sync (Pending|Failed|Synced|Not Applicable)\s*[–-]\s*/i, '')
      .trim();
    return (
      <span
        className={`text-[11px] px-2 py-0.5 rounded border ${style} inline-flex items-center gap-1 ml-2 align-middle`}
        title={title}
        data-testid={`listing-agoda-sync-status-${sectionKey}`}
      >
        <span aria-hidden="true">{icon}</span>
        <span>{label}</span>
        {cleanedReason && <span className="ml-1 opacity-80 truncate max-w-[280px]">– {cleanedReason}</span>}
      </span>
    );
  };

  const toggleListingFacility = (name) => {
    const current = listingSectionInputs.facilities || (listingDetail?.listing?.facilities || []);
    const next = current.includes(name) ? current.filter(x => x !== name) : [...current, name];
    setListingSectionInputs(prev => ({ ...prev, facilities: next }));
  };

  const uploadListingPhoto = async (file, category = 'Exterior', roomTypeId = null, captionCode = null) => {
    if (!selectedListingId || !file) return;
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file);
    });
    try {
      const body = { filename: file.name, data_url: dataUrl, category };
      if (roomTypeId) { body.room_type_id = roomTypeId; body.caption_code = captionCode; }
      const up = await axios.post(`${API}/listings/${selectedListingId}/photos`, body, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const res = await axios.get(`${API}/listings/${selectedListingId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setListingDetail(res.data);
      toast.success('Photo uploaded');
      const sync = up.data?.agoda_sync;
      if (sync) setListingSyncStatus(prev => ({ ...prev, photos: { ...sync, at: new Date().toISOString() } }));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
  };

  const fetchAgodaCaptions = async () => {
    if (!selectedListingId) return;
    setAgodaCaptions(prev => ({ ...prev, loading: true, error: null }));
    try {
      const res = await axios.get(`${API}/listings/${selectedListingId}/agoda/image-captions`, _authHeader());
      setAgodaCaptions({ loading: false, error: null, captions: res.data?.captions || [], default_code: res.data?.default_code || '', vacation_rental: !!res.data?.vacation_rental });
    } catch (err) {
      setAgodaCaptions({ loading: false, error: err?.response?.data?.detail || 'Could not load Agoda caption catalogue', captions: [], default_code: '', vacation_rental: false });
    }
  };

  const reassignListingPhotoRoom = async (photoId, roomTypeId, captionCode) => {
    if (!selectedListingId) return;
    try {
      const body = { room_type_id: roomTypeId || '' };
      if (captionCode !== undefined) body.caption_code = captionCode || '';
      await axios.put(`${API}/listings/${selectedListingId}/photos/${photoId}`, body, _authHeader());
      const res = await axios.get(`${API}/listings/${selectedListingId}`, _authHeader());
      setListingDetail(res.data);
      toast.warning('Room reassigned locally. Agoda room-image pushes are additive — any image already sent to Agoda for the previous room may remain on Agoda until removed there.', { duration: 7000 });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Reassign failed'); }
  };

  const deleteListingPhoto = async (photoId) => {
    if (!selectedListingId) return;
    const wasRoomPhoto = (listingDetail?.listing?.photos || []).find(p => p.id === photoId)?.room_type_id;
    try {
      const dl = await axios.delete(`${API}/listings/${selectedListingId}/photos/${photoId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const res = await axios.get(`${API}/listings/${selectedListingId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setListingDetail(res.data);
      if (wasRoomPhoto) {
        toast.warning('Photo deleted locally. Agoda room-image pushes are additive — if this image was already sent to Agoda it may remain on Agoda until removed there.', { duration: 7000 });
      }
      const sync = dl.data?.agoda_sync;
      if (sync) setListingSyncStatus(prev => ({ ...prev, photos: { ...sync, at: new Date().toISOString() } }));
    } catch (err) { toast.error('Delete failed'); }
  };

  const setListingPhotoMeta = async (photoId, patch) => {
    if (!selectedListingId) return;
    try {
      const up = await axios.put(`${API}/listings/${selectedListingId}/photos/${photoId}`, patch, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const res = await axios.get(`${API}/listings/${selectedListingId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setListingDetail(res.data);
      const sync = up.data?.agoda_sync;
      if (sync) setListingSyncStatus(prev => ({ ...prev, photos: { ...sync, at: new Date().toISOString() } }));
    } catch (err) { toast.error('Failed to update photo'); }
  };

  const setListingCoverPhoto = async (photoId) => {
    if (!selectedListingId) return;
    try {
      const up = await axios.put(`${API}/listings/${selectedListingId}/photos/${photoId}`, { is_cover: true }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const res = await axios.get(`${API}/listings/${selectedListingId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setListingDetail(res.data);
      toast.success('Cover photo set');
      const sync = up.data?.agoda_sync;
      if (sync) setListingSyncStatus(prev => ({ ...prev, photos: { ...sync, at: new Date().toISOString() } }));
    } catch (err) { toast.error('Failed to set cover'); }
  };

  const validateListing = async () => {
    if (!selectedListingId) return;
    try {
      await axios.post(`${API}/listings/${selectedListingId}/validate`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const res = await axios.get(`${API}/listings/${selectedListingId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setListingDetail(res.data);
      toast.success('Validation complete');
    } catch (err) { toast.error('Validation failed'); }
  };

  const publishListingToOTA = async (ota) => {
    if (!selectedListingId) return;
    try {
      await axios.post(`${API}/listings/${selectedListingId}/publish`, { ota }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const res = await axios.get(`${API}/listings/${selectedListingId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setListingDetail(res.data);
      toast.success(`Submitted to ${ota.toUpperCase()}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Publish failed'); }
  };

  const openSyncHistory = async () => {
    if (!selectedListingId) return;
    try {
      const res = await axios.get(`${API}/listings/${selectedListingId}/sync-history`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setListingSyncHistory(res.data?.logs || []);
      setListingSyncOpen(true);
    } catch (err) { toast.error('Failed to fetch sync history'); }
  };

  const backendOrigin = (typeof API === 'string' && API.replace(/\/api\/?$/, '')) || '';
  const listingStatusPill = (status) => {
    const cls = { draft: 'bg-gray-100 text-gray-700', ready: 'bg-blue-100 text-blue-700', submitted: 'bg-amber-100 text-amber-700', published: 'bg-green-100 text-green-700', failed: 'bg-red-100 text-red-700', needs_update: 'bg-orange-100 text-orange-700' }[status] || 'bg-gray-100 text-gray-700';
    return <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${cls}`}>{(status || 'draft').replace('_',' ')}</span>;
  };

  const renderListings = () => {
    if (!selectedListingId) {
      return (
        <div className="space-y-4">
          <div className="text-sm text-gray-600">Master OTA listing engine — enter content once, publish everywhere.</div>
          {listingsList.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 bg-gray-50 rounded">No properties available.</div>
          ) : listingsList.map(l => (
            <Card key={l.property_id} data-testid={`listing-card-${l.property_id}`}>
              <div className="p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-gray-800">{l.property_name}</div>
                    {listingStatusPill(l.status)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Completion: <strong>{l.completion_pct}%</strong>
                    {l.last_sync_at && <> · Last sync: {new Date(l.last_sync_at).toLocaleString()}</>}
                  </div>
                </div>
                <Button size="sm" onClick={() => openListingEditor(l.property_id)} className="bg-gradient-to-r from-orange-500 to-amber-600 text-white" data-testid={`listing-manage-${l.property_id}`}>Manage Listing</Button>
              </div>
            </Card>
          ))}
        </div>
      );
    }

    const L = listingDetail?.listing || {};
    const ctx = listingDetail?.context || {};
    const V = listingDetail?.validation || { completion_pct: 0, checks: [] };
    const otas = listingDetail?.otas || otasCatalog;
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <button onClick={closeListingEditor} className="text-sm text-orange-600 hover:underline" data-testid="listings-back">← Back to listings</button>
          <div className="flex items-center gap-2">
            {listingStatusPill(L.status)}
            <Button size="sm" variant="outline" onClick={validateListing} data-testid="listing-validate-btn">Validate</Button>
            <Button
              size="sm"
              variant="outline"
              className="border-orange-400 text-orange-700 hover:bg-orange-50"
              onClick={() => setAgodaPayloadOpen(true)}
              data-testid="listing-agoda-payload-btn"
            >Agoda Payload</Button>
            <Button
              size="sm"
              variant="outline"
              className="border-orange-400 text-orange-700 hover:bg-orange-50"
              onClick={() => {
                setAgodaPayloadOpen(true);
                setTimeout(() => {
                  document.querySelector('[data-testid="agoda-contracts-card"]')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 350);
              }}
              data-testid="listing-agoda-contracts-btn"
            >Agoda Contracts</Button>
            <Button size="sm" variant="outline" onClick={openSyncHistory} data-testid="listing-sync-history-btn">Sync History</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-3 space-y-3">
            <div className="flex flex-wrap gap-1 border-b">
              {LISTING_TABS.map(t => (
                <button key={t.id} onClick={() => setListingTab(t.id)} className={`px-3 py-2 text-sm border-b-2 transition-colors ${listingTab === t.id ? 'border-orange-500 text-orange-700 font-medium' : 'border-transparent text-gray-600 hover:text-gray-800'}`} data-testid={`listing-tab-${t.id}`}>{t.label}</button>
              ))}
            </div>

            <Card>
              <div className="p-4 space-y-3">
                {listingTab === 'property_details' && (<>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Property Name *</label>
                      <Input value={getListingField('property_details', 'property_name')} onChange={(e) => setListingField('property_details', 'property_name', e.target.value)} className="text-sm" data-testid="listing-input-property_details-property_name" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Brand / Chain Group</label>
                      <Input value={getListingField('property_details', 'brand')} onChange={(e) => setListingField('property_details', 'brand', e.target.value)} className="text-sm" data-testid="listing-input-property_details-brand" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Property Type *</label>
                      <select value={getListingField('property_details', 'property_type')} onChange={(e) => setListingField('property_details', 'property_type', e.target.value)} className="w-full text-sm border rounded px-2 py-1.5" data-testid="listing-input-property_details-property_type">
                        <option value="">Select…</option>
                        {LISTING_PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Star Rating *</label>
                      <select value={getListingField('property_details', 'star_rating')} onChange={(e) => setListingField('property_details', 'star_rating', e.target.value)} className="w-full text-sm border rounded px-2 py-1.5" data-testid="listing-input-property_details-star_rating">
                        <option value="">Select…</option>
                        {LISTING_STAR_RATINGS.map(s => <option key={s} value={s}>{s} Star</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Property Class</label>
                      <select value={getListingField('property_details', 'property_class')} onChange={(e) => setListingField('property_details', 'property_class', e.target.value)} className="w-full text-sm border rounded px-2 py-1.5" data-testid="listing-input-property_details-property_class">
                        <option value="">Select…</option>
                        {LISTING_PROPERTY_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Primary Currency *</label>
                      <select value={getListingField('property_details', 'primary_currency')} onChange={(e) => setListingField('property_details', 'primary_currency', e.target.value)} className="w-full text-sm border rounded px-2 py-1.5" data-testid="listing-input-property_details-primary_currency">
                        <option value="">Select…</option>
                        {LISTING_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Content Language * <span className="text-[10px] text-gray-500">(Agoda LanguageCode)</span></label>
                      <select value={getListingField('property_details', 'language_code') || 'en-US'} onChange={(e) => setListingField('property_details', 'language_code', e.target.value)} className="w-full text-sm border rounded px-2 py-1.5" data-testid="listing-input-property_details-language_code">
                        <option value="">Select…</option>
                        {LISTING_LANGUAGE_CODES.map(l => <option key={l.code} value={l.code}>{l.code} — {l.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Total Number of Rooms *</label>
                      <Input type="number" value={getListingField('property_details', 'number_of_rooms')} onChange={(e) => setListingField('property_details', 'number_of_rooms', e.target.value)} className="text-sm" data-testid="listing-input-property_details-number_of_rooms" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Total Floors</label>
                      <Input type="number" value={getListingField('property_details', 'total_floors')} onChange={(e) => setListingField('property_details', 'total_floors', e.target.value)} className="text-sm" data-testid="listing-input-property_details-total_floors" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Timezone</label>
                      <Input value={getListingField('property_details', 'timezone', 'Asia/Kolkata')} onChange={(e) => setListingField('property_details', 'timezone', e.target.value)} className="text-sm" data-testid="listing-input-property_details-timezone" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Year Built</label>
                      <Input type="number" value={getListingField('property_details', 'year_built')} onChange={(e) => setListingField('property_details', 'year_built', e.target.value)} className="text-sm" data-testid="listing-input-property_details-year_built" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Last Renovated</label>
                      <Input type="number" value={getListingField('property_details', 'last_renovated')} onChange={(e) => setListingField('property_details', 'last_renovated', e.target.value)} className="text-sm" data-testid="listing-input-property_details-last_renovated" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Languages Spoken</label>
                      <Input value={getListingField('property_details', 'languages_spoken')} onChange={(e) => setListingField('property_details', 'languages_spoken', e.target.value)} placeholder="English, Hindi, French" className="text-sm" data-testid="listing-input-property_details-languages_spoken" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Contact Number *</label>
                      <Input value={getListingField('property_details', 'contact_number')} onChange={(e) => setListingField('property_details', 'contact_number', e.target.value)} className="text-sm" data-testid="listing-input-property_details-contact_number" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Alternate Contact</label>
                      <Input value={getListingField('property_details', 'alternate_contact')} onChange={(e) => setListingField('property_details', 'alternate_contact', e.target.value)} className="text-sm" data-testid="listing-input-property_details-alternate_contact" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
                      <Input type="email" value={getListingField('property_details', 'email')} onChange={(e) => setListingField('property_details', 'email', e.target.value)} className="text-sm" data-testid="listing-input-property_details-email" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Website</label>
                      <Input value={getListingField('property_details', 'website')} onChange={(e) => setListingField('property_details', 'website', e.target.value)} placeholder="https://" className="text-sm" data-testid="listing-input-property_details-website" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">PAN / GST Number</label>
                      <Input value={getListingField('property_details', 'pan_gst_number')} onChange={(e) => setListingField('property_details', 'pan_gst_number', e.target.value)} className="text-sm" data-testid="listing-input-property_details-pan_gst_number" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Short Description * (marketing headline, 1-2 lines)</label>
                    <textarea rows={2} value={getListingField('property_details', 'short_description')} onChange={(e) => setListingField('property_details', 'short_description', e.target.value)} className="w-full text-sm border rounded px-2 py-1" data-testid="listing-input-property_details-short_description" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Long Description * (min 50 chars)</label>
                    <textarea rows={5} value={getListingField('property_details', 'description')} onChange={(e) => setListingField('property_details', 'description', e.target.value)} className="w-full text-sm border rounded px-2 py-1" data-testid="listing-input-property_details-description" />
                    <div className="text-xs text-gray-500 mt-1">{(getListingField('property_details', 'description') || '').length} / 50 chars minimum</div>
                  </div>
                  <div className="flex justify-end items-center gap-2"><Button size="sm" onClick={() => saveListingSection('property_details')} disabled={listingSaving} className="bg-orange-500 text-white" data-testid="listing-save-property_details">Save Property Details</Button>{renderListingSyncPill('property_details')}</div>
                </>)}

                {listingTab === 'location' && (<>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Street Address *</label>
                      <Input value={getListingField('location', 'address')} onChange={(e) => setListingField('location', 'address', e.target.value)} className="text-sm" data-testid="listing-input-location-address" />
                    </div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Area / Neighborhood</label><Input value={getListingField('location', 'neighborhood')} onChange={(e) => setListingField('location', 'neighborhood', e.target.value)} className="text-sm" data-testid="listing-input-location-neighborhood" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">City *</label><Input value={getListingField('location', 'city')} onChange={(e) => setListingField('location', 'city', e.target.value)} className="text-sm" data-testid="listing-input-location-city" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">State / Province</label><Input value={getListingField('location', 'state')} onChange={(e) => setListingField('location', 'state', e.target.value)} className="text-sm" data-testid="listing-input-location-state" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Country *</label><Input value={getListingField('location', 'country')} onChange={(e) => setListingField('location', 'country', e.target.value)} className="text-sm" data-testid="listing-input-location-country" /></div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Country ISO-2 * <span className="text-[10px] text-gray-500">(Agoda CountryName)</span></label>
                      <select value={getListingField('location', 'country_iso2')} onChange={(e) => { const iso = e.target.value; const picked = LISTING_ISO_COUNTRIES.find(c => c.iso2 === iso); setListingField('location', 'country_iso2', iso); if (picked && !getListingField('location', 'country')) setListingField('location', 'country', picked.name); }} className="w-full text-sm border rounded px-2 py-1.5" data-testid="listing-input-location-country_iso2">
                        <option value="">Select ISO code…</option>
                        {LISTING_ISO_COUNTRIES.map(c => <option key={c.iso2} value={c.iso2}>{c.iso2} — {c.name}</option>)}
                      </select>
                    </div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Postal Code *</label><Input value={getListingField('location', 'postal_code')} onChange={(e) => setListingField('location', 'postal_code', e.target.value)} className="text-sm" data-testid="listing-input-location-postal_code" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Latitude *</label><Input type="number" step="0.000001" value={getListingField('location', 'latitude')} onChange={(e) => setListingField('location', 'latitude', e.target.value)} className="text-sm" data-testid="listing-input-location-latitude" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Longitude *</label><Input type="number" step="0.000001" value={getListingField('location', 'longitude')} onChange={(e) => setListingField('location', 'longitude', e.target.value)} className="text-sm" data-testid="listing-input-location-longitude" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Airport Distance (km)</label><Input value={getListingField('location', 'airport_distance')} onChange={(e) => setListingField('location', 'airport_distance', e.target.value)} className="text-sm" data-testid="listing-input-location-airport_distance" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Railway Distance (km)</label><Input value={getListingField('location', 'railway_distance')} onChange={(e) => setListingField('location', 'railway_distance', e.target.value)} className="text-sm" data-testid="listing-input-location-railway_distance" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Metro Distance (km)</label><Input value={getListingField('location', 'metro_distance')} onChange={(e) => setListingField('location', 'metro_distance', e.target.value)} className="text-sm" data-testid="listing-input-location-metro_distance" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Bus Stand Distance (km)</label><Input value={getListingField('location', 'bus_distance')} onChange={(e) => setListingField('location', 'bus_distance', e.target.value)} className="text-sm" data-testid="listing-input-location-bus_distance" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Beach Distance (km)</label><Input value={getListingField('location', 'beach_distance')} onChange={(e) => setListingField('location', 'beach_distance', e.target.value)} className="text-sm" data-testid="listing-input-location-beach_distance" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">City Centre Distance (km)</label><Input value={getListingField('location', 'city_center_distance')} onChange={(e) => setListingField('location', 'city_center_distance', e.target.value)} className="text-sm" data-testid="listing-input-location-city_center_distance" /></div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Nearby Attractions / Landmarks (comma-separated)</label>
                    <textarea rows={2} value={getListingField('location', 'nearby_attractions')} onChange={(e) => setListingField('location', 'nearby_attractions', e.target.value)} className="w-full text-sm border rounded px-2 py-1" data-testid="listing-input-location-nearby_attractions" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Directions</label>
                    <textarea rows={2} value={getListingField('location', 'directions')} onChange={(e) => setListingField('location', 'directions', e.target.value)} className="w-full text-sm border rounded px-2 py-1" data-testid="listing-input-location-directions" />
                  </div>
                  <div className="text-xs text-gray-500 italic">Google Maps picker planned in Phase 2 — for now enter lat/lng manually.</div>
                  <div className="flex justify-end items-center gap-2"><Button size="sm" onClick={() => saveListingSection('location')} disabled={listingSaving} className="bg-orange-500 text-white" data-testid="listing-save-location">Save Location</Button>{renderListingSyncPill('location')}</div>
                </>)}

                {listingTab === 'facilities' && (() => {
                  const current = listingSectionInputs.facilities || (L.facilities || []);
                  return (<>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs text-gray-600"><strong>{current.length}</strong> selected · min 5 recommended for OTA visibility</div>
                      <div className="flex items-center gap-2"><Button size="sm" onClick={() => saveListingSection('facilities')} className="bg-orange-500 text-white" data-testid="listing-save-facilities">Save Facilities</Button>{renderListingSyncPill('facilities')}</div>
                    </div>
                    <div className="max-h-[500px] overflow-y-auto space-y-3">
                      {Object.entries(LISTING_FACILITIES_CATEGORISED).map(([cat, items]) => (
                        <div key={cat} className="border rounded p-2" data-testid={`listing-facility-cat-${cat.replace(/\s+/g,'-').toLowerCase()}`}>
                          <div className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">{cat} <span className="text-[10px] text-gray-500 normal-case">({items.filter(f => current.includes(f)).length}/{items.length})</span></div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                            {items.map(fname => (
                              <label key={fname} className="flex items-center gap-2 text-xs p-1 hover:bg-gray-50 rounded cursor-pointer">
                                <input type="checkbox" checked={current.includes(fname)} onChange={() => toggleListingFacility(fname)} data-testid={`listing-facility-${fname.replace(/\s+/g,'-').toLowerCase()}`} />
                                <span>{fname}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>);
                })()}

                {listingTab === 'policies' && (<>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Check-in Time *</label><Input type="time" value={getListingField('policies', 'checkin_time')} onChange={(e) => setListingField('policies', 'checkin_time', e.target.value)} className="text-sm" data-testid="listing-input-policies-checkin_time" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Check-out Time *</label><Input type="time" value={getListingField('policies', 'checkout_time')} onChange={(e) => setListingField('policies', 'checkout_time', e.target.value)} className="text-sm" data-testid="listing-input-policies-checkout_time" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Min Age for Check-in</label><Input type="number" value={getListingField('policies', 'min_age_checkin', 18)} onChange={(e) => setListingField('policies', 'min_age_checkin', e.target.value)} className="text-sm" data-testid="listing-input-policies-min_age_checkin" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Security Deposit Amount</label><Input value={getListingField('policies', 'security_deposit')} onChange={(e) => setListingField('policies', 'security_deposit', e.target.value)} className="text-sm" data-testid="listing-input-policies-security_deposit" /></div>
                    <div><label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!getListingField('policies', 'local_id_accepted')} onChange={(e) => setListingField('policies', 'local_id_accepted', e.target.checked)} data-testid="listing-input-policies-local_id_accepted" /> Local IDs Accepted</label></div>
                    <div><label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!getListingField('policies', 'foreign_guests_allowed')} onChange={(e) => setListingField('policies', 'foreign_guests_allowed', e.target.checked)} data-testid="listing-input-policies-foreign_guests_allowed" /> Foreign Guests Allowed</label></div>
                    <div><label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!getListingField('policies', 'couples_allowed')} onChange={(e) => setListingField('policies', 'couples_allowed', e.target.checked)} data-testid="listing-input-policies-couples_allowed" /> Unmarried Couples Allowed</label></div>
                    <div><label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!getListingField('policies', 'bachelor_allowed')} onChange={(e) => setListingField('policies', 'bachelor_allowed', e.target.checked)} data-testid="listing-input-policies-bachelor_allowed" /> Bachelors Allowed</label></div>
                    <div><label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!getListingField('policies', 'groups_allowed')} onChange={(e) => setListingField('policies', 'groups_allowed', e.target.checked)} data-testid="listing-input-policies-groups_allowed" /> Groups Allowed</label></div>
                    <div><label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!getListingField('policies', 'id_proof_required')} onChange={(e) => setListingField('policies', 'id_proof_required', e.target.checked)} data-testid="listing-input-policies-id_proof_required" /> ID Proof Required at Check-in</label></div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Payment Methods Accepted *</label>
                    <div className="flex flex-wrap gap-2">
                      {LISTING_PAYMENT_METHODS.map(pm => {
                        const arr = getListingField('policies', 'payment_methods', []) || [];
                        const cur = Array.isArray(arr) ? arr : [];
                        const on = cur.includes(pm);
                        return (
                          <label key={pm} className={`px-2 py-1 text-xs rounded border cursor-pointer ${on ? 'bg-orange-100 border-orange-500 text-orange-800' : 'bg-white border-gray-300'}`}>
                            <input type="checkbox" className="hidden" checked={on} onChange={() => setListingField('policies', 'payment_methods', on ? cur.filter(x => x !== pm) : [...cur, pm])} data-testid={`listing-payment-method-${pm.replace(/\s+/g,'-').toLowerCase()}`} />
                            {pm}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Accepted Credit Cards</label>
                    <div className="flex flex-wrap gap-2">
                      {LISTING_ACCEPTED_CARDS.map(c => {
                        const arr = getListingField('policies', 'accepted_cards', []) || [];
                        const cur = Array.isArray(arr) ? arr : [];
                        const on = cur.includes(c);
                        return (
                          <label key={c} className={`px-2 py-1 text-xs rounded border cursor-pointer ${on ? 'bg-orange-100 border-orange-500 text-orange-800' : 'bg-white border-gray-300'}`}>
                            <input type="checkbox" className="hidden" checked={on} onChange={() => setListingField('policies', 'accepted_cards', on ? cur.filter(x => x !== c) : [...cur, c])} data-testid={`listing-accepted-card-${c.replace(/\s+/g,'-').toLowerCase()}`} />
                            {c}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div><label className="block text-xs font-medium text-gray-700 mb-1">Children Policy</label><textarea rows={2} value={getListingField('policies', 'children_policy')} onChange={(e) => setListingField('policies', 'children_policy', e.target.value)} className="w-full text-sm border rounded px-2 py-1" data-testid="listing-input-policies-children_policy" /></div>
                  <div><label className="block text-xs font-medium text-gray-700 mb-1">Extra Bed Policy</label><textarea rows={2} value={getListingField('policies', 'extra_bed_policy')} onChange={(e) => setListingField('policies', 'extra_bed_policy', e.target.value)} className="w-full text-sm border rounded px-2 py-1" data-testid="listing-input-policies-extra_bed_policy" /></div>
                  <div><label className="block text-xs font-medium text-gray-700 mb-1">Smoking Policy</label><Input value={getListingField('policies', 'smoking_policy')} onChange={(e) => setListingField('policies', 'smoking_policy', e.target.value)} className="text-sm" data-testid="listing-input-policies-smoking_policy" /></div>
                  <div><label className="block text-xs font-medium text-gray-700 mb-1">Pet Policy</label><Input value={getListingField('policies', 'pet_policy')} onChange={(e) => setListingField('policies', 'pet_policy', e.target.value)} className="text-sm" data-testid="listing-input-policies-pet_policy" /></div>
                  <div><label className="block text-xs font-medium text-gray-700 mb-1">Cancellation Policy *</label><textarea rows={3} value={getListingField('policies', 'cancellation_policy')} onChange={(e) => setListingField('policies', 'cancellation_policy', e.target.value)} className="w-full text-sm border rounded px-2 py-1" data-testid="listing-input-policies-cancellation_policy" /></div>
                  <div><label className="block text-xs font-medium text-gray-700 mb-1">Refund Policy</label><textarea rows={2} value={getListingField('policies', 'refund_policy')} onChange={(e) => setListingField('policies', 'refund_policy', e.target.value)} className="w-full text-sm border rounded px-2 py-1" data-testid="listing-input-policies-refund_policy" /></div>
                  <div><label className="block text-xs font-medium text-gray-700 mb-1">Early Check-in Policy</label><Input value={getListingField('policies', 'early_checkin_policy')} onChange={(e) => setListingField('policies', 'early_checkin_policy', e.target.value)} className="text-sm" data-testid="listing-input-policies-early_checkin_policy" /></div>
                  <div><label className="block text-xs font-medium text-gray-700 mb-1">Late Check-out Policy</label><Input value={getListingField('policies', 'late_checkout_policy')} onChange={(e) => setListingField('policies', 'late_checkout_policy', e.target.value)} className="text-sm" data-testid="listing-input-policies-late_checkout_policy" /></div>
                  <div className="flex justify-end items-center gap-2"><Button size="sm" onClick={() => saveListingSection('policies')} className="bg-orange-500 text-white" data-testid="listing-save-policies">Save Policies</Button>{renderListingSyncPill('policies')}</div>
                </>)}

                {listingTab === 'photos' && (() => {
                  const roomsCtx = ctx.room_types || [];
                  const roomName = (rtid) => { const r = roomsCtx.find(x => x.id === rtid); return r ? (r.name || 'Room') : '(unknown room)'; };
                  const capLabel = (code) => { const c = (agodaCaptions.captions || []).find(x => String(x.code) === String(code)); return c ? c.name : (code ? `Code ${code}` : ''); };
                  const isRoomCat = photoUploadCat === 'Room Type';
                  const captionsReady = !agodaCaptions.loading && !agodaCaptions.error && (agodaCaptions.captions || []).length > 0;
                  const canUploadRoom = isRoomCat ? (!!photoUploadRoom && !!photoUploadCaption && captionsReady) : true;
                  return (<>
                  <div className="flex items-start gap-2 flex-wrap mb-3 bg-gray-50 border rounded p-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="text-xs font-medium text-gray-700">Upload as category:</label>
                      <select value={photoUploadCat} onChange={(e) => { setPhotoUploadCat(e.target.value); if (e.target.value === 'Room Type') setPhotoUploadCaption(agodaCaptions.default_code || ''); }} className="text-xs border rounded px-2 py-1" data-testid="listing-photo-upload-category">
                        <option value="Room Type">Room Type</option>
                        {LISTING_PHOTO_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    {isRoomCat && (
                      <div className="flex items-center gap-2 flex-wrap" data-testid="listing-photo-room-controls">
                        <select value={photoUploadRoom} onChange={(e) => setPhotoUploadRoom(e.target.value)} className="text-xs border rounded px-2 py-1" data-testid="listing-photo-upload-room">
                          <option value="">Select room type…</option>
                          {roomsCtx.map(rt => <option key={rt.id} value={rt.id}>{rt.name}{rt.max_occupancy ? ` (max ${rt.max_occupancy})` : ''}</option>)}
                        </select>
                        <select value={photoUploadCaption} onChange={(e) => setPhotoUploadCaption(e.target.value)} disabled={!captionsReady} className="text-xs border rounded px-2 py-1" data-testid="listing-photo-upload-caption-kind">
                          <option value="">{agodaCaptions.loading ? 'Loading captions…' : 'Select photo kind…'}</option>
                          {(agodaCaptions.captions || []).map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                        </select>
                        {agodaCaptions.vacation_rental && <span className="text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded" data-testid="listing-photo-vr-note">Vacation-rental: only VR-allowed captions shown</span>}
                      </div>
                    )}
                    <input type="file" accept="image/*" multiple disabled={!canUploadRoom} onChange={async (e) => { const files = Array.from(e.target.files || []); const cat = isRoomCat ? 'Room Type' : photoUploadCat; const room = isRoomCat ? photoUploadRoom : null; const caption = isRoomCat ? photoUploadCaption : null; e.target.value = ''; for (const f of files) { await uploadListingPhoto(f, cat, room, caption); } }} className="text-xs" data-testid="listing-photo-upload-input" />
                    <div className="text-[10px] text-gray-500 w-full">Min 3 photos required. First photo becomes cover automatically. Choose <strong>Room Type</strong> to attach a photo to a specific room (sent to Agoda inside that room).</div>
                    {isRoomCat && agodaCaptions.error && <div className="text-[11px] text-red-600 bg-red-50 p-2 rounded w-full" data-testid="listing-photo-caption-error">Agoda caption catalogue unavailable — room photos cannot be uploaded until it loads. {agodaCaptions.error} <button className="underline ml-1" onClick={fetchAgodaCaptions} data-testid="listing-photo-caption-retry">Retry</button></div>}
                    {isRoomCat && !photoUploadRoom && captionsReady && <div className="text-[10px] text-amber-600 w-full">Select a room type to enable upload.</div>}
                    <div className="ml-auto flex items-center">{renderListingSyncPill('photos')}</div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {(L.photos || []).map(p => (
                      <div key={p.id} className={`relative border rounded overflow-hidden ${p.is_cover ? 'ring-2 ring-orange-500' : ''} ${p.room_type_id ? 'border-indigo-300' : ''}`} data-testid={`listing-photo-${p.id}`}>
                        {p.room_type_id && <span className="absolute top-1 left-1 z-10 text-[10px] bg-indigo-600 text-white px-1.5 py-0.5 rounded" data-testid={`listing-photo-${p.id}-room-badge`}>Room: {roomName(p.room_type_id)}</span>}
                        <img src={`${backendOrigin}${p.url}`} alt={p.caption || p.category || 'photo'} className="w-full h-32 object-cover" loading="lazy" />
                        <div className="p-2 text-xs space-y-1">
                          {p.room_type_id ? (
                            <>
                              <select value={'Room Type'} onChange={(e) => { const v = e.target.value; if (v !== 'Room Type') setListingPhotoMeta(p.id, { category: v, room_type_id: '', caption_code: '' }); }} className="w-full text-xs border rounded px-1 py-0.5" data-testid={`listing-photo-${p.id}-kind`}>
                                <option value="Room Type">Room Type</option>
                                {LISTING_PHOTO_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                              <select value={p.room_type_id} onChange={(e) => reassignListingPhotoRoom(p.id, e.target.value)} className="w-full text-xs border rounded px-1 py-0.5" data-testid={`listing-photo-${p.id}-reassign-room`}>
                                {roomsCtx.map(rt => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                              </select>
                              <select value={p.caption_code || ''} onChange={(e) => setListingPhotoMeta(p.id, { caption_code: e.target.value })} disabled={!captionsReady} className="w-full text-xs border rounded px-1 py-0.5" data-testid={`listing-photo-${p.id}-caption-kind`}>
                                <option value="">{agodaCaptions.loading ? 'Loading captions…' : 'Select photo kind…'}</option>
                                {(agodaCaptions.captions || []).map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                              </select>
                            </>
                          ) : (
                            <select value={p.category || 'Exterior'} onChange={(e) => { const v = e.target.value; if (v === 'Room Type') { const rtid = (roomsCtx[0] && roomsCtx[0].id) || ''; if (!rtid) { toast.error('Add a room type first to assign this photo to a room.'); return; } reassignListingPhotoRoom(p.id, rtid, agodaCaptions.default_code || ''); } else { setListingPhotoMeta(p.id, { category: v }); } }} className="w-full text-xs border rounded px-1 py-0.5" data-testid={`listing-photo-${p.id}-kind`}>
                              <option value="Room Type">Room Type</option>
                              {LISTING_PHOTO_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          )}
                          <Input placeholder="Caption / alt text" defaultValue={p.caption || ''} onBlur={(e) => { if (e.target.value !== (p.caption || '')) setListingPhotoMeta(p.id, { caption: e.target.value }); }} className="text-xs h-7" data-testid={`listing-photo-${p.id}-caption`} />
                          <div className="flex gap-2 items-center">
                            {!p.is_cover && <button className="text-orange-600 underline text-[11px]" onClick={() => setListingCoverPhoto(p.id)} data-testid={`listing-photo-${p.id}-set-cover`}>Set cover</button>}
                            {p.is_cover && <span className="text-green-700 text-[11px]">★ Cover</span>}
                            <button className="text-red-600 underline ml-auto text-[11px]" onClick={() => deleteListingPhoto(p.id)} data-testid={`listing-photo-${p.id}-delete`}>Delete</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {(L.photos || []).length === 0 && <div className="text-xs text-gray-500 italic mt-3">No photos uploaded yet.</div>}
                  </>);
                })()}

                {listingTab === 'rooms' && (<>
                  <div className="text-xs text-gray-600 mb-1">Room types are managed under <strong>Settings → Room Types</strong>. Enrich each room here with OTA-facing content (a room is counted &quot;authored&quot; once its description is filled).</div>
                  {(ctx.room_types || []).length === 0 && <div className="text-xs text-red-600 bg-red-50 p-2 rounded">No room types yet — add them under Settings first.</div>}
                  {(ctx.room_types || []).map(rt => {
                    const rc = (L.room_content || {})[rt.id] || {};
                    const buf = ((listingSectionInputs.room_content || {})[rt.id]) || {};
                    const val = (k, fallback = '') => (buf[k] !== undefined ? buf[k] : (rc[k] !== undefined && rc[k] !== null ? rc[k] : fallback));
                    const upd = (k, v) => setListingSectionInputs(prev => ({ ...prev, room_content: { ...(prev.room_content || {}), [rt.id]: { ...(prev.room_content?.[rt.id] || {}), [k]: v } } }));
                    return (
                      <div key={rt.id} className="border rounded p-3 space-y-2 bg-gray-50" data-testid={`listing-room-${rt.id}`}>
                        <div className="font-medium text-sm text-gray-800">{rt.name} <span className="text-xs text-gray-500">(max occupancy {rt.max_occupancy})</span></div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-0.5">Description *</label>
                          <textarea rows={2} value={val('description')} onChange={(e) => upd('description', e.target.value)} className="w-full text-sm border rounded px-2 py-1" data-testid={`listing-room-${rt.id}-description`} />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          <div><label className="block text-xs text-gray-600 mb-0.5">Room Size (sq ft)</label><Input type="number" className="text-sm" value={val('room_size_sqft')} onChange={(e) => upd('room_size_sqft', e.target.value)} data-testid={`listing-room-${rt.id}-room_size_sqft`} /></div>
                          <div><label className="block text-xs text-gray-600 mb-0.5">Room Size (sq m)</label><Input type="number" className="text-sm" value={val('room_size_sqm')} onChange={(e) => upd('room_size_sqm', e.target.value)} data-testid={`listing-room-${rt.id}-room_size_sqm`} /></div>
                          <div><label className="block text-xs text-gray-600 mb-0.5">Bed Type</label>
                            <select value={val('bed_type')} onChange={(e) => upd('bed_type', e.target.value)} className="w-full text-sm border rounded px-2 py-1" data-testid={`listing-room-${rt.id}-bed_type`}>
                              <option value="">Select…</option>
                              {LISTING_BED_TYPES.map(b => <option key={b} value={b}>{b}</option>)}
                            </select>
                          </div>
                          <div><label className="block text-xs text-gray-600 mb-0.5">Number of Beds</label><Input type="number" className="text-sm" value={val('bed_count', 1)} onChange={(e) => upd('bed_count', e.target.value)} data-testid={`listing-room-${rt.id}-bed_count`} /></div>
                          <div><label className="block text-xs text-gray-600 mb-0.5">Number of Bathrooms</label><Input type="number" className="text-sm" value={val('bathrooms', 1)} onChange={(e) => upd('bathrooms', e.target.value)} data-testid={`listing-room-${rt.id}-bathrooms`} /></div>
                          <div><label className="block text-xs text-gray-600 mb-0.5">Max Adults</label><Input type="number" className="text-sm" value={val('max_adults', 2)} onChange={(e) => upd('max_adults', e.target.value)} data-testid={`listing-room-${rt.id}-max_adults`} /></div>
                          <div><label className="block text-xs text-gray-600 mb-0.5">Max Children</label><Input type="number" className="text-sm" value={val('max_children', 0)} onChange={(e) => upd('max_children', e.target.value)} data-testid={`listing-room-${rt.id}-max_children`} /></div>
                          <div><label className="block text-xs text-gray-600 mb-0.5">View Type</label>
                            <select value={val('view_type')} onChange={(e) => upd('view_type', e.target.value)} className="w-full text-sm border rounded px-2 py-1" data-testid={`listing-room-${rt.id}-view_type`}>
                              <option value="">Select…</option>
                              {LISTING_VIEW_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                          </div>
                          <div><label className="block text-xs text-gray-600 mb-0.5">Extra Bed Cost</label><Input type="number" className="text-sm" value={val('extra_bed_cost')} onChange={(e) => upd('extra_bed_cost', e.target.value)} data-testid={`listing-room-${rt.id}-extra_bed_cost`} /></div>
                        </div>
                        <div className="flex flex-wrap gap-3">
                          <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={!!val('non_smoking')} onChange={(e) => upd('non_smoking', e.target.checked)} data-testid={`listing-room-${rt.id}-non_smoking`} /> Non-Smoking</label>
                          <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={!!val('extra_bed_available')} onChange={(e) => upd('extra_bed_available', e.target.checked)} data-testid={`listing-room-${rt.id}-extra_bed_available`} /> Extra Bed Available</label>
                          <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={!!val('has_balcony')} onChange={(e) => upd('has_balcony', e.target.checked)} data-testid={`listing-room-${rt.id}-has_balcony`} /> Has Balcony</label>
                          <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={!!val('accessible')} onChange={(e) => upd('accessible', e.target.checked)} data-testid={`listing-room-${rt.id}-accessible`} /> Wheelchair Accessible</label>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-0.5">Room Amenities (comma-separated)</label>
                          <Input className="text-sm" value={val('amenities')} onChange={(e) => upd('amenities', e.target.value)} placeholder="TV, AC, WiFi, Minibar, Safe, Hair Dryer, Coffee Maker" data-testid={`listing-room-${rt.id}-amenities`} />
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex justify-end items-center gap-2"><Button size="sm" onClick={() => saveListingSection('room_content')} className="bg-orange-500 text-white" data-testid="listing-save-room_content">Save Rooms</Button>{renderListingSyncPill('room_content')}</div>
                </>)}

                {listingTab === 'rate_plans' && (() => {
                  const current = listingSectionInputs.rate_plans || (L.rate_plans || []);
                  const roomTypesCtx = ctx.room_types || [];
                  const setPlan = (i, k, v) => { const next = [...current]; next[i] = { ...(next[i] || {}), [k]: v }; setListingSectionInputs(prev => ({ ...prev, rate_plans: next })); };
                  const addPlan = () => setListingSectionInputs(prev => ({ ...prev, rate_plans: [...current, { code: 'CP', meal_plan: 'Breakfast (CP)', refundable: true, rate_type: 2, cancel_penalties: [{ deadline: 24, time_unit: 'hours', penalty_type: 'nights', amount: 1 }], room_bindings: [], inclusions: [], min_stay: 1, max_stay: 30 }] }));
                  const removePlan = (i) => setListingSectionInputs(prev => ({ ...prev, rate_plans: current.filter((_, idx) => idx !== i) }));
                  const toggleBinding = (i, rtId) => { const cur = current[i]?.room_bindings || []; const on = cur.includes(rtId); setPlan(i, 'room_bindings', on ? cur.filter(x => x !== rtId) : [...cur, rtId]); };
                  const addPenalty = (i) => { const cur = current[i]?.cancel_penalties || []; setPlan(i, 'cancel_penalties', [...cur, { deadline: 24, time_unit: 'hours', penalty_type: 'nights', amount: 1 }]); };
                  const setPenalty = (i, j, k, v) => { const cur = [...(current[i]?.cancel_penalties || [])]; cur[j] = { ...(cur[j] || {}), [k]: v }; setPlan(i, 'cancel_penalties', cur); };
                  const removePenalty = (i, j) => { const cur = (current[i]?.cancel_penalties || []).filter((_, idx) => idx !== j); setPlan(i, 'cancel_penalties', cur); };
                  return (<>
                    <div className="text-xs text-gray-600 mb-2">Every rate plan needs a <strong>RatePlanType</strong>, at least one <strong>CancelPenalty</strong>, and ≥1 linked room type — required by Agoda before content push.</div>
                    {current.length === 0 && <div className="text-xs text-red-600 bg-red-50 p-2 rounded">No rate plans yet — click &quot;+ Add rate plan link&quot;.</div>}
                    {current.map((p, i) => (
                      <div key={i} className="border rounded p-3 bg-gray-50 space-y-3" data-testid={`listing-rate-plan-${i}`}>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div><label className="block text-xs text-gray-600 mb-0.5">Plan Code *</label>
                            <select value={p.code || 'CP'} onChange={(e) => setPlan(i, 'code', e.target.value)} className="w-full text-sm border rounded px-2 py-1" data-testid={`listing-rate-plan-${i}-code`}>
                              {['EP','CP','MAP','AP','AI','CUSTOM'].map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div className="md:col-span-2"><label className="block text-xs text-gray-600 mb-0.5">Plan Name / Label</label>
                            <Input value={p.name || ''} onChange={(e) => setPlan(i, 'name', e.target.value)} placeholder="Best Available Rate" className="text-sm" data-testid={`listing-rate-plan-${i}-name`} />
                          </div>
                          <div><label className="block text-xs text-gray-600 mb-0.5">Meal Plan</label>
                            <select value={p.meal_plan || ''} onChange={(e) => setPlan(i, 'meal_plan', e.target.value)} className="w-full text-sm border rounded px-2 py-1" data-testid={`listing-rate-plan-${i}-meal_plan`}>
                              <option value="">Select…</option>
                              {LISTING_MEAL_PLANS.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                          </div>
                          <div className="md:col-span-2"><label className="block text-xs text-gray-600 mb-0.5">Rate Plan Type * <span className="text-[10px] text-gray-500">(Agoda RatePlanType 1-4)</span></label>
                            <select value={p.rate_type || ''} onChange={(e) => setPlan(i, 'rate_type', parseInt(e.target.value) || '')} className="w-full text-sm border rounded px-2 py-1" data-testid={`listing-rate-plan-${i}-rate_type`}>
                              <option value="">Select…</option>
                              {LISTING_RATE_TYPES.map(rt => <option key={rt.agoda_id} value={rt.agoda_id}>{rt.agoda_id} — {rt.label}</option>)}
                            </select>
                            {(() => { const rt = LISTING_RATE_TYPES.find(x => x.agoda_id === p.rate_type); return rt && <div className="text-[10px] text-gray-500 mt-0.5">{rt.desc}</div>; })()}
                          </div>
                          <div><label className="block text-xs text-gray-600 mb-0.5">Min Stay (nights)</label>
                            <Input type="number" value={p.min_stay || 1} onChange={(e) => setPlan(i, 'min_stay', e.target.value)} className="text-sm" data-testid={`listing-rate-plan-${i}-min_stay`} />
                          </div>
                          <div><label className="block text-xs text-gray-600 mb-0.5">Max Stay (nights)</label>
                            <Input type="number" value={p.max_stay || 30} onChange={(e) => setPlan(i, 'max_stay', e.target.value)} className="text-sm" data-testid={`listing-rate-plan-${i}-max_stay`} />
                          </div>
                          <div><label className="block text-xs text-gray-600 mb-0.5">Advance Booking (days)</label>
                            <Input type="number" value={p.advance_booking_days || 0} onChange={(e) => setPlan(i, 'advance_booking_days', e.target.value)} className="text-sm" data-testid={`listing-rate-plan-${i}-advance_booking_days`} />
                          </div>
                          <div><label className="block text-xs text-gray-600 mb-0.5">Prepayment %</label>
                            <Input type="number" value={p.prepayment_pct || 0} onChange={(e) => setPlan(i, 'prepayment_pct', e.target.value)} className="text-sm" data-testid={`listing-rate-plan-${i}-prepayment_pct`} />
                          </div>
                        </div>

                        {/* Room bindings — required by Agoda HotelProduct pairing */}
                        <div className="border-t pt-2">
                          <div className="text-xs font-semibold text-gray-700 mb-1">Linked Room Types * <span className="text-[10px] text-gray-500 font-normal">(Agoda HotelProduct = RatePlan × Room)</span></div>
                          {roomTypesCtx.length === 0 ? <div className="text-[11px] text-red-600 italic">No room types configured in Settings.</div> : (
                            <div className="flex flex-wrap gap-2">
                              {roomTypesCtx.map(rt => {
                                const on = (p.room_bindings || []).includes(rt.id);
                                return (
                                  <label key={rt.id} className={`px-2 py-1 text-xs rounded border cursor-pointer ${on ? 'bg-orange-100 border-orange-500 text-orange-800' : 'bg-white border-gray-300 text-gray-700'}`}>
                                    <input type="checkbox" className="hidden" checked={on} onChange={() => toggleBinding(i, rt.id)} data-testid={`listing-rate-plan-${i}-room-${rt.id}`} />
                                    {rt.name}
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Structured cancel penalties */}
                        <div className="border-t pt-2">
                          <div className="flex items-center justify-between mb-1">
                            <div className="text-xs font-semibold text-gray-700">Cancellation Penalties * <span className="text-[10px] text-gray-500 font-normal">(Agoda CancelPenalty)</span></div>
                            <Button size="sm" variant="outline" onClick={() => addPenalty(i)} data-testid={`listing-rate-plan-${i}-add-penalty`}>+ Penalty</Button>
                          </div>
                          {(p.cancel_penalties || []).length === 0 && <div className="text-[11px] text-red-600 italic">Add at least one penalty tier (deadline + amount).</div>}
                          {(p.cancel_penalties || []).map((pen, j) => (
                            <div key={j} className="grid grid-cols-2 md:grid-cols-6 gap-1.5 items-end mt-1" data-testid={`listing-rate-plan-${i}-penalty-${j}`}>
                              <div>
                                <label className="block text-[10px] text-gray-500">Deadline</label>
                                <Input type="number" value={pen.deadline ?? 24} onChange={(e) => setPenalty(i, j, 'deadline', parseFloat(e.target.value) || 0)} className="text-xs h-8" data-testid={`listing-rate-plan-${i}-penalty-${j}-deadline`} />
                              </div>
                              <div>
                                <label className="block text-[10px] text-gray-500">Unit</label>
                                <select value={pen.time_unit || 'hours'} onChange={(e) => setPenalty(i, j, 'time_unit', e.target.value)} className="w-full text-xs border rounded px-1 h-8" data-testid={`listing-rate-plan-${i}-penalty-${j}-time_unit`}>
                                  {LISTING_CANCEL_TIME_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="block text-[10px] text-gray-500">Penalty Type</label>
                                <select value={pen.penalty_type || 'nights'} onChange={(e) => setPenalty(i, j, 'penalty_type', e.target.value)} className="w-full text-xs border rounded px-1 h-8" data-testid={`listing-rate-plan-${i}-penalty-${j}-type`}>
                                  {LISTING_CANCEL_PENALTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="block text-[10px] text-gray-500">Amount</label>
                                <Input type="number" step="0.01" value={pen.amount ?? 1} onChange={(e) => setPenalty(i, j, 'amount', parseFloat(e.target.value) || 0)} className="text-xs h-8" data-testid={`listing-rate-plan-${i}-penalty-${j}-amount`} />
                              </div>
                              <div>
                                <label className="block text-[10px] text-gray-500">Currency</label>
                                <Input value={pen.currency || (getListingField('property_details', 'primary_currency') || '')} onChange={(e) => setPenalty(i, j, 'currency', e.target.value.toUpperCase())} className="text-xs h-8" placeholder="INR" data-testid={`listing-rate-plan-${i}-penalty-${j}-currency`} />
                              </div>
                              <button className="text-red-600 text-[11px] underline h-8" onClick={() => removePenalty(i, j)} data-testid={`listing-rate-plan-${i}-penalty-${j}-remove`}>Remove</button>
                            </div>
                          ))}
                          <div className="text-[10px] text-gray-500 mt-1">Example: <em>24 hours before check-in, penalty = 1 night</em> means guest forfeits 1 night if cancelling within 24h of check-in.</div>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-0.5">Inclusions (comma-separated)</label>
                          <Input value={Array.isArray(p.inclusions) ? p.inclusions.join(', ') : (p.inclusions || '')} onChange={(e) => setPlan(i, 'inclusions', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="Breakfast, WiFi, Airport Pickup" className="text-sm" data-testid={`listing-rate-plan-${i}-inclusions`} />
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs">
                          <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!p.refundable} onChange={(e) => setPlan(i, 'refundable', e.target.checked)} data-testid={`listing-rate-plan-${i}-refundable`} /> Refundable</label>
                          <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!p.corporate} onChange={(e) => setPlan(i, 'corporate', e.target.checked)} data-testid={`listing-rate-plan-${i}-corporate`} /> Corporate</label>
                          <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!p.prepayment_required} onChange={(e) => setPlan(i, 'prepayment_required', e.target.checked)} data-testid={`listing-rate-plan-${i}-prepayment_required`} /> Prepayment Required</label>
                          <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!p.mobile_only} onChange={(e) => setPlan(i, 'mobile_only', e.target.checked)} data-testid={`listing-rate-plan-${i}-mobile_only`} /> Mobile-only Rate</label>
                          <button className="text-red-600 text-xs ml-auto underline" onClick={() => removePlan(i)} data-testid={`listing-rate-plan-${i}-remove`}>Remove</button>
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between items-center pt-2"><Button size="sm" variant="outline" onClick={addPlan} data-testid="listing-add-rate-plan">+ Add rate plan link</Button><div className="flex items-center gap-2"><Button size="sm" onClick={() => saveListingSection('rate_plans')} className="bg-orange-500 text-white" data-testid="listing-save-rate_plans">Save Rate Plans</Button>{renderListingSyncPill('rate_plans')}</div></div>
                  </>);
                })()}

                {listingTab === 'taxes' && (<>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">GST %</label><Input type="number" step="0.01" value={getListingField('taxes', 'gst_pct')} onChange={(e) => setListingField('taxes', 'gst_pct', e.target.value)} className="text-sm" data-testid="listing-input-taxes-gst_pct" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Service Charge %</label><Input type="number" step="0.01" value={getListingField('taxes', 'service_charge_pct')} onChange={(e) => setListingField('taxes', 'service_charge_pct', e.target.value)} className="text-sm" data-testid="listing-input-taxes-service_charge_pct" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">City / Municipal Tax %</label><Input type="number" step="0.01" value={getListingField('taxes', 'city_tax_pct')} onChange={(e) => setListingField('taxes', 'city_tax_pct', e.target.value)} className="text-sm" data-testid="listing-input-taxes-city_tax_pct" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Tourism / Luxury Fee %</label><Input type="number" step="0.01" value={getListingField('taxes', 'tourism_fee_pct')} onChange={(e) => setListingField('taxes', 'tourism_fee_pct', e.target.value)} className="text-sm" data-testid="listing-input-taxes-tourism_fee_pct" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">VAT %</label><Input type="number" step="0.01" value={getListingField('taxes', 'vat_pct')} onChange={(e) => setListingField('taxes', 'vat_pct', e.target.value)} className="text-sm" data-testid="listing-input-taxes-vat_pct" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Tax ID / GSTIN</label><Input value={getListingField('taxes', 'tax_id')} onChange={(e) => setListingField('taxes', 'tax_id', e.target.value)} className="text-sm" data-testid="listing-input-taxes-tax_id" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Extra Person Charge</label><Input type="number" value={getListingField('taxes', 'extra_person_charge')} onChange={(e) => setListingField('taxes', 'extra_person_charge', e.target.value)} className="text-sm" data-testid="listing-input-taxes-extra_person_charge" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Child Charge</label><Input type="number" value={getListingField('taxes', 'child_charge')} onChange={(e) => setListingField('taxes', 'child_charge', e.target.value)} className="text-sm" data-testid="listing-input-taxes-child_charge" /></div>
                  </div>
                  <label className="flex items-center gap-2 text-xs mt-2"><input type="checkbox" checked={!!getListingField('taxes', 'tax_inclusive_pricing')} onChange={(e) => setListingField('taxes', 'tax_inclusive_pricing', e.target.checked)} data-testid="listing-input-taxes-tax_inclusive_pricing" /> Prices are tax-inclusive (rates on OTA already contain taxes)</label>
                  <div className="flex justify-end items-center gap-2"><Button size="sm" onClick={() => saveListingSection('taxes')} className="bg-orange-500 text-white" data-testid="listing-save-taxes">Save Taxes</Button>{renderListingSyncPill('taxes')}</div>
                </>)}

                {listingTab === 'seo' && (<>
                  <div><label className="block text-xs font-medium text-gray-700 mb-1">Meta Title * (max ~60 chars)</label><Input value={getListingField('seo', 'meta_title')} onChange={(e) => setListingField('seo', 'meta_title', e.target.value)} className="text-sm" data-testid="listing-input-seo-meta_title" /></div>
                  <div><label className="block text-xs font-medium text-gray-700 mb-1">Meta Description (max ~160 chars)</label><textarea rows={2} value={getListingField('seo', 'meta_description')} onChange={(e) => setListingField('seo', 'meta_description', e.target.value)} className="w-full text-sm border rounded px-2 py-1" data-testid="listing-input-seo-meta_description" /></div>
                  <div><label className="block text-xs font-medium text-gray-700 mb-1">Marketing Headline / Hero Tagline</label><Input value={getListingField('seo', 'hero_tagline')} onChange={(e) => setListingField('seo', 'hero_tagline', e.target.value)} placeholder="Where luxury meets the sea" className="text-sm" data-testid="listing-input-seo-hero_tagline" /></div>
                  <div><label className="block text-xs font-medium text-gray-700 mb-1">Unique Selling Points (one per line)</label><textarea rows={3} value={getListingField('seo', 'unique_selling_points')} onChange={(e) => setListingField('seo', 'unique_selling_points', e.target.value)} placeholder="Panoramic sea view\n5-min walk to beach\nRooftop infinity pool" className="w-full text-sm border rounded px-2 py-1" data-testid="listing-input-seo-unique_selling_points" /></div>
                  <div><label className="block text-xs font-medium text-gray-700 mb-1">Keywords (comma-separated)</label><Input value={getListingField('seo', 'keywords')} onChange={(e) => setListingField('seo', 'keywords', e.target.value)} className="text-sm" data-testid="listing-input-seo-keywords" /></div>
                  <div><label className="block text-xs font-medium text-gray-700 mb-1">Canonical Property URL</label><Input value={getListingField('seo', 'property_url')} onChange={(e) => setListingField('seo', 'property_url', e.target.value)} placeholder="https://" className="text-sm" data-testid="listing-input-seo-property_url" /></div>
                  <div className="flex justify-end items-center gap-2"><Button size="sm" onClick={() => saveListingSection('seo')} className="bg-orange-500 text-white" data-testid="listing-save-seo">Save SEO</Button>{renderListingSyncPill('seo')}</div>
                </>)}

                {listingTab === 'preview' && (() => {
                  const pdX = L.property_details || {};
                  const locX = L.location || {};
                  const polX = L.policies || {};
                  const taxX = L.taxes || {};
                  const seoX = L.seo || {};
                  const cover = (L.photos || []).find(p => p.is_cover) || (L.photos || [])[0];
                  const otherPhotos = (L.photos || []).filter(p => !cover || p.id !== cover.id).slice(0, 8);
                  return (
                    <div className="space-y-4" data-testid="listing-preview">
                      {cover && <img src={`${backendOrigin}${cover.url}`} alt={cover.caption || 'cover'} className="w-full h-56 object-cover rounded-lg" data-testid="listing-preview-cover" />}
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <div className="text-2xl font-bold text-gray-800">{pdX.property_name || 'Property Name'}</div>
                        {pdX.star_rating && <div className="text-amber-500 text-lg">{'★'.repeat(parseInt(pdX.star_rating) || 0)}</div>}
                        {pdX.property_type && <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded">{pdX.property_type}</span>}
                        {pdX.property_class && <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">{pdX.property_class}</span>}
                      </div>
                      {seoX.hero_tagline && <div className="italic text-orange-700">&ldquo;{seoX.hero_tagline}&rdquo;</div>}
                      <div className="text-sm text-gray-600 whitespace-pre-line">{pdX.short_description || pdX.description || 'No description added yet.'}</div>
                      <div className="text-xs text-gray-500"><strong>Location:</strong> {[locX.address, locX.neighborhood, locX.city, locX.state, locX.country, locX.postal_code].filter(Boolean).join(', ') || '—'}</div>
                      <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                        {locX.airport_distance && <span>✈️ Airport: {locX.airport_distance} km</span>}
                        {locX.railway_distance && <span>🚉 Railway: {locX.railway_distance} km</span>}
                        {locX.metro_distance && <span>🚇 Metro: {locX.metro_distance} km</span>}
                        {locX.beach_distance && <span>🏖️ Beach: {locX.beach_distance} km</span>}
                        {locX.city_center_distance && <span>🏙️ City Centre: {locX.city_center_distance} km</span>}
                      </div>
                      {otherPhotos.length > 0 && (
                        <div className="grid grid-cols-4 gap-2">
                          {otherPhotos.map(p => <img key={p.id} src={`${backendOrigin}${p.url}`} alt={p.caption || p.category} title={p.caption || p.category} className="h-20 w-full object-cover rounded" loading="lazy" />)}
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-semibold text-gray-800 mb-1">Facilities</div>
                        <div className="flex flex-wrap gap-1">
                          {(L.facilities || []).length === 0 ? <span className="text-xs text-gray-400">None selected</span> : (L.facilities || []).map(f => <span key={f} className="text-[11px] px-2 py-0.5 bg-gray-100 rounded">{f}</span>)}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-800 mb-1">Rooms</div>
                        {(ctx.room_types || []).length === 0 ? <div className="text-xs text-gray-400">No rooms configured</div> : (ctx.room_types || []).map(rt => {
                          const rc = (L.room_content || {})[rt.id] || {};
                          return (
                            <div key={rt.id} className="border rounded p-2 mb-2">
                              <div className="text-sm font-medium">{rt.name}</div>
                              <div className="text-xs text-gray-600">{rc.description || <em className="text-gray-400">No description</em>}</div>
                              <div className="text-[11px] text-gray-500 mt-1">
                                {rc.room_size_sqft && <>Size: {rc.room_size_sqft} sq ft · </>}
                                {rc.bed_type && <>{rc.bed_count || 1}× {rc.bed_type} · </>}
                                {rc.view_type && <>{rc.view_type} View · </>}
                                {(rc.max_adults || rc.max_children) && <>Max {rc.max_adults || 0}A + {rc.max_children || 0}C</>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-800 mb-1">Rate Plans</div>
                        {(L.rate_plans || []).length === 0 ? <div className="text-xs text-gray-400">No rate plans linked</div> : (
                          <div className="flex flex-wrap gap-2">
                            {(L.rate_plans || []).map((rp, i) => (
                              <div key={i} className="border rounded p-2 text-xs bg-gray-50 min-w-[180px]">
                                <div className="font-medium text-gray-800">{rp.code} — {rp.name || rp.meal_plan || '—'}</div>
                                <div className="text-gray-500">{rp.cancellation_type || '—'} · {rp.refundable ? 'Refundable' : 'Non-refundable'}</div>
                                <div className="text-gray-500">Stay {rp.min_stay || 1}–{rp.max_stay || 30} nights</div>
                                {rp.inclusions && rp.inclusions.length > 0 && <div className="text-gray-600 mt-1">Incl: {(Array.isArray(rp.inclusions) ? rp.inclusions : []).join(', ')}</div>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-800 mb-1">Policies</div>
                        <div className="text-xs text-gray-600 space-y-0.5">
                          <div>Check-in {polX.checkin_time || '—'} · Check-out {polX.checkout_time || '—'} · Min age {polX.min_age_checkin || '—'}</div>
                          {polX.payment_methods && <div>Pay: {(Array.isArray(polX.payment_methods) ? polX.payment_methods : []).join(', ') || '—'}</div>}
                          {polX.cancellation_policy && <div className="whitespace-pre-line"><strong>Cancellation:</strong> {polX.cancellation_policy}</div>}
                          <div className="flex flex-wrap gap-2 mt-1">
                            {polX.couples_allowed && <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded">Couples OK</span>}
                            {polX.foreign_guests_allowed && <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded">Foreign Guests OK</span>}
                            {polX.groups_allowed && <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded">Groups OK</span>}
                            {polX.bachelor_allowed && <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded">Bachelors OK</span>}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-800 mb-1">Taxes</div>
                        <div className="text-xs text-gray-600">
                          {[
                            taxX.gst_pct && `GST ${taxX.gst_pct}%`,
                            taxX.service_charge_pct && `Service ${taxX.service_charge_pct}%`,
                            taxX.city_tax_pct && `City ${taxX.city_tax_pct}%`,
                            taxX.tourism_fee_pct && `Tourism ${taxX.tourism_fee_pct}%`,
                            taxX.vat_pct && `VAT ${taxX.vat_pct}%`,
                          ].filter(Boolean).join(' · ') || '—'}
                          {taxX.tax_inclusive_pricing && <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-700 rounded">Tax Inclusive</span>}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {agodaPayloadOpen && (() => {
                  const AP = agodaPayloadState;
                  const sections = [
                    { id: 'full_property',    label: 'Full Property Payload' },
                    { id: 'property_details', label: 'Property Details' },
                    { id: 'location',         label: 'Location' },
                    { id: 'facilities',       label: 'Facilities' },
                    { id: 'policies',         label: 'Policies' },
                    { id: 'photos',           label: 'Photos' },
                    { id: 'rooms',            label: 'Rooms' },
                    { id: 'rate_plans',       label: 'Rate Plans' },
                    { id: 'taxes',            label: 'Taxes' },
                    { id: 'seo',              label: 'SEO' },
                  ];
                  return (
                    <Dialog open={agodaPayloadOpen} onOpenChange={setAgodaPayloadOpen}>
                      <DialogContent className="sm:max-w-[95vw] w-[95vw] max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="agoda-payload-dialog">
                        <DialogHeader><DialogTitle>Agoda Payload &amp; Contracts</DialogTitle></DialogHeader>
                    <div className="space-y-4" data-testid="agoda-payload-panel">
                      <div className="flex items-center gap-2 flex-wrap">
                        <label className="text-xs font-medium text-gray-700">Section:</label>
                        <Select value={AP.section} onValueChange={(v) => fetchAgodaPayloadPreview(v)}>
                          <SelectTrigger className="w-64 h-8 text-xs" data-testid="agoda-payload-section-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {sections.map(s => (<SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline"
                          onClick={() => fetchAgodaPayloadPreview(AP.section)}
                          data-testid="agoda-payload-refresh-btn">Refresh</Button>
                        <Button size="sm" className="bg-gradient-to-r from-orange-500 to-amber-600"
                          disabled={AP.loading || AP.pushing || !AP.preview}
                          onClick={() => pushAgodaPayload(AP.section)}
                          data-testid="agoda-payload-push-btn">{AP.pushing ? 'Pushing…' : 'Push to Agoda'}</Button>
                      </div>

                      {/* Phase 2 — staged Content Push (Rooms → Rate Plans → Products → Verify) */}
                      <Card data-testid="agoda-content-push-card">
                        <div className="p-3 space-y-2">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="text-sm font-semibold">Staged Content Push — Validate → Property → Rooms → Rate Plans → Products → Verify</div>
                            <Button size="sm" variant="outline" disabled={contentPush.loading || contentPush.running}
                              onClick={() => runAgodaContentPush(false)}
                              data-testid="agoda-content-preview-btn">{contentPush.loading ? 'Building…' : 'Preview Plan (dry-run)'}</Button>
                          </div>
                          {(() => {
                            const existingHotelCode = contentPush.data?.agoda_hotel_code
                              || AP.preview?.existing_agoda_hotel_id
                              || stageResults?.validate?.data?.agoda_hotel_code
                              || stageResults?.property?.data?.agoda_hotel_code;
                            const isLinked = !!existingHotelCode;
                            const visibleStages = AGODA_PUSH_STAGES.filter(s => s.key !== 'property' || !isLinked);
                            return (
                              <div className="flex flex-wrap gap-3" data-testid="agoda-stage-buttons">
                                <div className="w-full text-xs text-gray-500" data-testid="agoda-stage-link-state">
                                  {isLinked
                                    ? `Existing Agoda property (HotelCode ${existingHotelCode}) — Property Content step skipped.`
                                    : 'New property (no Agoda HotelCode yet) — all stages shown.'}
                                </div>
                                {visibleStages.map(s => {
                                  const r = stageResults[s.key];
                                  return (
                                    <div key={s.key} className="flex flex-col gap-1 min-w-[200px] max-w-[300px]">
                                      <Button size="sm"
                                        variant={s.readOnly ? 'outline' : undefined}
                                        className={s.readOnly ? '' : 'bg-gradient-to-r from-orange-500 to-amber-600'}
                                        disabled={r?.loading}
                                        onClick={() => runAgodaContentStage(s.key, s.label, s.readOnly)}
                                        data-testid={`agoda-stage-${s.key}-btn`}>
                                        {r?.loading ? 'Running…' : s.label}
                                      </Button>
                                      {r && !r.loading && (
                                        <div className="text-xs" data-testid={`agoda-stage-${s.key}-result`}>
                                          <div className={`flex items-start gap-1 ${r.success ? 'text-green-600' : 'text-red-600'}`}>
                                            {r.success
                                              ? <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                              : <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
                                            <span>
                                              {r.success
                                                ? (r.stage?.status === 'skipped' ? 'Skipped' : 'Success')
                                                : (r.error || r.stage?.error || `Failed${r.data?.failed_stage ? ` at ${r.data.failed_stage}` : ''}`)}
                                              {r.at ? ` · ${r.at}` : ''}
                                            </span>
                                          </div>
                                          <details className="mt-1 border rounded bg-gray-50">
                                            <summary className="cursor-pointer px-2 py-1 text-gray-700 select-none hover:bg-gray-100 rounded"
                                              data-testid={`agoda-stage-${s.key}-toggle`}>Request &amp; response</summary>
                                            <div className="p-2 space-y-2 max-h-80 overflow-auto">
                                              {(() => {
                                                const sx = (r.data?.stages || []).filter(x => x.request_xml || x.response_body);
                                                if (!sx.length) {
                                                  return (
                                                    <div className="text-gray-500" data-testid={`agoda-stage-${s.key}-payload`}>
                                                      {s.key === 'validate'
                                                        ? 'Local validation / readiness check — no Agoda request is sent for this stage.'
                                                        : (r.error || 'No Agoda request was sent for this stage.')}
                                                      {r.stage && (
                                                        <pre className="mt-1 whitespace-pre-wrap text-[11px] bg-white border rounded p-2 overflow-auto">{JSON.stringify(r.stage, null, 2)}</pre>
                                                      )}
                                                    </div>
                                                  );
                                                }
                                                return sx.map((x, i) => (
                                                  <div key={i} className="space-y-1" data-testid={`agoda-stage-${s.key}-payload`}>
                                                    <div className="text-gray-600 break-all">
                                                      {(x.method || 'POST')} {x.endpoint || ''}
                                                      {x.http_code != null ? ` · HTTP ${x.http_code}` : ''}
                                                      {x.notif_type ? ` · ${x.notif_type}` : ''}
                                                    </div>
                                                    <div className="font-semibold text-gray-700">Request payload</div>
                                                    <pre className="bg-white border rounded p-2 overflow-auto text-[11px] whitespace-pre-wrap"
                                                      data-testid={`agoda-stage-${s.key}-request`}>{x.request_xml || '(none)'}</pre>
                                                    <div className="font-semibold text-gray-700">Agoda response</div>
                                                    <pre className="bg-white border rounded p-2 overflow-auto text-[11px] whitespace-pre-wrap"
                                                      data-testid={`agoda-stage-${s.key}-response`}>{x.response_body || '(empty)'}</pre>
                                                  </div>
                                                ));
                                              })()}
                                            </div>
                                          </details>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                          {contentPush.data && (
                            <div className="text-xs space-y-2" data-testid="agoda-content-result">
                              <div className="flex gap-4 flex-wrap">
                                <span><strong>Mode:</strong> {contentPush.data.mode}</span>
                                <span><strong>Agoda Hotel:</strong> {contentPush.data.agoda_hotel_code || '— (not linked)'}</span>
                                {contentPush.data.plan && (
                                  <>
                                    <span><strong>Rooms:</strong> {contentPush.data.plan.rooms?.length ?? '—'}</span>
                                    <span><strong>Rate Plans:</strong> {contentPush.data.plan.rate_plans?.length ?? '—'}</span>
                                    <span><strong>Products:</strong> {contentPush.data.plan.products_count ?? '—'}</span>
                                    <span><strong>Orphans:</strong> {contentPush.data.plan.orphan_products_count ?? 0}</span>
                                  </>
                                )}
                              </div>
                              <table className="w-full text-left border-collapse">
                                <thead><tr className="text-gray-500">
                                  <th className="py-1">Stage</th><th>Status</th><th>Detail</th>
                                </tr></thead>
                                <tbody>
                                  {(contentPush.data.stages || []).map((st, i) => (
                                    <tr key={i} className="border-t" data-testid={`agoda-content-stage-${st.stage}`}>
                                      <td className="py-1 font-medium">{st.stage}</td>
                                      <td>
                                        <span className={
                                          st.status === 'success' ? 'text-green-700' :
                                          st.status === 'failed' ? 'text-red-600' :
                                          st.status === 'skipped' ? 'text-gray-500' : 'text-amber-700'
                                        }>{st.status}</span>
                                      </td>
                                      <td className="text-gray-600">
                                        {st.error || (st.errors && st.errors[0]?.text) ||
                                         (st.missing && st.missing.length ? `missing: ${st.missing.join(', ')}` : '') ||
                                         (st.completion_pct != null ? `${st.completion_pct}% complete` : '') ||
                                         (st.reason || (st.correlation_id ? `cid ${st.correlation_id}` : ''))}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {contentPush.data.verification && (
                                <div className="text-gray-700" data-testid="agoda-content-verification">
                                  <strong>Verification:</strong> property={contentPush.data.verification.property};
                                  rooms {contentPush.data.verification.rooms.found}/{contentPush.data.verification.rooms.expected};
                                  rate plans {contentPush.data.verification.rate_plans.found}/{contentPush.data.verification.rate_plans.expected};
                                  products {contentPush.data.verification.products.found}/{contentPush.data.verification.products.expected}
                                </div>
                              )}
                              {contentPush.data.verification?.property_status && (
                                <div className="mt-2 border border-amber-200 bg-amber-50 rounded p-2" data-testid="agoda-onboarding-status">
                                  <div className="text-[11px] font-semibold text-amber-800">Agoda Onboarding / Go-Live Status</div>
                                  <div className="text-[10px] text-amber-700 mb-1">
                                    A successful content push does NOT mean the property is live on Agoda. These are Agoda's onboarding gates from the Verify response.
                                  </div>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                                    {[
                                      ['PhotosIngested', 'Photos Ingested'],
                                      ['ContractSigned', 'Contract Signed'],
                                      ['FraudCheckPassed', 'Fraud Check Passed'],
                                      ['PropertyLived', 'Property Live'],
                                    ].map(([key, label]) => {
                                      const raw = contentPush.data.verification.property_status[key];
                                      const val = (raw === undefined || raw === null || raw === '') ? 'Unknown' : raw;
                                      const yes = String(val).toLowerCase() === 'yes';
                                      const unknown = val === 'Unknown';
                                      return (
                                        <div key={key} className="flex items-center justify-between" data-testid={`agoda-status-${key}`}>
                                          <span>{label}</span>
                                          <span className={`font-semibold ${unknown ? 'text-gray-500' : yes ? 'text-green-700' : 'text-red-700'}`} data-testid={`agoda-status-${key}-value`}>{val}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {contentPush.data.note && <div className="text-gray-400 italic">{contentPush.data.note}</div>}
                              <AgodaPayloadViewer stages={contentPush.data.stages} />
                            </div>
                          )}
                        </div>
                      </Card>
                      {/* Agoda Contract Management — Hotel + Host (separate workflows) */}
                      <Card data-testid="agoda-contracts-card">
                        <div className="p-3 space-y-4">
                          <div className="text-sm font-semibold">Agoda Contracts</div>
                          <div className="text-[11px] text-gray-500 -mt-2">
                            Hotel and Host contracts are separate agreements. Review the actual contract returned by Agoda before signing. Signing is legally binding and limited to the property owner/admin.
                          </div>

                          {/* --- Hotel / Property Contract --- */}
                          <div className="border rounded-md p-3 space-y-2" data-testid="hotel-contract-panel">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div className="text-sm font-medium">Hotel / Property Contract</div>
                              <Button size="sm" variant="outline" disabled={hotelContract.loading}
                                onClick={fetchHotelContract} data-testid="hotel-contract-retrieve-btn">
                                {hotelContract.loading ? 'Retrieving…' : 'Retrieve Hotel Contract'}</Button>
                            </div>
                            {hotelContract.data && (
                              <div className="text-xs space-y-2" data-testid="hotel-contract-result">
                                {hotelContract.data.message && (
                                  <div className={hotelContract.data.eligible ? 'text-gray-600' : 'text-amber-700'} data-testid="hotel-contract-message">
                                    {hotelContract.data.message}</div>
                                )}
                                {hotelContract.data.errors?.length > 0 && (
                                  <div className="text-red-600" data-testid="hotel-contract-errors">
                                    {hotelContract.data.errors.map((er, i) => (<div key={i}>Error {er.code}: {er.text}</div>))}</div>
                                )}
                                {hotelContract.data.contract && (
                                  <>
                                    <div className="flex gap-4 flex-wrap">
                                      <span><strong>Type:</strong> Hotel</span>
                                      <span><strong>Agoda Hotel:</strong> {hotelContract.data.agoda_hotel_code}</span>
                                      <span data-testid="hotel-contract-status"><strong>Status:</strong> {hotelContract.data.contract.is_signed ? 'Signed' : 'Not signed'}</span>
                                      {hotelContract.data.contract.signed_at && <span><strong>Signed at:</strong> {hotelContract.data.contract.signed_at}</span>}
                                    </div>
                                    <div data-testid="hotel-contract-title"><strong>Title:</strong> {hotelContract.data.contract.title || '—'}</div>
                                    <pre className="bg-gray-900 text-gray-100 text-[11px] p-3 rounded overflow-auto max-h-64 whitespace-pre-wrap" data-testid="hotel-contract-content">
{hotelContract.data.contract.content || '(no content returned)'}
                                    </pre>
                                    {!hotelContract.data.contract.is_signed && (
                                      <div className="space-y-2 border-t pt-2">
                                        <label className="flex items-start gap-2 cursor-pointer">
                                          <Checkbox checked={hotelContract.agreed}
                                            onCheckedChange={(v) => setHotelContract(s => ({ ...s, agreed: !!v }))}
                                            data-testid="hotel-contract-agree-checkbox" />
                                          <span className="text-[11px]">I am the authorized property owner and I have read and agree to this agreement.</span>
                                        </label>
                                        <Button size="sm" className="bg-gradient-to-r from-orange-500 to-amber-600"
                                          disabled={!hotelContract.agreed || hotelContract.signing}
                                          onClick={signHotelContract} data-testid="hotel-contract-sign-btn">
                                          {hotelContract.signing ? 'Signing…' : 'Review & Sign Hotel Contract'}</Button>
                                      </div>
                                    )}
                                  </>
                                )}
                                <div className="text-gray-400">Last checked: {hotelContract.data.checked_at || '—'}</div>
                                {hotelContract.data.outcome === 'ambiguous' && (
                                  <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="hotel-contract-ambiguous">
                                    Outcome unknown — do not retry blindly. Click “Retrieve Hotel Contract” to re-check the current signing status before attempting to sign again.
                                  </div>
                                )}
                                {hotelContract.data.diagnostics && (
                                  <details className="border rounded mt-1" data-testid="hotel-contract-diagnostics">
                                    <summary className="cursor-pointer px-2 py-1 text-[11px] font-medium bg-gray-50">Diagnostics (request / response XML)</summary>
                                    <div className="p-2 space-y-1 text-[11px]">
                                      <div><strong>Endpoint:</strong> {hotelContract.data.diagnostics.method} {hotelContract.data.diagnostics.endpoint}</div>
                                      <div><strong>HTTP status:</strong> {hotelContract.data.diagnostics.http_status ?? '—'}</div>
                                      <div><strong>Parsed status:</strong> {hotelContract.data.diagnostics.parsed_status}</div>
                                      <div><strong>Success confirmed by Agoda:</strong> {String(hotelContract.data.diagnostics.success_confirmed_by_agoda)}</div>
                                      {hotelContract.data.diagnostics.agoda_error_code && (
                                        <div className="text-red-600"><strong>Agoda error:</strong> {hotelContract.data.diagnostics.agoda_error_code} — {hotelContract.data.diagnostics.agoda_error_message}</div>
                                      )}
                                      <div><strong>Correlation/Request ID:</strong> {hotelContract.data.diagnostics.correlation_id || '—'}</div>
                                      <div><strong>Timestamp:</strong> {hotelContract.data.diagnostics.timestamp}</div>
                                      <div className="font-medium mt-1">Request XML (sanitized)</div>
                                      <pre className="bg-gray-900 text-gray-100 p-2 rounded overflow-auto max-h-48 whitespace-pre-wrap" data-testid="hotel-contract-diag-request">{hotelContract.data.diagnostics.request_xml || '—'}</pre>
                                      <div className="font-medium mt-1">Response XML (sanitized)</div>
                                      <pre className="bg-gray-900 text-gray-100 p-2 rounded overflow-auto max-h-48 whitespace-pre-wrap" data-testid="hotel-contract-diag-response">{hotelContract.data.diagnostics.response_xml || '—'}</pre>
                                    </div>
                                  </details>
                                )}
                              </div>
                            )}
                          </div>

                          {/* --- Host Contract --- */}
                          <div className="border rounded-md p-3 space-y-2" data-testid="host-contract-panel">
                            <div className="text-sm font-medium">Host Contract</div>
                            <div className="text-[11px] text-gray-500">For vacation-rental / Host Manage accounts only. Enter the Host Manage account email (not stored). Not every property qualifies.</div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Input className="h-8 text-xs max-w-xs" type="email" placeholder="Host Manage account email"
                                value={hostContract.email}
                                onChange={(e) => setHostContract(s => ({ ...s, email: e.target.value }))}
                                data-testid="host-contract-email-input" />
                              <Button size="sm" variant="outline" disabled={hostContract.loading}
                                onClick={fetchHostContract} data-testid="host-contract-retrieve-btn">
                                {hostContract.loading ? 'Retrieving…' : 'Retrieve Host Contract'}</Button>
                            </div>
                            {hostContract.data && (
                              <div className="text-xs space-y-2" data-testid="host-contract-result">
                                {hostContract.data.message && (
                                  <div className={hostContract.data.eligible ? 'text-gray-600' : 'text-amber-700'} data-testid="host-contract-message">
                                    {hostContract.data.message}</div>
                                )}
                                {hostContract.data.errors?.length > 0 && (
                                  <div className="text-red-600" data-testid="host-contract-errors">
                                    {hostContract.data.errors.map((er, i) => (<div key={i}>Error {er.code}: {er.text}</div>))}</div>
                                )}
                                {hostContract.data.contract && (
                                  <>
                                    <div className="flex gap-4 flex-wrap">
                                      <span><strong>Type:</strong> Host</span>
                                      <span><strong>Account:</strong> {hostContract.data.host_email}</span>
                                      <span data-testid="host-contract-status"><strong>Status:</strong> {hostContract.data.contract.is_signed ? 'Signed' : 'Not signed'}</span>
                                      {hostContract.data.contract.signed_at && <span><strong>Signed at:</strong> {hostContract.data.contract.signed_at}</span>}
                                    </div>
                                    <div data-testid="host-contract-title"><strong>Title:</strong> {hostContract.data.contract.title || '—'}</div>
                                    <pre className="bg-gray-900 text-gray-100 text-[11px] p-3 rounded overflow-auto max-h-64 whitespace-pre-wrap" data-testid="host-contract-content">
{hostContract.data.contract.content || '(no content returned)'}
                                    </pre>
                                    {!hostContract.data.contract.is_signed && (
                                      <div className="space-y-2 border-t pt-2">
                                        <label className="flex items-start gap-2 cursor-pointer">
                                          <Checkbox checked={hostContract.agreed}
                                            onCheckedChange={(v) => setHostContract(s => ({ ...s, agreed: !!v }))}
                                            data-testid="host-contract-agree-checkbox" />
                                          <span className="text-[11px]">I am the authorized account owner and I have read and agree to this agreement (applies to all properties under this Host Manage account).</span>
                                        </label>
                                        <Button size="sm" className="bg-gradient-to-r from-orange-500 to-amber-600"
                                          disabled={!hostContract.agreed || hostContract.signing}
                                          onClick={signHostContract} data-testid="host-contract-sign-btn">
                                          {hostContract.signing ? 'Signing…' : 'Review & Sign Host Contract'}</Button>
                                      </div>
                                    )}
                                  </>
                                )}
                                <div className="text-gray-400">Last checked: {hostContract.data.checked_at || '—'}</div>
                                {hostContract.data.outcome === 'ambiguous' && (
                                  <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="host-contract-ambiguous">
                                    Outcome unknown — do not retry blindly. Re-check the current host contract status before attempting to sign again.
                                  </div>
                                )}
                                {hostContract.data.diagnostics && (
                                  <details className="border rounded mt-1" data-testid="host-contract-diagnostics">
                                    <summary className="cursor-pointer px-2 py-1 text-[11px] font-medium bg-gray-50">Diagnostics (request / response XML)</summary>
                                    <div className="p-2 space-y-1 text-[11px]">
                                      <div><strong>Endpoint:</strong> {hostContract.data.diagnostics.method} {hostContract.data.diagnostics.endpoint}</div>
                                      <div><strong>HTTP status:</strong> {hostContract.data.diagnostics.http_status ?? '—'}</div>
                                      <div><strong>Parsed status:</strong> {hostContract.data.diagnostics.parsed_status}</div>
                                      <div><strong>Success confirmed by Agoda:</strong> {String(hostContract.data.diagnostics.success_confirmed_by_agoda)}</div>
                                      {hostContract.data.diagnostics.agoda_error_code && (
                                        <div className="text-red-600"><strong>Agoda error:</strong> {hostContract.data.diagnostics.agoda_error_code} — {hostContract.data.diagnostics.agoda_error_message}</div>
                                      )}
                                      <div><strong>Correlation/Request ID:</strong> {hostContract.data.diagnostics.correlation_id || '—'}</div>
                                      <div><strong>Timestamp:</strong> {hostContract.data.diagnostics.timestamp}</div>
                                      <div className="font-medium mt-1">Request XML (sanitized)</div>
                                      <pre className="bg-gray-900 text-gray-100 p-2 rounded overflow-auto max-h-48 whitespace-pre-wrap" data-testid="host-contract-diag-request">{hostContract.data.diagnostics.request_xml || '—'}</pre>
                                      <div className="font-medium mt-1">Response XML (sanitized)</div>
                                      <pre className="bg-gray-900 text-gray-100 p-2 rounded overflow-auto max-h-48 whitespace-pre-wrap" data-testid="host-contract-diag-response">{hostContract.data.diagnostics.response_xml || '—'}</pre>
                                    </div>
                                  </details>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>

                      <Card>
                        <div className="p-3 text-xs space-y-1">
                          <div><strong>Endpoint:</strong> <span className="font-mono">{AP.preview?.endpoint || 'https://content-push.agoda.com/cm/properties'}</span></div>
                          <div className="flex gap-4">
                            <div><strong>Method:</strong> POST</div>
                            <div><strong>Content-Type:</strong> application/xml</div>
                            <div><strong>Notif Type:</strong> {AP.preview?.notif_type || '—'}</div>
                          </div>
                          {AP.preview?.existing_agoda_hotel_id && (
                            <div><strong>Linked Agoda Hotel ID:</strong> <span className="font-mono">{AP.preview.existing_agoda_hotel_id}</span></div>
                          )}
                          {AP.preview?.photo_url_gate && !AP.preview.photo_url_gate.ok && (
                            <div className="text-red-600 mt-1"><strong>Photo URL gate FAILED</strong> — {AP.preview.photo_url_gate.offending.slice(0,3).join(', ')}{AP.preview.photo_url_gate.offending.length > 3 ? ` +${AP.preview.photo_url_gate.offending.length - 3} more` : ''}</div>
                          )}
                          {AP.preview?.agoda_readiness && (
                            <div className={AP.preview.agoda_readiness.ready ? 'text-green-700' : 'text-amber-700'}>
                              <strong>Agoda Readiness:</strong> {AP.preview.agoda_readiness.ready ? 'Complete' : `Incomplete (${AP.preview.agoda_readiness.passed}/${AP.preview.agoda_readiness.total})`}
                            </div>
                          )}
                        </div>
                      </Card>
                      <Card>
                        <div className="p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-semibold">Request XML</div>
                            <Button size="sm" variant="outline" data-testid="agoda-payload-copy-request-btn"
                              onClick={() => { navigator.clipboard.writeText(AP.preview?.request_xml_pretty || AP.preview?.request_xml || ''); toast.success('Request XML copied'); }}
                              disabled={!AP.preview}>Copy</Button>
                          </div>
                          {AP.loading ? (<div className="text-xs text-gray-500">Loading…</div>) : (
                            <pre className="bg-gray-900 text-green-200 text-[11px] p-3 rounded overflow-auto max-h-96 whitespace-pre" data-testid="agoda-payload-request-xml">
{AP.preview?.request_xml_pretty || AP.preview?.request_xml || '(no preview loaded)'}
                            </pre>
                          )}
                        </div>
                      </Card>
                      {AP.pushResult && (
                        <Card>
                          <div className="p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="text-sm font-semibold">Response XML</div>
                              <span className={`text-[11px] px-1.5 py-0.5 rounded ${AP.pushResult.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} data-testid="agoda-payload-push-status">
                                {AP.pushResult.success ? 'SUCCESS' : 'FAILED'} · HTTP {AP.pushResult.http_code ?? '—'} · {AP.pushResult.duration_ms}ms
                              </span>
                            </div>
                            {AP.pushResult.error && (<div className="text-xs text-red-600 mb-1"><strong>Error:</strong> {AP.pushResult.error}</div>)}
                            <pre className="bg-gray-900 text-green-200 text-[11px] p-3 rounded overflow-auto max-h-72 whitespace-pre" data-testid="agoda-payload-response-xml">
{AP.pushResult.response_xml_pretty || AP.pushResult.response_xml || '(empty)'}
                            </pre>
                          </div>
                        </Card>
                      )}
                      <Card>
                        <div className="p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-semibold">Agoda Payload History</div>
                            <Button size="sm" variant="outline" onClick={fetchAgodaPayloadHistory}>Refresh</Button>
                          </div>
                          {(AP.history || []).length === 0 ? (
                            <div className="text-[11px] text-gray-500">No manual pushes yet.</div>
                          ) : (
                            <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto" data-testid="agoda-payload-history-list">
                              {AP.history.map(h => (
                                <div key={h.log_id} className="flex items-center gap-2 py-1 text-[11px]" data-testid={`agoda-payload-history-row-${h.log_id}`}>
                                  <span className={`px-1.5 py-0.5 rounded ${h.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{h.status}</span>
                                  <span className="w-24 truncate">{h.section}</span>
                                  <span className="text-gray-500 w-16">{h.notif_type || '—'}</span>
                                  <span className="text-gray-500 w-16">HTTP {h.http_code ?? '—'}</span>
                                  <span className="text-gray-500 w-14">{h.duration_ms ?? 0}ms</span>
                                  <span className="text-gray-400 flex-1 truncate">{h.created_at ? new Date(h.created_at).toLocaleString() : ''}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </Card>
                    </div>
                      </DialogContent>
                    </Dialog>
                  );
                })()}
              </div>
            </Card>
          </div>

          <div className="space-y-3">
            <Card>
              <div className="p-3">
                <div className="text-sm font-semibold mb-2">Listing Completion</div>
                <div className="text-2xl font-bold text-orange-600">{V.completion_pct}%</div>
                <div className="w-full h-1.5 bg-gray-100 rounded mt-1"><div className="h-1.5 bg-orange-500 rounded" style={{ width: `${V.completion_pct}%` }}></div></div>
                <ul className="mt-3 space-y-1 max-h-64 overflow-y-auto">
                  {(V.checks || []).map(c => (<li key={c.key} className="flex items-center gap-2 text-xs">{c.ok ? <CheckCircle className="w-3 h-3 text-green-600" /> : <XCircle className="w-3 h-3 text-red-500" />}<span className={c.ok ? 'text-gray-700' : 'text-red-700'}>{c.label}</span></li>))}
                </ul>
              </div>
            </Card>

            {/* Agoda Readiness — Phase 1 P0 */}
            {(() => {
              const AR = listingDetail?.agoda_readiness || { ready: false, passed: 0, total: 0, checks: [], unmapped: {} };
              const pct = AR.total ? Math.round(AR.passed * 100 / AR.total) : 0;
              const anyUnmapped = AR.unmapped && Object.values(AR.unmapped).some(v => Array.isArray(v) && v.length > 0);
              return (
                <Card data-testid="listing-agoda-readiness">
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold">Agoda Readiness</div>
                      {AR.ready ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">READY</span> : <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{AR.passed}/{AR.total}</span>}
                    </div>
                    <div className="text-2xl font-bold text-blue-700">{pct}%</div>
                    <div className="w-full h-1.5 bg-gray-100 rounded mt-1"><div className={`h-1.5 rounded ${AR.ready ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }}></div></div>
                    <ul className="mt-3 space-y-1 max-h-64 overflow-y-auto">
                      {(AR.checks || []).map(c => (<li key={c.key} className="flex items-center gap-2 text-xs" data-testid={`listing-agoda-check-${c.key}`}>{c.ok ? <CheckCircle className="w-3 h-3 text-green-600" /> : <XCircle className="w-3 h-3 text-red-500" />}<span className={c.ok ? 'text-gray-700' : 'text-red-700'}>{c.label}</span></li>))}
                    </ul>
                    {anyUnmapped && (
                      <div className="mt-2 text-[10px] text-red-600 border-t pt-2">
                        <div className="font-semibold mb-0.5">Unmapped items (blocking):</div>
                        {Object.entries(AR.unmapped).map(([k, v]) => (Array.isArray(v) && v.length > 0) ? (
                          <div key={k}><span className="text-gray-600">{k.replace(/_/g, ' ')}:</span> {v.slice(0, 5).join(', ')}{v.length > 5 ? ` +${v.length - 5} more` : ''}</div>
                        ) : null)}
                      </div>
                    )}
                    <div className="mt-2 text-[10px] text-gray-500 italic">This gate is stricter than Completion — required before Agoda Content Push.</div>
                  </div>
                </Card>
              );
            })()}

            <Card>
              <div className="p-3">
                <div className="text-sm font-semibold mb-2">OTA Publishing</div>
                <div className="space-y-2">
                  {(otas || []).map(o => {
                    const otaSt = (L.ota_status || {})[o.key] || {};
                    return (
                      <div key={o.key} className="border rounded p-2 flex items-center justify-between" data-testid={`listing-ota-${o.key}`}>
                        <div className="text-sm">
                          <div className="font-medium">{o.logo} {o.label}</div>
                          {o.active ? (<div className="text-[10px] text-gray-500 capitalize">{otaSt.status || 'draft'}{otaSt.last_sync ? ` · ${new Date(otaSt.last_sync).toLocaleString()}` : ''}</div>) : (<div className="text-[10px] text-gray-400 italic">Coming Soon</div>)}
                        </div>
                        {o.active && (<Button size="sm" onClick={() => publishListingToOTA(o.key)} className="bg-gradient-to-r from-orange-500 to-amber-600 text-white" disabled={V.completion_pct < 100} data-testid={`listing-publish-${o.key}`}>{V.completion_pct < 100 ? `Need ${100 - V.completion_pct}%` : 'Publish'}</Button>)}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          </div>
        </div>

        <Dialog open={listingSyncOpen} onOpenChange={setListingSyncOpen}>
          <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Sync History</DialogTitle></DialogHeader>
            {listingSyncHistory.length === 0 ? (<div className="text-sm text-gray-500">No sync events yet.</div>) : (
              <div className="space-y-2">
                {listingSyncHistory.map(g => (
                  <div key={g.id} className="border rounded p-2 text-xs">
                    <div className="flex justify-between"><span><strong>{g.ota?.toUpperCase()}</strong> · {g.endpoint}</span><span className="text-gray-500">{new Date(g.created_at).toLocaleString()}</span></div>
                    <div>Status: <span className="capitalize">{g.status}</span> · Retry {g.retry_count || 0}</div>
                    {g.error && <div className="text-red-600 mt-1">{g.error}</div>}
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  };




  // Fetch Promotions
  const fetchPromotions = async () => {
    if (!selectedProperty) return;

    try {
      // Fetch regular promotions
      const promotionsResponse = await axios.get(`${API}/promotions/${selectedProperty.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Fetch AGX analytics
      const agxResponse = await axios.get(`${API}/agx`, {
        params: { property_id: selectedProperty.id },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Combine promotions and AGX data
      const regularPromotions = promotionsResponse.data.promotions || [];
      const agxData = agxResponse.data || [];
      
      // Transform AGX data to look like promotions
      const agxPromotions = agxData.map(agx => ({
        id: agx.id,
        property_id: agx.property_id,
        name: `AGX - ${agx.property_name}`,
        type: 'agx',
        discount: agx.revenue || 0,
        start_date: agx.created_date,
        end_date: agx.created_date,
        status: 'active',
        description: `Revenue: ₹${agx.revenue} | Room Nights: ${agx.room_night} | Bookings: ${agx.booking}`,
        agx_data: agx // Store full AGX data for details view
      }));
      
      setPromotions([...regularPromotions, ...agxPromotions]);
    } catch (error) {
      console.error('Failed to fetch promotions:', error);
      setPromotions([]);
    }
  };

  // Export OTA room-type mappings to Excel (header button near property dropdown)
  const handleExportOtaMappings = async () => {
    const loadingToast = toast.loading('Exporting OTA mappings...');
    try {
      const resp = await axios.get(`${API}/export/ota-mappings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const rows = resp?.data?.rows || [];
      if (rows.length === 0) {
        toast.dismiss(loadingToast);
        toast.error('No properties to export');
        return;
      }
      const XLSX = await import('xlsx');
      const header = [
        'Property Name', 'MMT Hotel Code', 'Agoda Hotel Code', 'Yatra Hotel Code',
        'MMT Room Type Name', 'MMT Room Type Code',
        'Agoda Room Type Name', 'Agoda Room Type Code',
        'Yatra Room Type Name', 'Yatra Room Type Code',
      ];
      const worksheet = XLSX.utils.json_to_sheet(rows, { header });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'OTA Mappings');
      const currentDate = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `OTA_Mappings_${currentDate}.xlsx`);
      toast.dismiss(loadingToast);
      toast.success('✅ OTA mappings exported successfully!');
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Failed to export OTA mappings');
      console.error('Export OTA mappings error:', error);
    }
  };

  // Export Analytics Excel
  const handleExportAnalytics = async () => {
    if (!analyticsData || analyticsData.length === 0) {
      toast.error('No analytics data to export');
      return;
    }

    const loadingToast = toast.loading('Exporting analytics...');
    
    try {
      const XLSX = await import('xlsx');
      
      // Group analytics data by property
      const dataByProperty = {};
      
      // Fetch all properties to get names
      const propertiesResponse = await axios.get(`${API}/properties`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const propertiesMap = {};
      propertiesResponse.data.forEach(prop => {
        propertiesMap[prop.id] = prop.name;
      });
      
      // Group data by property
      analyticsData.forEach(record => {
        const propertyName = record.property_name || propertiesMap[record.property_id] || 'Unknown Property';
        if (!dataByProperty[propertyName]) {
          dataByProperty[propertyName] = [];
        }
        
        dataByProperty[propertyName].push({
          'Week Start Date': record.week_start || 'N/A',
          'Week End Date': record.week_end || 'N/A',
          'Competitor Room Nights': record.ota?.room_nights || 0,
          'Competitor Revenue': record.ota?.revenue || 0,
          'Competitor ASP': record.ota?.asp || 0,
          'Competitor Property Visits': record.ota?.property_visits || 0,
          'Competitor Conversion': record.ota?.conversion || 0,
          'GRB Room Nights': record.grb?.room_nights || 0,
          'GRB Revenue': record.grb?.revenue || 0,
          'GRB ASP': record.grb?.asp || 0,
          'GRB Property Visits': record.grb?.property_visits || 0,
          'GRB Conversion': record.grb?.conversion || 0
        });
      });
      
      // Create workbook with multiple sheets
      const workbook = XLSX.utils.book_new();
      
      Object.keys(dataByProperty).forEach(propertyName => {
        const worksheet = XLSX.utils.json_to_sheet(dataByProperty[propertyName]);
        XLSX.utils.book_append_sheet(workbook, worksheet, propertyName.substring(0, 31)); // Excel sheet name limit
      });
      
      // Generate file name with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `Combined_Analytics_${currentDate}.xlsx`;
      
      // Write file
      XLSX.writeFile(workbook, fileName);
      
      toast.dismiss(loadingToast);
      toast.success('✅ Analytics Excel exported successfully!');
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Failed to export analytics');
      console.error('Export error:', error);
    }
  };

  // Create Promotion with safe refresh
  const handleCreatePromotion = async () => {
    // Validation
    if (!promotionFormData.promotion_name || !promotionFormData.discount_percentage) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!promotionFormData.start_date || !promotionFormData.end_date) {
      toast.error('Please select start and end dates');
      return;
    }

    if (!promotionFormData.property_id) {
      toast.error('Please select a property');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/promotions`,
        {
          ...promotionFormData,
          discount_percentage: Number(promotionFormData.discount_percentage)
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      toast.success('✅ Promotion created successfully!');
      setShowCreatePromotionModal(false);
      
      // Reset form
      setPromotionFormData({
        property_id: '',
        promotion_name: '',
        discount_percentage: '',
        start_date: '',
        end_date: '',
        applicable_room_types: [],
        status: 'active',
        description: ''
      });
      
      // Safe refresh - only fetch once
      await fetchPromotions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promotion');
      console.error('Create promotion error:', error);
    }
  };

  // Upload AGX Excel with safe refresh
  const handleAGXUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    const loadingToast = toast.loading('Uploading AGX analytics...');

    try {
      const response = await axios.post(`${API}/agx/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.dismiss(loadingToast);
      toast.success(`✅ AGX uploaded: ${response.data.total_records} records from ${response.data.processed_hotels} hotels`);
      
      if (response.data.skipped_rows && response.data.skipped_rows.length > 0) {
        toast.warning(`${response.data.skipped_rows.length} rows skipped. Check console for details.`);
        console.log('Skipped rows:', response.data.skipped_rows);
      }

      // Reset file input
      event.target.value = '';
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.response?.data?.detail || 'Failed to upload AGX');
      console.error('AGX upload error:', error);
    }
  };

  // Generate AGX Template (Weekly Format)
  const generateAGXTemplate = () => {
    import('xlsx').then(XLSX => {
      const template = [
        {
          'S.N.': 1,
          'hotel_id': 'HOTEL001',
          'hotel_name': 'Sample Hotel Name',
          
          // Week 1
          'Impression 1': 1000,
          'Rank 1': 5,
          'Room Night 1': 50,
          'Revenue 1': 75000,
          'Growth Program': 'Standard',
          'Cost Per Bookings 1': 500,
          'Cost 1': 25000,
          'Click 1': 200,
          'Booking 1': 50,
          
          // Week 2
          'Impression 2': 1200,
          'Rank 2': 4,
          'Room Night 2': 60,
          'Revenue 2': 85000,
          'Cost Per Bookings 2': 480,
          'Created Date': '2025-01-15',
          'Cost 2': 28000,
          'Click 2': 220,
          'Booking 2': 55,
          
          // Week 3
          'Impression 3': 1100,
          'Rank 3': 5,
          'Room Night 3': 55,
          'Revenue 3': 80000,
          'Cost 3': 26000,
          'Click 3': 210,
          'Booking 3': 52
        }
      ];
      
      const ws = XLSX.utils.json_to_sheet(template);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'AGX Template');
      XLSX.writeFile(wb, 'AGX_Weekly_Template.xlsx');
      toast.success('✅ AGX Weekly Template downloaded!');
    });
  };

  // Sync Calendar Rates to GRB
  const syncCalendarToGRB = async () => {
    if (!selectedProperty) {
      toast.error('Please select a property first');
      return;
    }

    const loadingToast = toast.loading('Syncing calendar rates to GRB...');

    try {
      const response = await axios.post(`${API}/availability/sync-to-grb`, null, {
        params: { property_id: selectedProperty.id },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.dismiss(loadingToast);
      toast.success(`✅ ${response.data.message}`);
      console.log('Sync result:', response.data);
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.response?.data?.detail || 'Failed to sync rates to GRB');
      console.error('Sync error:', error);
    }
  };

  // Fetch tab preferences on user login
  React.useEffect(() => {
    if (user) {
      fetchTabPreferences();
    }
  }, [user]);

  // Helper to check if tab should be shown
  const isTabVisible = (tabId) => {
    return !hiddenTabs.includes(tabId);
  };

  // Feb 2026 redesign — sidebar has two distinct navigation modes driven by
  // the top PropertySelector. Portfolio mode ("All Properties") shows a small
  // ops-focused set; single-property mode shows the property-scoped set.
  // No backend / MMT logic is affected — this is a purely visual gate.
  const NAV_ALLOWED_ALL_MODE = new Set([
    'dashboard', 'bookings', 'analytics-ops', 'listings', 'settings',
  ]);
  const NAV_ALLOWED_SINGLE_MODE = new Set([
    'dashboard', 'calendar', 'bookings', 'competitor', 'property-info',
    'payments', 'accounting', 'logs', 'property-profile', 'property-settings',
    'listings', 'promotions',
  ]);
  const isNavAllowedInMode = (tabId) =>
    (allPropertiesMode ? NAV_ALLOWED_ALL_MODE : NAV_ALLOWED_SINGLE_MODE).has(tabId);
  const isNavVisible = (tabId) => isTabVisible(tabId) && isNavAllowedInMode(tabId);

  // Fetch AGX (Agoda Campaign) Data
  const fetchAGXData = async () => {
    if (!selectedProperty) return;
    
    try {
      const response = await axios.get(`${API}/agx`, {
        params: { property_id: selectedProperty.id },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setAgxData(response.data || []);
    } catch (error) {
      console.error('Failed to fetch AGX data:', error);
      setAgxData([]);
    }
  };

  // Handle Bookings Upload
  const handleBookingsUpload = async (file) => {
    if (!selectedProperty || !file) {
      toast.error('Please select a property and file');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('property_id', selectedProperty.id);
    
    const loadingToast = toast.loading('Uploading bookings...');
    
    try {
      const response = await axios.post(`${API}/bookings/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      toast.dismiss(loadingToast);
      toast.success(`✅ ${response.data.count} bookings uploaded successfully!`);
      
      // Refresh bookings and KPIs
      await fetchBookings();
      await fetchDashboardKPIs();
      
      setShowBookingsUploadModal(false);
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.response?.data?.detail || 'Failed to upload bookings');
      console.error('Bookings upload error:', error);
    }
  };

  // Generate Analytics Template
  const generateAnalyticsTemplate = () => {
    import('xlsx').then(XLSX => {
      const template = [
        {
          'Week Start Date': '2025-01-01',
          'Week End Date': '2025-01-07',
          'MMT Room Nights': 0,
          'MMT Revenue': 0,
          'MMT ASP': 0,
          'MMT Property Visits': 0,
          'MMT Conversion': 0,
          'GRB Room Nights': 0,
          'GRB Revenue': 0,
          'GRB ASP': 0,
          'GRB Property Visits': 0,
          'GRB Conversion': 0
        }
      ];
      
      const ws = XLSX.utils.json_to_sheet(template);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Analytics Template');
      XLSX.writeFile(wb, 'Analytics_Upload_Template.xlsx');
      toast.success('✅ Template downloaded!');
    });
  };

  // Generate Bookings Template
  const generateBookingsTemplate = () => {
    import('xlsx').then(XLSX => {
      const template = [
        {
          'Booking ID': 'BK001',
          'Guest Name': 'John Doe',
          'Booking Date': '2025-01-01',
          'Check-in Date': '2025-01-15',
          'Check-out Date': '2025-01-17',
          'Nights': 2,
          'Room Type': 'Deluxe',
          'Rate': 5000,
          'Total Amount': 10000,
          'Status': 'Confirmed'
        }
      ];
      
      const ws = XLSX.utils.json_to_sheet(template);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Bookings Template');
      XLSX.writeFile(wb, 'Bookings_Upload_Template.xlsx');
      toast.success('✅ Template downloaded!');
    });
  };

  // Delete Promotion
  const handleDeletePromotion = async (promotionId) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) {
      return;
    }

    try {
      await axios.delete(`${API}/promotions/${promotionId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.success('Promotion deleted successfully');
      await fetchPromotions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete promotion');
    }
  };

  const handlePropertyFilterChange = (filter) => {
    setPropertyFilter(filter);
    setShowPropertySubMenu(false);
  };

  const handleChangePropertyStatus = async (propertyId, newStatus) => {
    if (!window.confirm(`Are you sure you want to ${newStatus === 'terminated' ? 'terminate' : 'reactivate'} this property?`)) {
      return;
    }

    try {
      const response = await axios.put(
        `${API}/properties/${propertyId}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      toast.success(response.data.message);
      
      // Refresh properties and notifications
      await fetchProperties();
      await fetchNotifications();
      
      // Refresh calendar data if we're viewing the calendar tab
      if (activeTab === 'calendar' && selectedProperty?.id === propertyId) {
        await fetchCalendarData();
      }
      
      // Show notification toast
      if (newStatus === 'terminated') {
        toast.error('Property has been terminated. All rooms are now closed.');
      } else {
        toast.success('Property has been reactivated! Previous rates restored.');
        // Force calendar refresh for reactivation to show restored rates
        if (activeTab === 'calendar' && selectedProperty?.id === propertyId) {
          setTimeout(() => fetchCalendarData(), 500); // Small delay to ensure backend processing is complete
        }
      }
    } catch (error) {
      console.error('Failed to change property status:', error);
      toast.error(error.response?.data?.detail || 'Failed to change property status');
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(
        `${API}/notifications/${notificationId}/read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Refetch properties when filter changes
  useEffect(() => {
    fetchProperties();
  }, [propertyFilter]);


  // Fetch analytics and promotions when property or tab changes (safe implementation)
  useEffect(() => {
    if (!selectedProperty) return;
    
    // Only fetch when switching to these tabs
    if (activeTab === 'analytics' && analyticsData.length === 0) {
      fetchAnalytics();
    } else if (activeTab === 'promotions' && promotions.length === 0) {
      fetchPromotions();
    }
  }, [selectedProperty?.id, activeTab]); // Only depend on IDs, not functions

  const fetchCalendarData = async () => {
    if (!selectedProperty) return;
    const reqPropertyId = selectedProperty.id;
    try {
      // Format as LOCAL YYYY-MM-DD (not UTC) — toISOString() would shift the
      // date by the browser's tz offset (e.g. Aug 27 IST -> Aug 26 UTC),
      // making the calendar jump one day back when the user picks a date.
      const startDate = new Date(currentDate);
      const formattedDate = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      
      const response = await axios.get(`${API}/availability/calendar`, {
        params: {
          property_id: selectedProperty.id,
          start_date: formattedDate,
          days: 14
        }
      });
      if (selectedPropertyIdRef.current !== reqPropertyId) return;
      setCalendarData(response.data);
    } catch (error) {
      console.error('Calendar fetch error:', error);
      if (selectedPropertyIdRef.current !== reqPropertyId) return;
      
      // If property is terminated or calendar fails, create empty calendar data
      const isTerminated = selectedProperty?.status === 'terminated';
      
      if (isTerminated || error.response?.status === 404 || error.response?.status === 500) {
        // Create empty calendar structure with terminated dates
        const startDate = new Date(currentDate);
        const dates = [];
        const roomTypesData = [];
        
        // Generate 14 days (LOCAL formatting to avoid tz shift)
        for (let i = 0; i < 14; i++) {
          const date = new Date(startDate);
          date.setDate(date.getDate() + i);
          dates.push(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
        }
        
        // Get room types or use empty array
        try {
          const rtResponse = await axios.get(`${API}/properties/${selectedProperty.id}/room-types`);
          const roomTypes = rtResponse.data || [];
          
          roomTypes.forEach(rt => {
            roomTypesData.push({
              room_type: {
                id: rt.id,
                name: rt.name,
                max_occupancy: rt.max_occupancy || 2
              },
              availability: dates.map(date => ({
                date,
                available_rooms: 0,
                rate: 0,
                base_rate: 0,
                net_rate: 0,
                is_closed: true
              }))
            });
          });
        } catch (rtError) {
          console.error('Failed to fetch room types:', rtError);
        }
        
        if (selectedPropertyIdRef.current !== reqPropertyId) return;
        setCalendarData({
          property_id: selectedProperty.id,
          start_date: dates[0],
          dates,
          room_types: roomTypesData
        });
        
        if (isTerminated) {
          toast.info('Property is terminated - showing empty calendar');
        }
      } else {
        toast.error('Failed to fetch calendar data');
      }
    }
  };

  const fetchBookings = async () => {
    if (!selectedProperty) return;
    
    try {
      const response = await axios.get(`${API}/bookings`, {
        params: { property_id: selectedProperty.id },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to fetch bookings');
    }
  };

  // Fetch bookings from database with pagination
  const fetchGoogleSheetsBookings = async (page = bookingsPage, limit = bookingsLimit) => {
    setIsLoadingGoogleSheets(true);
    setGoogleSheetsError(null);
    
    try {
      // Build query params for server-side pagination and filtering
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      params.append('grouped', 'true');  // Enable grouping by Booking ID
      
      // Multi-OTA filter - send as comma-separated list
      if (bookingsOTAFilter && bookingsOTAFilter.length > 0) {
        params.append('otas', bookingsOTAFilter.join(','));
      }
      // Multi-status filter - send as comma-separated list
      if (bookingsStatusFilter && bookingsStatusFilter.length > 0) {
        params.append('statuses', bookingsStatusFilter.join(','));
      }
      // Multi-hotel filter - send as comma-separated list
      if (bookingsHotelFilter && bookingsHotelFilter.length > 0) {
        params.append('hotel_names', bookingsHotelFilter.join(','));
      }
      if (bookingsDateRange.start) {
        params.append('from_date', bookingsDateRange.start);
      }
      if (bookingsDateRange.end) {
        params.append('to_date', bookingsDateRange.end);
      }
      // Global search
      if (bookingsSearchDebounced && bookingsSearchDebounced.trim()) {
        params.append('search', bookingsSearchDebounced.trim());
      }
      // Per-property scoping (Feb 2026) — the Bookings tab must NEVER
      // show bookings from another property. When no property is selected
      // we send a sentinel value that never matches, so the API returns
      // zero rows (fail-closed).
      //
      // "All Properties" mode (Feb 2026 nav redesign): omit `property_id`
      // entirely so the API returns bookings across every property the
      // logged-in user can access.
      if (allPropertiesMode) {
        // No property_id → cross-property listing.
      } else if (selectedProperty?.id) {
        params.append('property_id', selectedProperty.id);
      } else {
        params.append('property_id', '__no_property_selected__');
      }

      const response = await axios.get(`${API}/bookings/synced?${params.toString()}`);
      console.log('Bookings API Response:', response.data);  // Debug logging
      
      if (response.data.success) {
        // Frontend safety: Ensure deduplication by booking_id
        const bookingsData = response.data.data || [];
        const deduplicatedBookings = response.data.grouped 
          ? bookingsData  // Already deduplicated by backend
          : Object.values(
              bookingsData.reduce((acc, curr) => {
                const id = curr.booking_id || curr['Booking/Reservation ID'];
                if (!acc[id]) acc[id] = curr;
                return acc;
              }, {})
            );
        
        console.log(`Bookings: ${bookingsData.length} received, ${deduplicatedBookings.length} after dedup`);
        
        setGoogleSheetsBookings(deduplicatedBookings);
        setBookingsTotalCount(response.data.total);
        setBookingsTotalPages(response.data.totalPages);
        setBookingsPage(response.data.page);
        setLastGoogleSheetsUpdate(response.data.lastUpdated);
        
        // Scroll to top of bookings section
        if (page > 1) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else {
        throw new Error('Failed to fetch bookings data');
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch bookings';
      setGoogleSheetsError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoadingGoogleSheets(false);
    }
  };

  // Fetch dashboard metrics
  const fetchDashboardMetrics = async () => {
    try {
      const params = new URLSearchParams();
      if (bookingsHotelFilter && bookingsHotelFilter.length > 0) {
        params.append('hotel_names', bookingsHotelFilter.join(','));
      }
      if (bookingsDateRange.start) {
        params.append('from_date', bookingsDateRange.start);
      }
      if (bookingsDateRange.end) {
        params.append('to_date', bookingsDateRange.end);
      }
      // Per-property scoping (Feb 2026) — never mix properties on the KPI cards.
      if (selectedProperty?.id) {
        params.append('property_id', selectedProperty.id);
      } else {
        params.append('property_id', '__no_property_selected__');
      }

      const response = await axios.get(`${API}/bookings/dashboard-metrics?${params.toString()}`);
      console.log('Dashboard Metrics API Response:', response.data);  // Debug logging
      if (response.data.success) {
        setDashboardMetrics(response.data.metrics);
        console.log('Dashboard Metrics Set:', response.data.metrics);  // Debug logging
      }
    } catch (error) {
      console.error('Failed to fetch dashboard metrics:', error);
    }
  };

  // Fetch booking details for modal
  const fetchBookingDetails = async (bookingId) => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}/details`);
      if (response.data.success) {
        setSelectedBookingDetail(response.data);
        setShowBookingDetailModal(true);
      }
    } catch (error) {
      console.error('Failed to fetch booking details:', error);
      toast.error('Failed to fetch booking details');
    }
  };

  // Fetch filter options (unique OTAs, hotels, statuses) - called once
  const fetchBookingsFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/bookings/filter-options`);
      if (response.data.success) {
        setBookingsFilterOptions({
          otas: response.data.otas || [],
          hotels: response.data.hotels || [],
          statuses: response.data.statuses || []
        });
      }
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const triggerBookingsSync = async () => {
    setIsLoadingGoogleSheets(true);
    try {
      const response = await axios.post(`${API}/bookings/sync-now`, {});
      const { fetched = 0, errors = 0 } = response.data || {};
      if (errors > 0) {
        toast.warning(`Synced ${fetched} bookings with ${errors} error(s)`);
      } else {
        toast.success(`Sync complete — ${fetched} booking(s) updated`);
      }
      // Refresh bookings after sync (reset to page 1)
      setBookingsPage(1);
      await fetchGoogleSheetsBookings(1, bookingsLimit);
      // Also refresh filter options
      await fetchBookingsFilterOptions();
    } catch (error) {
      console.error('Failed to sync bookings:', error);
      toast.error(error.response?.data?.detail || 'Failed to sync bookings');
    } finally {
      setIsLoadingGoogleSheets(false);
    }
  };

  // Handle page change
  const handleBookingsPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= bookingsTotalPages) {
      setBookingsPage(newPage);
      fetchGoogleSheetsBookings(newPage, bookingsLimit);
    }
  };

  // Handle limit change
  const handleBookingsLimitChange = (newLimit) => {
    setBookingsLimit(newLimit);
    setBookingsPage(1);  // Reset to first page
    fetchGoogleSheetsBookings(1, newLimit);
  };

  // Debounce search input (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setBookingsSearchDebounced(bookingsSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [bookingsSearchQuery]);

  // Fetch bookings when filters or debounced search change
  useEffect(() => {
    if (activeTab === 'bookings') {
      setBookingsPage(1);
      fetchGoogleSheetsBookings(1, bookingsLimit);
      fetchDashboardMetrics();
    }
  }, [bookingsOTAFilter, bookingsStatusFilter, bookingsHotelFilter, bookingsDateRange, bookingsSearchDebounced]);

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/audit-logs`, {
        params: { limit: 50 }
      });
      setAuditLogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    }
  };

  // Unified Logs feed (Feb 20 2026 (p)) — reads from /api/logs which
  // unions update_logs + audit_logs + agoda_ari_pushes on the backend.
  // Property filter defaults to "All" (property_id omitted). Server-side
  // scoping ensures non-admin users only see their assigned properties.
  const [logsPagination, setLogsPagination] = useState({ total: 0, limit: 100, offset: 0 });
  const [logsSearch, setLogsSearch] = useState('');
  const fetchLogs = async () => {
    try {
      const params = { limit: logsPagination.limit, offset: logsPagination.offset };
      // "All Properties" ⇒ omit property_id; single property ⇒ scope it.
      if (!allPropertiesMode && selectedProperty?.id) {
        params.property_id = selectedProperty.id;
      }
      if (logsFilter.dateFrom) params.start_date = logsFilter.dateFrom;
      if (logsFilter.dateTo)   params.end_date   = logsFilter.dateTo;
      if (logsSearch)          params.q          = logsSearch;
      const res = await axios.get(`${API}/logs`, { params });
      setLogs(res?.data?.logs || []);
      setLogsPagination(p => ({ ...p, total: res?.data?.total || 0 }));
    } catch (err) {
      toast.error('Failed to fetch logs');
    }
  };

  const fetchUpdateLogs = async () => {
    if (!selectedProperty) return;
    
    try {
      const response = await axios.get(`${API}/update-logs`, {
        params: { property_id: selectedProperty.id, limit: 50 }
      });
      setUpdateLogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch update logs');
    }
  };

  const fetchSystemHealth = async () => {
    try {
      const response = await axios.get(`${API}/system/health`);
      setSystemHealth(response.data);
    } catch (error) {
      console.error('Failed to fetch system health');
    }
  };

  const fetchRoomTypes = async () => {
    if (!selectedProperty) return;
    const reqPropertyId = selectedProperty.id;
    try {
      const response = await axios.get(`${API}/room-types`, {
        params: { property_id: selectedProperty.id }
      });
      if (selectedPropertyIdRef.current !== reqPropertyId) return;
      setRoomTypes(response.data);
    } catch (error) {
      toast.error('Failed to fetch room types');
    }
  };

  const navigateCalendar = (direction) => {
    const newDate = new Date(currentDate);
    // Move exactly 1 day at a time (per user spec)
    newDate.setDate(currentDate.getDate() + (direction === 'next' ? 1 : -1));
    setCurrentDate(newDate);
  };

  const [mmtLastPayload, setMmtLastPayload] = useState(null);  // {status, http_code, request_xml, response_xml, error, room_type_id, date, at}
  const [showMmtPayloadDrawer, setShowMmtPayloadDrawer] = useState(false);

  // ---- Agoda Payload viewer ----------------------------------------
  // Feb 2026: mirrors the MMT payload button but reads from the Agoda
  // audit collection `agoda_ari_pushes` via GET /api/agoda/ari/last-push.
  // Stays completely separate from MMT state so an MMT-only tenant never
  // sees an Agoda button flash, and an Agoda-only tenant never sees the
  // MMT viewer render.
  //
  // Feb 20 2026 (p): after a bulk apply we now capture the FULL list of
  // pushes from `resp.agoda_sync_all` so the operator can inspect every
  // XML in the batch, not just the last one. `agodaBatchPayloads` holds
  // that list; `agodaLastPayload` remains the single most-recent push.
  const [agodaLastPayload, setAgodaLastPayload] = useState(null);
  const [agodaBatchPayloads, setAgodaBatchPayloads] = useState([]);
  const [showAgodaPayloadDrawer, setShowAgodaPayloadDrawer] = useState(false);

  // ---- Yatra / Travelguru Payload viewer (Jun 2026) — mirrors MMT/Agoda.
  // Populated from POST /api/availability/bulk → yatra_sync_all[]. Fully
  // independent state so it never interferes with MMT or Agoda.
  const [yatraLastPayload, setYatraLastPayload] = useState(null);
  const [yatraBatchPayloads, setYatraBatchPayloads] = useState([]);
  const [showYatraPayloadDrawer, setShowYatraPayloadDrawer] = useState(false);
  const captureYatraSync = (r) => {
    if (Array.isArray(r?.yatra_sync_all) && r.yatra_sync_all.length > 0) {
      setYatraBatchPayloads(r.yatra_sync_all);
      // Prefer the last SUCCESS; else the last row so errors surface too.
      const ok = [...r.yatra_sync_all].reverse().find(x => x.status === 'success');
      setYatraLastPayload(ok || r.yatra_sync_all[r.yatra_sync_all.length - 1]);
    }
  };

  const fetchAgodaLastPayload = async (roomTypeId = null, date = null, opts = {}) => {
    const pid = selectedProperty?.id;
    if (!pid) return;
    // Ad-hoc single-record fetches (e.g. from the toolbar button) clear
    // any stale batch payloads so the viewer's per-row selector doesn't
    // linger from a previous cascade. Callers that just captured a
    // batch pass `preserveBatch: true`.
    if (!opts.preserveBatch) {
      setAgodaBatchPayloads([]);
    }
    try {
      const params = { property_id: pid };
      if (roomTypeId) params.room_type_id = roomTypeId;
      if (date) params.date = date;
      const res = await axios.get(`${API}/agoda/ari/last-push`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const fetched = res?.data?.push || null;
      // If the caller wants the batch preserved and we already have one,
      // only overwrite `agodaLastPayload` when the audit record isn't
      // richer than what we already have (i.e. when the audit fetch
      // returned nothing, keep the batch's selected row untouched).
      if (opts.preserveBatch) {
        if (fetched) setAgodaLastPayload(fetched);
      } else {
        setAgodaLastPayload(fetched);
      }
    } catch (err) {
      // Silent — a 400 (no mapping) or 403 (non-admin) just means the
      // button stays dim. This must never noisy up the calendar toolbar.
    }
  };

  // Read the mmt_sync block from a PUT /availability response and surface as a toast
  // + persist as "latest MMT payload" for the header drawer.
  const handleMmtSyncResult = (mmt) => {
    // Feb 2026 hardening — this handler was written for a single object
    // shape, but several backend endpoints now return `mmt_sync` as an
    // ARRAY (consolidated push emits one entry per rate plan sharing the
    // same status/response_xml). Silently ignore empty/non-object shapes
    // so a `[]` from a non-MMT-mapped tenant doesn't render as a mystery
    // toast, and so an accidental string/null can never crash the render.
    if (!mmt) return;
    let payload = mmt;
    if (Array.isArray(mmt)) {
      if (mmt.length === 0) return;
      const firstObj = mmt.find((x) => x && typeof x === 'object' && !Array.isArray(x));
      if (!firstObj) return;
      payload = firstObj;
    }
    if (typeof payload !== 'object' || Array.isArray(payload)) return;

    setMmtLastPayload({ ...payload, at: new Date().toISOString() });
    const code = payload.http_code;
    const ok   = payload.status === 'success';
    if (ok) {
      toast.success('MMT Synced Successfully!');
    } else if (code) {
      // Try to pull MMT's own error code out of <Error Code="XXXX">
      const m = /<Error\s+Code="([^"]+)"[^>]*>([^<]*)/i.exec(payload.response_xml || '');
      const errCode = m ? m[1] : code;
      const errMsg  = m ? m[2] : 'MMT rejected the payload';
      toast.warning(`Local updated, but MMT Sync Error ${errCode}: ${errMsg}`);
    } else if (payload.error) {
      toast.warning(`Local updated, but MMT transport error: ${payload.error}`);
    }
  };

  // Feb 20 2026 — dedicated Agoda toast handler, mirrors
  // handleMmtSyncResult but reads from `agoda_sync` only. Never modifies
  // handleMmtSyncResult (frozen by product spec).
  const handleAgodaSyncResult = (agoda) => {
    if (!agoda) return { ok: false, present: false };
    let payload = agoda;
    if (Array.isArray(agoda)) {
      if (agoda.length === 0) return { ok: false, present: false };
      const firstObj = agoda.find((x) => x && typeof x === 'object' && !Array.isArray(x));
      if (!firstObj) return { ok: false, present: false };
      payload = firstObj;
    }
    if (typeof payload !== 'object' || Array.isArray(payload)) return { ok: false, present: false };
    const ok = payload.status === 'success';
    const code = payload.http_code;
    if (ok) {
      toast.success('Agoda Synced Successfully!');
    } else if (code) {
      const errs = Array.isArray(payload.errors) ? payload.errors : [];
      const first = errs[0] || {};
      const errCode = first.code || code;
      const errMsg  = first.description || 'Agoda rejected the payload';
      toast.warning(`Local updated, but Agoda Sync Error ${errCode}: ${errMsg}`);
    } else if (payload.error) {
      toast.warning(`Local updated, but Agoda transport error: ${payload.error}`);
    } else if (payload.status === 'partial') {
      toast.warning('Local updated, but Agoda returned partial success — see Agoda Payload viewer.');
    }
    return { ok, present: true };
  };

  // Yatra toast for a PUT /availability response (direct cell edit). Mirrors
  // the Bulk Actions toasts; success ✓ on the Yatra Payload button is driven
  // by captureYatraSync (only a real <Success/> flips it to success).
  const handleYatraSyncResult = (data) => {
    const y = data?.yatra_summary;
    if (!y || (y.attempted_pushes || 0) === 0) return false;
    if (y.success_pushes > 0) toast.success(`Yatra: ${y.success_pushes}/${y.attempted_pushes} update${y.attempted_pushes === 1 ? '' : 's'} sent — click Yatra Payload to inspect`);
    if (y.failed_pushes > 0) toast.error(`Yatra: ${y.failed_pushes} update${y.failed_pushes === 1 ? '' : 's'} failed — open Yatra Payload for the response`);
    if (!y.success_pushes && !y.failed_pushes && y.skipped_pushes > 0) toast.message(`Yatra: skipped — open Yatra Payload for what's missing`);
    return true;
  };

  // Batched direct-cell save (Jun 2026): sends ALL staged fields in ONE
  // PUT /availability so the backend can detect exactly what changed and
  // collapse an inventory+closed edit into a single Yatra Availability
  // Update. Captures MMT, Agoda AND Yatra sync results.
  const saveAvailabilityCellEdits = async (roomTypeId, date, fields) => {
    try {
      const update = { room_type_id: roomTypeId, date, ...fields };
      const resp = await axios.put(`${API}/availability`, [update]);
      handleMmtSyncResult(resp?.data?.mmt_sync);
      const agoda = handleAgodaSyncResult(resp?.data?.agoda_sync);
      captureYatraSync(resp?.data);
      const yatraPresent = handleYatraSyncResult(resp?.data);
      fetchAgodaLastPayload(roomTypeId, date);
      const mmtPresent = !!resp?.data?.mmt_sync;
      if (!mmtPresent && !agoda.present && !yatraPresent) {
        toast.success('Availability updated successfully');
      }
      fetchCalendarData();
      if ('rate' in fields) { fetchRatePlanCalendar(); }
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  const updateAvailability = async (roomTypeId, date, field, value) => {
    try {
      const update = {
        room_type_id: roomTypeId,
        date: date,
        [field]: value
      };
      
      const resp = await axios.put(`${API}/availability`, [update]);
      handleMmtSyncResult(resp?.data?.mmt_sync);
      const agoda = handleAgodaSyncResult(resp?.data?.agoda_sync);
      // Refresh Agoda payload viewer with the cell that was just edited so
      // the operator sees the exact Agoda push (if any) that fired. Never
      // touches MMT state.
      fetchAgodaLastPayload(roomTypeId, date);
      // Show a generic success toast ONLY when neither OTA had a push
      // (i.e. property has no MMT AND no Agoda mapping). A failed Agoda
      // push must NOT trigger the success toast — handleAgodaSyncResult
      // has already surfaced the exact error via toast.warning.
      const mmtPresent = !!resp?.data?.mmt_sync;
      if (!mmtPresent && !agoda.present) {
        toast.success('Availability updated successfully');
      }
      // Refetch BOTH grids so the green Base Rate cell and the EP-occ-1 cell stay
      // in strict lockstep (backend mirrors availability.rate → EP-occ-1 per-date).
      fetchCalendarData();
      if (field === 'rate') { fetchRatePlanCalendar(); }
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  // Fetch the property's Room Type Link config (base → linked room types + increments).
  const fetchRoomTypeLinks = async (propertyId) => {
    if (!propertyId) { setRoomTypeLinks([]); return; }
    try {
      const res = await axios.get(`${API}/properties/${propertyId}/room-type-links`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setRoomTypeLinks(res.data?.links || []);
    } catch (err) {
      console.error('Failed to fetch room type links:', err);
      setRoomTypeLinks([]);
    }
  };

  // Low-level rate write for a single (rate_plan, occupancy, date) — reuses the
  // existing /rate-plans/bulk-update endpoint (and its OTA push/mirror behaviour).
  const writeRatePlanOccupancyRate = async (ratePlanId, occupancy, date, rateVal) => {
    await axios.post(`${API}/rate-plans/bulk-update`, {
      rate_plan_id: ratePlanId,
      start_date: date,
      end_date: date,
      occupancy_rates: { [String(occupancy)]: rateVal },
    }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  };

  // Apply the base room type's rate edit ONLY (no linked cascade).
  const applyBaseRateEdit = async ({ ratePlanId, occupancy, date, newRate }) => {
    try {
      await writeRatePlanOccupancyRate(ratePlanId, occupancy, date, newRate);
      toast.success(`Rate updated for occupancy ${occupancy}`);
      fetchRatePlanCalendar();
      fetchCalendarData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update rate');
    }
  };

  // Apply the base edit AND cascade to every linked room type for the same
  // (rate plan code, occupancy, date), using each link's configured increment.
  const applyLinkedRateEdit = async ({ roomTypeId, ratePlanId, ratePlanCode, occupancy, date, newRate }) => {
    try {
      await writeRatePlanOccupancyRate(ratePlanId, occupancy, date, newRate);
      const linkDoc = (roomTypeLinks || []).find(l => l.base_room_type_id === roomTypeId);
      const warnings = [];
      let applied = 0;
      for (const link of (linkDoc?.links || [])) {
        const rpRoom = (ratePlanCalendar?.room_types || []).find(r => r.room_type_id === link.linked_room_type_id);
        const linkedRoomName = rpRoom?.room_type_name
          || (calendarData?.room_types || []).find(rt => rt.room_type.id === link.linked_room_type_id)?.room_type?.name
          || 'Linked room';
        const plan = (rpRoom?.rate_plans || []).find(p => (p.code || '').toUpperCase() === (ratePlanCode || '').toUpperCase());
        if (!plan) {
          warnings.push(`${linkedRoomName}: no "${ratePlanCode}" rate plan — skipped`);
          continue;
        }
        const occExists = (plan.occupancies || []).some(o => o.occupancy === occupancy);
        if (!occExists) {
          warnings.push(`${linkedRoomName} (${ratePlanCode}): no occupancy ${occupancy} — skipped`);
          continue;
        }
        const incVal = Number(link.increments?.[ratePlanCode]?.[String(occupancy)] ?? 0);
        const linkedRate = (link.increment_type === 'percentage')
          ? newRate + (newRate * incVal / 100)
          : newRate + incVal;
        try {
          await writeRatePlanOccupancyRate(plan.id, occupancy, date, Math.max(0, Math.round(linkedRate * 100) / 100));
          applied += 1;
        } catch (e) {
          warnings.push(`${linkedRoomName} (${ratePlanCode}): update failed`);
        }
      }
      if (applied > 0) toast.success(`Updated base + ${applied} linked room type${applied > 1 ? 's' : ''}`);
      else toast.success('Base rate updated');
      if (warnings.length) warnings.forEach(w => toast.warning(w));
      fetchRatePlanCalendar();
      fetchCalendarData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update rate');
    }
  };

  // Inline edit entry point for a (rate_plan, occupancy, date) cell in the
  // Calendar grid. If the room type has linked room types configured, prompt
  // the operator; otherwise apply directly.
  const updateRatePlanOccupancyRate = async (ratePlanId, occupancy, date, newRate, roomTypeId, ratePlanCode) => {
    const val = parseFloat(newRate);
    if (isNaN(val) || val < 0) { toast.error('Enter a valid rate ≥ 0'); return; }
    const hasLinks = !!(roomTypeLinks || []).find(l => l.base_room_type_id === roomTypeId && (l.links || []).length > 0);
    const pending = { roomTypeId, ratePlanId, ratePlanCode, occupancy, date, newRate: val };
    if (!hasLinks) {
      await applyBaseRateEdit(pending);
      return;
    }
    setLinkPrompt(pending);
  };

  const handleLinkPromptChoice = async (updateLinked) => {
    if (!linkPrompt) return;
    setLinkPromptBusy(true);
    try {
      if (updateLinked) await applyLinkedRateEdit(linkPrompt);
      else await applyBaseRateEdit(linkPrompt);
    } finally {
      setLinkPromptBusy(false);
      setLinkPrompt(null);
    }
  };

  const handlePropertyFormChange = (e) => {
    const { name, value } = e.target;
    setPropertyFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateProperty = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!propertyFormData.name.trim()) {
      toast.error('Property name is required');
      return;
    }
    if (!propertyFormData.address.trim()) {
      toast.error('Address is required');
      return;
    }
    if (!propertyFormData.city.trim()) {
      toast.error('City is required');
      return;
    }
    if (!propertyFormData.phone.trim()) {
      toast.error('Contact number is required');
      return;
    }
    if (!propertyFormData.email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!propertyFormData.total_rooms || propertyFormData.total_rooms < 1) {
      toast.error('Number of rooms must be at least 1');
      return;
    }

    // Phone validation (basic)
    const phoneRegex = /^[\+]?[0-9\-\s\(\)]{10,}$/;
    if (!phoneRegex.test(propertyFormData.phone)) {
      toast.error('Please enter a valid phone number');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(propertyFormData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      const response = await axios.post(`${API}/properties`, {
        name: propertyFormData.name,
        address: propertyFormData.address,
        city: propertyFormData.city,
        country: propertyFormData.country || 'India',
        phone: propertyFormData.phone,
        email: propertyFormData.email,
        total_rooms: parseInt(propertyFormData.total_rooms),
        description: propertyFormData.description || '',
        number_of_rooms: propertyFormData.number_of_rooms ? parseInt(propertyFormData.number_of_rooms) : null,
        grb_agreement_date: propertyFormData.grb_agreement_date || null,
        sales_person_name: propertyFormData.sales_person_name || null,
        ota_live_date: propertyFormData.ota_live_date || null,
        first_reservation_date: propertyFormData.first_reservation_date || null,
        setup_agreed: propertyFormData.setup_agreed || null,
        token: propertyFormData.token ? parseFloat(propertyFormData.token) : null,
        grb_commission_agreed: propertyFormData.grb_commission_agreed ? parseFloat(propertyFormData.grb_commission_agreed) : null,
        setup_completion_status: propertyFormData.setup_completion_status || null,
        property_status: propertyFormData.property_status || null
      });

      toast.success('Property created successfully!');
      setShowPropertyModal(false);
      
      // Reset form
      setPropertyFormData({
        name: '',
        address: '',
        city: '',
        country: '',
        phone: '',
        email: '',
        total_rooms: '',
        description: '',
        number_of_rooms: '',
        grb_agreement_date: '',
        sales_person_name: '',
        ota_live_date: '',
        first_reservation_date: '',
        setup_agreed: '',
        token: '',
        grb_commission_agreed: '',
        setup_completion_status: '',
        property_status: ''
      });

      // Refresh properties list
      fetchProperties();
      
      // Set the new property as selected
      setSelectedProperty(response.data);
      
    } catch (error) {
      console.error('Property creation error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again');
        logout();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create property');
      }
    }
  };

  // New comprehensive onboarding handler
  const handleOnboardingSubmit = async (formData) => {
    try {
      toast.info('Uploading property data...');
      
      const response = await axios.post(`${API}/properties/onboard`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(response.data.message);
      setShowPropertyModal(false);
      
      // Store the new property ID
      const newPropertyId = response.data.property_id;
      
      // Refresh properties list
      const propertiesResponse = await axios.get(`${API}/properties`);
      setProperties(propertiesResponse.data);
      
      // Find and select the newly created property from the refreshed list
      const newProperty = propertiesResponse.data.find(p => p.id === newPropertyId);
      if (newProperty) {
        setSelectedProperty(newProperty);
      } else {
        // Fallback: select the first property if new property not found
        if (propertiesResponse.data.length > 0) {
          setSelectedProperty(propertiesResponse.data[0]);
        }
      }
      
    } catch (error) {
      console.error('Onboarding error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again');
        logout();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to onboard property');
      }
    }
  };

  // Competitor data import handler
  const handleImportCompetitorData = async () => {
    try {
      // Validation
      if (!competitorUploadData.selectedHotel) {
        toast.error('Please select a hotel');
        return;
      }
      if (!competitorUploadData.sheetUrl.trim()) {
        toast.error('Please enter Google Sheet URL');
        return;
      }

      // Basic URL validation
      const urlPattern = /^https:\/\/docs\.google\.com\/spreadsheets\//;
      if (!urlPattern.test(competitorUploadData.sheetUrl)) {
        toast.error('Please enter a valid Google Sheets URL');
        return;
      }

      // Show loading toast
      const loadingToast = toast.loading('Importing competitor data...');

      const response = await axios.post(`${API}/competitor-sheets/import`, {
        property_id: competitorUploadData.selectedHotel,
        sheet_url: competitorUploadData.sheetUrl
      });

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      if (response.data) {
        toast.success(`Successfully imported ${response.data.rows_imported || 0} competitor rates!`);
        setShowCompetitorUpload(false);
        
        // Reset form
        setCompetitorUploadData({
          selectedHotel: '',
          sheetUrl: ''
        });
        
        // Trigger refresh of competitor tab by updating a counter
        // This will cause the competitor tab to re-fetch data via useEffect
        setCompetitorRefreshTrigger(prev => prev + 1);
      }
      
    } catch (error) {
      toast.dismiss();
      console.error('Competitor import error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again');
        logout();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to import competitor data');
      }
    }
  };

  // Fetch competitor rates for the selected property
  const fetchCompetitorRatesForTab = async (propertyId) => {
    if (!propertyId) return;
    
    try {
      setLoadingCompetitorRates(true);
      const response = await axios.get(`${API}/competitor-rates`, {
        params: { property_id: propertyId }
      });
      setRealCompetitorRates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch competitor rates:', error);
      setRealCompetitorRates([]);
    } finally {
      setLoadingCompetitorRates(false);
    }
  };

  // Fetch last updated timestamp for competitor data
  const fetchCompetitorLastUpdated = async (propertyId) => {
    if (!propertyId) return;
    
    try {
      const response = await axios.get(
        `${API}/competitor-rates/last-updated/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.last_updated) {
        setCompetitorLastUpdated(response.data.last_updated);
        setCompetitorLastUpdatedBy(response.data.last_updated_by_name);
      } else {
        setCompetitorLastUpdated(null);
        setCompetitorLastUpdatedBy(null);
      }
    } catch (error) {
      console.error('Failed to fetch last updated timestamp:', error);
      setCompetitorLastUpdated(null);
      setCompetitorLastUpdatedBy(null);
    }
  };

  // Fetch storage metrics
  const fetchStorageMetrics = async () => {
    try {
      setLoadingStorageMetrics(true);
      const response = await axios.get(
        `${API}/storage/metrics`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setStorageMetrics(response.data);
    } catch (error) {
      console.error('Failed to fetch storage metrics:', error);
      toast.error('Failed to load storage metrics');
    } finally {
      setLoadingStorageMetrics(false);
    }
  };

  // Clear storage
  const handleClearStorage = async () => {
    const selectedOptions = Object.keys(clearStorageOptions).filter(
      key => clearStorageOptions[key]
    );

    if (selectedOptions.length === 0) {
      toast.error('Please select at least one option to clear');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/storage/clear`,
        {
          clear_options: selectedOptions
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      toast.success(
        `Storage cleared! ${response.data.total_cleared_mb.toFixed(2)} MB freed`
      );

      // Reset options and close modal
      setClearStorageOptions({
        competitor_data: false,
        audit_logs: false,
        old_uploads: false
      });
      setShowClearStorageModal(false);

      // Refresh storage metrics
      fetchStorageMetrics();
    } catch (error) {
      console.error('Failed to clear storage:', error);
      toast.error('Failed to clear storage');
    }
  };

  // ==================== OTA API INTEGRATION FUNCTIONS ====================
  
  // Fetch all OTA APIs
  const fetchOTAApis = async () => {
    try {
      const response = await axios.get(`${API}/ota-apis`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setOtaApis(response.data || []);
    } catch (error) {
      console.error('Failed to fetch OTA APIs:', error);
      toast.error('Failed to load OTA APIs');
    }
  };

  // Add endpoint row to form
  const addEndpointRow = () => {
    setOtaFormData(prev => ({
      ...prev,
      endpoints: [
        ...prev.endpoints,
        {
          endpoint_name: '',
          base_url: '',
          method: 'POST',
          category: 'ARI',
          notes: '',
          is_active: true
        }
      ]
    }));
  };

  // Remove endpoint row
  const removeEndpointRow = (index) => {
    setOtaFormData(prev => ({
      ...prev,
      endpoints: prev.endpoints.filter((_, i) => i !== index)
    }));
  };

  // Update endpoint field
  const updateEndpointField = (index, field, value) => {
    setOtaFormData(prev => ({
      ...prev,
      endpoints: prev.endpoints.map((endpoint, i) => 
        i === index ? { ...endpoint, [field]: value } : endpoint
      )
    }));
  };

  // Submit OTA API
  const handleSubmitOTA = async () => {
    try {
      if (!otaFormData.ota_name) {
        toast.error('OTA Name is required');
        return;
      }

      if (otaFormData.auth_type === 'API Key' && !otaFormData.api_key) {
        toast.error('API Key is required for API Key auth type');
        return;
      }

      if (otaFormData.auth_type === 'Basic Auth' && (!otaFormData.username || !otaFormData.password)) {
        toast.error('Username and Password are required for Basic Auth');
        return;
      }

      if (otaFormData.auth_type === 'Bearer Token' && !otaFormData.bearer_token) {
        toast.error('Bearer Token is required for Bearer Token auth type');
        return;
      }

      if (otaFormData.endpoints.length === 0) {
        toast.error('At least one endpoint is required');
        return;
      }

      const response = await axios.post(`${API}/ota-apis`, otaFormData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.success('OTA API created successfully!');
      setShowAddOTAModal(false);
      setOtaModalStep(1);
      setOtaFormData({
        ota_name: '',
        auth_type: 'API Key',
        api_key: '',
        username: '',
        password: '',
        oauth_token_url: '',
        bearer_token: '',
        description: '',
        endpoints: []
      });
      fetchOTAApis();
    } catch (error) {
      console.error('Failed to create OTA API:', error);
      toast.error(error.response?.data?.detail || 'Failed to create OTA API');
    }
  };

  // Test OTA connection
  const handleTestOTAConnection = async (otaId, endpointId = null) => {
    setTestingEndpoint(endpointId || 'general');
    try {
      const url = endpointId 
        ? `${API}/ota-apis/${otaId}/test-connection?endpoint_id=${endpointId}`
        : `${API}/ota-apis/${otaId}/test-connection`;
      
      const response = await axios.post(url, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      setTestResults(prev => ({
        ...prev,
        [endpointId || 'general']: response.data
      }));

      if (response.data.success) {
        toast.success(`Connection test successful! Status: ${response.data.status_code}`);
      } else {
        toast.error(`Connection test failed: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Failed to test connection:', error);
      toast.error('Failed to test connection');
      setTestResults(prev => ({
        ...prev,
        [endpointId || 'general']: {
          success: false,
          message: error.response?.data?.detail || 'Connection test failed'
        }
      }));
    } finally {
      setTestingEndpoint(null);
    }
  };

  // Delete OTA API
  const handleDeleteOTA = async (otaId) => {
    if (!window.confirm('Are you sure you want to delete this OTA API? This will remove all endpoints and property links.')) {
      return;
    }

    try {
      await axios.delete(`${API}/ota-apis/${otaId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      toast.success('OTA API deleted successfully');
      fetchOTAApis();
      setShowOTADetailModal(false);
    } catch (error) {
      console.error('Failed to delete OTA API:', error);
      toast.error('Failed to delete OTA API');
    }
  };

  // View OTA details
  const handleViewOTA = async (otaId) => {
    try {
      const response = await axios.get(`${API}/ota-apis/${otaId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setSelectedOTA(response.data);
      setShowOTADetailModal(true);
      setTestResults({});
    } catch (error) {
      console.error('Failed to fetch OTA details:', error);
      toast.error('Failed to load OTA details');
    }
  };


  // Open Link Property Modal
  const handleOpenLinkPropertyModal = (ota) => {
    setLinkPropertyFormData({
      ota_id: ota.id,
      ota_name: ota.ota_name,
      property_id: '',
      external_property_id: '',
      mapped_endpoints: []
    });
    setShowLinkPropertyModal(true);
  };

  // Link Property to OTA
  const handleLinkProperty = async () => {
    try {
      // Validation
      if (!linkPropertyFormData.property_id) {
        toast.error('Please select a property');
        return;
      }
      if (!linkPropertyFormData.external_property_id || !linkPropertyFormData.external_property_id.trim()) {
        toast.error('Please enter the external property ID');
        return;
      }

      setIsLinkingProperty(true);

      await axios.post(
        `${API}/property-api-links`,
        {
          property_id: linkPropertyFormData.property_id,
          ota_id: linkPropertyFormData.ota_id,
          external_property_id: linkPropertyFormData.external_property_id.trim(),
          mapped_endpoints: linkPropertyFormData.mapped_endpoints
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      toast.success('Property linked to OTA successfully!');
      setShowLinkPropertyModal(false);
      
      // Refresh OTA APIs list
      await fetchOTAApis();
      
      // Refresh OTA details if detail modal is open
      if (showOTADetailModal && selectedOTA) {
        await handleViewOTA(selectedOTA.id);
      }
    } catch (error) {
      console.error('Failed to link property:', error);
      toast.error(error.response?.data?.detail || 'Failed to link property to OTA');
    } finally {
      setIsLinkingProperty(false);
    }
  };

  // Unlink Property from OTA
  const handleUnlinkProperty = async (linkId, propertyName) => {
    if (!window.confirm(`Are you sure you want to unlink "${propertyName}" from this OTA?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/property-api-links/${linkId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.success('Property unlinked successfully!');
      
      // Refresh OTA details
      if (selectedOTA) {
        await handleViewOTA(selectedOTA.id);
      }
      
      // Refresh OTA APIs list
      await fetchOTAApis();
    } catch (error) {
      console.error('Failed to unlink property:', error);
      toast.error(error.response?.data?.detail || 'Failed to unlink property');
    }
  };


  // Fetch OTA APIs when Settings tab is opened
  useEffect(() => {
    if (activeTab === 'settings' && user?.role === 'admin') {
      fetchOTAApis();
    }
  }, [activeTab, user]);

  // Load all properties + dependency counts whenever "See All Properties" opens
  const reloadAllProperties = async () => {
    setLoadingAllProperties(true);
    try {
      const res = await axios.get(`${API}/properties/all-with-dependencies`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setAllPropertiesWithDeps(res.data.properties || []);
      try { fetchProperties(); } catch (_) { /* refresh sidebar/picker */ }
    } catch (e) {
      console.error('Failed to load property dependencies', e);
      toast.error(e.response?.data?.detail || 'Failed to load properties');
    } finally {
      setLoadingAllProperties(false);
    }
  };

  useEffect(() => {
    if (!showAllPropertiesModal) return;
    let cancelled = false;
    const load = async () => {
      setLoadingAllProperties(true);
      try {
        const res = await axios.get(`${API}/properties/all-with-dependencies`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!cancelled) setAllPropertiesWithDeps(res.data.properties || []);
      } catch (e) {
        console.error('Failed to load property dependencies', e);
        if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load properties');
      } finally {
        if (!cancelled) setLoadingAllProperties(false);
      }
    };
    load();
    setSelectedPropertyIds(new Set());
    setPropSearchQuery('');
    setBulkDeleteResults(null);
    setAllPropsTab('properties');
    return () => { cancelled = true; };
  }, [showAllPropertiesModal]);

  // Bulk delete selected properties
  const handleBulkDeleteProperties = async () => {
    const ids = Array.from(selectedPropertyIds);
    if (ids.length === 0) return;
    setBulkDeleteBusy(true);
    try {
      const res = await axios.post(
        `${API}/properties/bulk-delete`,
        { property_ids: ids, confirm: true },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setBulkDeleteResults(res.data);
      const successIds = new Set(
        (res.data.results || []).filter(r => r.success).map(r => r.property_id)
      );
      const okCount = successIds.size;
      const failCount = res.data.failed || 0;
      if (okCount) toast.success(`Deleted ${okCount} property record${okCount === 1 ? '' : 's'}`);
      if (failCount) toast.error(`${failCount} property record${failCount === 1 ? '' : 's'} failed to delete`);

      // Optimistic UI update — remove ONLY successful deletions from the
      // list and selection. Failed rows stay visible so the user can retry
      // and the error surfaces in the bulk-delete results panel.
      setAllPropertiesWithDeps(prev => prev.filter(p => !successIds.has(p.id)));
      setSelectedPropertyIds(prev => {
        const next = new Set(prev);
        successIds.forEach(id => next.delete(id));
        return next;
      });
      await fetchProperties();
    } catch (e) {
      console.error('Bulk delete failed', e);
      toast.error(e.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleteBusy(false);
    }
  };

  // Delete property function
  const handleDeleteProperty = async (propertyId) => {
    try {
      await axios.delete(`${API}/properties/${propertyId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.success('Property deleted successfully');
      setPropertyToDelete(null);
      // Optimistic UI update — remove from the See All Properties list &
      // selection immediately. The popup stays open so the user can keep
      // reviewing the remaining properties.
      setAllPropertiesWithDeps(prev => prev.filter(p => p.id !== propertyId));
      setSelectedPropertyIds(prev => {
        const next = new Set(prev);
        next.delete(propertyId);
        return next;
      });

      // Refresh global properties list (sidebar/property picker)
      await fetchProperties();
    } catch (error) {
      console.error('Failed to delete property:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete property');
    }
  };

  // Save edited property (also persists any room-type edits from the modal)
  const handleSavePropertyChanges = async () => {
    try {
      const required = ['name', 'address', 'city', 'country', 'phone', 'email', 'total_rooms'];
      const missing = required.filter(k => {
        const v = editPropertyData[k];
        return v === undefined || v === null || String(v).trim() === '';
      });
      if (missing.length > 0) {
        toast.error('Please fill in all required fields');
        return;
      }
      if (Number.isNaN(Number(editPropertyData.total_rooms))) {
        toast.error('Total rooms must be a valid number');
        return;
      }

      const authHdrs = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

      // 1) Save property core fields — coerce total_rooms to an integer at submit
      const propertyPayload = {
        ...editPropertyData,
        total_rooms: parseInt(editPropertyData.total_rooms, 10),
      };
      await axios.put(`${API}/properties/${editPropertyData.id}`, propertyPayload, authHdrs);

      // 2) Save any edited room-type rows (capacity, count, base_rate, extras)
      const rtEdits = editPropertyData.room_type_edits || {};
      const rtIds = Object.keys(rtEdits);
      const rtResults = await Promise.allSettled(rtIds.map(async (rtId) => {
        const patch = rtEdits[rtId];
        // Coerce numeric strings; drop empties
        const clean = {};
        ['max_occupancy','room_count'].forEach(k => {
          if (patch[k] !== undefined && patch[k] !== '') clean[k] = parseInt(patch[k], 10);
        });
        ['base_rate','extra_adult_rate','extra_child_rate','extra_child_rate_age2'].forEach(k => {
          if (patch[k] !== undefined && patch[k] !== '') clean[k] = parseFloat(patch[k]);
        });
        if (Object.keys(clean).length === 0) return { skipped: true };
        return axios.put(`${API}/room-types/${rtId}`, clean, authHdrs);
      }));
      const rtFails = rtResults.filter(r => r.status === 'rejected').length;
      if (rtFails > 0) toast.warning(`${rtFails} room type update(s) failed — see console`);

      toast.success('Property updated successfully');
      setShowEditProperty(false);

      // Refresh properties list + room types (auto-propagates to Property Settings / Calendar / Competitor
      // tabs because each of those re-fetches on tab switch or via effect on selectedProperty change)
      await fetchProperties();
      if (selectedProperty?.id === editPropertyData.id) {
        const updatedProperty = await axios.get(`${API}/properties`);
        const updated = updatedProperty.data.find(p => p.id === editPropertyData.id);
        if (updated) setSelectedProperty(updated);
      }
      try { await fetchRoomTypes(); } catch { /* fetchRoomTypes may not be in scope on all pages */ }
    } catch (error) {
      console.error('Failed to save property:', error);
      toast.error(error.response?.data?.detail || 'Failed to save property');
    }
  };

  // Forgot Password - Step 1: Request reset code
  const handleForgotPasswordSubmit = async () => {
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotPasswordEmail
      });

      if (response.data.mock_mode) {
        // Mock mode - show the reset code
        toast.success(`Reset code: ${response.data.mock_reset_code}`, { duration: 10000 });
        setResetCode(response.data.mock_reset_code); // Auto-fill for convenience
      } else {
        toast.success('Reset code sent to your email');
      }
      
      setResetStep(2); // Move to step 2
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reset code');
    }
  };

  // Forgot Password - Step 2: Reset password
  const handleResetPasswordSubmit = async () => {
    if (!resetCode || !newPassword) {
      toast.error('Please enter reset code and new password');
      return;
    }

    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: forgotPasswordEmail,
        reset_code: resetCode,
        new_password: newPassword
      });

      toast.success('Password reset successfully! You can now login.');
      
      // Reset form and close modal
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
      setResetCode('');
      setNewPassword('');
      setResetStep(1);
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  // Clear all logs
  const handleClearLogs = async () => {
    if (!window.confirm('Do you want to clear all logs? This action cannot be undone.')) {
      return;
    }

    try {
      // Unified endpoint clears update_logs + audit_logs + agoda_ari_pushes.
      // When a single property is selected, only that property's logs
      // are removed; in "All Properties" the wipe is global (admin only).
      const params = {};
      if (!allPropertiesMode && selectedProperty?.id) params.property_id = selectedProperty.id;
      await axios.delete(`${API}/logs`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success('All logs cleared successfully');
      fetchLogs(); // reload the now-empty feed
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear logs');
    }
  };

  // Export logs to CSV — server-side stream so we don't have to
  // re-fetch every page just to build the file.
  const handleExportLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (!allPropertiesMode && selectedProperty?.id) params.set('property_id', selectedProperty.id);
      if (logsFilter.dateFrom) params.set('start_date', logsFilter.dateFrom);
      if (logsFilter.dateTo)   params.set('end_date',   logsFilter.dateTo);
      if (logsSearch)          params.set('q',          logsSearch);
      const res = await axios.get(`${API}/logs/export.csv?${params.toString()}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `logs_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Logs exported to CSV');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export logs');
    }
  };

  // Fetch storage metrics when Settings tab is opened (Admin only)
  useEffect(() => {
    if (activeTab === 'settings' && user?.role === 'admin') {
      fetchStorageMetrics();
    }
  }, [activeTab, user]);

  // Auto-fetch unified logs when the Logs tab is opened or its scope
  // changes (property switch, date filter, search query, pagination).
  useEffect(() => {
    if (activeTab !== 'logs') return;
    fetchLogs();
    // eslint-disable-next-line
  }, [
    activeTab, allPropertiesMode, selectedProperty?.id,
    logsFilter.dateFrom, logsFilter.dateTo, logsSearch,
    logsPagination.offset, logsPagination.limit,
  ]);

  // Fetch competitor rates when property changes or refresh is triggered
  useEffect(() => {
    if (selectedProperty && activeTab === 'competitor') {
      fetchCompetitorRatesForTab(selectedProperty.id);
      fetchCompetitorLastUpdated(selectedProperty.id);
    }
  }, [selectedProperty, competitorRefreshTrigger, activeTab]);

  // Handle Excel file upload for competitor data (multi-hotel support)
  const handleCompetitorExcelUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        if (workbook.SheetNames.length === 0) {
          toast.error('Excel file has no sheets');
          return;
        }

        const allHotelData = {};
        let totalHotelsUpdated = 0;
        let totalHotelsNotFound = 0;
        const notFoundHotels = [];

        // Process each sheet (each sheet = one hotel)
        workbook.SheetNames.forEach(sheetName => {
          // Find matching hotel by name (case-insensitive)
          const matchingProperty = properties.find(
            prop => prop.name.toLowerCase().trim() === sheetName.toLowerCase().trim()
          );

          if (!matchingProperty) {
            notFoundHotels.push(sheetName);
            totalHotelsNotFound++;
            return; // Skip this sheet
          }

          // Parse sheet data
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            return; // Skip empty sheets
          }

          // Extract competitor names from first row
          const firstRow = jsonData[0];
          const excludedColumns = ['Date', 'Day', 'Our Rate (₹)', 'Average Competitor Rate (₹)', 'Comparison'];
          const competitorNames = Object.keys(firstRow).filter(key => !excludedColumns.includes(key));

          if (competitorNames.length === 0) {
            return; // Skip sheets with no competitor columns
          }

          // Parse the data into structured format
          const parsedData = {
            competitors: competitorNames,
            dates: [],
            rates: {}
          };

          jsonData.forEach(row => {
            const dateStr = row['Date'];
            if (!dateStr) return;

            // Convert date to YYYY-MM-DD format
            let formattedDate;
            if (typeof dateStr === 'number') {
              // Excel serial date
              const excelDate = new Date((dateStr - 25569) * 86400 * 1000);
              formattedDate = excelDate.toISOString().split('T')[0];
            } else {
              // Try to parse as string date
              const parsedDate = new Date(dateStr);
              if (!isNaN(parsedDate)) {
                formattedDate = parsedDate.toISOString().split('T')[0];
              } else {
                return; // Skip invalid dates
              }
            }

            parsedData.dates.push(formattedDate);
            parsedData.rates[formattedDate] = {
              ourRate: parseFloat(row['Our Rate (₹)']) || 5999,
              competitors: {}
            };

            // Extract competitor rates
            competitorNames.forEach(competitor => {
              const rate = parseFloat(row[competitor]);
              if (!isNaN(rate)) {
                parsedData.rates[formattedDate].competitors[competitor] = rate;
              }
            });
          });

          // Store data for this hotel
          allHotelData[matchingProperty.id] = parsedData;
          totalHotelsUpdated++;
        });

        if (totalHotelsUpdated === 0) {
          toast.error('No matching hotels found in Excel sheets');
          return;
        }

        // Update state with all hotel data
        setUploadedCompetitorData(allHotelData);
        
        // Save data to backend for persistence
        const savePromises = [];
        for (const [propertyId, hotelData] of Object.entries(allHotelData)) {
          // Transform data into backend format
          const ratesArray = [];
          
          hotelData.dates.forEach(date => {
            const dateRates = hotelData.rates[date];
            if (dateRates) {
              // Add each competitor rate
              Object.entries(dateRates.competitors).forEach(([competitorName, rate]) => {
                ratesArray.push({
                  competitor_name: competitorName,
                  date: date,
                  rate: rate,
                  our_rate: dateRates.ourRate
                });
              });
            }
          });
          
          // Call backend API to save
          const savePromise = axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/competitor-rates/batch-upload`,
            {
              property_id: propertyId,
              rates_data: ratesArray
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }
          ).then(response => {
            // Update last updated timestamp for this property
            if (propertyId === selectedProperty?.id) {
              setCompetitorLastUpdated(response.data.last_updated);
              setCompetitorLastUpdatedBy(user?.full_name);
            }
            return { propertyId, success: true, count: response.data.rows_imported };
          }).catch(error => {
            console.error(`Failed to save competitor data for property ${propertyId}:`, error);
            return { propertyId, success: false, error: error.message };
          });
          
          savePromises.push(savePromise);
        }
        
        // Wait for all save operations to complete
        const saveResults = await Promise.all(savePromises);
        const successCount = saveResults.filter(r => r.success).length;
        const failCount = saveResults.filter(r => !r.success).length;
        
        // Show success message
        let message = `Excel uploaded! ${totalHotelsUpdated} hotel(s) updated`;
        if (successCount > 0) {
          message += `. ${successCount} saved to database`;
        }
        if (totalHotelsNotFound > 0) {
          message += `. ${totalHotelsNotFound} sheet(s) not matched`;
        }
        toast.success(message);
        
        if (failCount > 0) {
          toast.error(`Failed to save ${failCount} hotel(s) to database`);
        }
        
        // Show info for unmatched hotels
        if (notFoundHotels.length > 0) {
          setTimeout(() => {
            notFoundHotels.forEach(hotelName => {
              toast.info(`No hotel found for sheet: "${hotelName}"`, { duration: 4000 });
            });
          }, 500);
        }
        
        // Clear the file input
        event.target.value = '';
      } catch (error) {
        console.error('Excel parsing error:', error);
        toast.error('Failed to parse Excel file. Please check the format.');
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleRoomTypeFormChange = (e) => {
    const { name, value } = e.target;
    setRoomTypeFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateRoomType = async (e) => {
    e.preventDefault();
    
    if (!selectedProperty) {
      toast.error('Please select a property first');
      return;
    }

    // Validation
    if (!roomTypeFormData.name.trim()) {
      toast.error('Room type name is required');
      return;
    }
    if (!roomTypeFormData.max_occupancy || roomTypeFormData.max_occupancy < 1) {
      toast.error('Max occupancy must be at least 1');
      return;
    }
    if (!roomTypeFormData.base_rate || roomTypeFormData.base_rate < 0) {
      toast.error('Base rate must be a positive number');
      return;
    }
    if (!roomTypeFormData.room_count || roomTypeFormData.room_count < 1) {
      toast.error('Room count must be at least 1');
      return;
    }

    try {
      const amenitiesList = roomTypeFormData.amenities
        ? roomTypeFormData.amenities.split(',').map(a => a.trim()).filter(a => a)
        : [];

      await axios.post(`${API}/room-types`, {
        property_id: selectedProperty.id,
        name: roomTypeFormData.name,
        description: roomTypeFormData.description || '',
        max_occupancy: parseInt(roomTypeFormData.max_occupancy),
        base_rate: parseFloat(roomTypeFormData.base_rate),
        room_count: parseInt(roomTypeFormData.room_count),
        amenities: amenitiesList
      });

      toast.success('Room type created successfully!');
      setShowRoomTypeModal(false);
      
      // Reset form
      setRoomTypeFormData({
        name: '',
        description: '',
        max_occupancy: '',
        base_rate: '',
        room_count: '',
        amenities: ''
      });

      // Refresh room types and calendar data
      fetchRoomTypes();
      fetchCalendarData();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create room type');
    }
  };

  const handleBulkAction = (actionType) => {
    // ---- Feb 26 2026: force-reset the sidebar first so re-selecting the same
    //      action reliably re-opens the drawer. Setting `showBulkSidebar` to
    //      the same value twice in a row is a React no-op, so we always tear
    //      down first before setting up the new action.
    setShowBulkSidebar(false);
    setBulkActionType('');
    setTimeout(() => {
      setBulkActionType(actionType);
      setBulkFormData({
        checkInDate: '',
        checkOutDate: '',
        hotelId: selectedProperty?.id || '',
        roomTypeIds: [], // Initialize as empty array for multi-select
        value: '',
        rateType: 'base',
        restrictionType: 'block', // Default to block for restrictions
        dateRanges: [{ from_date: '', to_date: '' }],
        daysOfWeek: { sun: true, mon: true, tue: true, wed: true, thu: true, fri: true, sat: true },
      });
      // Also clear any staged extra-guest and restriction inputs so a fresh
      // drawer opens with a clean slate.
      setBulkExtraGuestInputs({});
      setBulkRatePlanRestrictions({});
      setShowBulkSidebar(true);
    }, 30);
  };

  // Feb 27 2026: single close handler for the Bulk Actions right drawer.
  // Explicitly resets `bulkActionType` so clicking ANY item in the Bulk
  // Actions dropdown afterwards reliably re-opens the drawer (previously
  // React skipped the state transition when the same action was picked twice).
  const closeBulkSidebar = () => {
    setShowBulkSidebar(false);
    setBulkActionType('');
  };

  const handleBulkSubmit = async () => {
    // ── MMT CHM V3 (Feb 2026) — bulk submit now routes through
    // POST /api/availability/bulk which accepts date_ranges + days_of_week,
    // writes per-date rows in Mongo, and pushes ONE MMT XML per room type
    // with multi-<DateRange> (dow attrs) + multi-occupancy PerOccupancyRates.
    const ranges = (bulkFormData.dateRanges || []).filter(r => r.from_date && r.to_date);
    if (ranges.length === 0 || bulkFormData.roomTypeIds.length === 0) {
      toast.error('Please add at least one date range and select at least one room type');
      return;
    }
    const dow = bulkFormData.daysOfWeek || {};
    if (!['sun','mon','tue','wed','thu','fri','sat'].some(k => dow[k])) {
      toast.error('Select at least one day of week');
      return;
    }
    // Feb 28 2026 rev.3 — per-DateRange days_of_week map (fallback to template).
    const rangesForApi = ranges.map(r => ({
      from_date: r.from_date,
      to_date:   r.to_date,
      days_of_week: r.days_of_week || dow,
    }));

    if (bulkActionType === 'inventory') {
      // Per-room inventory validation — each checked room must have a non-empty, in-range value
      const inv = bulkFormData.inventory || {};
      const propRts = (roomTypes || []).filter(rt => rt.property_id === selectedProperty?.id);
      const rtById = Object.fromEntries(propRts.map(rt => [rt.id, rt]));
      const problems = [];
      for (const rtId of bulkFormData.roomTypeIds) {
        const raw = inv[rtId];
        if (raw === undefined || raw === '' || raw === null) {
          problems.push(`${rtById[rtId]?.name || rtId}: enter an inventory count`);
          continue;
        }
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0) {
          problems.push(`${rtById[rtId]?.name || rtId}: value must be ≥ 0`);
          continue;
        }
        const cap = Number(rtById[rtId]?.room_count ?? 0);
        if (cap > 0 && n > cap) {
          problems.push(`${rtById[rtId]?.name || rtId}: ${n} exceeds max ${cap}`);
        }
      }
      if (problems.length) {
        toast.error(problems[0] + (problems.length > 1 ? ` (+${problems.length - 1} more)` : ''));
        return;
      }
    }

    try {
      const body = {
        property_id: selectedProperty?.id,
        action: bulkActionType === 'restrictions' ? 'restrictions' : bulkActionType,
        room_type_ids: bulkFormData.roomTypeIds,
        date_ranges: rangesForApi,
        days_of_week: dow,
      };
      if (bulkActionType === 'inventory') {
        body.available_rooms_by_room = Object.fromEntries(
          bulkFormData.roomTypeIds.map(rt => [rt, parseInt((bulkFormData.inventory || {})[rt], 10)])
        );
      } else if (bulkActionType === 'restrictions') {
        body.is_closed = bulkFormData.restrictionType === 'block';
      }
      const { data: resp } = await axios.post(`${API}/availability/bulk`, body);
      handleMmtSyncResult(resp?.mmt_sync);

      const roomCount = bulkFormData.roomTypeIds.length;
      const actionName = bulkActionType === 'restrictions'
        ? (bulkFormData.restrictionType === 'block' ? 'Block' : 'Unblock')
        : bulkActionType;
      toast.success(`Bulk ${actionName}: ${resp?.writes || 0} rows updated across ${roomCount} room type${roomCount > 1 ? 's' : ''}`);
      // Agoda parallel push summary — kept isolated from MMT so operators
      // see per-OTA status without cross-labelling.
      if (resp?.agoda_summary && (resp.agoda_summary.attempted_pushes || 0) > 0) {
        const s = resp.agoda_summary;
        if (s.success_pushes > 0) {
          toast.success(
            `Agoda: ${s.success_pushes}/${s.attempted_pushes} pushes across `
            + `${s.success_date_count} date${s.success_date_count === 1 ? '' : 's'}, `
            + `${s.success_room_type_count} room type${s.success_room_type_count === 1 ? '' : 's'}`
          );
        }
        if (s.failed_pushes > 0) {
          toast.error(`Agoda: ${s.failed_pushes} push${s.failed_pushes === 1 ? '' : 'es'} failed — check Agoda audit for details`);
        }
      }
      // Feb 20 2026 (p): capture the FULL batch so the Agoda Payload
      // viewer can walk every XML, not just the most recent one.
      if (Array.isArray(resp?.agoda_sync_all) && resp.agoda_sync_all.length > 0) {
        setAgodaBatchPayloads(resp.agoda_sync_all);
        setAgodaLastPayload(resp.agoda_sync_all[resp.agoda_sync_all.length - 1]);
      }
      // Jun 2026 — Yatra / Travelguru push summary + payload viewer capture.
      // Covers Bulk Inventory (availability_update), Close-Out and Rates via
      // this shared submit path. Success/checkmark only reflects the REAL
      // Yatra API response captured server-side.
      captureYatraSync(resp);
      if (resp?.yatra_summary && (resp.yatra_summary.attempted_pushes || 0) > 0) {
        const y = resp.yatra_summary;
        if (y.success_pushes > 0) toast.success(`Yatra: ${y.success_pushes}/${y.attempted_pushes} update${y.attempted_pushes === 1 ? '' : 's'} sent — click Yatra Payload to inspect`);
        if (y.failed_pushes > 0) toast.error(`Yatra: ${y.failed_pushes} update${y.failed_pushes === 1 ? '' : 's'} failed — open Yatra Payload for the response`);
        if (!y.success_pushes && !y.failed_pushes && y.skipped_pushes > 0) toast.message(`Yatra: skipped — open Yatra Payload for what's missing`);
      }
      closeBulkSidebar();

      setBulkFormData({
        checkInDate: '',
        checkOutDate: '',
        hotelId: selectedProperty?.id || '',
        roomTypeIds: [],
        value: '',
        rateType: 'base',
        restrictionType: 'block',
        dateRanges: [{ from_date: '', to_date: '' }],
        daysOfWeek: { sun: true, mon: true, tue: true, wed: true, thu: true, fri: true, sat: true },
      });
      // Feb 27 2026: refresh main calendar grid + rate-plan grid inline so
      // restrictions / inventory / rate updates surface without a reload.
      fetchCalendarData();
      try { fetchRatePlanCalendar(); } catch { /* noop */ }
    } catch (error) {
      console.error('Bulk update error:', error);
      toast.error(error?.response?.data?.detail || `Failed to apply bulk ${bulkActionType} update`);
    }
  };

  const renderImprovedCompetitorTab = () => {
    // Get data for current selected property
    const currentPropertyData = selectedProperty && uploadedCompetitorData[selectedProperty.id];
    
    // Use uploaded data if available for current property, otherwise use real data or defaults
    let allCompetitors;
    let useUploadedData = false;
    
    if (currentPropertyData && currentPropertyData.competitors.length > 0) {
      // Use uploaded Excel data for this property
      allCompetitors = currentPropertyData.competitors;
      useUploadedData = true;
    } else {
      // Use real data from backend or default list
      allCompetitors = [...new Set(realCompetitorRates.map(r => r.competitor_name))].sort();
      if (allCompetitors.length === 0) {
        allCompetitors = ['Grand Palace', 'Hotel ABC', 'Hotel XYZ', 'Resort Paradise'];
      }
    }
    
    // Generate stable dummy rate using hash of competitor name + date
    const getStableDummyRate = (competitorName, date) => {
      const grbRate = 5999;
      // Create a simple hash from competitor name and date
      const str = competitorName + date;
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash = hash & hash; // Convert to 32bit integer
      }
      // Use hash to generate consistent value between -200 and +200
      const offset = (Math.abs(hash) % 401) - 200;
      return grbRate + offset;
    };
    
    // Generate dates based on offset (keep original date navigation)
    const baseDates = [];
    const startDate = new Date('2025-01-15');
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i + (competitorDateOffset * 7));
      baseDates.push(date.toISOString().split('T')[0]);
    }

    // Get competitor rates for a specific date from uploaded data, real data, or stable dummy fallback
    const getCompetitorRatesForDate = (date) => {
      // Determine our rate from uploaded data or default
      const grbRate = currentPropertyData?.rates[date]?.ourRate || 5999;
      
      // If using uploaded data for current property
      if (useUploadedData && currentPropertyData?.rates[date]) {
        return allCompetitors.map(competitor => {
          const rate = currentPropertyData.rates[date].competitors[competitor];
          return {
            name: competitor,
            rate: rate || null,
            isDummy: false
          };
        });
      }
      
      // Otherwise use real data or dummy
      const ratesForDate = realCompetitorRates.filter(r => r.date === date);
      
      return allCompetitors.map(competitor => {
        const rate = ratesForDate.find(r => r.competitor_name === competitor);
        
        // If no actual rate, use stable dummy rate (doesn't change on re-render)
        if (!rate || rate.rate === null) {
          const dummyRate = getStableDummyRate(competitor, date);
          return {
            name: competitor,
            rate: dummyRate,
            isDummy: true // Flag to indicate this is dummy data
          };
        }
        
        return {
          name: competitor,
          rate: rate.rate,
          isDummy: false
        };
      });
    };

    // Generate mock inventory (keeping original functionality)
    const generateCompetitorInventory = (date) => {
      return allCompetitors.map(competitor => ({
        name: competitor,
        inventory: Math.floor(Math.random() * 40) + 10
      }));
    };

    const calculateAverage = (competitors, type) => {
      if (!competitors || competitors.length === 0) return { avg: 0, count: 0 };
      const validItems = competitors.filter(item => type === 'rate' ? item.rate !== null : true);
      if (validItems.length === 0) return { avg: 0, count: 0 };
      const sum = validItems.reduce((acc, item) => acc + (type === 'rate' ? item.rate : item.inventory), 0);
      return { avg: Math.round(sum / validItems.length), count: validItems.length };
    };

    const navigateCompetitorDates = (direction) => {
      setCompetitorDateOffset(prev => prev + direction);
    };

    // Export competitor data to Excel
    const exportToExcel = () => {
      try {
        const grbRate = 5999; // Our rate
        
        // Prepare data for Excel
        const excelData = [];
        
        // Add header row
        const headerRow = ['Date', 'Day', 'Our Rate (₹)', ...allCompetitors, 'Average Competitor Rate (₹)', 'Comparison'];
        excelData.push(headerRow);
        
        // Add data rows for each date
        baseDates.forEach(date => {
          const dateObj = new Date(date);
          const dayName = dateObj.toLocaleDateString('en', { weekday: 'short' });
          const formattedDate = `${dateObj.getDate()} ${dateObj.toLocaleDateString('en', { month: 'short' })} ${dateObj.getFullYear()}`;
          
          const competitorData = getCompetitorRatesForDate(date);
          const { avg, count } = calculateAverage(competitorData, 'rate');
          
          const row = [
            formattedDate,
            dayName,
            grbRate,
            ...competitorData.map(c => c.rate ? Math.round(c.rate) : '—'),
            count > 0 ? Math.round(avg) : '—',
            count > 0 ? (avg > grbRate ? 'Competitors Higher' : avg < grbRate ? 'We Are Higher' : 'Equal') : '—'
          ];
          
          excelData.push(row);
        });
        
        // Create worksheet
        const ws = XLSX.utils.aoa_to_sheet(excelData);
        
        // Set column widths
        ws['!cols'] = [
          { wch: 15 }, // Date
          { wch: 10 }, // Day
          { wch: 15 }, // Our Rate
          ...allCompetitors.map(() => ({ wch: 15 })), // Competitor columns
          { wch: 20 }, // Average
          { wch: 20 }  // Comparison
        ];
        
        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Competitor Rates');
        
        // Generate filename with current date
        const filename = `Competitor_Rates_${new Date().toISOString().split('T')[0]}.xlsx`;
        
        // Download file
        XLSX.writeFile(wb, filename);
        
        toast.success('Excel file downloaded successfully!');
      } catch (error) {
        console.error('Excel export error:', error);
        toast.error('Failed to export Excel file');
      }
    };

    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Competitor Analysis</h3>
          <div className="flex items-center gap-3">
            {competitorLastUpdated && (
              <div className="text-xs text-gray-500 mr-2" title={competitorLastUpdatedBy ? `Updated by: ${competitorLastUpdatedBy}` : ''}>
                Last Updated: {new Date(competitorLastUpdated).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
              </div>
            )}
            <Badge variant="outline" className="text-blue-600">
              {loadingCompetitorRates 
                ? 'Loading...' 
                : currentPropertyData 
                  ? `${currentPropertyData.competitors.length} competitors from Excel`
                  : `${realCompetitorRates.length} rates loaded`}
            </Badge>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleCompetitorExcelUpload}
              style={{ display: 'none' }}
              id="competitor-excel-upload"
            />
            <label htmlFor="competitor-excel-upload">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                onClick={() => document.getElementById('competitor-excel-upload').click()}
                type="button"
              >
                <Upload className="w-4 h-4" />
                Upload Excel
              </Button>
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={exportToExcel}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Excel
            </Button>
          </div>
        </div>

        {/* Rates Comparison Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <div className="w-3 h-3 bg-green-400 rounded"></div>
                Rates Comparison
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigateCompetitorDates(-1)}
                  data-testid="rates-prev-dates"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous Dates
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigateCompetitorDates(1)}
                  data-testid="rates-next-dates"
                >
                  Next Dates
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loadingCompetitorRates ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                <p className="mt-4 text-gray-600">Loading competitor rates...</p>
              </div>
            ) : allCompetitors.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No competitor data available. Import data from Settings.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700 sticky left-0 bg-gray-50 border-r min-w-[150px]">
                        Source
                      </th>
                      {baseDates.map((date) => {
                        const dateObj = new Date(date);
                        const dayName = dateObj.toLocaleDateString('en', { weekday: 'short' });
                        const dayNum = dateObj.getDate();
                        const monthName = dateObj.toLocaleDateString('en', { month: 'short' });
                        return (
                          <th key={date} className="px-3 py-3 text-center font-medium text-gray-700 min-w-[120px] border-r">
                            <div>{dayName}</div>
                            <div className="text-xs text-gray-500">{dayNum} {monthName}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Competitor Average Row */}
                    <tr className="border-t bg-blue-50">
                      <td className="px-4 py-3 font-medium text-blue-800 sticky left-0 bg-blue-50 border-r">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-blue-600 text-xs">Competitor</Badge>
                            <span>Average</span>
                          </div>
                          <button
                            onClick={() => setExpandedCompetitorRates(!expandedCompetitorRates)}
                            className="text-blue-600 hover:text-blue-800"
                            data-testid="expand-all-rates"
                          >
                            {expandedCompetitorRates ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                      {baseDates.map((date) => {
                        const competitorData = getCompetitorRatesForDate(date);
                        const { avg, count } = calculateAverage(competitorData, 'rate');
                        const grbRate = 5999; // Our rate
                        
                        // Background color logic: green if avg < our rate, red if avg > our rate
                        const avgBgColor = count > 0 
                          ? (avg < grbRate ? 'bg-green-100' : avg > grbRate ? 'bg-red-100' : 'bg-gray-50')
                          : 'bg-gray-50';
                        
                        return (
                          <td key={date} className="px-3 py-3 text-center border-r">
                            <div className={`${avgBgColor} border border-gray-200 rounded px-2 py-1 font-medium text-xs text-gray-800`}>
                              {count > 0 ? `Avg: ₹${avg} (competitors n=${count})` : '—'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>

                    {/* Individual Competitor Rows (Expandable) */}
                    {expandedCompetitorRates && allCompetitors.map((competitor, index) => (
                      <tr key={`rates-${competitor}`} className="border-t bg-blue-25 animate-in slide-in-from-top">
                        <td className="px-4 py-2 text-sm text-blue-700 sticky left-0 bg-blue-25 border-r pl-8">
                          {competitor}
                        </td>
                        {baseDates.map((date) => {
                          const competitorData = getCompetitorRatesForDate(date);
                          const competitorRate = competitorData.find(c => c.name === competitor);
                          const grbRate = 5999; // Our rate
                          
                          // Background color logic: red if competitor rate < our rate, green if competitor rate > our rate
                          const rateBgColor = competitorRate?.rate 
                            ? (competitorRate.rate < grbRate ? 'bg-red-100' : competitorRate.rate > grbRate ? 'bg-green-100' : 'bg-gray-50')
                            : 'bg-gray-50';
                          
                          return (
                            <td key={date} className="px-3 py-2 text-center border-r">
                              <div className={`${rateBgColor} border border-gray-200 rounded px-2 py-1 text-xs text-gray-800 font-medium`}>
                                {competitorRate?.rate ? `₹${Math.round(competitorRate.rate)}` : '—'}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}

                    {/* GRB Rates Row */}
                    <tr className="border-t bg-green-50">
                      <td className="px-4 py-3 font-medium text-green-800 sticky left-0 bg-green-50 border-r">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-green-600 text-xs">GRB</Badge>
                          <span>Our Rates</span>
                        </div>
                      </td>
                      {baseDates.map((date) => {
                        const ourRate = currentPropertyData?.rates[date]?.ourRate || 5999;
                        return (
                          <td key={date} className="px-3 py-3 text-center border-r">
                            <div className="bg-green-100 border border-green-200 rounded px-2 py-1 text-green-800 font-medium text-xs">
                              ₹{ourRate}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Inventory Comparison Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-400 rounded"></div>
                Inventory Comparison
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigateCompetitorDates(-1)}
                  data-testid="inventory-prev-dates"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous Dates
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigateCompetitorDates(1)}
                  data-testid="inventory-next-dates"
                >
                  Next Dates
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {allCompetitors.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No competitor data available.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700 sticky left-0 bg-gray-50 border-r min-w-[150px]">
                        Source
                      </th>
                      {baseDates.map((date) => {
                        const dateObj = new Date(date);
                        const dayName = dateObj.toLocaleDateString('en', { weekday: 'short' });
                        const dayNum = dateObj.getDate();
                        const monthName = dateObj.toLocaleDateString('en', { month: 'short' });
                        return (
                          <th key={date} className="px-3 py-3 text-center font-medium text-gray-700 min-w-[120px] border-r">
                            <div>{dayName}</div>
                            <div className="text-xs text-gray-500">{dayNum} {monthName}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Competitor Average Row */}
                    <tr className="border-t bg-blue-50">
                      <td className="px-4 py-3 font-medium text-blue-800 sticky left-0 bg-blue-50 border-r">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-blue-600 text-xs">Competitor</Badge>
                            <span>Average</span>
                          </div>
                          <button
                            onClick={() => setExpandedCompetitorInventory(!expandedCompetitorInventory)}
                            className="text-blue-600 hover:text-blue-800"
                            data-testid="expand-all-inventory"
                          >
                            {expandedCompetitorInventory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                      {baseDates.map((date) => {
                        const inventoryData = generateCompetitorInventory(date);
                        const { avg, count } = calculateAverage(inventoryData, 'inventory');
                        return (
                          <td key={date} className="px-3 py-3 text-center border-r">
                            <div className="bg-blue-100 border border-blue-200 rounded px-2 py-1 text-blue-800 font-medium text-xs">
                              Avg: {avg} (competitors n={count})
                            </div>
                          </td>
                        );
                      })}
                    </tr>

                    {/* Individual Competitor Inventory Rows (Expandable) */}
                    {expandedCompetitorInventory && allCompetitors.map((competitor, index) => (
                      <tr key={`inventory-${competitor}`} className="border-t bg-blue-25 animate-in slide-in-from-top">
                        <td className="px-4 py-2 text-sm text-blue-700 sticky left-0 bg-blue-25 border-r pl-8">
                          {competitor}
                        </td>
                        {baseDates.map((date) => {
                          const inventoryData = generateCompetitorInventory(date);
                          const competitorInventory = inventoryData.find(c => c.name === competitor);
                          return (
                            <td key={date} className="px-3 py-2 text-center border-r">
                              <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1 text-blue-700 text-xs">
                                {competitorInventory?.inventory || '—'}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}

                    {/* GRB Inventory Row */}
                    <tr className="border-t bg-green-50">
                      <td className="px-4 py-3 font-medium text-green-800 sticky left-0 bg-green-50 border-r">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-green-600 text-xs">GRB</Badge>
                          <span>Our Inventory</span>
                        </div>
                      </td>
                      {baseDates.map((date) => (
                        <td key={date} className="px-3 py-3 text-center border-r">
                          <div className="bg-blue-100 border border-blue-200 rounded px-2 py-1 text-blue-800 font-medium text-xs">
                            49
                          </div>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderPropertyInfoTab = () => {
    const handleEditProperty = () => {
      if (selectedProperty) {
        setEditPropertyData({
          id: selectedProperty.id,
          name: selectedProperty.name,
          address: selectedProperty.address,
          city: selectedProperty.city,
          country: selectedProperty.country,
          phone: selectedProperty.phone,
          email: selectedProperty.email,
          total_rooms: selectedProperty.total_rooms,
          description: selectedProperty.description || ''
        });
        setShowEditProperty(true);
      }
    };

    if (!selectedProperty) {
      return (
        <div className="text-center py-12">
          <Info className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No Property Selected</h3>
          <p className="text-gray-600">Select a property from the dropdown to view details</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-800">Property Information</h3>
            {selectedProperty && (
              <span className={`property-status-badge ${selectedProperty.status || 'active'}`}>
                <span className={`property-status-indicator ${selectedProperty.status || 'active'}`}></span>
                {(selectedProperty.status || 'active').charAt(0).toUpperCase() + (selectedProperty.status || 'active').slice(1)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap property-info-buttons-mobile">
            {user?.role === 'admin' && selectedProperty && (
              <Button
                onClick={() => handleChangePropertyStatus(
                  selectedProperty.id,
                  selectedProperty.status === 'terminated' ? 'active' : 'terminated'
                )}
                variant="outline"
                size="sm"
                className={selectedProperty.status === 'terminated' ? 'border-green-500 text-green-600 hover:bg-green-50' : 'border-red-500 text-red-600 hover:bg-red-50'}
              >
                {selectedProperty.status === 'terminated' ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Reactivate
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-1" />
                    Terminate
                  </>
                )}
              </Button>
            )}
            <Button 
              onClick={handleEditProperty}
              size="sm"
              className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700"
              data-testid="edit-property-btn"
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Hotel Name</label>
                <div className="text-lg font-semibold text-gray-800">{selectedProperty.name}</div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-600">Address</label>
                <div className="text-gray-800">{selectedProperty.address}</div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">City</label>
                  <div className="text-gray-800">{selectedProperty.city}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Country</label>
                  <div className="text-gray-800">{selectedProperty.country}</div>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-600">Total Rooms</label>
                <div className="text-xl font-bold text-blue-600">{selectedProperty.total_rooms}</div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Email</label>
                <div className="text-gray-800">{selectedProperty.email}</div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-600">Phone</label>
                <div className="text-gray-800">{selectedProperty.phone}</div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-600">Created Date</label>
                <div className="text-gray-800">
                  {new Date(selectedProperty.created_at).toLocaleDateString()}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Business Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Business Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600">GRB Agreement Date</label>
                <div className="text-gray-800">
                  {selectedProperty.grb_agreement_date || 'Not set'}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-600">GRB Setup Status</label>
                <div className="text-gray-800">
                  <Badge variant={selectedProperty.grb_setup_status === 'Completed' ? 'default' : 'secondary'}>
                    {selectedProperty.grb_setup_status || 'Pending'}
                  </Badge>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-600">OTA Live Date</label>
                <div className="text-gray-800">{selectedProperty.ota_live_date || 'Not set'}</div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-600">First Reservation Date</label>
                <div className="text-gray-800">{selectedProperty.first_reservation_date || 'Not set'}</div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-600">Salesperson</label>
                <div className="text-gray-800">{selectedProperty.salesperson_name || 'Not assigned'}</div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-600">Property Status</label>
                <div className="text-gray-800">
                  <Badge variant={
                    selectedProperty.status === 'active' ? 'default' : 
                    selectedProperty.status === 'inactive' ? 'secondary' : 
                    'destructive'
                  }>
                    {selectedProperty.status || 'Active'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Room Types Overview */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Room Types Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(roomTypes || []).map((roomType) => (
                  <div key={roomType.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="font-semibold text-gray-800 mb-2">{roomType.name}</div>
                    <div className="text-sm text-gray-600 mb-2">{roomType.description}</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Capacity:</span>
                        <span className="font-medium ml-1">{roomType.max_occupancy}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Count:</span>
                        <span className="font-medium ml-1">{roomType.room_count}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-500">Base Rate:</span>
                        <span className="font-semibold text-green-600 ml-1">₹{roomType.base_rate}</span>
                      </div>
                    </div>
                    {roomType.amenities && roomType.amenities.length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs text-gray-500 mb-1">Amenities:</div>
                        <div className="flex flex-wrap gap-1">
                          {roomType.amenities.slice(0, 3).map((amenity) => (
                            <Badge key={amenity} variant="outline" className="text-xs">
                              {amenity}
                            </Badge>
                          ))}
                          {roomType.amenities.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{roomType.amenities.length - 3}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // Render Logs Tab
  const renderLogs = () => {
    const filteredLogs = logs.filter(log => {
      if (logsFilter.actionType && log.actionType !== logsFilter.actionType) return false;
      if (logsFilter.property && !log.propertyName?.toLowerCase().includes(logsFilter.property.toLowerCase())) return false;
      if (logsFilter.dateFrom && new Date(log.timestamp) < new Date(logsFilter.dateFrom)) return false;
      if (logsFilter.dateTo && new Date(log.timestamp) > new Date(logsFilter.dateTo)) return false;
      return true;
    });

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Activity Logs</h3>
            <p className="text-sm text-gray-600">Track all changes and actions in the system</p>
          </div>
          {user?.role === 'admin' && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportLogs}
                className="bg-green-50 border-green-200 hover:bg-green-100"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Logs
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearLogs}
                className="bg-red-50 border-red-200 hover:bg-red-100 text-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Logs
              </Button>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action Type</label>
                <Select value={logsFilter.actionType || "all"} onValueChange={(value) => setLogsFilter({...logsFilter, actionType: value === "all" ? "" : value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    <SelectItem value="Rate Updated">Rate Updated</SelectItem>
                    <SelectItem value="Property Edited">Property Edited</SelectItem>
                    <SelectItem value="Settings Changed">Settings Changed</SelectItem>
                    <SelectItem value="Room Type Updated">Room Type Updated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property</label>
                <Input
                  placeholder="Search by property"
                  value={logsFilter.property}
                  onChange={(e) => setLogsFilter({...logsFilter, property: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
                <Input
                  type="date"
                  value={logsFilter.dateFrom}
                  onChange={(e) => setLogsFilter({...logsFilter, dateFrom: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
                <Input
                  type="date"
                  value={logsFilter.dateTo}
                  onChange={(e) => setLogsFilter({...logsFilter, dateTo: e.target.value})}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Action Type</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Room Type</TableHead>
                    <TableHead>Check-in Date</TableHead>
                    <TableHead>Check-out Date</TableHead>
                    <TableHead>New Rate</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                        No logs found. Activity logs will appear here as you make changes.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm">
                          {new Date(log.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.actionType}</Badge>
                        </TableCell>
                        <TableCell>{log.propertyName || '-'}</TableCell>
                        <TableCell>{log.roomTypeName || '-'}</TableCell>
                        <TableCell className="text-sm">{log.checkinDate || '-'}</TableCell>
                        <TableCell className="text-sm">{log.checkoutDate || '-'}</TableCell>
                        <TableCell className="font-medium">
                          {log.newRate ? `₹${log.newRate}` : '-'}
                        </TableCell>
                        <TableCell className="text-sm">{log.user}</TableCell>
                        <TableCell className="text-xs text-gray-600 max-w-xs truncate">
                          {log.details || '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render Analytics Tab
  // Render AGX Campaign Analytics for Agoda
  const renderAgodaCampaign = () => {
    const agxMetrics = [
      { id: 'impression', name: 'Impression', key: 'impression' },
      { id: 'rank', name: 'Rank', key: 'rank' },
      { id: 'room_night', name: 'Room Night', key: 'room_night' },
      { id: 'revenue', name: 'Revenue', key: 'revenue' },
      { id: 'cost', name: 'Cost', key: 'cost' },
      { id: 'click', name: 'Click', key: 'click' },
      { id: 'booking', name: 'Booking', key: 'booking' }
    ];

    const hasAGXData = agxData && agxData.length > 0;
    const currentMetric = agxMetrics.find(m => m.id === agxMetric) || agxMetrics[0];

    // Prepare chart data - use week_number for X-axis
    const chartData = hasAGXData ? agxData.map(item => ({
      week: item.week_number ? `Week ${item.week_number}` : item.created_date,
      value: item[currentMetric.key] || 0
    })) : [];

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Agoda Campaign Analytics</h3>
        </div>

        {/* Metric Tabs */}
        <div className="flex gap-2 border-b overflow-x-auto">
          {agxMetrics.map(metric => (
            <button
              key={metric.id}
              onClick={() => setAgxMetric(metric.id)}
              className={`px-4 py-2 font-medium text-sm transition-colors whitespace-nowrap ${
                agxMetric === metric.id
                  ? 'text-orange-600 border-b-2 border-orange-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {metric.name}
            </button>
          ))}
        </div>

        {!hasAGXData ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No AGX Data</h3>
            <p className="text-gray-600">No AGX campaign data uploaded for this property</p>
          </div>
        ) : (
          <>
            {/* Chart */}
            <Card>
              <CardHeader>
                <CardTitle>{currentMetric.name} Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="value" stroke="#f97316" strokeWidth={2} name={currentMetric.name} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Data Table */}
            <Card>
              <CardHeader>
                <CardTitle>Campaign Data</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 text-sm font-medium">Week</th>
                        <th className="text-right p-2 text-sm font-medium">Impression</th>
                        <th className="text-right p-2 text-sm font-medium">Rank</th>
                        <th className="text-right p-2 text-sm font-medium">Room Night</th>
                        <th className="text-right p-2 text-sm font-medium">Revenue</th>
                        <th className="text-right p-2 text-sm font-medium">Cost</th>
                        <th className="text-right p-2 text-sm font-medium">Click</th>
                        <th className="text-right p-2 text-sm font-medium">Booking</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agxData.map((item, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-2 text-sm">Week {item.week_number || idx + 1}</td>
                          <td className="p-2 text-sm text-right">{item.impression?.toLocaleString()}</td>
                          <td className="p-2 text-sm text-right">{item.rank}</td>
                          <td className="p-2 text-sm text-right">{item.room_night}</td>
                          <td className="p-2 text-sm text-right">₹{item.revenue?.toLocaleString()}</td>
                          <td className="p-2 text-sm text-right">₹{item.cost?.toLocaleString()}</td>
                          <td className="p-2 text-sm text-right">{item.click}</td>
                          <td className="p-2 text-sm text-sm text-right">{item.booking}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  };

  const renderAnalytics = () => {
    // Safety checks
    if (!selectedProperty) {
      return (
        <div className="text-center py-12">
          <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No Property Selected</h3>
          <p className="text-gray-600">Please select a property to view analytics</p>
        </div>
      );
    }

    const metricTabs = [
      { id: 'room_nights', name: 'Room Nights', key: 'room_nights' },
      { id: 'revenue', name: 'Revenue', key: 'revenue' },
      { id: 'asp', name: 'ASP', key: 'asp' },
      { id: 'property_visits', name: 'Property Visits', key: 'property_visits' },
      { id: 'conversion', name: 'Conversion', key: 'conversion' }
    ];

    const currentMetric = metricTabs.find(m => m.id === analyticsMetricTab) || metricTabs[0];
    
    // Safe data check
    const hasData = Array.isArray(analyticsData) && analyticsData.length > 0;

    return (
      <div className="space-y-6">
        {/* Header with Actions */}
        <div className="flex items-center justify-between analytics-header-mobile">
          <h3 className="text-lg font-semibold text-gray-800 analytics-title">Analytics Dashboard</h3>
          {/* Hide these buttons when Agoda is selected - AGX managed from Settings */}
          {analyticsOTAFilter !== 'Agoda' && (
            <>
              {/* Desktop: Show inline buttons */}
              <div className="hidden md:flex items-center gap-3">
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={generateAnalyticsTemplate}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleExportAnalytics}
                  disabled={loadingAnalytics || !analyticsData || analyticsData.length === 0}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Excel
                </Button>
                <Button 
                  className="bg-gradient-to-r from-orange-500 to-amber-600"
                  onClick={() => setShowAnalyticsUploadModal(true)}
                  disabled={loadingAnalytics}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Excel
                </Button>
              </div>
              
              {/* Mobile: Three-dot menu */}
              <div className="md:hidden analytics-actions-menu-mobile">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAnalyticsMobileMenuOpen(!analyticsMobileMenuOpen)}
                  className="p-2"
                >
                  <MoreVertical className="w-5 h-5" />
                </Button>
                
                {analyticsMobileMenuOpen && (
                  <>
                    {/* Backdrop to close menu */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setAnalyticsMobileMenuOpen(false)}
                    />
                    <div className="analytics-actions-dropdown">
                      <button onClick={() => {
                        generateAnalyticsTemplate();
                        setAnalyticsMobileMenuOpen(false);
                      }}>
                        <Download className="w-4 h-4" />
                        Download Template
                      </button>
                      <button 
                        onClick={() => {
                          handleExportAnalytics();
                          setAnalyticsMobileMenuOpen(false);
                        }}
                        disabled={loadingAnalytics || !analyticsData || analyticsData.length === 0}
                      >
                        <Download className="w-4 h-4" />
                        Export Excel
                      </button>
                      <button onClick={() => {
                        setShowAnalyticsUploadModal(true);
                        setAnalyticsMobileMenuOpen(false);
                      }}>
                        <Upload className="w-4 h-4" />
                        Upload Excel
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Filters Row */}
        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg flex-wrap analytics-filters-mobile">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Date Filter:</label>
            <Select value={analyticsDateFilter} onValueChange={setAnalyticsDateFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select date range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="15days">Last 15 Days</SelectItem>
                <SelectItem value="1month">Last 1 Month</SelectItem>
                <SelectItem value="3months">Last 3 Months</SelectItem>
                <SelectItem value="6months">Last 6 Months</SelectItem>
                <SelectItem value="9months">Last 9 Months</SelectItem>
                <SelectItem value="1year">Last 1 Year</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">OTA Filter:</label>
            <Select value={analyticsOTAFilter} onValueChange={setAnalyticsOTAFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select OTA" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MMT">MMT</SelectItem>
                <SelectItem value="Expedia">Expedia</SelectItem>
                <SelectItem value="Agoda">Agoda</SelectItem>
                <SelectItem value="Booking.com">Booking.com</SelectItem>
                <SelectItem value="Airbnb">Airbnb</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Conditional Content: Show Campaign for Agoda, else show regular analytics */}
        {analyticsOTAFilter === 'Agoda' ? (
          renderAgodaCampaign()
        ) : (
          <>
            {/* Metric Tabs */}
            <div className="flex gap-2 border-b border-gray-200 overflow-x-auto">
              {metricTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setAnalyticsMetricTab(tab.id)}
                  className={`px-4 py-2 font-medium transition-colors border-b-2 whitespace-nowrap ${
                    analyticsMetricTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </div>

            {/* Content */}
            <Card>
          <CardContent className="p-6">
            {loadingAnalytics ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading analytics data...</p>
              </div>
            ) : !hasData ? (
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No Analytics Data</h3>
                <p className="text-gray-600 mb-4">Upload an Excel file to view analytics</p>
                <p className="text-xs text-gray-500">
                  Format: Excel with sheets named after hotels<br />
                  Columns: Week, Room Nights, Revenue, ASP, Property Visits, Conversion
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Chart Controls */}
                <div className="flex justify-between items-center mb-4">
                  {/* Desktop: Show inline buttons */}
                  <div className="hidden md:flex gap-2 analytics-chart-options-desktop">
                    <Button 
                      variant={analyticsViewMode === 'both' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAnalyticsViewMode('both')}
                      className={analyticsViewMode === 'both' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                    >
                      Show Competitor & GRB
                    </Button>
                    <Button 
                      variant={analyticsViewMode === 'competitor' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAnalyticsViewMode('competitor')}
                      className={analyticsViewMode === 'competitor' ? 'bg-blue-500 hover:bg-blue-600' : ''}
                    >
                      Show Competitor Only
                    </Button>
                    <Button 
                      variant={analyticsViewMode === 'grb' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAnalyticsViewMode('grb')}
                      className={analyticsViewMode === 'grb' ? 'bg-green-500 hover:bg-green-600' : ''}
                    >
                      Show GRB Only
                    </Button>
                  </div>
                  <div className="hidden md:flex gap-2">
                    <Button 
                      variant={analyticsChartType === 'line' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAnalyticsChartType('line')}
                      disabled={analyticsViewMode !== 'both'}
                    >
                      Multi Line
                    </Button>
                    <Button 
                      variant={analyticsChartType === 'bar' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAnalyticsChartType('bar')}
                      disabled={analyticsViewMode !== 'both'}
                    >
                      Multi Bar
                    </Button>
                  </div>
                  
                  {/* Mobile: Three-dot menu for chart options */}
                  <div className="md:hidden analytics-chart-options-mobile w-full">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">
                        {analyticsViewMode === 'both' ? 'Competitor & GRB' : analyticsViewMode === 'competitor' ? 'Competitor Only' : 'GRB Only'}
                        {analyticsViewMode === 'both' && ` (${analyticsChartType === 'line' ? 'Line' : 'Bar'})`}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setAnalyticsChartOptionsMenuOpen(!analyticsChartOptionsMenuOpen)}
                        className="p-2"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </Button>
                    </div>
                    
                    {analyticsChartOptionsMenuOpen && (
                      <>
                        <div 
                          className="fixed inset-0 z-40" 
                          onClick={() => setAnalyticsChartOptionsMenuOpen(false)}
                        />
                        <div className="analytics-chart-options-dropdown">
                          <button 
                            className={analyticsViewMode === 'both' ? 'active' : ''}
                            onClick={() => {
                              setAnalyticsViewMode('both');
                              setAnalyticsChartOptionsMenuOpen(false);
                            }}
                          >
                            Show Competitor & GRB
                          </button>
                          <button 
                            className={analyticsViewMode === 'competitor' ? 'active' : ''}
                            onClick={() => {
                              setAnalyticsViewMode('competitor');
                              setAnalyticsChartOptionsMenuOpen(false);
                            }}
                          >
                            Show Competitor Only
                          </button>
                          <button 
                            className={analyticsViewMode === 'grb' ? 'active' : ''}
                            onClick={() => {
                              setAnalyticsViewMode('grb');
                              setAnalyticsChartOptionsMenuOpen(false);
                            }}
                          >
                            Show GRB Only
                          </button>
                          {analyticsViewMode === 'both' && (
                            <>
                              <div className="border-t border-gray-200 my-1"></div>
                              <button 
                                className={analyticsChartType === 'line' ? 'active' : ''}
                                onClick={() => {
                                  setAnalyticsChartType('line');
                                  setAnalyticsChartOptionsMenuOpen(false);
                                }}
                              >
                                Multi Line Chart
                              </button>
                              <button 
                                className={analyticsChartType === 'bar' ? 'active' : ''}
                                onClick={() => {
                                  setAnalyticsChartType('bar');
                                  setAnalyticsChartOptionsMenuOpen(false);
                                }}
                              >
                                Multi Bar Chart
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Navigation Arrows */}
                <div className="flex justify-between items-center mb-4">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setAnalyticsDataPage(Math.max(0, analyticsDataPage - 1))}
                    disabled={analyticsDataPage === 0}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {analyticsDataPage + 1} of {Math.ceil(allAnalyticsData.length / analyticsPageSize) || 1}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setAnalyticsDataPage(Math.min(Math.ceil(allAnalyticsData.length / analyticsPageSize) - 1, analyticsDataPage + 1))}
                    disabled={analyticsDataPage >= Math.ceil(allAnalyticsData.length / analyticsPageSize) - 1}
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>

                {/* Chart */}
                <div style={{ width: '100%', minHeight: '400px' }}>
                  {getFilteredAnalyticsData().length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      {analyticsChartType === 'line' ? (
                        <LineChart data={getFilteredAnalyticsData().map(item => ({
                          week: `${item.week_start || 'N/A'} → ${item.week_end || 'N/A'}`,
                          competitor: item.ota?.[currentMetric.key] || 0,
                          grb: item.grb?.[currentMetric.key] || 0
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="week" 
                            tick={{ fontSize: 11 }}
                            interval={0}
                            height={80}
                          />
                          <YAxis />
                          <Tooltip
                            labelFormatter={(label) => `Week: ${label}`}
                            formatter={(value, name) => [
                              currentMetric.id === 'conversion' 
                                ? `${value}%`
                                : currentMetric.id === 'revenue' || currentMetric.id === 'asp'
                                ? `₹${Number(value).toLocaleString()}`
                                : Number(value).toLocaleString(),
                              name === 'competitor' ? 'Competitor' : 'GRB'
                            ]}
                          />
                          <Legend />
                          {analyticsViewMode !== 'grb' && <Line dataKey="competitor" stroke="#007BFF" strokeWidth={2} name="Competitor" />}
                          {analyticsViewMode !== 'competitor' && <Line dataKey="grb" stroke="#00C853" strokeWidth={2} name="GRB" />}
                        </LineChart>
                      ) : (
                        <BarChart data={getFilteredAnalyticsData().map(item => ({
                          week: `${item.week_start || 'N/A'} → ${item.week_end || 'N/A'}`,
                          competitor: item.ota?.[currentMetric.key] || 0,
                          grb: item.grb?.[currentMetric.key] || 0
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="week" 
                            tick={{ fontSize: 11 }}
                            interval={0}
                            height={80}
                          />
                          <YAxis />
                          <Tooltip
                            labelFormatter={(label) => `Week: ${label}`}
                            formatter={(value, name) => [
                              currentMetric.id === 'conversion' 
                                ? `${value}%`
                                : currentMetric.id === 'revenue' || currentMetric.id === 'asp'
                                ? `₹${Number(value).toLocaleString()}`
                                : Number(value).toLocaleString(),
                              name === 'competitor' ? 'Competitor' : 'GRB'
                            ]}
                          />
                          <Legend />
                          {analyticsViewMode !== 'grb' && <Bar dataKey="competitor" fill="#007BFF" name="Competitor" />}
                          {analyticsViewMode !== 'competitor' && <Bar dataKey="grb" fill="#00C853" name="GRB" />}
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      No data to display for selected filters
                    </div>
                  )}
                </div>

                {/* Data Table */}
                {getFilteredAnalyticsData().length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">Week</th>
                          {analyticsViewMode !== 'grb' && (
                            <th className="px-4 py-2 text-right font-medium text-blue-700">Competitor {currentMetric.name}</th>
                          )}
                          {analyticsViewMode !== 'competitor' && (
                            <th className="px-4 py-2 text-right font-medium text-green-700">GRB {currentMetric.name}</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {getFilteredAnalyticsData().map((row, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2">{`${row.week_start || 'N/A'} → ${row.week_end || 'N/A'}`}</td>
                            {analyticsViewMode !== 'grb' && (
                              <td className="px-4 py-2 text-right font-medium text-blue-600">
                                {row.ota?.[currentMetric.key] !== undefined && row.ota?.[currentMetric.key] !== null ? (
                                  currentMetric.id === 'conversion' 
                                    ? `${row.ota[currentMetric.key]}%`
                                    : currentMetric.id === 'revenue' || currentMetric.id === 'asp'
                                    ? `₹${Number(row.ota[currentMetric.key]).toLocaleString()}`
                                    : Number(row.ota[currentMetric.key]).toLocaleString()
                                ) : 'N/A'}
                              </td>
                            )}
                            {analyticsViewMode !== 'competitor' && (
                              <td className="px-4 py-2 text-right font-medium text-green-600">
                                {row.grb?.[currentMetric.key] !== undefined && row.grb?.[currentMetric.key] !== null ? (
                                  currentMetric.id === 'conversion' 
                                    ? `${row.grb[currentMetric.key]}%`
                                    : currentMetric.id === 'revenue' || currentMetric.id === 'asp'
                                    ? `₹${Number(row.grb[currentMetric.key]).toLocaleString()}`
                                    : Number(row.grb[currentMetric.key]).toLocaleString()
                                ) : 'N/A'}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
          </>
        )}
      </div>
    );
  };

  const renderPromotions = () => {
    // Safety check
    if (!selectedProperty) {
      return (
        <div className="text-center py-12">
          <Tag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No Property Selected</h3>
          <p className="text-gray-600">Please select a property to view promotions</p>
        </div>
      );
    }

    // Safe array check
    const hasPromotions = Array.isArray(promotions) && promotions.length > 0;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Promotions & Campaigns</h3>
          <Button 
            className="bg-gradient-to-r from-orange-500 to-amber-600" 
            onClick={() => setShowCreatePromotionModal(true)}
            data-testid="create-promotion-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Promotion
          </Button>
        </div>
        
        {!hasPromotions ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Tag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Promotions Yet</h3>
              <p className="text-gray-600">Create your first promotion or upload AGX data</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {promotions.map(promotion => (
              <Card 
                key={promotion.id} 
                className={`overflow-hidden ${promotion.type === 'agx' ? 'border-purple-200 bg-purple-50' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800">
                        {promotion.type === 'agx' ? promotion.name : (promotion.promotion_name || 'Untitled')}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {promotion.type === 'agx' 
                          ? promotion.description 
                          : `${promotion.discount_percentage || 0}% OFF`
                        }
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      promotion.type === 'agx'
                        ? 'bg-purple-100 text-purple-800'
                        : promotion.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {promotion.type === 'agx' ? 'AGX' : (promotion.status || 'unknown')}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="w-4 h-4" />
                      <span>{promotion.start_date || 'N/A'} {promotion.type !== 'agx' && `to ${promotion.end_date || 'N/A'}`}</span>
                    </div>
                    
                    {promotion.type !== 'agx' && promotion.applicable_room_types && Array.isArray(promotion.applicable_room_types) && promotion.applicable_room_types.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Hotel className="w-4 h-4" />
                        <span>{promotion.applicable_room_types.length} room type(s)</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    {promotion.type === 'agx' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => {
                          setSelectedAGXForView(promotion.agx_data);
                          setShowAGXDetailModal(true);
                        }}
                      >
                        View Details
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-red-600 hover:bg-red-50"
                          onClick={() => handleDeletePromotion(promotion.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render Dashboard Tab
  const renderDashboard = () => {
    // Phase 2 redesign — when "All Properties" is active on the Dashboard tab,
    // render the new Master Portfolio Dashboard instead of the property-scoped one.
    // Single-property behavior below is untouched.
    if (allPropertiesMode) {
      return (
        <PortfolioDashboard
          onDrillDownToBookings={({ status }) => {
            // Simple drill-down: switch to Bookings tab. Filters remain on Bookings side.
            if (status) {
              try { localStorage.setItem('portfolio_drilldown_status', status); } catch (_) { /* noop */ }
            }
            handleTabChange('bookings');
          }}
        />
      );
    }
    // Calculate property breakdown
    const totalActive = properties.filter(p => (p.status || 'active') === 'active').length;
    const totalTerminated = properties.filter(p => p.status === 'terminated').length;
    const totalProperties = properties.length;

    return (
      <div className="space-y-6">
        {/* Date Range Filter */}
        {selectedProperty && (
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">Dashboard</h2>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Date Range:</label>
              <Select value={dashboardDateRange} onValueChange={setDashboardDateRange}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="7days">7 days</SelectItem>
                  <SelectItem value="15days">15 days</SelectItem>
                  <SelectItem value="30days">30 days</SelectItem>
                  <SelectItem value="60days">60 days</SelectItem>
                  <SelectItem value="90days">90 days</SelectItem>
                  <SelectItem value="6months">6 months</SelectItem>
                  <SelectItem value="1year">1 year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Property Status Card with Hover Breakdown */}
        {user?.role === 'admin' && (
          <div className="relative inline-block">
            <Card 
              className="cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-105 border-2 hover:border-blue-400"
              onMouseEnter={() => setShowPropertyBreakdown(true)}
              onMouseLeave={() => setShowPropertyBreakdown(false)}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <Building2 className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-600 mb-1">Properties</div>
                    <div className="text-3xl font-bold text-gray-900">{totalProperties}</div>
                    <div className="text-xs text-gray-500 mt-1">Total Properties</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Hover Breakdown Tooltip */}
            {showPropertyBreakdown && (
              <div className="absolute top-full left-0 mt-2 w-80 z-50 animate-fadeIn">
                <Card className="border-2 border-blue-200 shadow-xl">
                  <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <CardTitle className="text-sm font-semibold text-gray-800">Property Status Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      {/* Active Properties */}
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          <span className="text-sm font-medium text-gray-700">Active Properties</span>
                        </div>
                        <span className="text-lg font-bold text-green-700">{totalActive}</span>
                      </div>

                      {/* Inactive Properties */}
                      <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                          <span className="text-sm font-medium text-gray-700">Inactive Properties</span>
                        </div>
                        <span className="text-lg font-bold text-orange-700">{totalProperties - totalActive - totalTerminated}</span>
                      </div>

                      {/* Terminated Properties */}
                      <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                          <span className="text-sm font-medium text-gray-700">Terminated Properties</span>
                        </div>
                        <span className="text-lg font-bold text-red-700">{totalTerminated}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* KPI Cards from Bookings (uses grouped/deduplicated metrics) */}
        {dashboardKPIs && (
          <div>
            <div className="dashboard-grid grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <Card data-testid="dashboard-kpi-total-bookings">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-indigo-600">
                    {(dashboardKPIs.total_bookings || 0).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="dashboard-kpi-net-revenue">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Net Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    ₹{(dashboardKPIs.net_revenue || 0).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="dashboard-kpi-avg-adr">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Avg ADR</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">
                    ₹{(dashboardKPIs.avg_adr || 0).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="dashboard-kpi-avg-los">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Avg Length of Stay</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-purple-600">
                    {dashboardKPIs.avg_length_of_stay} nights
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="dashboard-kpi-avg-booking-window">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Avg Booking Window</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-orange-600">
                    {dashboardKPIs.avg_booking_window} days
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="dashboard-kpi-cancellation-rate">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Cancellation Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">
                    {dashboardKPIs.cancellation_rate}%
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Show Total Properties card only for non-admins */}
        {user?.role !== 'admin' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-gray-600">Total Properties</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">{properties.length}</div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  };

  // Render Payments Tab
  // Render Payments Tab
  const calculateAmountPending = (payment) => {
    const totalPaid = payment.payments.reduce((sum, p) => sum + p.amount, 0);
    return payment.totalAmount - totalPaid;
  };

  const handleAddPayment = () => {
    setPaymentFormData({
      hotelName: '',
      totalAmount: '',
      amount: '',
      date: '',
      bookingId: ''
    });
    setShowAddPayment(true);
  };

  const handleEditPaymentRow = (payment) => {
    setSelectedPayment(payment);
    setPaymentFormData({
      hotelName: payment.hotelName,
      totalAmount: payment.totalAmount,
      bookingId: payment.bookingId
    });
    setShowEditPayment(true);
  };

  const handleDeletePayment = (paymentId) => {
    if (window.confirm('Are you sure you want to delete this payment record?')) {
      setPaymentsData(paymentsData.filter(p => p.id !== paymentId));
      toast.success('Payment deleted successfully');
    }
  };

  const handleSavePayment = () => {
    if (showEditPayment && selectedPayment) {
      // Update existing
      setPaymentsData(paymentsData.map(p => 
        p.id === selectedPayment.id 
          ? { ...p, hotelName: paymentFormData.hotelName, totalAmount: parseFloat(paymentFormData.totalAmount), bookingId: paymentFormData.bookingId }
          : p
      ));
      toast.success('Payment updated successfully');
    } else {
      // Add new
      const newPayment = {
        id: `PAY${Date.now()}`,
        hotelName: paymentFormData.hotelName,
        hotelId: `HOTEL${Date.now()}`,
        totalAmount: parseFloat(paymentFormData.totalAmount),
        bookingId: paymentFormData.bookingId,
        payments: paymentFormData.amount && paymentFormData.date ? [
          { amount: parseFloat(paymentFormData.amount), date: paymentFormData.date, invoiceUrl: '#' }
        ] : []
      };
      setPaymentsData([...paymentsData, newPayment]);
      toast.success('Payment added successfully');
    }
    setShowAddPayment(false);
    setShowEditPayment(false);
    setSelectedPayment(null);
  };

  const handleDownloadInvoice = (payment, paymentDetail) => {
    try {
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.setTextColor(249, 115, 22); // Orange color
      doc.text('GRB INVOICE', 105, 20, { align: 'center' });
      
      // Invoice Details
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Invoice ID: INV-${Date.now()}`, 20, 40);
      doc.text(`Date: ${new Date(paymentDetail.date).toLocaleDateString()}`, 20, 50);
      
      // Property Details
      doc.setFontSize(14);
      doc.text('Property Details:', 20, 70);
      doc.setFontSize(11);
      doc.text(`Hotel Name: ${payment.hotelName}`, 20, 80);
      doc.text(`Booking ID: ${payment.bookingId}`, 20, 90);
      
      // Payment Details
      doc.setFontSize(14);
      doc.text('Payment Details:', 20, 110);
      doc.setFontSize(11);
      doc.text(`Amount Paid: ₹${paymentDetail.amount.toLocaleString()}`, 20, 120);
      doc.text(`Payment Date: ${new Date(paymentDetail.date).toLocaleDateString()}`, 20, 130);
      doc.text(`Total Amount: ₹${payment.totalAmount.toLocaleString()}`, 20, 140);
      
      // Footer
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Thank you for your payment!', 105, 270, { align: 'center' });
      doc.text('GRB Hotel Management', 105, 280, { align: 'center' });
      
      // Save PDF
      doc.save(`Invoice_${payment.hotelName}_${paymentDetail.date}.pdf`);
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate invoice');
    }
  };

  const handleDownloadOverallPDF = (payment) => {
    try {
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.setTextColor(249, 115, 22);
      doc.text('OVERALL PAYMENT REPORT', 105, 20, { align: 'center' });
      
      // Property Details
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text('Property Details:', 20, 40);
      doc.setFontSize(11);
      doc.text(`Hotel Name: ${payment.hotelName}`, 20, 50);
      doc.text(`Booking ID: ${payment.bookingId}`, 20, 60);
      doc.text(`Total Amount: ₹${payment.totalAmount.toLocaleString()}`, 20, 70);
      
      // Payment History
      doc.setFontSize(14);
      doc.text('Payment History:', 20, 90);
      
      let yPos = 100;
      payment.payments.forEach((p, idx) => {
        doc.setFontSize(11);
        doc.text(`Payment ${idx + 1}:`, 20, yPos);
        doc.text(`Amount: ₹${p.amount.toLocaleString()}`, 40, yPos + 10);
        doc.text(`Date: ${new Date(p.date).toLocaleDateString()}`, 40, yPos + 20);
        yPos += 35;
      });
      
      // Summary
      const totalPaid = payment.payments.reduce((sum, p) => sum + p.amount, 0);
      doc.setFontSize(12);
      doc.text(`Total Paid: ₹${totalPaid.toLocaleString()}`, 20, yPos + 10);
      doc.text(`Status: FULLY PAID ✓`, 20, yPos + 20);
      
      // Footer
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('All payments received. Thank you!', 105, 270, { align: 'center' });
      doc.text('GRB Hotel Management', 105, 280, { align: 'center' });
      
      doc.save(`Overall_Payment_${payment.hotelName}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Overall payment PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const togglePaymentExpand = (paymentId) => {
    setExpandedPayments(prev => ({
      ...prev,
      [paymentId]: !prev[paymentId]
    }));
  };

  const handleAddAccounting = () => {
    setSelectedAccounting(null);
    setShowAddAccounting(true);
  };

  const handleEditAccounting = (entry) => {
    setSelectedAccounting(entry);
    setShowEditAccounting(true);
  };

  const handleDeleteAccounting = (entryId) => {
    if (window.confirm('Are you sure you want to delete this accounting entry?')) {
      setAccountingData(accountingData.filter(e => e.id !== entryId));
      toast.success('Accounting entry deleted successfully');
    }
  };

  const handleSaveAccounting = (entryData) => {
    const calculatedEntry = {
      ...entryData,
      to_be_paid_to_hotel: (entryData.pay_to_hotel || 0) - (entryData.payment_received || 0)
    };

    if (selectedAccounting) {
      // Update
      setAccountingData(accountingData.map(e => 
        e.id === selectedAccounting.id ? { ...calculatedEntry, id: e.id } : e
      ));
      toast.success('Accounting entry updated successfully');
    } else {
      // Add new
      setAccountingData([...accountingData, { ...calculatedEntry, id: `ACC${Date.now()}` }]);
      toast.success('Accounting entry added successfully');
    }
    setShowAddAccounting(false);
    setShowEditAccounting(false);
    setSelectedAccounting(null);
  };

  const exportAccountingCSV = () => {
    const headers = Object.keys(accountingData[0] || {}).join(',');
    const rows = accountingData.map(entry => Object.values(entry).join(','));
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'accounting_data.csv';
    a.click();
    toast.success('CSV exported successfully');
  };

  const filteredAccountingData = accountingData.filter(entry => {
    if (accountingFilters.hotel && !entry.hotel_name.toLowerCase().includes(accountingFilters.hotel.toLowerCase())) return false;
    if (accountingFilters.city && !entry.city.toLowerCase().includes(accountingFilters.city.toLowerCase())) return false;
    if (accountingFilters.dateFrom && entry.booking_date < accountingFilters.dateFrom) return false;
    if (accountingFilters.dateTo && entry.booking_date > accountingFilters.dateTo) return false;
    return true;
  });

  const renderPayments = () => {
    // Get unique hotel names from payments data
    const hotelOptions = [...new Set(paymentsData.map(p => p.hotelName))];
    
    // Filter payments by selected hotel
    const filteredPayments = selectedPaymentHotel 
      ? paymentsData.filter(p => p.hotelName === selectedPaymentHotel)
      : [];

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Payments Management</h3>
            <p className="text-sm text-gray-600">Track payments, invoices, and pending amounts</p>
          </div>
          <Button 
            onClick={handleAddPayment}
            className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Payment
          </Button>
        </div>

        {/* Hotel Dropdown Filter */}
        <Card>
          <CardContent className="pt-6">
            <div className="max-w-sm">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Hotel</label>
              <Select value={selectedPaymentHotel} onValueChange={setSelectedPaymentHotel}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a hotel to view payments" />
                </SelectTrigger>
                <SelectContent>
                  {hotelOptions.map(hotel => (
                    <SelectItem key={hotel} value={hotel}>{hotel}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Display message if no hotel selected */}
        {!selectedPaymentHotel && (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <DollarSign className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium">Select a hotel to view payments</p>
            </CardContent>
          </Card>
        )}

        {/* Payment Data Display */}
        {selectedPaymentHotel && (
          <div className="space-y-4">
            {filteredPayments.map(payment => {
              const amountPending = calculateAmountPending(payment);
              const isExpanded = expandedPayments[payment.id];
              const isFullyPaid = amountPending === 0;

              return (
                <Card key={payment.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <button
                          onClick={() => togglePaymentExpand(payment.id)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-800">{payment.hotelName}</h4>
                          <p className="text-sm text-gray-600">Booking ID: {payment.bookingId}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Total Amount</div>
                          <div className="font-semibold text-lg">₹{payment.totalAmount.toLocaleString()}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Amount Pending</div>
                          <div className={`font-semibold text-lg ${isFullyPaid ? 'text-green-600' : 'text-red-600'}`}>
                            ₹{amountPending.toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditPaymentRow(payment)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeletePayment(payment.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {isExpanded && payment.payments.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-2 px-3 font-medium text-gray-700">Amount Paid</th>
                                {payment.payments.map((p, idx) => (
                                  <th key={idx} className="text-center py-2 px-3 font-semibold text-gray-800">₹{p.amount.toLocaleString()}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b">
                                <td className="py-2 px-3 text-gray-700">Date</td>
                                {payment.payments.map((p, idx) => (
                                  <td key={idx} className="text-center py-2 px-3 text-gray-600">{new Date(p.date).toLocaleDateString()}</td>
                                ))}
                              </tr>
                              <tr>
                                <td className="py-2 px-3 text-gray-700">Invoice</td>
                                {payment.payments.map((p, idx) => (
                                  <td key={idx} className="text-center py-2 px-3">
                                    <button
                                      onClick={() => handleDownloadInvoice(payment, p)}
                                      className="text-orange-600 hover:text-orange-700 underline flex items-center justify-center gap-1"
                                    >
                                      <Download className="w-4 h-4" />
                                      Download PDF
                                    </button>
                                  </td>
                                ))}
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Overall PDF Download Button - Only if fully paid */}
                        {isFullyPaid && (
                          <div className="mt-4 pt-4 border-t">
                            <Button
                              onClick={() => handleDownloadOverallPDF(payment)}
                              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download Overall Payment PDF
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Add/Edit Payment Modal */}
        <Dialog open={showAddPayment || showEditPayment} onOpenChange={(open) => {
          setShowAddPayment(open && showAddPayment);
          setShowEditPayment(open && showEditPayment);
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{showEditPayment ? 'Edit Payment' : 'Add New Payment'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Name *</label>
                <Input
                  value={paymentFormData.hotelName}
                  onChange={(e) => setPaymentFormData({...paymentFormData, hotelName: e.target.value})}
                  placeholder="Grand Palace Hotel"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount *</label>
                <Input
                  type="number"
                  value={paymentFormData.totalAmount}
                  onChange={(e) => setPaymentFormData({...paymentFormData, totalAmount: e.target.value})}
                  placeholder="5000"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Booking ID *</label>
                <Input
                  value={paymentFormData.bookingId}
                  onChange={(e) => setPaymentFormData({...paymentFormData, bookingId: e.target.value})}
                  placeholder="BKG001"
                  required
                />
              </div>
              {!showEditPayment && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Initial Payment Amount</label>
                    <Input
                      type="number"
                      value={paymentFormData.amount}
                      onChange={(e) => setPaymentFormData({...paymentFormData, amount: e.target.value})}
                      placeholder="2000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                    <Input
                      type="date"
                      value={paymentFormData.date}
                      onChange={(e) => setPaymentFormData({...paymentFormData, date: e.target.value})}
                    />
                  </div>
                </>
              )}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddPayment(false);
                    setShowEditPayment(false);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePayment}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600"
                >
                  Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // Render Accounting Tab
  const renderAccounting = () => {
    // Get unique hotel names from accounting data
    const hotelOptions = [...new Set(accountingData.map(e => e.hotel_name))];
    
    // Filter by selected hotel
    const displayData = selectedAccountingHotel
      ? accountingData.filter(e => e.hotel_name === selectedAccountingHotel)
      : [];

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Accounting Management</h3>
            <p className="text-sm text-gray-600">Financial records and accounting entries</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={exportAccountingCSV}
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={handleAddAccounting}
              className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
          </div>
        </div>

        {/* Hotel Dropdown Filter */}
        <Card>
          <CardContent className="pt-6">
            <div className="max-w-sm">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Hotel</label>
              <Select value={selectedAccountingHotel} onValueChange={setSelectedAccountingHotel}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a hotel to view accounting records" />
                </SelectTrigger>
                <SelectContent>
                  {hotelOptions.map(hotel => (
                    <SelectItem key={hotel} value={hotel}>{hotel}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Display message if no hotel selected */}
        {!selectedAccountingHotel && (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium">Select a hotel to view accounting records</p>
            </CardContent>
          </Card>
        )}

        {/* Accounting Table */}
        {selectedAccountingHotel && displayData.length > 0 && (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[150px]">Hotel Name</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>OTA</TableHead>
                      <TableHead>Guest Name</TableHead>
                      <TableHead>Booking Date</TableHead>
                      <TableHead>Check-In</TableHead>
                      <TableHead>Check-Out</TableHead>
                      <TableHead className="text-right">Sell Rate</TableHead>
                      <TableHead className="text-right">Pay to Hotel</TableHead>
                      <TableHead className="text-right">Payment Received</TableHead>
                      <TableHead className="text-right">Discrepancy</TableHead>
                      <TableHead>Booking ID</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayData.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="font-medium">{entry.hotel_name}</TableCell>
                        <TableCell>{entry.city}</TableCell>
                        <TableCell>{entry.state || '-'}</TableCell>
                        <TableCell>{entry.ota || '-'}</TableCell>
                        <TableCell>{entry.guest_name}</TableCell>
                        <TableCell>{new Date(entry.booking_date).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(entry.check_in_date).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(entry.check_out_date).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">₹{entry.sell_rate.toLocaleString()}</TableCell>
                        <TableCell className="text-right">₹{entry.pay_to_hotel.toLocaleString()}</TableCell>
                        <TableCell className="text-right">₹{entry.payment_received.toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <span className={entry.to_be_paid_to_hotel > 0 ? 'text-red-600 font-semibold' : 'text-green-600'}>
                            ₹{entry.to_be_paid_to_hotel.toLocaleString()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{entry.booking_id}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditAccounting(entry)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteAccounting(entry.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Accounting Modal */}
        <Dialog open={showAddAccounting || showEditAccounting} onOpenChange={(open) => {
          setShowAddAccounting(open && showAddAccounting);
          setShowEditAccounting(open && showEditAccounting);
        }}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{showEditAccounting ? 'Edit Accounting Entry' : 'Add Accounting Entry'}</DialogTitle>
            </DialogHeader>
            <AccountingForm
              entry={selectedAccounting}
              onSave={handleSaveAccounting}
              onCancel={() => {
                setShowAddAccounting(false);
                setShowEditAccounting(false);
                setSelectedAccounting(null);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // Logging Function
  const addLog = (logData) => {
    const newLog = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      user: user?.full_name || user?.username || 'System',
      userType: user?.role || user?.user_type || 'System',
      ...logData
    };
    setLogs(prevLogs => [newLog, ...prevLogs]);
  };

  // Hotel Owner Management Functions
  const fetchHotelOwners = async () => {
    if (user?.role !== 'admin') return;
    
    try {
      const response = await axios.get(`${API}/hotel-owners`);
      setHotelOwners(response.data);
    } catch (error) {
      console.error('Failed to fetch hotel owners:', error);
    }
  };

  const handleCreateHotelOwner = async (e) => {
    e.preventDefault();
    
    if (ownerFormData.password !== ownerFormData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (!ownerFormData.email || !ownerFormData.password || !ownerFormData.hotelName) {
      toast.error('Please fill all required fields');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(ownerFormData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    try {
      await axios.post(`${API}/hotel-owners`, {
        email: ownerFormData.email,
        password: ownerFormData.password,
        confirm_password: ownerFormData.confirmPassword,
        hotel_name: ownerFormData.hotelName,
        hotel_id: ownerFormData.hotelId
      });
      
      toast.success('Hotel Owner login created successfully', {
        description: `Email: ${ownerFormData.email} - Share these credentials with the hotel owner.`,
        duration: 5000
      });
      setShowCreateOwnerModal(false);
      setOwnerFormData({
        hotelName: '',
        hotelId: '',
        email: '',
        password: '',
        confirmPassword: ''
      });
      
      // Add log entry
      addLog({
        actionType: 'Hotel Owner Created',
        propertyName: ownerFormData.hotelName,
        details: `Hotel owner account created for ${ownerFormData.email}`
      });
      
      fetchHotelOwners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create hotel owner account');
    }
  };

  // Rate Cascade Logic with Settings
  const applyRateCascade = async (checkinDate, checkoutDate, baseRate) => {
    if (!calendarData || !calendarData.room_types || calendarData.room_types.length === 0) {
      toast.error('No room types available');
      return;
    }

    try {
      const startDate = new Date(checkinDate);
      const endDate = new Date(checkoutDate);
      const roomTypes = calendarData.room_types;
      const updatedCells = new Set();

      // 1) Collect the cells to animate. The availability base-rate mirror is
      //    NO LONGER computed from the old index×increment formula here — it is
      //    rebuilt below from the cascade's OWN per-room EP (the source of
      //    truth), so the follow-up PUT can never overwrite the cascade with a
      //    stale frontend calculation (Fix 3).
      const availabilityUpdates = [];
      for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        const dateStr = date.toISOString().split('T')[0];
        roomTypes.forEach((roomTypeData) => {
          updatedCells.add(`${roomTypeData.room_type.id}_${dateStr}`);
        });
      }

      // Animate updated cells
      setAnimatedCells(updatedCells);
      setTimeout(() => setAnimatedCells(new Set()), 2000);

      // 2) SOURCE OF TRUTH — await the bulk cascade first. This writes the full
      //    (room × plan × occupancy × date) matrix into rate_plan_rates using the
      //    property's Room Type Hierarchy + Global Rate Plan Hierarchy + per-room configs.
      //    It also emits ONE consolidated MMT push AND per-plan Agoda pushes carrying
      //    the full matrix, filtered by the active OTA Mapping from Property Settings
      //    (unmapped room types are silently skipped by the backend).
      let cascadeApplied = 0;
      let agodaPushed = 0;
      let agodaSkipped = 0;
      let mmtPushed = 0;
      let mmtSkipped = 0;
      let firstAgodaRoomTypeId = null;
      try {
        const apiResp = await axios.post(`${API}/rate-plans/apply-base-rate`, {
          property_id: selectedProperty.id,
          start_date: checkinDate,
          end_date: checkoutDate,
          base_rate: parseFloat(baseRate),
        }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        cascadeApplied = apiResp?.data?.updates_applied || 0;
        handleMmtSyncResult(apiResp?.data?.mmt_sync);

        // Track how many rooms actually reached each OTA vs. how many were
        // skipped due to a missing OTA mapping in Property Settings.
        const summaryRows = apiResp?.data?.summary || [];
        // Fix 3 — rebuild the availability base-rate mirror from the cascade's
        // OWN per-room EP (room_ep from apply-base-rate), NOT the old frontend
        // index×increment formula. This makes the follow-up PUT write the exact
        // cascaded value instead of overwriting it with a stale recalculation.
        const epByRoom = {};
        summaryRows.forEach(r => {
          if (r && r.room_type_id != null && r.room_ep != null) epByRoom[r.room_type_id] = r.room_ep;
        });
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          const ds = d.toISOString().split('T')[0];
          roomTypes.forEach((rtd) => {
            const rid = rtd.room_type.id;
            if (epByRoom[rid] != null) {
              availabilityUpdates.push({ room_type_id: rid, date: ds, rate: epByRoom[rid] });
            }
          });
        }
        const mmtRts = new Set((apiResp?.data?.mmt_sync || []).map(r => r.room_type_id).filter(Boolean));
        const agodaSyncRows = apiResp?.data?.agoda_sync || [];
        const agodaRts = new Set(agodaSyncRows.map(r => r.room_type_id).filter(Boolean));
        // Grab the first successful Agoda push so the payload viewer can
        // hydrate with a real recently-pushed room. Falls back to any
        // Agoda row so the viewer still refreshes on partial failures.
        const firstAgodaSuccess = agodaSyncRows.find(r => r.status === 'success') || agodaSyncRows[0];
        firstAgodaRoomTypeId = firstAgodaSuccess?.room_type_id || null;
        // Expose the FULL batch to the Agoda Payload viewer so the operator
        // can inspect every per-room request/response from this cascade,
        // not just the last one. Uses `agoda_sync` from apply-base-rate.
        if (agodaSyncRows.length > 0) {
          setAgodaBatchPayloads(agodaSyncRows);
          setAgodaLastPayload(firstAgodaSuccess || agodaSyncRows[0]);
        }
        summaryRows.forEach(row => {
          const hasRatePlans = (row.rate_plans || []).length > 0;
          if (!hasRatePlans) return;
          if (mmtRts.has(row.room_type_id)) mmtPushed += 1; else mmtSkipped += 1;
          if (agodaRts.has(row.room_type_id)) agodaPushed += 1; else agodaSkipped += 1;
        });
      } catch (rpErr) {
        // Non-fatal: legacy availability update below still runs. Surface the
        // failure to the operator instead of swallowing it, so any future
        // error (e.g. an OTA push failure) is visible.
        const msg = rpErr?.response?.data?.detail || rpErr?.message || 'Unknown error';
        console.warn('Rate plan auto-derivation skipped:', msg);
        toast.error(`Rate cascade failed: ${msg}`);
      }

      // 3) After the cascade, sync per-room availability rates in ONE awaited
      //    batched PUT (replaces the previous un-awaited forEach fan-out that
      //    raced with — and overwrote — the cascade result).
      //    `skip_mmt_push=true` tells the backend to skip the per-row
      //    `_push_mmt_ari_for_change` push — the cascade above has already
      //    emitted the full-matrix consolidated MMT push (mmt_sync in the
      //    apply-base-rate response) using `_push_mmt_ari_consolidated`.
      if (availabilityUpdates.length > 0) {
        try {
          const availResp = await axios.put(
            `${API}/availability?skip_mmt_push=true`,
            availabilityUpdates,
          );
          handleMmtSyncResult(availResp?.data?.mmt_sync);
        } catch (availErr) {
          toast.error('Failed to update availability');
        }
      }

      // 4) Refetch BOTH grids exactly once at the very end — after all writes finish.
      await Promise.all([fetchCalendarData(), fetchRatePlanCalendar()]);

      // Refresh the Agoda Payload viewer with the room + check-in just
      // pushed, mirroring what Deviation Mode does after its push. Uses
      // agoda_sync (from apply-base-rate), NOT agoda_sync_all.
      // The batch itself was already captured above so the viewer's
      // per-room selector renders every push in this cascade. This
      // single-record fetch just hydrates any richer meta/timestamps
      // from the audit collection for the currently-selected row.
      try {
        fetchAgodaLastPayload(firstAgodaRoomTypeId, checkinDate, { preserveBatch: true });
      } catch (_e) { /* non-fatal — panel refresh only */ }

      if (cascadeApplied > 0) {
        toast.success(`Auto-derived ${cascadeApplied} rate-plan rates from base ₹${baseRate}`);
      }
      // Surface per-OTA push counts so operators can see at a glance
      // how many room types actually reached each channel vs. how many
      // were bypassed due to a missing OTA Mapping.
      if (mmtPushed || agodaPushed || mmtSkipped || agodaSkipped) {
        const parts = [];
        parts.push(`MMT: ${mmtPushed} pushed${mmtSkipped ? ` · ${mmtSkipped} skipped (no mapping)` : ''}`);
        parts.push(`Agoda: ${agodaPushed} pushed${agodaSkipped ? ` · ${agodaSkipped} skipped (no mapping)` : ''}`);
        toast.message(parts.join('  ·  '));
      }

      // Add log entry
      addLog({
        actionType: 'Rate Updated',
        propertyName: selectedProperty?.name || 'Unknown',
        roomTypeName: 'All Room Types',
        checkinDate,
        checkoutDate,
        newRate: baseRate,
        details: `Base rate updated to ₹${baseRate} with ${settings.rateType === 'percentage' ? settings.inclusionRate + '%' : '₹' + settings.inclusionRate} inclusion rate and ${settings.rateType === 'percentage' ? settings.roomTypeIncrement + '%' : '₹' + settings.roomTypeIncrement} room type increment`
      });

      toast.success(`Rates updated for ${roomTypes.length} room types across selected dates`);
      setShowRatesPopup(false);
      setRatesFormData({ checkinDate: '', checkoutDate: '', baseRate: '' });
      
    } catch (error) {
      toast.error('Failed to update rates');
      console.error(error);
    }
  };

  // Fetch the property's Agoda-mapped rooms + their rate plans when
  // Deviation Mode is picked. Cheap read; skipped otherwise.
  useEffect(() => {
    if (!showRatesPopup) return;
    if (ratesFormData.updateMode !== 'deviation') return;
    if (!selectedProperty?.id) return;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/rate-plans/agoda-mapped-rooms/${selectedProperty.id}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
        );
        setAgodaDeviationRooms(res.data?.rooms || []);
      } catch (err) {
        console.warn('agoda-mapped-rooms fetch failed', err?.response?.data);
        setAgodaDeviationRooms([]);
      }
    })();
    // eslint-disable-next-line
  }, [showRatesPopup, ratesFormData.updateMode, selectedProperty?.id]);

  const handleRatesSubmit = async (e) => {
    e.preventDefault();
    
    if (!ratesFormData.checkinDate || !ratesFormData.checkoutDate) {
      toast.error('Please choose check-in and check-out dates');
      return;
    }
    if (new Date(ratesFormData.checkoutDate) < new Date(ratesFormData.checkinDate)) {
      toast.error('Check-out date must be after check-in date');
      return;
    }
    
    if (ratesFormData.updateMode === 'deviation') {
      const basePrice = parseFloat(ratesFormData.basePrice);
      if (isNaN(basePrice) || basePrice <= 0) {
        toast.error('Enter a positive base price');
        return;
      }
      const roomTypeId = ratesFormData.deviationRoomId;
      const ratePlanId = ratesFormData.deviationRatePlanId;
      if (!roomTypeId) {
        toast.error('Pick a room type');
        return;
      }
      if (!ratePlanId) {
        toast.error('Pick a rate plan');
        return;
      }
      const raw = ratesFormData.occupancyPercentages || {};
      const occupancy_percentages = Object.entries(raw)
        .map(([person, pct]) => [parseInt(person, 10), parseFloat(pct)])
        .filter(([person, pct]) => Number.isFinite(person) && Number.isFinite(pct))
        .map(([person, pct]) => ({ person, percentage: pct }));
      if (occupancy_percentages.length === 0) {
        toast.error('Enter at least one occupancy percentage');
        return;
      }
      try {
        const resp = await axios.post(
          `${API}/rate-plans/apply-rate-deviation`,
          {
            property_id: selectedProperty.id,
            room_type_id: roomTypeId,
            rate_plan_id: ratePlanId,
            start_date: ratesFormData.checkinDate,
            end_date: ratesFormData.checkoutDate,
            base_price: basePrice,
            occupancy_percentages,
          },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            validateStatus: (s) => s === 200 || s === 207,
          },
        );
        const rows = resp.data?.agoda_sync || [];
        const failed = resp.data?.agoda_push_failed || 0;
        if (failed > 0) {
          toast.error(`Deviation push: ${failed} Agoda failure${failed === 1 ? '' : 's'} — see console`);
          rows.filter(r => r.status !== 'success').forEach(r =>
            console.warn('[deviation] failure', r));
        } else {
          toast.success(`Deviation applied — Agoda: ${rows.length} push${rows.length === 1 ? '' : 'es'}`);
        }
        // Refresh the Agoda Payload panel with the room + check-in just pushed,
        // mirroring the absolute-price cascade flow.
        try {
          fetchAgodaLastPayload(roomTypeId, ratesFormData.checkinDate);
        } catch (_e) { /* non-fatal — panel refresh only */ }
        setShowRatesPopup(false);
        setRatesFormData({
          checkinDate: '', checkoutDate: '', baseRate: '',
          basePrice: '', occupancyPercentages: {},
          deviationRoomId: '', deviationRatePlanId: '',
        });
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message;
        toast.error(`Deviation push failed: ${detail}`);
      }
      return;
    }

    if (ratesFormData.updateMode === 'room_types') {
      // Feb 2026 (rev.5) — richer per-plan flow. Fan every filled room
      // type out to /availability/bulk with its per-plan rates
      // (occ_1..occ_max all equal), extra-guest prices, and
      // restrictions. Batch every Agoda push row into the payload
      // viewer so ops can inspect every request/response.
      const rtIds = Object.keys(ratesFormData.rtDetails || {});
      // Also honour the legacy `perRoomRates` shape so keyboard entries
      // from before the accordion still submit.
      Object.keys(ratesFormData.perRoomRates || {}).forEach(id => {
        if (!rtIds.includes(id)) rtIds.push(id);
      });
      const roomTypeIdToRoom = {};
      (ratePlanCalendar?.room_types || []).forEach(rt => {
        roomTypeIdToRoom[rt.room_type_id] = rt;
      });
      const token = localStorage.getItem('token');
      const submissions = [];
      for (const rtId of rtIds) {
        const rt = roomTypeIdToRoom[rtId];
        if (!rt) continue;
        const bucket = (ratesFormData.rtDetails || {})[rtId] || {};
        const rpMap = { ...(bucket.ratePlans || {}) };
        // Bridge legacy perRoomRates → apply to the FIRST rate plan.
        const legacyRate = (ratesFormData.perRoomRates || {})[rtId];
        if (legacyRate && !Object.keys(rpMap).length && (rt.rate_plans || [])[0]) {
          rpMap[rt.rate_plans[0].id] = legacyRate;
        }
        const rate_plans = [];
        const maxOcc = Math.max(1, parseInt(rt.max_occupancy, 10) || 2);
        Object.entries(rpMap).forEach(([rpId, val]) => {
          const num = parseFloat(val);
          if (!rpId || isNaN(num) || num <= 0) return;
          const occupancy_rates = {};
          for (let p = 1; p <= maxOcc; p += 1) occupancy_rates[p] = num;
          rate_plans.push({ rate_plan_id: rpId, occupancy_rates });
        });

        const extras = bucket.extras || {};
        const _numOrNull = (v) => {
          const n = parseFloat(v);
          return Number.isFinite(n) && n >= 0 ? n : null;
        };
        const extra_adult_rate      = _numOrNull(extras.adult);
        const extra_child_rate_age1 = _numOrNull(extras.child1);
        const extra_child_rate_age2 = _numOrNull(extras.child2);
        const extra_child_rate_age3 = _numOrNull(extras.child3);

        const restrictionsSrc = bucket.restrictions || {};
        const rate_plan_restrictions = [];
        Object.entries(restrictionsSrc).forEach(([rpId, r]) => {
          if (!rpId || !r) return;
          const entry = { rate_plan_id: rpId };
          if (r.closed === true || r.closed === false) entry.closed = r.closed;
          if (r.closed_to_arrival === true || r.closed_to_arrival === false)
            entry.closed_to_arrival = r.closed_to_arrival;
          if (r.closed_to_departure === true || r.closed_to_departure === false)
            entry.closed_to_departure = r.closed_to_departure;
          const _n = (v) => {
            if (v === '' || v == null) return undefined;
            const n = parseInt(v, 10);
            return Number.isFinite(n) && n > 0 ? n : undefined;
          };
          // Full field set matching the bulk drawer.
          const numFields = [
            'min_los_stay_based', 'max_los_stay_based',
            'min_los_arrival_based', 'max_los_arrival_based',
            'min_advance_booking_offset', 'max_advance_booking_offset',
            // Legacy shorthand still accepted by the backend.
            'min_los', 'max_los',
          ];
          for (const k of numFields) {
            const n = _n(r[k]);
            if (n !== undefined) entry[k] = n;
          }
          // Keep only entries that actually carry a change.
          if (Object.keys(entry).length > 1) rate_plan_restrictions.push(entry);
        });

        const hasAnything =
          rate_plans.length > 0 ||
          rate_plan_restrictions.length > 0 ||
          [extra_adult_rate, extra_child_rate_age1,
           extra_child_rate_age2, extra_child_rate_age3].some(v => v !== null);
        if (!hasAnything) continue;

        submissions.push({
          rtId,
          rtName: rt.room_type_name,
          payload: {
            action: 'rates',
            property_id: selectedProperty.id,
            date_ranges: [{ from_date: ratesFormData.checkinDate, to_date: ratesFormData.checkoutDate }],
            days_of_week: { sun:true, mon:true, tue:true, wed:true, thu:true, fri:true, sat:true },
            room_type_ids: [rtId],
            rate_plans,
            extra_adult_rate,
            extra_child_rate_age1,
            extra_child_rate_age2,
            extra_child_rate_age3,
            rate_plan_restrictions: rate_plan_restrictions.length ? rate_plan_restrictions : undefined,
          },
        });
      }

      if (submissions.length === 0) {
        toast.error('Enter at least one rate, extra, or restriction on an expanded room type');
        return;
      }

      const aggregatedAgoda = [];
      let totalUpdates = 0;
      let failures = 0;
      for (const { rtName, payload } of submissions) {
        try {
          const resp = await axios.post(`${API}/availability/bulk`, payload, {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: (s) => s === 200 || s === 207,
          });
          totalUpdates += resp.data?.updates_applied || 0;
          // Bulk returns `agoda_sync_all` (full array) AND `agoda_sync`
          // (the LAST row only). Prefer the full array so every per-plan
          // push lands in the batch viewer. Fall back to wrapping the
          // single-row shape when only that is present.
          const rowsRaw = resp.data?.agoda_sync_all
            ?? (resp.data?.agoda_sync
                  ? (Array.isArray(resp.data.agoda_sync)
                      ? resp.data.agoda_sync
                      : [resp.data.agoda_sync])
                  : []);
          if (Array.isArray(rowsRaw) && rowsRaw.length) aggregatedAgoda.push(...rowsRaw);
          if ((resp.data?.agoda_push_failed || 0) > 0) failures += resp.data.agoda_push_failed;
        } catch (err) {
          failures += 1;
          console.error('room_types bulk apply failed for', rtName, err?.response?.data);
        }
      }

      if (aggregatedAgoda.length > 0) {
        setAgodaBatchPayloads(aggregatedAgoda);
        const firstOk = aggregatedAgoda.find(r => r.status === 'success') || aggregatedAgoda[0];
        setAgodaLastPayload(firstOk);
      }

      if (totalUpdates > 0) {
        toast.success(`Applied ${totalUpdates} rate row${totalUpdates === 1 ? '' : 's'} across ${submissions.length} room type${submissions.length === 1 ? '' : 's'}`);
      }
      if (failures > 0) {
        toast.error(`${failures} Agoda push${failures === 1 ? '' : 'es'} failed — open the Agoda Payload viewer for details`);
      } else if (aggregatedAgoda.length > 0) {
        toast.message(`Agoda: ${aggregatedAgoda.length} push${aggregatedAgoda.length === 1 ? '' : 'es'} — click Agoda Payload to inspect`);
      }

      await Promise.all([
        (async () => { try { await fetchCalendarData(); } catch (_) { /* non-fatal */ } })(),
        fetchRatePlanCalendar(),
      ]);
      setShowRatesPopup(false);
      setRatesFormData({
        checkinDate: '', checkoutDate: '', baseRate: '',
        updateMode: 'all', perRoomRates: {}, rtDetails: {}, rtExpanded: {},
      });
      return;
    }

    if (false) {  // dead legacy branch kept out of hot path
      // Per-room-type amounts. Call apply-base-rate scoped to each room.
      const entries = Object.entries(ratesFormData.perRoomRates || {})
        .map(([rtId, val]) => [rtId, parseFloat(val)])
        .filter(([, num]) => !isNaN(num) && num > 0);
      if (entries.length === 0) {
        toast.error('Enter at least one room type amount');
        return;
      }
      const token = localStorage.getItem('token');
      let total = 0;
      let errors = 0;

      // Pre-compute the list of dates in the inclusive range so we can also keep
      // the LEGACY availability.rate (= room-type header box) in sync. Without
      // this, the room-type header keeps showing the old rate while the rate
      // plans below it would show the new derived rates -> inconsistent UI.
      const startDate = new Date(ratesFormData.checkinDate);
      const endDate = new Date(ratesFormData.checkoutDate);
      const datesInRange = [];
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        datesInRange.push(d.toISOString().split('T')[0]);
      }

      try {
        for (const [roomTypeId, amount] of entries) {
          // 1) Auto-derive rate plan × occupancy rates
          try {
            const resp = await axios.post(`${API}/rate-plans/apply-base-rate`, {
              property_id: selectedProperty.id,
              room_type_id: roomTypeId,
              start_date: ratesFormData.checkinDate,
              end_date: ratesFormData.checkoutDate,
              base_rate: amount,
            }, { headers: { Authorization: `Bearer ${token}` } });
            total += resp.data?.updates_applied || 0;
          } catch (err) {
            errors += 1;
            console.error('apply-base-rate failed for room', roomTypeId, err?.response?.data);
            continue; // skip availability sync if rate-plan call failed
          }
          // 2) Keep LEGACY availability.rate (room-type header box) in sync.
          // Batch all (room, date) cells into a SINGLE PUT to avoid N round-trips.
          try {
            const availUpdates = datesInRange.map((dateStr) => ({
              room_type_id: roomTypeId,
              date: dateStr,
              rate: amount,
            }));
            await axios.put(`${API}/availability`, availUpdates, {
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch (availErr) {
            console.warn('availability.rate sync failed for room', roomTypeId, availErr?.response?.data);
          }
        }
        if (total > 0) toast.success(`Auto-derived ${total} rate-plan rates across ${entries.length} room type(s)`);
        if (errors > 0) toast.error(`${errors} room type(s) failed`);
        // Refresh BOTH datasets (legacy availability calendar + rate plan calendar)
        await Promise.all([
          (async () => { try { await fetchCalendarData(); } catch (_) { /* non-fatal */ } })(),
          fetchRatePlanCalendar(),
        ]);
        setShowRatesPopup(false);
        setRatesFormData({ checkinDate: '', checkoutDate: '', baseRate: '', updateMode: 'all', perRoomRates: {} });
      } catch (err) {
        toast.error('Failed to update rates');
      }
      return;
    }
    
    // 'all' mode — single base rate applied to every room type via legacy cascade + auto-derive
    if (!ratesFormData.baseRate) {
      toast.error('Please enter the base rate');
      return;
    }
    applyRateCascade(ratesFormData.checkinDate, ratesFormData.checkoutDate, ratesFormData.baseRate);
  };

  const toggleRoomType = (roomTypeId) => {
    setExpandedRoomType(expandedRoomType === roomTypeId ? null : roomTypeId);
  };

  const renderSimplifiedCalendar = () => {
    if (!calendarData || !calendarData.room_types) {
      return <div className="p-8 text-center text-gray-500">Loading calendar data...</div>;
    }

    const days = parseInt(calendarView);
    const displayDates = calendarData.dates ? calendarData.dates.slice(0, days) : [];
    const isTerminated = selectedProperty?.status === 'terminated';

    // If property is terminated, override all availability data
    const processedCalendarData = isTerminated ? {
      ...calendarData,
      room_types: calendarData.room_types.map(roomTypeData => ({
        ...roomTypeData,
        availability: roomTypeData.availability.map(avail => ({
          ...avail,
          available_rooms: 0,
          rate: 0,
          base_rate: 0,
          net_rate: 0,
          is_closed: true
        }))
      }))
    } : calendarData;

    return (
      <div className="space-y-6">
        {/* Terminated Property Banner */}
        {isTerminated && (
          <div className="terminated-banner">
            <span className="terminated-banner-icon">⚠️</span>
            <span>This property is terminated — All rooms are closed (Inventory & Rates = 0)</span>
          </div>
        )}

        {/* Calendar Header Controls */}
        <div className="flex items-center justify-between calendar-header-mobile">
          <div className="flex flex-col gap-2 w-full">
            <h3 className="text-lg font-semibold text-gray-800 calendar-title">
              {selectedProperty?.name} - Rate & Inventory Calendar
            </h3>
            {selectedProperty?.id && (
              <OtaSyncToggles apiBase={API} propertyId={selectedProperty.id} />
            )}
          </div>
          
          <div className="flex flex-col gap-2 w-full">
            {/* Actions Row */}
            <div className="flex items-center gap-2 calendar-controls-row">
              {/* Rates Button */}
              <Button
                onClick={() => setShowRatesPopup(true)}
                className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700"
                size="sm"
              >
                <DollarSign className="w-4 h-4 mr-1" />
                Rates
              </Button>

              {/* Last MMT Payload viewer */}
              <Button
                onClick={() => setShowMmtPayloadDrawer(true)}
                variant="outline"
                size="sm"
                disabled={!mmtLastPayload}
                title={mmtLastPayload ? `Last MMT sync: ${mmtLastPayload.status} · HTTP ${mmtLastPayload.http_code ?? '—'} · ${new Date(mmtLastPayload.at).toLocaleTimeString()}` : 'No MMT sync yet in this session'}
                data-testid="mmt-view-last-payload-btn"
                className={mmtLastPayload?.status === 'success' ? 'border-emerald-400 text-emerald-700' : mmtLastPayload ? 'border-amber-400 text-amber-700' : ''}
              >
                MMT Payload {mmtLastPayload ? (mmtLastPayload.status === 'success' ? '✓' : '!') : ''}
              </Button>

              {/* Last Agoda Payload viewer — mirrors MMT button style but
                  reads from `agoda_ari_pushes` via /api/agoda/ari/last-push.
                  Independent state → never interferes with MMT. */}
              <Button
                onClick={() => {
                  // When a batch is already in state (e.g. from the most
                  // recent apply-base-rate cascade), open the dialog
                  // directly so the per-row selector strip renders. Only
                  // fall back to the single last-push audit fetch when
                  // there's nothing batched to inspect.
                  if (agodaBatchPayloads.length > 1) {
                    setShowAgodaPayloadDrawer(true);
                  } else {
                    fetchAgodaLastPayload();
                    setShowAgodaPayloadDrawer(true);
                  }
                }}
                variant="outline"
                size="sm"
                title={agodaLastPayload ? `Last Agoda push: ${agodaLastPayload.status} · HTTP ${agodaLastPayload.http_code ?? '—'} · ${agodaLastPayload.created_at ? new Date(agodaLastPayload.created_at).toLocaleTimeString() : ''}` : 'Click to load the most recent Agoda push for this property'}
                data-testid="agoda-view-last-payload-btn"
                className={agodaLastPayload?.status === 'success' ? 'border-red-400 text-red-700' : agodaLastPayload ? 'border-amber-400 text-amber-700' : ''}
              >
                Agoda Payload {agodaLastPayload ? (agodaLastPayload.status === 'success' ? '✓' : '!') : ''}
                {agodaBatchPayloads.length > 1 ? ` (${agodaBatchPayloads.length})` : ''}
              </Button>

              {/* Yatra / Travelguru Payload viewer (Jun 2026) — mirrors the
                  MMT/Agoda button style. Reads yatra_sync_all[] captured from
                  the last Bulk Rates apply. Checkmark ONLY when the backend
                  received a successful Yatra response. */}
              <Button
                onClick={() => setShowYatraPayloadDrawer(true)}
                variant="outline"
                size="sm"
                disabled={!yatraLastPayload}
                title={yatraLastPayload ? `Last Yatra push: ${yatraLastPayload.status} · HTTP ${yatraLastPayload.http_code ?? '—'}` : 'No Yatra push yet in this session'}
                data-testid="yatra-view-last-payload-btn"
                className={yatraLastPayload?.status === 'success' ? 'border-orange-400 text-orange-700' : yatraLastPayload ? 'border-amber-400 text-amber-700' : ''}
              >
                Yatra Payload {yatraLastPayload ? (yatraLastPayload.status === 'success' ? '✓' : '!') : ''}
                {yatraBatchPayloads.length > 1 ? ` (${yatraBatchPayloads.length})` : ''}
              </Button>

              {/* Bulk Actions Dropdown — Popover-driven menu so re-selecting
                  the same action reliably reopens the drawer (Radix Select
                  swallows the callback when the value doesn't change). */}
              <Popover open={bulkActionsMenuOpen} onOpenChange={setBulkActionsMenuOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="w-40 flex-shrink-0 justify-between" data-testid="bulk-actions-dropdown">
                    <span className="text-sm">Bulk Actions</span>
                    <ChevronDown className="w-4 h-4 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-1 z-[80]" align="end">
                  {[
                    { key: 'rates',          label: 'Bulk Rates' },
                    { key: 'inventory',      label: 'Bulk Inventory' },
                    { key: 'rate_inventory', label: 'Rate + Inventory' },
                    { key: 'restrictions',   label: 'Restrictions' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      data-testid={`bulk-action-item-${opt.key}`}
                      className="w-full text-left px-3 py-2 text-sm rounded hover:bg-orange-50 hover:text-orange-700"
                      onClick={() => {
                        setBulkActionsMenuOpen(false);
                        handleBulkAction(opt.key);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </PopoverContent>
              </Popover>
            </div>

            {/* Navigation Row */}
            <div className="flex items-center justify-center gap-2 calendar-nav-row">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigateCalendar('prev')}
                data-testid="calendar-prev-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {/* Clickable date range — opens a DatePicker popover so the user
                  can jump the calendar to any custom date instead of arrowing
                  week-by-week. */}
              <Popover open={showCalendarDatePicker} onOpenChange={setShowCalendarDatePicker}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-sm text-gray-800 hover:text-orange-700 hover:bg-orange-50 px-2 py-1 rounded whitespace-nowrap border border-transparent hover:border-orange-200 transition-colors"
                    data-testid="calendar-date-jump-btn"
                    title="Click to jump to a specific date"
                  >
                    {processedCalendarData.start_date}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[60]" align="center">
                  <Calendar
                    mode="single"
                    selected={currentDate}
                    onSelect={(d) => {
                      if (!d) return;
                      setCurrentDate(d);
                      setShowCalendarDatePicker(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigateCalendar('next')}
                data-testid="calendar-next-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Simplified Legend */}
        <div className="flex items-center justify-end bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-200 rounded"></div>
              <span>Available Rooms</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-200 rounded"></div>
              <span>Room Rate (₹)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span>Closed</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-700 sticky left-0 bg-gray-50 border-r min-w-[200px]">
                  Room Type
                </th>
                {displayDates.map((date) => {
                  const dateObj = new Date(date);
                  const dayName = dateObj.toLocaleDateString('en', { weekday: 'short' });
                  const dayNum = dateObj.getDate();
                  const monthName = dateObj.toLocaleDateString('en', { month: 'short' });
                  return (
                    <th key={date} className="px-2 py-3 text-center font-medium text-gray-700 min-w-[140px] border-r">
                      <div className="font-semibold">{dayName}</div>
                      <div className="text-xs text-gray-500">{dayNum} {monthName}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {processedCalendarData.room_types.map((roomTypeData) => {
                const isExpanded = expandedRoomType === roomTypeData.room_type.id;
                const maxOccupancy = roomTypeData.room_type.max_occupancy || 3;

                // Header green cell now shows the Occupancy-1 price of this room
                // type's first/default rate plan (EP for MMT / Room Only for Agoda —
                // whichever is listed first), per date. Falls back to the base rate
                // (availability.rate) when no Occupancy-1 rate exists for that date.
                const rpRoomHeader = (ratePlanCalendar?.room_types || []).find(r => r.room_type_id === roomTypeData.room_type.id);
                const primaryPlan = rpRoomHeader?.rate_plans?.[0];
                const primaryOcc1 = (primaryPlan?.occupancies || []).find(o => o.occupancy === 1);
                const primaryOcc1RateByDate = {};
                (primaryOcc1?.rates || []).forEach(rc => { primaryOcc1RateByDate[rc.date] = rc.rate; });

                return (
                  <React.Fragment key={roomTypeData.room_type.id}>
                    {/* Main Room Type Row */}
                    <tr className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800 sticky left-0 bg-white border-r">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleRoomType(roomTypeData.room_type.id)}
                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                            data-testid={`expand-room-type-${roomTypeData.room_type.id}`}
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                          <div>
                            <div className="font-semibold text-sm">{roomTypeData.room_type.name}</div>
                            <div className="text-xs text-gray-500">
                              Max: {roomTypeData.room_type.room_count} rooms
                            </div>
                            <div className="text-xs text-blue-600">
                              Base: ₹{roomTypeData.room_type.base_rate}
                            </div>
                          </div>
                        </div>
                      </td>
                      {roomTypeData.availability.slice(0, days).map((avail) => {
                        const cellId = `${roomTypeData.room_type.id}_${avail.date}`;
                        const isAnimated = animatedCells.has(cellId);
                        const staged = pendingCellEdits[cellId] || {};
                        const isDirty = Object.keys(staged).length > 0;
                        // Effective values shown in the inputs — staged
                        // values win over server-side values so keystrokes
                        // never bounce back after render.
                        const effRooms  = staged.available_rooms ?? avail.available_rooms;
                        const effClosed = staged.is_closed       ?? avail.is_closed;
                        // Read-only display rate: Occupancy-1 of the primary rate
                        // plan for this date, falling back to the room-level base rate.
                        const occ1Rate = primaryOcc1RateByDate[avail.date];
                        const displayRate = (occ1Rate !== undefined && occ1Rate !== null)
                          ? occ1Rate
                          : avail.rate;

                        // Update helpers — write into the staged bag; no
                        // network activity until the Save button fires.
                        const stageField = (field, value) => {
                          setPendingCellEdits(prev => {
                            const cur = { ...(prev[cellId] || {}) };
                            cur[field] = value;
                            return { ...prev, [cellId]: cur };
                          });
                        };
                        const discardCell = () => {
                          setPendingCellEdits(prev => {
                            const cp = { ...prev };
                            delete cp[cellId];
                            return cp;
                          });
                        };
                        const saveCell = async () => {
                          // Snapshot & clear first so a slow API doesn't
                          // trap the operator in a stale-input UX. Send ALL
                          // staged fields in ONE PUT so the backend detects
                          // exactly what changed and Yatra collapses an
                          // inventory+closed edit into a single Availability
                          // Update.
                          const snap = staged;
                          discardCell();
                          if (Object.keys(snap).length === 0) return;
                          try {
                            await saveAvailabilityCellEdits(
                              roomTypeData.room_type.id, avail.date, snap,
                            );
                          } catch (_err) {
                            // saveAvailabilityCellEdits surfaces its own toast.
                          }
                        };

                        return (
                          <td
                            key={cellId}
                            className={`px-2 py-2 text-center border-r transition-all ${
                              isAnimated ? 'bg-green-100 animate-pulse' : ''
                            } ${isDirty ? 'bg-amber-50' : ''}`}
                          >
                            <div className="space-y-1">
                              {/* Available Rooms — staged locally */}
                              <input
                                type="number"
                                value={effRooms}
                                onChange={(e) => stageField(
                                  'available_rooms',
                                  parseInt(e.target.value, 10),
                                )}
                                className={`w-full text-xs text-center border rounded px-1 py-1 font-medium ${
                                  effClosed
                                    ? 'bg-red-500 border-red-600 text-white font-bold'
                                    : (isDirty && staged.available_rooms !== undefined
                                        ? 'bg-amber-100 border-amber-400'
                                        : 'bg-blue-50 border-blue-200')
                                }`}
                                min="0"
                                max={roomTypeData.room_type.room_count}
                                data-testid={`availability-rooms-${roomTypeData.room_type.id}-${avail.date}`}
                                disabled={effClosed}
                              />

                              {/* Room Rate — read-only; shows Occupancy-1 of the
                                  primary rate plan (fallback: base rate) */}
                              <div
                                className={`w-full text-xs text-center border rounded px-1 py-1 font-medium ${
                                  effClosed
                                    ? 'bg-red-500 border-red-600 text-white font-bold'
                                    : 'bg-green-50 border-green-200 text-gray-800'
                                }`}
                                data-testid={`availability-rate-${roomTypeData.room_type.id}-${avail.date}`}
                                title="Occupancy 1 price from the default rate plan"
                              >
                                ₹{Number(displayRate || 0).toLocaleString()}
                              </div>

                              {/* Closed Checkbox — staged locally */}
                              <div className="flex items-center justify-center">
                                <input
                                  type="checkbox"
                                  checked={effClosed}
                                  onChange={(e) => stageField('is_closed', e.target.checked)}
                                  className="w-3 h-3 mr-1"
                                  data-testid={`availability-closed-${roomTypeData.room_type.id}-${avail.date}`}
                                />
                                <span className={`text-xs ${effClosed ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                  {effClosed ? 'CLOSED' : 'Closed'}
                                </span>
                              </div>

                              {/* Per-cell Save/Discard controls — only
                                  render when the operator has staged an
                                  unsaved edit for this cell. */}
                              {isDirty && (
                                <div className="flex items-center justify-center gap-1 pt-1">
                                  <button
                                    type="button"
                                    onClick={saveCell}
                                    className="text-[10px] px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 font-medium"
                                    data-testid={`availability-save-${roomTypeData.room_type.id}-${avail.date}`}
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    onClick={discardCell}
                                    className="text-[10px] px-1.5 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
                                    data-testid={`availability-discard-${roomTypeData.room_type.id}-${avail.date}`}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>

                    {/* Expanded: Rate Plan rows for this room type */}
                    {isExpanded && (() => {
                      const rpRoom = (ratePlanCalendar?.room_types || []).find(r => r.room_type_id === roomTypeData.room_type.id);
                      const ratePlans = rpRoom?.rate_plans || [];
                      const dateCount = roomTypeData.availability.slice(0, days).length;
                      if (ratePlans.length === 0) {
                        return (
                          <tr className="bg-gray-50">
                            <td colSpan={1 + dateCount} className="px-4 py-3 pl-12 text-sm text-gray-500 italic border-r border-b">
                              No rate plans configured for this room type.&nbsp;
                              {user?.role === 'admin' && (
                                <button
                                  type="button"
                                  className="text-orange-600 underline hover:text-orange-800"
                                  onClick={openRatePlanManager}
                                  data-testid={`empty-rate-plans-cta-${roomTypeData.room_type.id}`}
                                >
                                  Add via Settings → Add Rate Plan
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <>
                          {ratePlans.map(rp => {
                            const planExpanded = expandedRatePlans.has(rp.id);
                            // Feb 27 2026 — per-date restrictions map surfaced
                            // from /rate-plans/calendar. Cells whose date is
                            // in this map get red styling to signal that the
                            // rate plan is blocked for that date (room-level
                            // inventory stays untouched).
                            const restrictionsByDate = rp.restrictions_by_date || {};
                            const isRestrictedOn = (dateStr) => {
                              const r = restrictionsByDate[dateStr];
                              if (!r) return false;
                              return !!(r.closed
                                       || r.min_los !== undefined
                                       || r.min_advance_booking !== undefined
                                       || r.closed_to_arrival
                                       || r.closed_to_departure);
                            };
                            const dateSlice = (rp.occupancies?.[0]?.rates || []).slice(0, dateCount);
                            return (
                              <React.Fragment key={`rp-${rp.id}`}>
                                {/* Rate plan header row (collapsible) */}
                                <tr className="bg-orange-50 hover:bg-orange-100 cursor-pointer" onClick={() => toggleRatePlanExpanded(rp.id)} data-testid={`calendar-rate-plan-row-${rp.id}`}>
                                  <td className="px-4 py-2 pl-10 text-sm font-medium text-orange-900 sticky left-0 bg-orange-50 border-r">
                                    <span className="inline-flex items-center gap-2">
                                      {planExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                      {rp.code}{rp.name ? ` — ${rp.name}` : ''}
                                      <span className="text-xs text-gray-500 font-normal">(base ₹{(rp.base_rate || 0).toLocaleString()})</span>
                                    </span>
                                  </td>
                                  {/* Per-date collapsed strip so restricted dates
                                      remain visually flagged even when the plan
                                      is collapsed. */}
                                  {dateSlice.map((rcell) => {
                                    const restricted = isRestrictedOn(rcell.date);
                                    return (
                                      <td
                                        key={`rp-${rp.id}-strip-${rcell.date}`}
                                        className={`px-2 py-2 text-center border-r text-[10px] ${restricted ? 'bg-red-50 border-red-400 text-red-700 font-semibold' : 'text-gray-400'}`}
                                        data-testid={`rp-restricted-strip-${rp.id}-${rcell.date}`}
                                        title={restricted ? 'Rate plan restricted for this date' : ''}
                                      >
                                        {restricted ? 'BLOCKED' : ''}
                                      </td>
                                    );
                                  })}
                                </tr>
                                {/* Occupancy rows under this rate plan */}
                                {planExpanded && (rp.occupancies || []).map(occ => (
                                  <tr key={`rp-${rp.id}-occ-${occ.occupancy}`} className="bg-white">
                                    <td className="px-4 py-2 pl-16 text-xs text-gray-600 sticky left-0 bg-white border-r">
                                      Occupancy {occ.occupancy}
                                    </td>
                                    {(occ.rates || []).slice(0, dateCount).map((rcell) => {
                                      const restricted = isRestrictedOn(rcell.date);
                                      const isEpOcc1 = (rp.code || '').toUpperCase() === 'EP' && occ.occupancy === 1;
                                      // Admins can inline-edit every (rate plan, occupancy)
                                      // cell. Editing a cell whose room type has linked room
                                      // types prompts the operator to cascade the change.
                                      if (user?.role !== 'admin') {
                                        return (
                                          <td
                                            key={`rp-${rp.id}-occ-${occ.occupancy}-${rcell.date}`}
                                            className={`px-2 py-2 text-center border-r ${restricted ? 'bg-red-50 border-red-400' : ''}`}
                                            data-testid={`rp-occ-cell-${rp.id}-${occ.occupancy}-${rcell.date}`}
                                            title={restricted ? 'Rate plan restricted for this date' : ''}
                                          >
                                            <div className={`text-xs font-medium ${restricted ? 'text-red-700' : (rcell.is_override ? 'text-orange-700' : 'text-gray-700')}`}>
                                              ₹{Number(rcell.rate || 0).toLocaleString()}
                                            </div>
                                          </td>
                                        );
                                      }
                                      return (
                                        <td
                                          key={`rp-${rp.id}-occ-${occ.occupancy}-${rcell.date}`}
                                          className={`px-2 py-2 text-center border-r ${restricted ? 'bg-red-50 border-red-400' : ''}`}
                                          data-testid={`rp-occ-cell-${rp.id}-${occ.occupancy}-${rcell.date}`}
                                          title={restricted ? 'Rate plan restricted for this date' : ''}
                                        >
                                          {(() => {
                                            const cellKey = `${rp.id}-${occ.occupancy}-${rcell.date}`;
                                            const isEditing = editingRateCell && editingRateCell.key === cellKey;
                                            const isSaving = savingRateCell === cellKey;
                                            const commit = async () => {
                                              const v = parseFloat(editingRateCell?.value);
                                              if (isNaN(v) || v < 0) { toast.error('Enter a valid rate (0 or more)'); return; }
                                              setSavingRateCell(cellKey);
                                              try {
                                                if (v !== Number(rcell.rate || 0)) {
                                                  await updateRatePlanOccupancyRate(rp.id, occ.occupancy, rcell.date, v, roomTypeData.room_type.id, rp.code);
                                                }
                                                setEditingRateCell(null);
                                              } finally {
                                                setSavingRateCell(null);
                                              }
                                            };
                                            if (!isEditing) {
                                              return (
                                                <button
                                                  type="button"
                                                  onClick={() => setEditingRateCell({ key: cellKey, value: String(Number(rcell.rate || 0)) })}
                                                  className={`w-full text-xs text-center border rounded px-1 py-1 font-medium cursor-pointer transition-colors ${
                                                    restricted
                                                      ? 'bg-red-50 border-red-400 text-red-700'
                                                      : rcell.is_override
                                                        ? 'bg-orange-50 border-orange-300 text-orange-800 hover:bg-orange-100'
                                                        : 'bg-green-50 border-green-200 text-gray-800 hover:bg-green-100'
                                                  }`}
                                                  title={isEpOcc1 ? 'EP occupancy-1 — synced with Base Rate. Click to edit.' : `Click to edit ${rp.code} occupancy ${occ.occupancy} rate`}
                                                  data-testid={`rp-occ-rate-display-${rp.id}-${occ.occupancy}-${rcell.date}`}
                                                >
                                                  ₹{Number(rcell.rate || 0).toLocaleString()}
                                                </button>
                                              );
                                            }
                                            return (
                                              <div className="flex items-center gap-0.5">
                                                <input
                                                  type="number"
                                                  min="0"
                                                  step="0.01"
                                                  autoFocus
                                                  disabled={isSaving}
                                                  value={editingRateCell.value}
                                                  onChange={(e) => setEditingRateCell({ key: cellKey, value: e.target.value })}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter') { e.preventDefault(); commit(); }
                                                    else if (e.key === 'Escape') { e.preventDefault(); setEditingRateCell(null); }
                                                  }}
                                                  className="w-full text-xs text-center border border-blue-400 rounded px-1 py-1 font-medium bg-white"
                                                  data-testid={`rp-occ-inline-edit-${rp.id}-${occ.occupancy}-${rcell.date}`}
                                                />
                                                <button
                                                  type="button"
                                                  onClick={commit}
                                                  disabled={isSaving}
                                                  className="shrink-0 text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                                                  title="Save (Enter)"
                                                  data-testid={`rp-occ-save-${rp.id}-${occ.occupancy}-${rcell.date}`}
                                                >
                                                  {isSaving
                                                    ? <RefreshCcw className="w-3.5 h-3.5 animate-spin" />
                                                    : <Check className="w-3.5 h-3.5" />}
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => setEditingRateCell(null)}
                                                  disabled={isSaving}
                                                  className="shrink-0 text-red-500 hover:text-red-600 disabled:opacity-50"
                                                  title="Cancel (Esc)"
                                                  data-testid={`rp-occ-cancel-${rp.id}-${occ.occupancy}-${rcell.date}`}
                                                >
                                                  <X className="w-3.5 h-3.5" />
                                                </button>
                                              </div>
                                            );
                                          })()}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </>
                      );
                    })()}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* MMT Last Payload viewer — request + response XML from the most recent sync */}
        <Dialog open={showMmtPayloadDrawer} onOpenChange={setShowMmtPayloadDrawer}>
          <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="mmt-payload-dialog">
            <DialogHeader>
              <DialogTitle>
                Last MMT Sync — {mmtLastPayload?.status === 'success' ? '✅ Success' : '⚠️ Error'}
                {mmtLastPayload?.http_code != null && (<span className="text-xs font-normal ml-2 text-gray-500">HTTP {mmtLastPayload.http_code}</span>)}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Raw outbound MakeMyTrip ARI request XML and the response XML received from MMT for the latest Calendar edit. Use the copy buttons to attach to a support ticket.
              </DialogDescription>
            </DialogHeader>
            {mmtLastPayload ? (
              <div className="space-y-3 overflow-y-auto pr-1">
                <div className="text-[11px] text-gray-600">
                  <div>Timestamp: {mmtLastPayload.at}</div>
                  {mmtLastPayload.room_type_id && <div>Room type: <code>{mmtLastPayload.room_type_id}</code></div>}
                  {mmtLastPayload.date && <div>Date: <code>{mmtLastPayload.date}</code></div>}
                  {mmtLastPayload.error && <div className="text-red-600">Error: {mmtLastPayload.error}</div>}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-gray-800">Outbound Request XML</label>
                    <button type="button" className="text-[10px] underline text-blue-700"
                      onClick={() => { navigator.clipboard.writeText(mmtLastPayload.request_xml || ''); toast.success('Request XML copied'); }}
                      data-testid="mmt-copy-request-xml">copy</button>
                  </div>
                  <pre className="text-[11px] bg-gray-900 text-green-200 rounded p-2 overflow-x-auto max-h-64" data-testid="mmt-last-request-xml">
{mmtLastPayload.request_xml || '(empty)'}
                  </pre>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-gray-800">Inbound Response XML</label>
                    <button type="button" className="text-[10px] underline text-blue-700"
                      onClick={() => { navigator.clipboard.writeText(mmtLastPayload.response_xml || ''); toast.success('Response XML copied'); }}
                      data-testid="mmt-copy-response-xml">copy</button>
                  </div>
                  <pre className="text-[11px] bg-gray-900 text-amber-200 rounded p-2 overflow-x-auto max-h-64" data-testid="mmt-last-response-xml">
{mmtLastPayload.response_xml || '(empty)'}
                  </pre>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500 py-6 text-center">No MMT sync recorded in this session yet.</div>
            )}
          </DialogContent>
        </Dialog>

        {/* Yatra / Travelguru Last Payload viewer (Jun 2026) — shows the real
            OTA request + response XML for the last Rate / Inventory / Close-Out
            push. Password is already masked server-side. */}
        <Dialog open={showYatraPayloadDrawer} onOpenChange={setShowYatraPayloadDrawer}>
          <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="yatra-payload-dialog">
            <DialogHeader>
              <DialogTitle>
                Yatra Payload — {yatraLastPayload?.status === 'success' ? '✅ Success' : (yatraLastPayload?.status === 'skipped' ? '⏭️ Skipped' : '⚠️ Error')}
                {yatraLastPayload?.http_code != null && (<span className="text-xs font-normal ml-2 text-gray-500">HTTP {yatraLastPayload.http_code}</span>)}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Raw outbound Travelguru/Yatra ARI request XML and the response XML received from Yatra for the latest {yatraLastPayload?.action === 'availability_update' ? 'Availability Update' : (yatraLastPayload?.action === 'close_inventory' ? 'Block Inventory' : yatraLastPayload?.action === 'open_inventory' ? 'Unblock Inventory' : yatraLastPayload?.action === 'close_out' ? 'Close-Out' : yatraLastPayload?.action === 'rate_update' ? 'Rate Update' : yatraLastPayload?.action === 'extend_inventory' ? 'Extend Inventory' : 'Bulk Rates')} action.
              </DialogDescription>
            </DialogHeader>
            {yatraLastPayload ? (
              <div className="space-y-3 overflow-y-auto pr-1">
                {/* Batch selector when multiple Yatra pushes were dispatched. */}
                {yatraBatchPayloads.length > 1 && (
                  <div className="flex flex-wrap gap-1" data-testid="yatra-payload-batch-selector">
                    {yatraBatchPayloads.map((p, i) => (
                      <button
                        key={p.audit_id || i}
                        type="button"
                        onClick={() => setYatraLastPayload(p)}
                        className={`text-[10px] px-2 py-0.5 rounded border ${p === yatraLastPayload ? 'bg-orange-100 border-orange-400 text-orange-800' : 'border-gray-300 text-gray-600'}`}
                        data-testid={`yatra-payload-batch-item-${i}`}
                      >
                        {p.status === 'success' ? '✓' : (p.status === 'skipped' ? '⏭' : '✗')} {p.action} #{i + 1}
                      </button>
                    ))}
                  </div>
                )}
                <div className="text-[11px] text-gray-600 grid grid-cols-2 gap-x-4 gap-y-0.5" data-testid="yatra-payload-meta">
                  <div>OTA: <strong>Yatra</strong></div>
                  <div>Action: <code>{yatraLastPayload.action}</code></div>
                  <div>Hotel Code: <code>{yatraLastPayload.hotel_code || '—'}</code></div>
                  <div>InvCode: <code>{yatraLastPayload.inv_code || '—'}</code></div>
                  {yatraLastPayload.inv_type ? <div>InvTypeCode: <code>{yatraLastPayload.inv_type}</code></div> : null}
                  <div>RatePlanID: <code>{yatraLastPayload.rate_plan_id || '—'}</code></div>
                  <div>Rate Plan Type: <code>{yatraLastPayload.rate_plan_type || '—'}</code></div>
                  <div>Date range: <code>{yatraLastPayload.date_start} → {yatraLastPayload.date_end}</code></div>
                  {(yatraLastPayload.action === 'availability_update' || yatraLastPayload.action === 'close_inventory' || yatraLastPayload.action === 'open_inventory') ? (
                    <>
                      {yatraLastPayload.booking_limit != null ? <div>BookingLimit: <code>{yatraLastPayload.booking_limit}</code></div> : null}
                      <div>SellThroughOpenIndicator: <code>{String(yatraLastPayload.sell_through_open_indicator ?? false)}</code></div>
                    </>
                  ) : (
                    <div>Pricing Occupancy: <code>{yatraLastPayload.pricing_occupancy ?? '—'}</code></div>
                  )}
                  {yatraLastPayload.duration_ms != null && <div>Response time: {yatraLastPayload.duration_ms} ms</div>}
                  <div>Status: <span className={yatraLastPayload.status === 'success' ? 'text-emerald-700' : 'text-red-600'}>{yatraLastPayload.status}</span></div>
                </div>
                {yatraLastPayload.error && <div className="text-[11px] text-red-600" data-testid="yatra-payload-error">Error: {yatraLastPayload.error}</div>}
                {Array.isArray(yatraLastPayload.errors) && yatraLastPayload.errors.length > 0 && (
                  <ul className="text-[11px] bg-red-50 border border-red-200 rounded p-2 space-y-1" data-testid="yatra-payload-errors">
                    {yatraLastPayload.errors.map((e, i) => (<li key={i}>{e.code ? `[${e.code}] ` : ''}{e.description}</li>))}
                  </ul>
                )}
                {Array.isArray(yatraLastPayload.warnings) && yatraLastPayload.warnings.length > 0 && (
                  <ul className="text-[11px] bg-amber-50 border border-amber-200 rounded p-2 space-y-1" data-testid="yatra-payload-warnings">
                    {yatraLastPayload.warnings.map((w, i) => (<li key={i}>{w}</li>))}
                  </ul>
                )}
                {yatraLastPayload.action === 'extend_inventory' && (
                  <div className="text-[11px] bg-blue-50 border border-blue-200 rounded p-2 text-blue-800" data-testid="yatra-payload-stage-note">
                    <strong>Note (stage server):</strong> The Yatra <em>stage</em> server may return
                    <code className="mx-1">[04] "Load Inventory cannot be happened for exist/before date"</code>
                    even for valid future dates. This is a known stage-environment limitation and does
                    <strong> not</strong> reflect production behavior — Extend Inventory will be validated on production credentials.
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-gray-800">Request XML</label>
                    <button type="button" className="text-[10px] underline text-blue-700"
                      onClick={() => { navigator.clipboard.writeText(yatraLastPayload.request_xml || ''); toast.success('Request XML copied'); }}
                      data-testid="yatra-copy-request-xml">copy</button>
                  </div>
                  <pre className="text-[11px] bg-gray-900 text-green-200 rounded p-2 overflow-x-auto max-h-64 whitespace-pre-wrap" data-testid="yatra-last-request-xml">
{yatraLastPayload.request_xml || '(empty)'}
                  </pre>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-gray-800">Response XML</label>
                    <button type="button" className="text-[10px] underline text-blue-700"
                      onClick={() => { navigator.clipboard.writeText(yatraLastPayload.response_xml || ''); toast.success('Response XML copied'); }}
                      data-testid="yatra-copy-response-xml">copy</button>
                  </div>
                  <pre className="text-[11px] bg-gray-900 text-amber-200 rounded p-2 overflow-x-auto max-h-64 whitespace-pre-wrap" data-testid="yatra-last-response-xml">
{yatraLastPayload.response_xml || '(empty)'}
                  </pre>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500 py-6 text-center" data-testid="yatra-payload-empty">No Yatra push recorded in this session yet.</div>
            )}
          </DialogContent>
        </Dialog>

        {/* Agoda Last Payload viewer — most recent entry from agoda_ari_pushes
            for this property (or a specific room+date after a cell edit). */}
        <Dialog open={showAgodaPayloadDrawer} onOpenChange={setShowAgodaPayloadDrawer}>
          <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="agoda-payload-dialog">
            <DialogHeader>
              <DialogTitle>
                {agodaBatchPayloads.length > 1 ? 'Agoda ARI Batch Push' : 'Last Agoda ARI Push'} — {
                  agodaLastPayload?.status === 'success'
                    ? '✅ Success'
                    : agodaLastPayload?.status === 'partial'
                      ? '⚠️ Partial'
                      : agodaLastPayload
                        ? '⚠️ Error'
                        : '—'
                }
                {agodaLastPayload?.http_code != null && (
                  <span className="text-xs font-normal ml-2 text-gray-500">HTTP {agodaLastPayload.http_code}</span>
                )}
                {agodaLastPayload?.duration_ms != null && (
                  <span className="text-xs font-normal ml-2 text-gray-500">· {agodaLastPayload.duration_ms} ms</span>
                )}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Raw outbound Agoda SetARI V2 request XML and response XML from the most recent Agoda push for this property.
              </DialogDescription>
            </DialogHeader>

            {agodaBatchPayloads.length > 1 && (() => {
              // Batch selector — one row per push in the most recent
              // apply-base-rate cascade. Clicking a row swaps the main
              // request/response viewer below to that push.
              const roomNameLookup = {};
              (ratePlanCalendar?.room_types || calendarData?.room_types || []).forEach(rt => {
                const id = rt.room_type_id || rt.room_type?.id;
                const nm = rt.room_type_name || rt.room_type?.name;
                if (id && nm) roomNameLookup[id] = nm;
              });
              const successCount = agodaBatchPayloads.filter(r => r.status === 'success').length;
              const failedCount = agodaBatchPayloads.length - successCount;
              return (
                <div className="border border-gray-200 rounded overflow-hidden shrink-0" data-testid="agoda-batch-selector">
                  <div className="flex items-center justify-between px-2 py-1 bg-gray-50 text-[11px] text-gray-700">
                    <span>
                      <strong>{agodaBatchPayloads.length}</strong> pushes ·{' '}
                      <span className="text-green-700">{successCount} success</span>
                      {failedCount > 0 && <> · <span className="text-red-700">{failedCount} failed</span></>}
                    </span>
                    <span className="text-gray-400">click a row to inspect</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto divide-y divide-gray-100">
                    {agodaBatchPayloads.map((row, idx) => {
                      const isActive = agodaLastPayload === row
                        || (agodaLastPayload
                            && agodaLastPayload.room_type_id === row.room_type_id
                            && agodaLastPayload.rate_plan_id === row.rate_plan_id);
                      const label = roomNameLookup[row.room_type_id] || row.room_type_id || `Row ${idx + 1}`;
                      const pill = row.status === 'success' ? 'bg-green-100 text-green-800'
                                 : row.status === 'partial' ? 'bg-amber-100 text-amber-800'
                                 : row.status === 'skipped' ? 'bg-gray-100 text-gray-700'
                                 : 'bg-red-100 text-red-800';
                      return (
                        <button
                          type="button"
                          key={`${row.room_type_id || 'row'}-${row.rate_plan_id || idx}`}
                          onClick={() => setAgodaLastPayload(row)}
                          className={`w-full text-left px-2 py-1 text-[11px] flex items-center gap-2 hover:bg-blue-50 ${isActive ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : ''}`}
                          data-testid={`agoda-batch-row-${idx}`}
                        >
                          <span className={`px-1.5 py-0.5 rounded ${pill} shrink-0`}>{row.status || 'unknown'}</span>
                          <span className="truncate flex-1"><strong>{label}</strong>{row.rate_plan_id ? <span className="text-gray-500"> · {row.rate_plan_id}</span> : null}</span>
                          {row.http_code != null && <span className="text-gray-500 shrink-0">HTTP {row.http_code}</span>}
                          {row.duration_ms != null && <span className="text-gray-500 shrink-0">{row.duration_ms}ms</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {agodaLastPayload ? (
              <div className="space-y-3 overflow-y-auto pr-1">
                <div className="text-[11px] text-gray-600 space-y-0.5">
                  {agodaLastPayload.created_at && <div>Timestamp: <code>{agodaLastPayload.created_at}</code></div>}
                  {agodaLastPayload.kind && <div>Kind: <code>{agodaLastPayload.kind}</code></div>}
                  {agodaLastPayload.meta?.room_type_id && <div>Room type: <code>{agodaLastPayload.meta.room_type_id}</code></div>}
                  {agodaLastPayload.meta?.rate_plan_id && <div>Rate plan: <code>{agodaLastPayload.meta.rate_plan_id}</code></div>}
                  {agodaLastPayload.meta?.date && <div>Date: <code>{agodaLastPayload.meta.date}</code></div>}
                  {Array.isArray(agodaLastPayload.meta?.dates) && agodaLastPayload.meta.dates.length > 0 && (
                    <div>Dates: <code>{agodaLastPayload.meta.dates.join(', ')}</code></div>
                  )}
                  {agodaLastPayload.meta?.date_from && (
                    <div>Range: <code>{agodaLastPayload.meta.date_from} → {agodaLastPayload.meta.date_to}</code></div>
                  )}
                </div>
                {Array.isArray(agodaLastPayload.errors) && agodaLastPayload.errors.length > 0 && (
                  <div>
                    <label className="text-xs font-semibold text-red-700">Errors</label>
                    <ul className="mt-1 text-[11px] bg-red-50 border border-red-200 rounded p-2 space-y-1" data-testid="agoda-payload-errors">
                      {agodaLastPayload.errors.map((e, i) => (
                        <li key={i}><strong>{e.code || 'error'}</strong>: {e.description || JSON.stringify(e)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-gray-800">Outbound Request XML</label>
                    <button type="button" className="text-[10px] underline text-blue-700"
                      onClick={() => { navigator.clipboard.writeText(agodaLastPayload.request_xml || ''); toast.success('Request XML copied'); }}
                      data-testid="agoda-copy-request-xml">copy</button>
                  </div>
                  <pre className="text-[11px] bg-gray-900 text-green-200 rounded p-2 overflow-x-auto max-h-64" data-testid="agoda-last-request-xml">
{agodaLastPayload.request_xml || '(empty)'}
                  </pre>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-gray-800">Inbound Response XML</label>
                    <button type="button" className="text-[10px] underline text-blue-700"
                      onClick={() => { navigator.clipboard.writeText(agodaLastPayload.response_xml || ''); toast.success('Response XML copied'); }}
                      data-testid="agoda-copy-response-xml">copy</button>
                  </div>
                  <pre className="text-[11px] bg-gray-900 text-amber-200 rounded p-2 overflow-x-auto max-h-64" data-testid="agoda-last-response-xml">
{agodaLastPayload.response_xml || '(empty)'}
                  </pre>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500 py-6 text-center" data-testid="agoda-payload-empty">
                No Agoda ARI push found for this date.
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Rates Popup Modal */}
        <Dialog open={showRatesPopup} onOpenChange={setShowRatesPopup}>
          <DialogContent
            className={
              ratesFormData.updateMode === 'room_types'
                ? 'sm:max-w-5xl max-h-[92vh] overflow-hidden flex flex-col p-0'
                : 'sm:max-w-md'
            }
            data-testid="rates-popup-dialog"
          >
            <DialogHeader className={ratesFormData.updateMode === 'room_types' ? 'px-6 pt-5 pb-3 border-b shrink-0' : ''}>
              <DialogTitle>Update Room Rates</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={handleRatesSubmit}
              className={ratesFormData.updateMode === 'room_types' ? 'flex flex-col flex-1 min-h-0' : 'space-y-4'}
              data-testid="rates-popup-form"
            >
              <div
                className={
                  ratesFormData.updateMode === 'room_types'
                    ? 'flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4'
                    : 'contents'
                }
                data-testid="rates-popup-scroll-area"
              >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Date *</label>
                <Input
                  type="date"
                  value={ratesFormData.checkinDate}
                  onChange={(e) => setRatesFormData({...ratesFormData, checkinDate: e.target.value})}
                  required
                  data-testid="apply-base-checkin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Date *</label>
                <Input
                  type="date"
                  value={ratesFormData.checkoutDate}
                  onChange={(e) => setRatesFormData({...ratesFormData, checkoutDate: e.target.value})}
                  required
                  data-testid="apply-base-checkout"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Update Mode *</label>
                <Select
                  value={ratesFormData.updateMode || 'all'}
                  onValueChange={(v) => setRatesFormData({ ...ratesFormData, updateMode: v })}
                >
                  <SelectTrigger data-testid="apply-base-update-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All (single amount → every room type)</SelectItem>
                    <SelectItem value="room_types">Room Types (per-room-type amount)</SelectItem>
                    <SelectItem value="deviation">Deviation Mode (% per occupancy)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {ratesFormData.updateMode === 'room_types' ? (
                <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4 overflow-hidden">
                  {/* Left panel: hint / summary. Common controls above the
                      grid stay in flow so they don't duplicate. */}
                  <aside className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2 h-fit sticky top-0" data-testid="room-types-mode-side-panel">
                    <div className="text-xs font-semibold text-gray-800">Per-Room-Type Amount</div>
                    <p className="text-[11px] text-gray-600 leading-snug">
                      Click a room type to expand and enter a rate for each rate plan,
                      set extra-guest pricing, and apply restrictions. The rate you
                      enter is applied to every occupancy tier on THAT plan only.
                    </p>
                    <p className="text-[11px] text-gray-600 leading-snug">
                      On save, each rate plan with data is pushed to Agoda; results
                      appear in the <strong>Agoda Payload</strong> batch viewer.
                    </p>
                    <div className="text-[11px] text-gray-500">
                      Filled: <strong className="text-gray-800">{
                        Object.values(ratesFormData.rtDetails || {}).filter(b => {
                          const r = Object.values(b?.ratePlans || {}).some(v => parseFloat(v) > 0);
                          const e = Object.values(b?.extras || {}).some(v => parseFloat(v) > 0);
                          const s = Object.values(b?.restrictions || {}).some(x =>
                            x && (x.closed !== undefined || x.closed_to_arrival !== undefined
                                  || x.closed_to_departure !== undefined || x.min_los || x.max_los)
                          );
                          return r || e || s;
                        }).length
                      }</strong> / {(ratePlanCalendar?.room_types || []).length} room type(s)
                    </div>
                  </aside>

                  {/* Right panel: accordion of room types */}
                  <div className="border border-gray-200 rounded-lg overflow-y-auto max-h-[62vh] divide-y divide-gray-100" data-testid="room-types-mode-list">
                    {(ratePlanCalendar?.room_types || []).length === 0 ? (
                      <div className="text-xs text-gray-500 p-3">No room types loaded for this property.</div>
                    ) : (
                      (ratePlanCalendar?.room_types || []).map(rt => {
                        const rtId = rt.room_type_id;
                        const bucket = (ratesFormData.rtDetails || {})[rtId] || {};
                        const isOpen = !!(ratesFormData.rtExpanded || {})[rtId];
                        const rpMap = bucket.ratePlans || {};
                        const filledPlans = Object.values(rpMap).filter(v => parseFloat(v) > 0).length;
                        const totalPlans = (rt.rate_plans || []).length;
                        const setBucket = (patch) => setRatesFormData(prev => ({
                          ...prev,
                          rtDetails: {
                            ...(prev.rtDetails || {}),
                            [rtId]: { ...((prev.rtDetails || {})[rtId] || {}), ...patch },
                          },
                        }));
                        const setRatePlan = (rpId, val) => setBucket({
                          ratePlans: { ...(bucket.ratePlans || {}), [rpId]: val },
                        });
                        const setExtras = (k, val) => setBucket({
                          extras: { ...(bucket.extras || {}), [k]: val },
                        });
                        const setRestr = (rpId, patch) => setBucket({
                          restrictions: {
                            ...(bucket.restrictions || {}),
                            [rpId]: { ...((bucket.restrictions || {})[rpId] || {}), ...patch },
                          },
                        });
                        return (
                          <div key={rtId} data-testid={`room-type-row-${rtId}`}>
                            {/* Accordion header */}
                            <button
                              type="button"
                              onClick={() => setRatesFormData(prev => ({
                                ...prev,
                                rtExpanded: { ...(prev.rtExpanded || {}), [rtId]: !isOpen },
                              }))}
                              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
                              data-testid={`room-type-toggle-${rtId}`}
                            >
                              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-0' : '-rotate-90'}`} />
                              <span className="flex-1 text-sm font-medium text-gray-800">{rt.room_type_name}</span>
                              <span className="text-[11px] text-gray-500">max {rt.max_occupancy} · {totalPlans} plan{totalPlans === 1 ? '' : 's'}</span>
                              {filledPlans > 0 && (
                                <span className="text-[10px] bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5">
                                  {filledPlans}/{totalPlans}
                                </span>
                              )}
                            </button>

                            {isOpen && (
                              <div className="px-3 pb-3 pt-1 space-y-3 bg-gray-50" data-testid={`room-type-body-${rtId}`}>
                                {/* Rate plans */}
                                {totalPlans === 0 ? (
                                  <div className="text-[11px] text-gray-500 py-1">No rate plans on this room type.</div>
                                ) : (
                                  <div className="space-y-1">
                                    <div className="text-[11px] font-semibold text-gray-700">Rate Plans</div>
                                    {(rt.rate_plans || []).map(rp => (
                                      <div key={rp.id} className="flex items-center gap-2">
                                        <label className="flex-1 text-[12px] text-gray-700 truncate">
                                          {rp.name || rp.code || rp.id}
                                          {rp.code && rp.name && <span className="text-[10px] text-gray-400"> · {rp.code}</span>}
                                        </label>
                                        <Input
                                          type="number"
                                          value={rpMap[rp.id] || ''}
                                          onChange={(e) => setRatePlan(rp.id, e.target.value)}
                                          placeholder="₹ rate"
                                          min="0"
                                          step="1"
                                          className="w-32 h-8 text-xs"
                                          data-testid={`rt-plan-rate-${rtId}-${rp.id}`}
                                        />
                                      </div>
                                    ))}
                                    <p className="text-[10px] text-gray-500">Applied equally to every occupancy tier (1..{rt.max_occupancy}) for that plan only.</p>
                                  </div>
                                )}

                                {/* Extra guest pricing */}
                                <div>
                                  <div className="text-[11px] font-semibold text-gray-700 mb-1">Extra Guest Pricing</div>
                                  <div className="grid grid-cols-4 gap-2">
                                    {[
                                      { key: 'adult',  label: 'Extra Adult' },
                                      { key: 'child1', label: 'Child 1' },
                                      { key: 'child2', label: 'Child 2' },
                                      { key: 'child3', label: 'Child 3' },
                                    ].map(({ key, label }) => (
                                      <div key={key}>
                                        <label className="block text-[10px] text-gray-600 mb-0.5">{label}</label>
                                        <Input
                                          type="number"
                                          value={(bucket.extras || {})[key] || ''}
                                          onChange={(e) => setExtras(key, e.target.value)}
                                          placeholder="₹"
                                          min="0"
                                          step="1"
                                          className="h-8 text-xs"
                                          data-testid={`rt-extra-${key}-${rtId}`}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Restrictions */}
                                {totalPlans > 0 && (
                                  <div>
                                    <div className="text-[11px] font-semibold text-gray-700 mb-1">Set Rate Restrictions</div>
                                    <div className="space-y-2">
                                      {(rt.rate_plans || []).map(rp => {
                                        const r = ((bucket.restrictions || {})[rp.id]) || {};
                                        const _tri = (v) => v === true ? 'true' : v === false ? 'false' : 'no_change';
                                        const _triSet = (k, sel) => setRestr(rp.id, { [k]: sel === 'true' ? true : sel === 'false' ? false : undefined });
                                        return (
                                          <div key={rp.id} className="border border-gray-200 rounded p-2 bg-white space-y-2" data-testid={`rt-restr-${rtId}-${rp.id}`}>
                                            <div className="text-[11px] font-semibold text-gray-800">{rp.name || rp.code || rp.id}</div>

                                            {/* Tri-state booleans — mirror bulk drawer field set exactly */}
                                            <div className="grid grid-cols-3 gap-2 text-[10px]">
                                              {[
                                                { k: 'closed',                label: 'Closed',              testid: 'closed' },
                                                { k: 'closed_to_arrival',     label: 'Closed to Arrival',   testid: 'cta' },
                                                { k: 'closed_to_departure',   label: 'Closed to Departure', testid: 'ctd' },
                                              ].map(({ k, label, testid }) => (
                                                <div key={k}>
                                                  <label className="block text-[10px] text-gray-600 mb-0.5">{label}</label>
                                                  <Select
                                                    value={_tri(r[k])}
                                                    onValueChange={(v) => _triSet(k, v)}
                                                  >
                                                    <SelectTrigger className="h-7 text-[10px]" data-testid={`rt-restr-${testid}-${rtId}-${rp.id}`}>
                                                      <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                      <SelectItem value="no_change">No Change</SelectItem>
                                                      <SelectItem value="true">True</SelectItem>
                                                      <SelectItem value="false">False</SelectItem>
                                                    </SelectContent>
                                                  </Select>
                                                </div>
                                              ))}
                                            </div>

                                            {/* LOS Stay-Based */}
                                            <div className="grid grid-cols-2 gap-2">
                                              <div>
                                                <label className="block text-[10px] text-gray-600 mb-0.5">Min LOS (Stay-Based)</label>
                                                <Input
                                                  type="number" min="1" max="450" className="h-7 text-[10px]"
                                                  value={r.min_los_stay_based ?? r.min_los ?? ''}
                                                  onChange={(e) => setRestr(rp.id, { min_los_stay_based: e.target.value })}
                                                  placeholder="—"
                                                  data-testid={`rt-restr-min-los-stay-${rtId}-${rp.id}`}
                                                />
                                              </div>
                                              <div>
                                                <label className="block text-[10px] text-gray-600 mb-0.5">Max LOS (Stay-Based)</label>
                                                <Input
                                                  type="number" min="1" max="450" className="h-7 text-[10px]"
                                                  value={r.max_los_stay_based ?? r.max_los ?? ''}
                                                  onChange={(e) => setRestr(rp.id, { max_los_stay_based: e.target.value })}
                                                  placeholder="—"
                                                  data-testid={`rt-restr-max-los-stay-${rtId}-${rp.id}`}
                                                />
                                              </div>
                                            </div>

                                            {/* LOS Arrival-Based */}
                                            <div className="grid grid-cols-2 gap-2">
                                              <div>
                                                <label className="block text-[10px] text-gray-600 mb-0.5">Min LOS (Arrival-Based)</label>
                                                <Input
                                                  type="number" min="1" max="450" className="h-7 text-[10px]"
                                                  value={r.min_los_arrival_based ?? ''}
                                                  onChange={(e) => setRestr(rp.id, { min_los_arrival_based: e.target.value })}
                                                  placeholder="—"
                                                  data-testid={`rt-restr-min-los-arr-${rtId}-${rp.id}`}
                                                />
                                              </div>
                                              <div>
                                                <label className="block text-[10px] text-gray-600 mb-0.5">Max LOS (Arrival-Based)</label>
                                                <Input
                                                  type="number" min="1" max="450" className="h-7 text-[10px]"
                                                  value={r.max_los_arrival_based ?? ''}
                                                  onChange={(e) => setRestr(rp.id, { max_los_arrival_based: e.target.value })}
                                                  placeholder="—"
                                                  data-testid={`rt-restr-max-los-arr-${rtId}-${rp.id}`}
                                                />
                                              </div>
                                            </div>

                                            {/* Advance Booking Offsets */}
                                            <div className="grid grid-cols-2 gap-2">
                                              <div>
                                                <label className="block text-[10px] text-gray-600 mb-0.5">Min Advance Booking Offset</label>
                                                <Input
                                                  type="number" className="h-7 text-[10px]"
                                                  value={r.min_advance_booking_offset ?? r.min_advance_booking ?? ''}
                                                  onChange={(e) => setRestr(rp.id, { min_advance_booking_offset: e.target.value })}
                                                  placeholder="—"
                                                  data-testid={`rt-restr-min-abo-${rtId}-${rp.id}`}
                                                />
                                              </div>
                                              <div>
                                                <label className="block text-[10px] text-gray-600 mb-0.5">Max Advance Booking Offset</label>
                                                <Input
                                                  type="number" className="h-7 text-[10px]"
                                                  value={r.max_advance_booking_offset ?? ''}
                                                  onChange={(e) => setRestr(rp.id, { max_advance_booking_offset: e.target.value })}
                                                  placeholder="—"
                                                  data-testid={`rt-restr-max-abo-${rtId}-${rp.id}`}
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ) : ratesFormData.updateMode === 'deviation' ? (
                (() => {
                  const selectedRoom = (agodaDeviationRooms || []).find(
                    r => r.room_type_id === ratesFormData.deviationRoomId
                  );
                  const maxOcc = selectedRoom?.effective_max_occupancy || 0;
                  const plans = selectedRoom?.rate_plans || [];
                  return (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Room Type *</label>
                        <Select
                          value={ratesFormData.deviationRoomId || ''}
                          onValueChange={(v) => setRatesFormData({
                            ...ratesFormData,
                            deviationRoomId: v,
                            deviationRatePlanId: '',
                            occupancyPercentages: {},
                          })}
                        >
                          <SelectTrigger data-testid="apply-deviation-room-select">
                            <SelectValue placeholder={
                              (agodaDeviationRooms || []).length === 0
                                ? 'No Agoda-mapped rooms found'
                                : 'Select room type'
                            } />
                          </SelectTrigger>
                          <SelectContent>
                            {(agodaDeviationRooms || []).map(r => (
                              <SelectItem key={r.room_type_id} value={r.room_type_id}>
                                {r.room_type_name}
                                <span className="text-xs text-gray-400"> (max {r.effective_max_occupancy})</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {(agodaDeviationRooms || []).length === 0 && (
                          <p className="text-xs text-amber-600 mt-1">
                            Add an Agoda mapping in <strong>Property Settings → OTA Mappings</strong> first.
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Rate Plan *</label>
                        <Select
                          value={ratesFormData.deviationRatePlanId || ''}
                          onValueChange={(v) => setRatesFormData({ ...ratesFormData, deviationRatePlanId: v })}
                          disabled={!selectedRoom || plans.length === 0}
                        >
                          <SelectTrigger data-testid="apply-deviation-plan-select">
                            <SelectValue placeholder={
                              !selectedRoom
                                ? 'Select a room type first'
                                : plans.length === 0
                                  ? 'No rate plans on this room'
                                  : 'Select rate plan'
                            } />
                          </SelectTrigger>
                          <SelectContent>
                            {plans.map(p => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name}{p.code ? ` (${p.code})` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Base Price (₹) *</label>
                        <Input
                          type="number"
                          value={ratesFormData.basePrice || ''}
                          onChange={(e) => setRatesFormData({ ...ratesFormData, basePrice: e.target.value })}
                          placeholder="6000"
                          min="0"
                          step="1"
                          required
                          data-testid="apply-deviation-base-price"
                        />
                      </div>

                      {selectedRoom && maxOcc > 0 && (
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Occupancy Deviation (%) *
                          </label>
                          <p className="text-xs text-gray-500">
                            Positive or negative percentage applied to base price per person count.
                          </p>
                          {Array.from({ length: maxOcc }, (_, i) => i + 1).map(person => (
                            <div key={person} className="flex items-center gap-2">
                              <label className="flex-1 text-sm text-gray-700">
                                {person} {person === 1 ? 'person' : 'people'}
                              </label>
                              <Input
                                type="number"
                                value={(ratesFormData.occupancyPercentages || {})[person] ?? ''}
                                onChange={(e) => setRatesFormData({
                                  ...ratesFormData,
                                  occupancyPercentages: {
                                    ...(ratesFormData.occupancyPercentages || {}),
                                    [person]: e.target.value,
                                  },
                                })}
                                placeholder="0"
                                step="0.01"
                                className="w-28"
                                data-testid={`apply-deviation-pct-${person}`}
                              />
                              <span className="text-sm text-gray-500 w-6">%</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Base Rate (₹) *</label>
                  <Input
                    type="number"
                    value={ratesFormData.baseRate}
                    onChange={(e) => setRatesFormData({...ratesFormData, baseRate: e.target.value})}
                    placeholder="6000"
                    min="0"
                    step="1"
                    required
                    data-testid="apply-base-rate-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Applied as a single base rate across every mapped room type. Rate Plan × Occupancy
                    rates are auto-derived from each room type's <strong>Settings → Rate Configuration</strong>.
                  </p>
                </div>
              )}
              </div>
              <div
                className={
                  ratesFormData.updateMode === 'room_types'
                    ? 'shrink-0 border-t bg-white px-6 py-3 flex gap-3'
                    : 'flex gap-3 pt-4'
                }
                data-testid="rates-popup-footer"
              >
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowRatesPopup(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600"
                  data-testid="apply-base-submit-btn"
                >
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  const getOTALogo = (otaType) => {
    const logoStyle = "w-16 h-10 object-contain rounded border bg-white p-1.5";
    
    // Handle null or undefined otaType
    if (!otaType) {
      return <div className={`${logoStyle} bg-gray-100 text-gray-600 text-xs font-bold flex items-center justify-center`}>OTA</div>;
    }
    
    // Using actual OTA logo images
    switch (otaType.toLowerCase()) {
      case 'mmt':
      case 'makemytrip':
        return <img src="/assets/ota/mmt.png" alt="MakeMyTrip" className={logoStyle} />;
      case 'booking.com':
      case 'booking':
      case 'booking_com':
        return <img src="/assets/ota/booking.png" alt="Booking.com" className={logoStyle} />;
      case 'expedia':
        return <img src="/assets/ota/expedia.png" alt="Expedia" className={logoStyle} />;
      case 'airbnb':
        return <div className={`${logoStyle} bg-pink-100 text-pink-600 text-xs font-bold flex items-center justify-center`}>AIR</div>;
      case 'agoda':
        return <img src="/assets/ota/agoda.png" alt="Agoda" className={logoStyle} />;
      case 'yatra':
        return <div className={`${logoStyle} bg-orange-100 text-orange-600 text-xs font-bold flex items-center justify-center`}>YTR</div>;
      case 'goibibo':
        return <img src="/assets/ota/goibibo.png" alt="Goibibo" className={logoStyle} />;
      default:
        return <div className={`${logoStyle} bg-gray-100 text-gray-600 text-xs font-bold flex items-center justify-center`}>OTA</div>;
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      toast.success('Booking deleted successfully!');
      setShowDeleteConfirm(null);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const handleEditBooking = (booking) => {
    setSelectedBookingForEdit(booking);
    setNewBookingData({
      bookingDate: booking.created_at?.split('T')[0] || '',
      bookingId: booking.ota_booking_id,
      checkIn: booking.check_in_date,
      checkOut: booking.check_out_date,
      otaName: booking.ota_type,
      rate: booking.total_amount / booking.nights / booking.rooms_booked,
      guestName: booking.guest_name,
      guestEmail: booking.guest_email,
      guestPhone: booking.guest_phone,
      roomType: booking.room_type_id,
      numberOfRooms: booking.rooms_booked,
      status: booking.status,
      _bookingId: booking.id  // Store the actual booking ID for updates
    });
    setShowCreateBooking(true);
  };

  const handleCreateBooking = async () => {
    if (!newBookingData.guestName || !newBookingData.checkIn || !newBookingData.checkOut || !newBookingData.otaName) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      // Calculate nights
      const checkIn = new Date(newBookingData.checkIn);
      const checkOut = new Date(newBookingData.checkOut);
      const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));

      const bookingPayload = {
        property_id: selectedProperty.id,
        room_type_id: newBookingData.roomType || roomTypes[0]?.id,
        ota_type: newBookingData.otaName.toLowerCase(),
        ota_booking_id: newBookingData.bookingId || `BK-${Date.now()}`,
        guest_name: newBookingData.guestName,
        guest_email: newBookingData.guestEmail,
        guest_phone: newBookingData.guestPhone,
        check_in_date: newBookingData.checkIn,
        check_out_date: newBookingData.checkOut,
        rooms_booked: parseInt(newBookingData.numberOfRooms),
        total_amount: parseFloat(newBookingData.rate) * nights * parseInt(newBookingData.numberOfRooms),
        status: newBookingData.status || 'confirmed'
      };

      // Check if we're updating or creating
      if (newBookingData._bookingId) {
        // Update existing booking
        await axios.put(`${API}/bookings/${newBookingData._bookingId}`, bookingPayload);
        toast.success('Booking updated successfully!');
      } else {
        // Create new booking
        await axios.post(`${API}/bookings/simulate`, bookingPayload);
        toast.success('Booking created successfully!');
      }
      
      setShowCreateBooking(false);
      setSelectedBookingForEdit(null);
      
      // Reset form
      setNewBookingData({
        bookingDate: '',
        bookingId: '',
        checkIn: '',
        checkOut: '',
        otaName: '',
        rate: '',
        guestName: '',
        guestEmail: '',
        guestPhone: '',
        roomType: '',
        numberOfRooms: 1,
        status: 'confirmed'
      });

      // Refresh bookings
      fetchBookings();
    } catch (error) {
      toast.error(`Failed to ${newBookingData._bookingId ? 'update' : 'create'} booking`);
    }
  };

  // Helper function to format date
  const formatBookingDate = (dateValue) => {
    if (!dateValue) return '-';
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return dateValue; // Return as is if invalid
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return dateValue;
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (bookingsTotalPages <= maxVisible) {
      for (let i = 1; i <= bookingsTotalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      // Calculate middle pages
      let start = Math.max(2, bookingsPage - 1);
      let end = Math.min(bookingsTotalPages - 1, bookingsPage + 1);
      
      if (start > 2) pages.push('...');
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (end < bookingsTotalPages - 1) pages.push('...');
      
      // Always show last page
      pages.push(bookingsTotalPages);
    }
    
    return pages;
  };

  const renderEnhancedBookings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Bookings</h3>
          <p className="text-sm text-gray-600">
            Live OTA Booking Sync — Receiving bookings from connected OTA APIs
            {lastGoogleSheetsUpdate && <span className="ml-2 text-xs text-gray-500">
              Last Sync: {new Date(lastGoogleSheetsUpdate).toLocaleString()}
            </span>}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Badge variant="secondary" data-testid="bookings-count-badge">
            Showing {googleSheetsBookings.length} of {bookingsTotalCount} bookings
          </Badge>
          <Button 
            variant="outline"
            size="sm"
            onClick={() => fetchGoogleSheetsBookings(bookingsPage, bookingsLimit)}
            disabled={isLoadingGoogleSheets}
            data-testid="refresh-bookings-btn"
          >
            {isLoadingGoogleSheets ? (
              <>Loading...</>
            ) : (
              <>
                <RefreshCcw className="w-4 h-4 mr-2" />
                Refresh
              </>
            )}
          </Button>
          <Button 
            size="sm"
            onClick={triggerBookingsSync}
            disabled={isLoadingGoogleSheets}
            className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Sync Now
          </Button>
        </div>
      </div>

      {/* Global Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          type="text"
          placeholder="Search by Booking ID, Guest Name, Email, Phone, OTA, or Hotel..."
          value={bookingsSearchQuery}
          onChange={(e) => setBookingsSearchQuery(e.target.value)}
          className="pl-10 pr-4 py-2 w-full text-sm border-gray-300 focus:border-orange-400 focus:ring-orange-400"
          data-testid="bookings-search-input"
        />
        {bookingsSearchQuery && (
          <button
            onClick={() => setBookingsSearchQuery('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Filters - Remove sticky positioning */}
      <Card className="border-gray-200 shadow-sm bg-white bookings-filter-card">
        <CardHeader className="pb-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-gray-800">Filter Bookings</CardTitle>
            {(bookingsOTAFilter.length > 0 || bookingsStatusFilter.length > 0 || bookingsHotelFilter.length > 0 || bookingsDateRange.start || bookingsDateRange.end || bookingsSearchQuery) && (
              <Badge variant="secondary" className="bg-blue-50 text-blue-700">
                {[
                  bookingsOTAFilter.length > 0 && 'OTA',
                  bookingsStatusFilter.length > 0 && 'Status',
                  bookingsHotelFilter.length > 0 && 'Hotel',
                  (bookingsDateRange.start || bookingsDateRange.end) && 'Date',
                  bookingsSearchQuery && 'Search'
                ].filter(Boolean).length} active
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* OTA Filter - Multi-select with search */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                OTA Platform
              </label>
              <Popover open={otaFilterOpen} onOpenChange={setOtaFilterOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={otaFilterOpen}
                    className="w-full justify-between hover:border-blue-400 transition-colors font-normal"
                    data-testid="ota-filter"
                  >
                    {bookingsOTAFilter.length === 0
                      ? "All OTAs"
                      : bookingsOTAFilter.length === 1
                        ? bookingsOTAFilter[0]
                        : `${bookingsOTAFilter.length} OTAs selected`}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-0" align="start">
                  <div className="p-2 border-b">
                    <Input
                      placeholder="Search OTAs..."
                      value={otaFilterSearch}
                      onChange={(e) => setOtaFilterSearch(e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <div className="max-h-[200px] overflow-y-auto p-2">
                    {bookingsFilterOptions.otas
                      .filter(ota => 
                        ota && typeof ota === 'string' && ota.toLowerCase().includes(otaFilterSearch.toLowerCase())
                      )
                      .map((ota) => (
                        <div
                          key={ota}
                          className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                          onClick={() => {
                            setBookingsOTAFilter(prev => 
                              prev.includes(ota)
                                ? prev.filter(o => o !== ota)
                                : [...prev, ota]
                            );
                          }}
                        >
                          <Checkbox
                            checked={bookingsOTAFilter.includes(ota)}
                            className="pointer-events-none"
                          />
                          <span className="text-sm truncate">{ota}</span>
                        </div>
                      ))}
                  </div>
                  {bookingsOTAFilter.length > 0 && (
                    <div className="p-2 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs"
                        onClick={() => setBookingsOTAFilter([])}
                      >
                        Clear selection ({bookingsOTAFilter.length})
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
              {bookingsOTAFilter.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {bookingsOTAFilter.slice(0, 2).map(ota => (
                    <Badge 
                      key={ota} 
                      variant="secondary" 
                      className="text-xs bg-blue-50 text-blue-700 cursor-pointer hover:bg-blue-100"
                      onClick={() => setBookingsOTAFilter(prev => prev.filter(o => o !== ota))}
                    >
                      {ota}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                  {bookingsOTAFilter.length > 2 && (
                    <Badge variant="secondary" className="text-xs bg-gray-100">
                      +{bookingsOTAFilter.length - 2}
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {/* Status Filter - Multi-select with search */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Booking Status
              </label>
              <Popover open={statusFilterOpen} onOpenChange={setStatusFilterOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={statusFilterOpen}
                    className="w-full justify-between hover:border-blue-400 transition-colors font-normal"
                    data-testid="status-filter"
                  >
                    {bookingsStatusFilter.length === 0
                      ? "All Statuses"
                      : bookingsStatusFilter.length === 1
                        ? bookingsStatusFilter[0]
                        : `${bookingsStatusFilter.length} statuses selected`}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-0" align="start">
                  <div className="p-2 border-b">
                    <Input
                      placeholder="Search statuses..."
                      value={statusFilterSearch}
                      onChange={(e) => setStatusFilterSearch(e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <div className="max-h-[200px] overflow-y-auto p-2">
                    {bookingsFilterOptions.statuses
                      .filter(status => 
                        status && typeof status === 'string' && status.toLowerCase().includes(statusFilterSearch.toLowerCase())
                      )
                      .map((status) => (
                        <div
                          key={status}
                          className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                          onClick={() => {
                            setBookingsStatusFilter(prev => 
                              prev.includes(status)
                                ? prev.filter(s => s !== status)
                                : [...prev, status]
                            );
                          }}
                        >
                          <Checkbox
                            checked={bookingsStatusFilter.includes(status)}
                            className="pointer-events-none"
                          />
                          <span className="text-sm truncate capitalize">{status}</span>
                        </div>
                      ))}
                  </div>
                  {bookingsStatusFilter.length > 0 && (
                    <div className="p-2 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs"
                        onClick={() => setBookingsStatusFilter([])}
                      >
                        Clear selection ({bookingsStatusFilter.length})
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
              {bookingsStatusFilter.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {bookingsStatusFilter.map(status => (
                    <Badge 
                      key={status} 
                      variant="secondary" 
                      className={`text-xs cursor-pointer ${
                        status.toLowerCase() === 'cancelled' ? 'bg-red-50 text-red-700' :
                        status.toLowerCase() === 'amended' ? 'bg-yellow-50 text-yellow-700' :
                        'bg-green-50 text-green-700'
                      }`}
                      onClick={() => setBookingsStatusFilter(prev => prev.filter(s => s !== status))}
                    >
                      {status}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Hotel/Property filter removed (Feb 2026). Property scoping is
                now driven exclusively by the global top PropertySelector. */}

            {/* Date Range Filter */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Check-In Date Range
              </label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={bookingsDateRange.start}
                  onChange={(e) => setBookingsDateRange({...bookingsDateRange, start: e.target.value})}
                  className="text-sm hover:border-blue-400 transition-colors"
                  placeholder="Start date"
                />
                <Input
                  type="date"
                  value={bookingsDateRange.end}
                  onChange={(e) => setBookingsDateRange({...bookingsDateRange, end: e.target.value})}
                  className="text-sm hover:border-blue-400 transition-colors"
                  placeholder="End date"
                />
              </div>
            </div>
          </div>

          {/* Filter Actions */}
          {(bookingsOTAFilter.length > 0 || bookingsStatusFilter.length > 0 || bookingsHotelFilter.length > 0 || bookingsDateRange.start || bookingsDateRange.end || bookingsSearchQuery) && (
            <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Filters applied - Showing {googleSheetsBookings.length} of {bookingsTotalCount} unique bookings
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setBookingsOTAFilter([]);
                    setBookingsStatusFilter([]);
                    setBookingsHotelFilter([]);
                    setBookingsDateRange({ start: '', end: '' });
                    setBookingsSearchQuery('');
                  }}
                  className="hover:bg-gray-50"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear All Filters
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Message */}
      {googleSheetsError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-red-900 mb-1">Failed to load bookings</h4>
                <p className="text-sm text-red-700">{googleSheetsError}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={fetchGoogleSheetsBookings}
                >
                  Try Again
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bookings Table */}
      <Card>
        <CardContent className="p-0">
          {isLoadingGoogleSheets && googleSheetsBookings.length === 0 ? (
            <div className="text-center py-12">
              <RefreshCcw className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-spin" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Loading Bookings...</h3>
              <p className="text-gray-600">Fetching data from database</p>
            </div>
          ) : googleSheetsBookings.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Bookings Found</h3>
              <p className="text-gray-600">
                {bookingsTotalCount === 0 
                  ? "No bookings synced yet. Click 'Sync Now' to fetch the latest bookings from your connected OTAs." 
                  : "No bookings match your current filters"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Loading overlay */}
              {isLoadingGoogleSheets && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                  <RefreshCcw className="w-8 h-8 text-orange-500 animate-spin" />
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">S.No</TableHead>
                    <TableHead className="font-semibold">Booking Date</TableHead>
                    <TableHead className="font-semibold">Booking ID</TableHead>
                    <TableHead className="font-semibold">Hotel</TableHead>
                    <TableHead className="font-semibold">Guest Name</TableHead>
                    <TableHead className="font-semibold">Check-In</TableHead>
                    <TableHead className="font-semibold">Check-Out</TableHead>
                    <TableHead className="font-semibold">Room Type</TableHead>
                    <TableHead className="font-semibold">OTA</TableHead>
                    <TableHead className="font-semibold text-right">Net Amount</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {googleSheetsBookings.map((booking, index) => {
                    // Use the Google Sheet Booking ID (which is now in booking.booking_id after backend grouping)
                    const sheetBookingId = booking.booking_id || booking['Booking/Reservation ID'];
                    // Vendor Booking ID (display-only): prefer the MMT-parsed field on the doc,
                    // then the raw_data mirror, then fall back to the internal Booking ID so the
                    // UI never renders an empty cell. No booking record is mutated.
                    const vendorBookingId =
                      booking.vendor_booking_id ||
                      booking['Vendor Booking ID'] ||
                      booking.raw_data?.['Vendor Booking ID'] ||
                      sheetBookingId;
                    // Net Amount (display-only): prefer the top-level canonical `net_amount`,
                    // then raw_data mirror `Net Amount`, then fall back to the legacy displayed
                    // value so nothing regresses when net isn't present on older records.
                    const netAmount =
                      booking.net_amount ??
                      booking['Net Amount'] ??
                      booking.raw_data?.['Net Amount'] ??
                      booking.final_amount ??
                      booking['Amount/Net Rate'] ??
                      0;
                    const finalStatus = booking.final_status || booking.Status;
                    const hasMultipleRecords = booking.record_count > 1;
                    
                    return (
                    <TableRow 
                      key={sheetBookingId || index} 
                      className={`hover:bg-gray-50 cursor-pointer ${hasMultipleRecords ? 'border-l-4 border-l-orange-400' : ''}`}
                      onClick={() => sheetBookingId && fetchBookingDetails(sheetBookingId)}
                    >
                      <TableCell className="text-sm">{((bookingsPage - 1) * bookingsLimit) + index + 1}</TableCell>
                      <TableCell className="text-sm">{formatBookingDate(booking['Booked/Email Date'] || booking.booked_date)}</TableCell>
                      <TableCell>
                        <div className="font-mono text-sm font-medium text-blue-600 hover:underline" data-testid={`booking-vendor-id-${sheetBookingId}`}>
                          {vendorBookingId}
                        </div>
                        {hasMultipleRecords && (
                          <div className="text-xs text-orange-600">{booking.record_count} records</div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium text-sm">{booking['Hotel Name'] || booking.hotel_name}</TableCell>
                      <TableCell className="text-sm">{booking['Guest Name'] || booking.guest_name}</TableCell>
                      <TableCell className="text-sm">{formatBookingDate(booking['Check-In'] || booking.check_in_date)}</TableCell>
                      <TableCell className="text-sm">{formatBookingDate(booking['Check-Out'] || booking.check_out_date)}</TableCell>
                      <TableCell className="text-sm">
                        <div>{booking['Room Type'] || booking.room_type}</div>
                        <div className="text-xs text-gray-500">{booking['No. of Rooms'] || booking.rooms_booked || 1} room(s)</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{booking.OTA || booking.ota}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="font-semibold text-green-600" data-testid={`booking-net-amount-${sheetBookingId}`}>
                          ₹{parseFloat(netAmount || 0).toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">{booking['Room Nights'] || booking.room_nights} nights</div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          className={
                            finalStatus?.toLowerCase() === 'cancelled' 
                              ? 'bg-red-100 text-red-700 border-red-300' 
                              : finalStatus?.toLowerCase() === 'amended'
                                ? 'bg-yellow-100 text-yellow-700 border-yellow-300'
                                : 'bg-green-100 text-green-700 border-green-300'
                          }
                        >
                          {finalStatus}
                          {hasMultipleRecords && booking.has_amendments && <span className="ml-1">*</span>}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1 h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            sheetBookingId && fetchBookingDetails(sheetBookingId);
                          }}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {booking['PDF URL'] && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1 h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(booking['PDF URL'], '_blank');
                            }}
                            title="View PDF"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Pagination Controls */}
          {bookingsTotalPages > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t bg-gray-50">
              {/* Page size selector and info */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Rows per page:</span>
                  <Select value={String(bookingsLimit)} onValueChange={(val) => handleBookingsLimitChange(Number(val))}>
                    <SelectTrigger className="w-20 h-8" data-testid="page-size-selector">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-sm text-gray-600" data-testid="pagination-info">
                  Showing {((bookingsPage - 1) * bookingsLimit) + 1}–{Math.min(bookingsPage * bookingsLimit, bookingsTotalCount)} of {bookingsTotalCount} bookings
                </span>
              </div>
              
              {/* Page navigation */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBookingsPageChange(bookingsPage - 1)}
                  disabled={bookingsPage <= 1 || isLoadingGoogleSheets}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Prev
                </Button>
                
                <div className="flex items-center gap-1">
                  {getPageNumbers().map((pageNum, idx) => (
                    pageNum === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>
                    ) : (
                      <Button
                        key={pageNum}
                        variant={bookingsPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleBookingsPageChange(pageNum)}
                        disabled={isLoadingGoogleSheets}
                        className={`w-8 h-8 p-0 ${bookingsPage === pageNum ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                        data-testid={`page-${pageNum}-btn`}
                      >
                        {pageNum}
                      </Button>
                    )
                  ))}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBookingsPageChange(bookingsPage + 1)}
                  disabled={bookingsPage >= bookingsTotalPages || isLoadingGoogleSheets}
                  data-testid="next-page-btn"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking Debug & XML Monitor (dev-only view of the MMT webhook flow).
          Hidden in "All Properties" mode — the portfolio view shows the
          bookings table only, per Feb 2026 nav redesign. In single-property
          mode we still surface it exactly as before. */}
      {!allPropertiesMode && <WebhookMonitor propertyId={selectedProperty?.id} />}
    </div>
  );

  const renderEnhancedSettings = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-800">Settings & Configuration</h3>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Property Management</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 settings-card-content">
          <div className="text-sm text-gray-600">
            Manage your properties, room types, and OTA mappings
          </div>
          <div className="flex gap-2 flex-wrap settings-buttons-mobile">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowPropertyModal(true)}
              data-testid="add-property-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Property
            </Button>
            {user?.role === 'admin' && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowAllPropertiesModal(true)}
                className="bg-blue-50 border-blue-200 hover:bg-blue-100"
                data-testid="see-all-properties-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                See All Properties
              </Button>
            )}
            {user?.role === 'admin' && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowCreateOwnerModal(true)}
                className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 hover:border-blue-300"
              >
                <Users className="w-4 h-4 mr-2" />
                Create Login for Hotel Owner
              </Button>
            )}
          </div>
          <div className="text-[11px] text-gray-500 italic pt-1">
            Add Room Type, Add Rate Plan, and Room Type Hierarchy have moved to
            <strong> Property Settings</strong>. Select a property from the top
            selector to manage them for that property.
          </div>
          {user?.role === 'admin' && (
            <OtaPropertyOnboarding apiBase={API} onCreated={fetchProperties} />
          )}
          {user?.role === 'admin' && (
            <div className="border-t pt-3 mt-1" data-testid="bulk-ota-sync-section">
              <div className="text-sm font-medium text-gray-700">OTA Sync (All Properties)</div>
              <div className="text-[11px] text-gray-500 mb-2">
                Turning a toggle OFF stops ARI sync (availability, rates, inventory) to that OTA for <strong>every</strong> property right now; ON re-enables it for all. Each property can still be changed individually afterwards from its Calendar.
              </div>
              <BulkOtaSyncToggles apiBase={API} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* City/State bulk update via Excel — only in "All Properties" mode */}
      {allPropertiesMode && (
        <Card data-testid="city-state-upload-card">
          <CardHeader>
            <CardTitle className="text-base">Update City &amp; State via Excel</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600">
              Upload an <strong>.xlsx</strong> / <strong>.csv</strong> file with columns
              <strong> Hotel Name</strong>, <strong>City</strong>, and <strong>State</strong>.
              Each row is matched to an existing property by Hotel Name (case-insensitive) and
              only its <strong>City</strong> and <strong>State</strong> are updated. No new
              properties are created.
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Input
                type="file"
                accept=".xlsx,.xls,.csv"
                disabled={cityStateUploading}
                onChange={(e) => setCityStateFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                className="max-w-sm text-sm"
                data-testid="city-state-file-input"
              />
              <Button
                type="button"
                onClick={uploadCityStateExcel}
                disabled={!cityStateFile || cityStateUploading}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white"
                data-testid="city-state-upload-btn"
              >
                {cityStateUploading ? 'Uploading…' : 'Upload Excel'}
              </Button>
            </div>
            {cityStateSummary && (
              <div className="rounded-md border bg-gray-50 p-3 text-sm space-y-1" data-testid="city-state-summary">
                <div>Rows in file: <strong data-testid="city-state-rows-total">{cityStateSummary.rows_total}</strong></div>
                <div className="text-emerald-700">Properties matched &amp; updated: <strong data-testid="city-state-updated">{cityStateSummary.properties_updated}</strong></div>
                <div className="text-amber-700">Rows with no matching property: <strong data-testid="city-state-unmatched">{cityStateSummary.rows_unmatched}</strong></div>
                {cityStateSummary.unmatched_names && cityStateSummary.unmatched_names.length > 0 && (
                  <div className="text-xs text-gray-500 pt-1">
                    Unmatched: {cityStateSummary.unmatched_names.join(', ')}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Import Base Rates via Excel — Admin only, All Properties mode */}
      {allPropertiesMode && user?.role === 'admin' && (
        <Card data-testid="ota-rate-multipliers-card">
          <CardHeader>
            <CardTitle className="text-base">OTA Rate Multipliers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600">
              Global multipliers applied across <strong>all properties</strong>. When a room rate is
              updated (bulk or individual cell edit), each OTA receives <strong>base rate × its multiplier</strong>
              — e.g. base 2000 with MMT ×2 pushes 4000. The stored base rate is unchanged, and an OTA that is
              toggled OFF for a property is skipped. Range 0.1–10; use 1 for no change.
            </div>
            <div className="grid grid-cols-3 gap-4 max-w-lg">
              {['mmt', 'agoda', 'yatra'].map((ota) => (
                <div key={ota}>
                  <Label className="uppercase text-xs">{ota}</Label>
                  <Input
                    type="number" step="0.1" min="0.1" max="10"
                    value={otaMultipliers[ota]}
                    onChange={(e) => setOtaMultipliers((p) => ({ ...p, [ota]: e.target.value }))}
                    className="mt-1"
                    data-testid={`ota-mult-${ota}-input`}
                  />
                </div>
              ))}
            </div>
            <Button type="button" onClick={saveOtaMultipliers} disabled={otaMultSaving}
              className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white"
              data-testid="ota-mult-save-btn">
              {otaMultSaving ? 'Saving…' : 'Save Multipliers'}
            </Button>
          </CardContent>
        </Card>
      )}

      {allPropertiesMode && user?.role === 'admin' && (
        <Card data-testid="base-rate-import-card">
          <CardHeader>
            <CardTitle className="text-base">Import Base Rates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600">
              Upload an <strong>.xlsx</strong> file with columns
              <strong> Hotel Code</strong>, <strong>Room Type Code</strong>, and
              <strong> Base Rate</strong> (one row per room type). Each row is matched
              by Agoda Hotel Code and Room Type Code. The Base Rate is written for every
              date from today through the next 365 days, <strong>skipping any date that
              already has a rate &gt; 0</strong>. No OTA push — database only.
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Input
                type="file"
                accept=".xlsx"
                disabled={baseRateUploading}
                onChange={(e) => setBaseRateFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                className="max-w-sm text-sm"
                data-testid="base-rate-file-input"
              />
              <Button
                type="button"
                onClick={uploadBaseRatesExcel}
                disabled={!baseRateFile || baseRateUploading}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white"
                data-testid="base-rate-import-btn"
              >
                {baseRateUploading ? 'Importing…' : 'Import Base Rates'}
              </Button>
            </div>
            {baseRateResult && (
              <div className="rounded-md border bg-gray-50 p-3 text-sm space-y-2" data-testid="base-rate-summary">
                <div className="flex flex-wrap gap-4">
                  <span>Rows: <strong data-testid="base-rate-total-rows">{baseRateResult.summary?.total_rows ?? 0}</strong></span>
                  <span className="text-emerald-700">Matched: <strong data-testid="base-rate-matched">{baseRateResult.summary?.matched_rows ?? 0}</strong></span>
                  <span className="text-amber-700">Unmatched: <strong data-testid="base-rate-unmatched">{baseRateResult.summary?.unmatched_rows ?? 0}</strong></span>
                  <span>Dates set: <strong data-testid="base-rate-dates-set">{baseRateResult.summary?.total_dates_set ?? 0}</strong></span>
                  <span>Dates skipped (existing): <strong data-testid="base-rate-dates-skipped">{baseRateResult.summary?.total_dates_skipped_existing ?? 0}</strong></span>
                </div>
                <div className="overflow-auto max-h-72 border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">Row</th>
                        <th className="px-2 py-1 text-left">Hotel Code</th>
                        <th className="px-2 py-1 text-left">Room Type Code</th>
                        <th className="px-2 py-1 text-left">Status</th>
                        <th className="px-2 py-1 text-right">Dates Set</th>
                        <th className="px-2 py-1 text-right">Skipped (existing)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(baseRateResult.rows || []).map((r, i) => (
                        <tr key={i} className="border-t" data-testid={`base-rate-row-${i}`}>
                          <td className="px-2 py-1">{r.row}</td>
                          <td className="px-2 py-1">{r.hotel_code}</td>
                          <td className="px-2 py-1">{r.room_type_code}</td>
                          <td className="px-2 py-1">
                            {r.matched
                              ? <span className="text-emerald-700 font-semibold">matched</span>
                              : <span className="text-amber-700" title={r.unmatched || ''}>unmatched{r.unmatched ? ` — ${r.unmatched}` : ''}</span>}
                          </td>
                          <td className="px-2 py-1 text-right">{r.dates_set}</td>
                          <td className="px-2 py-1 text-right">{r.dates_skipped_existing}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}



      {/* Bulk Property Import via Excel — Admin only */}
      {user?.role === 'admin' && (
        <Card data-testid="property-import-card">
          <CardHeader>
            <CardTitle className="text-base">Import Properties via Excel</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600">
              {importOta === 'agoda' ? (
                <>Upload an <strong>.xlsx</strong> file with columns <strong>Hotel Name</strong> and
                <strong> Hotel Code</strong>. Every row is processed in the background: property created →
                Agoda Supply API called (auth via OAuth2 — no bearer token needed) → room types &amp;
                rate plans imported. A single row&apos;s failure won&apos;t stop the rest.</>
              ) : importOta === 'yatra' ? (
                <>Upload an <strong>.xlsx</strong> file with columns <strong>Hotel Name</strong> and
                <strong> Hotel Code</strong>. Every row is processed in the background: property created →
                Yatra hotel detail read (auth via global Yatra credentials — no bearer token needed) → room types &amp;
                rate plans imported. A single row&apos;s failure won&apos;t stop the rest.</>
              ) : (
                <>Upload an <strong>.xlsx</strong> file with columns <strong>Hotel Name</strong>,
                <strong> Bearer Token</strong>, and <strong>Hotel Code</strong>. Every row is
                processed in the background: property created → MMT GetHotelListing called →
                room types &amp; rate plans imported. Bearer tokens are stored encrypted; a single
                row&apos;s failure won&apos;t stop the rest.</>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">OTA</label>
                <Select
                  value={importOta}
                  onValueChange={setImportOta}
                  disabled={importUploading || (importJobStatus && ['pending','running'].includes(importJobStatus.status))}
                >
                  <SelectTrigger className="w-36" data-testid="property-import-ota-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mmt" data-testid="property-import-ota-mmt">MMT</SelectItem>
                    <SelectItem value="agoda" data-testid="property-import-ota-agoda">Agoda</SelectItem>
                    <SelectItem value="yatra" data-testid="property-import-ota-yatra">Yatra</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input
                type="file"
                accept=".xlsx,.xls"
                key={importOta}
                disabled={importUploading || (importJobStatus && importJobStatus.status === 'running')}
                onChange={(e) => setImportFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                className="max-w-sm text-sm"
                data-testid="property-import-file-input"
              />
              <Button
                type="button"
                onClick={uploadImportExcel}
                disabled={!importFile || importUploading || (importJobStatus && ['pending','running'].includes(importJobStatus.status))}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white"
                data-testid="property-import-upload-btn"
              >
                {importUploading ? 'Uploading…' : 'Upload & Start Import'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={downloadImportTemplate}
                data-testid="property-import-template-btn"
              >
                Download Excel Template
              </Button>
              {importJobId && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetImportJob}
                  data-testid="property-import-reset-btn"
                >
                  Reset
                </Button>
              )}
            </div>

            {importJobStatus && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="font-semibold">Job status:</span>
                  <span
                    data-testid="property-import-job-status"
                    className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                      importJobStatus.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      importJobStatus.status === 'failed'    ? 'bg-red-100 text-red-700' :
                      importJobStatus.status === 'running'   ? 'bg-blue-100 text-blue-700 animate-pulse' :
                                                               'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {importJobStatus.status}
                  </span>
                  <span className="text-gray-600" data-testid="property-import-progress">
                    {importJobStatus.processed_rows || 0} / {importJobStatus.total_rows || 0} processed
                  </span>
                  {importJobStatus.error && (
                    <span className="text-red-600 text-xs">{importJobStatus.error}</span>
                  )}
                </div>

                {(importJobStatus.rows || []).length > 0 && (
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-3 py-2 text-left">#</th>
                          <th className="px-3 py-2 text-left">Hotel Name</th>
                          <th className="px-3 py-2 text-left">Hotel Code</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-left">Rooms</th>
                          <th className="px-3 py-2 text-left">Rate Plans</th>
                          <th className="px-3 py-2 text-left">Detail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(importJobStatus.rows || []).map((r) => (
                          <tr key={r.index} className="border-t" data-testid={`property-import-row-${r.index}`}>
                            <td className="px-3 py-2">{r.index + 1}</td>
                            <td className="px-3 py-2">{r.hotel_name}</td>
                            <td className="px-3 py-2 font-mono text-xs">{r.hotel_code}</td>
                            <td className="px-3 py-2">
                              <span
                                data-testid={`property-import-row-status-${r.index}`}
                                className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                                  r.status === 'done'              ? 'bg-emerald-100 text-emerald-700' :
                                  r.status === 'failed'            ? 'bg-red-100 text-red-700' :
                                  r.status === 'skipped_duplicate' ? 'bg-yellow-100 text-yellow-700' :
                                  r.status === 'fetching_codes'    ? 'bg-blue-100 text-blue-700' :
                                  r.status === 'creating_property' ? 'bg-amber-100 text-amber-700' :
                                  r.status === 'pending'           ? 'bg-gray-100 text-gray-700' :
                                                                     'bg-slate-100 text-slate-700'
                                }`}
                              >
                                {r.status === 'done' ? 'done' :
                                 r.status === 'failed' ? 'failed' :
                                 r.status === 'skipped_duplicate' ? 'skipped' :
                                 r.status === 'fetching_codes' ? 'fetching codes' :
                                 r.status === 'creating_property' ? 'creating' :
                                 r.status === 'pending' ? 'pending' :
                                 (r.status || 'pending')}
                              </span>
                            </td>
                            <td className="px-3 py-2">{r.room_types_count || 0}</td>
                            <td className="px-3 py-2">{r.rate_plans_count || 0}</td>
                            <td className="px-3 py-2 text-xs text-gray-600">
                              {r.error ? (
                                <span className="text-red-600" title={r.error}>{r.error.slice(0, 80)}{r.error.length > 80 ? '…' : ''}</span>
                              ) : r.status === 'skipped_duplicate' ? (
                                <span className="text-yellow-700" title={r.note || r.skipped_reason || 'skipped — duplicate'}>
                                  {r.skipped_reason || 'duplicate'}
                                </span>
                              ) : r.property_id ? (
                                <span className="font-mono">{r.property_id.slice(0, 8)}…</span>
                              ) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Room Type Hierarchy — moved to Property Settings (Feb 2026 redesign).
          The card and all its handlers are unchanged; only its render location
          moved. See renderPropertySettings(). */}

      {/* Global Rate Plan Hierarchy — applies to ALL properties */}
      {user?.role === 'admin' && (
        <Card data-testid="rate-plan-hierarchy-card">
          <CardHeader>
            <CardTitle className="text-base">Global Rate Plan Hierarchy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600">
              Order the rate plans from <strong>Lowest (Base = EP)</strong> to <strong>Highest</strong>. This ordering is used across
              <strong> every property</strong>. Within each room type, higher rate plans are derived from EP using that room's
              <em> Rate Plan Increment</em>.
            </div>
            {ratePlanOrderLoading ? (
              <div className="text-sm text-gray-500">Loading…</div>
            ) : (
              <>
                <div className="space-y-2">
                  {ratePlanOrder.map((code, idx) => (
                    <div
                      key={code}
                      className="flex items-center gap-3 p-3 border rounded-lg bg-white"
                      data-testid={`rate-plan-hierarchy-row-${code}`}
                    >
                      <div className="flex flex-col items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-800">{labelForRatePlanCode(code)}</div>
                        <div className="text-xs text-gray-500">
                          {idx === 0 ? 'LOWEST · Base plan (receives room EP)' :
                           idx === ratePlanOrder.length - 1 ? 'HIGHEST' : `Tier ${idx + 1}`}
                          {code !== labelForRatePlanCode(code) && (
                            <span className="ml-1 font-mono text-[10px] text-gray-400">· {code}</span>
                          )}
                        </div>
                      </div>
                      <Button type="button" size="sm" variant="outline"
                        onClick={() => moveRatePlanOrder(idx, -1)} disabled={idx === 0}
                        className="px-2" data-testid={`rate-plan-hierarchy-up-${code}`}>↑</Button>
                      <Button type="button" size="sm" variant="outline"
                        onClick={() => moveRatePlanOrder(idx, 1)} disabled={idx === ratePlanOrder.length - 1}
                        className="px-2" data-testid={`rate-plan-hierarchy-down-${code}`}>↓</Button>
                      <Button type="button" size="sm" variant="ghost"
                        onClick={() => removeRatePlanFromOrder(code)}
                        className="px-2 text-red-600 hover:text-red-700"
                        data-testid={`rate-plan-hierarchy-remove-${code}`}>×</Button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    value={newRatePlanCode}
                    onChange={(e) => setNewRatePlanCode(e.target.value.toUpperCase())}
                    placeholder="Add custom code (e.g., BB)"
                    className="max-w-xs text-sm"
                    data-testid="rate-plan-hierarchy-add-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRatePlanToOrder(); } }}
                  />
                  <Button type="button" size="sm" variant="outline" onClick={addRatePlanToOrder}
                    data-testid="rate-plan-hierarchy-add-btn">Add</Button>
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={saveRatePlanOrder}
                    disabled={ratePlanOrderSaving}
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white"
                    data-testid="save-rate-plan-hierarchy-btn"
                  >
                    {ratePlanOrderSaving ? 'Saving…' : 'Save Rate Plan Hierarchy'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab Settings */}
      {user?.role === 'admin' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tab Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600 mb-4">
              Customize which tabs appear in your sidebar
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { id: 'dashboard', name: 'Dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
                { id: 'calendar', name: 'Calendar', icon: <CalendarDays className="w-4 h-4" /> },
                { id: 'bookings', name: 'Bookings', icon: <Users className="w-4 h-4" /> },
                { id: 'analytics', name: 'Analytics', icon: <BarChart3 className="w-4 h-4" /> },
                { id: 'promotions', name: 'Promotions', icon: <Tag className="w-4 h-4" /> },
                { id: 'competitor', name: 'Competitor Rates', icon: <TrendingUp className="w-4 h-4" /> },
              ].map(tab => (
                <div key={tab.id} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2">
                    {tab.icon}
                    <span className="font-medium text-sm">{tab.name}</span>
                  </div>
                  <Button
                    variant={hiddenTabs.includes(tab.id) ? "outline" : "default"}
                    size="sm"
                    onClick={() => toggleTabVisibility(tab.id)}
                    className={hiddenTabs.includes(tab.id) ? 'bg-white' : 'bg-green-500 hover:bg-green-600 text-white'}
                  >
                    {hiddenTabs.includes(tab.id) ? 'Show' : 'Hide'}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clear Analytics Data */}
      {user?.role === 'admin' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Clear Analytics Data</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600 mb-4">
              Remove analytics data for specific OTAs. This will only clear processed analytics, not uploaded files.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { id: 'agoda', name: 'Agoda (AGX Campaign)' },
                { id: 'MMT', name: 'MakeMyTrip' },
                { id: 'Booking.com', name: 'Booking.com' },
                { id: 'Expedia', name: 'Expedia' },
                { id: 'Goibibo', name: 'Goibibo' },
                { id: 'Airbnb', name: 'Airbnb' }
              ].map(ota => (
                <div key={ota.id} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2">
                    <Trash2 className="w-4 h-4 text-red-600" />
                    <span className="font-medium text-sm">{ota.name}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => clearAnalyticsData(ota.id)}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Clear Data
                  </Button>
                </div>
              ))}
            </div>
            <div className="text-xs text-red-500 mt-2">
              ⚠️ Warning: This action cannot be undone. Analytics data will be permanently deleted.
            </div>
          </CardContent>
        </Card>
      )}

      {/* AGX Analytics Upload */}
      {user?.role === 'admin' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">AGX Analytics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600">
              Bulk upload AGX analytics data for multiple hotels from Excel file
            </div>
            <input
              type="file"
              id="agx-upload"
              accept=".xlsx,.xls,.csv"
              onChange={handleAGXUpload}
              className="hidden"
            />
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={generateAGXTemplate}
                className="bg-blue-50 border-blue-200 hover:bg-blue-100"
              >
                <Download className="w-4 h-4 mr-2" />
                AGX Template
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => document.getElementById('agx-upload').click()}
                className="bg-purple-50 border-purple-200 hover:bg-purple-100"
              >
                <Upload className="w-4 h-4 mr-2" />
                Add AGX
              </Button>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Expected columns: hotel_id, hotel_name, city_name, state_name, Created Date, Impression, Rank, Room Night, Revenue, Growth Program, Cost Per Bookings, Cost, Click, Booking
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar to GRB Rate Sync */}
      {user?.role === 'admin' && selectedProperty && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Calendar Rate Sync</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600">
              Sync all calendar rates to GRB Our Rates in Competitor tab. This ensures GRB rates always match your calendar.
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={syncCalendarToGRB}
              className="bg-green-50 border-green-200 hover:bg-green-100"
            >
              <Upload className="w-4 h-4 mr-2" />
              Sync Calendar to GRB
            </Button>
            <div className="text-xs text-gray-500 mt-2">
              This will update all GRB "Our Rates" in the Competitor tab to match the rates from Calendar tab for the same dates.
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Connected OTAs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {['MMT', 'Goibibo', 'Expedia', 'Airbnb', 'Yatra', 'Booking.com', 'Agoda'].map((ota) => (
              <div key={ota} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="font-medium">{ota}</span>
                <Badge variant="outline" className="text-green-600">Connected</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Admin-only Competitor Management */}
      {user?.role === 'admin' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Competitor Data Management</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600">
              Import competitor rates from Google Sheets for price comparison analysis
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowCompetitorUpload(true)}
                data-testid="upload-competitor-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Competitor Sheet
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                data-testid="sync-competitor-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Sync Now
              </Button>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-sm text-blue-800">
                <div className="font-medium mb-1">Auto-sync Status:</div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span>Active - Last sync: 2 hours ago</span>
                </div>
                <div className="text-xs mt-2">Next sync scheduled in 30 minutes</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin-only Database Storage Monitoring */}
      {user?.role === 'admin' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Database Storage Usage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingStorageMetrics ? (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                <p className="mt-2 text-sm text-gray-600">Loading storage metrics...</p>
              </div>
            ) : storageMetrics ? (
              <>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Storage Used</span>
                    <span className="font-semibold text-gray-800">
                      {storageMetrics.total_storage_mb.toFixed(2)} MB / {(storageMetrics.max_storage_mb / 1024).toFixed(0)} GB
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                    <div 
                      className={`h-full ${
                        storageMetrics.usage_percentage > 80 
                          ? 'bg-red-500' 
                          : storageMetrics.usage_percentage > 60 
                            ? 'bg-yellow-500' 
                            : 'bg-green-500'
                      } transition-all duration-300`}
                      style={{ width: `${Math.min(storageMetrics.usage_percentage, 100)}%` }}
                    >
                      <span className="text-xs text-white font-semibold pl-2 leading-4">
                        {storageMetrics.usage_percentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Storage Breakdown */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-600">Database</div>
                    <div className="text-lg font-semibold text-blue-600">
                      {storageMetrics.database.size_mb.toFixed(2)} MB
                    </div>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-600">Files</div>
                    <div className="text-lg font-semibold text-green-600">
                      {storageMetrics.files.size_mb.toFixed(2)} MB
                    </div>
                  </div>
                </div>

                {/* Collection Details */}
                {storageMetrics.database.collections && Object.keys(storageMetrics.database.collections).length > 0 && (
                  <div className="mt-4 border-t pt-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Top Collections:</div>
                    <div className="space-y-1 text-sm">
                      {Object.entries(storageMetrics.database.collections)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 5)
                        .map(([name, size]) => (
                          <div key={name} className="flex justify-between text-gray-600">
                            <span>{name}</span>
                            <span className="font-mono text-xs">{size.toFixed(2)} MB</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Clear Storage Button */}
                <div className="mt-4 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowClearStorageModal(true)}
                    className="w-full bg-red-50 border-red-200 hover:bg-red-100 hover:border-red-300 text-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear Storage
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-4 text-sm text-gray-500">
                No storage metrics available
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Admin-only OTA API Management */}
      {user?.role === 'admin' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">OTA API Integrations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600">
              Manage OTA API connections for centralized rate, availability, and booking synchronization
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowAddOTAModal(true);
                setOtaModalStep(1);
                setOtaFormData({
                  ota_name: '',
                  auth_type: 'API Key',
                  api_key: '',
                  username: '',
                  password: '',
                  oauth_token_url: '',
                  bearer_token: '',
                  description: '',
                  endpoints: []
                });
              }}
              className="w-full bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 hover:border-green-300"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add OTA API
            </Button>

            {/* OTA API List */}
            {otaApis.length > 0 ? (
              <div className="space-y-2">
                <div className="text-xs font-medium text-gray-700 mb-2">Connected OTAs ({otaApis.length})</div>
                {otaApis.map((ota) => (
                  <div key={ota.id} className="border border-gray-200 rounded-lg p-3 hover:border-orange-300 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-sm text-gray-800">{ota.ota_name}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {ota.auth_type} • {ota.endpoints_count} endpoint(s) • {ota.linked_properties_count} propert(ies)
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenLinkPropertyModal(ota)}
                          className="text-xs bg-blue-50 hover:bg-blue-100 border-blue-200"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Link
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewOTA(ota.id)}
                          className="text-xs"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteOTA(ota.id)}
                          className="text-xs text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-sm text-gray-500">
                No OTA APIs configured yet. Click "Add OTA API" to get started.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">OTA Mapping Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-gray-600">
            Configure room type mappings for each OTA platform
          </div>
          
          <div className="space-y-3">
            {roomTypes.slice(0, 3).map((roomType) => (
              <div key={roomType.id} className="border border-gray-200 rounded-lg p-3">
                <div className="font-medium text-sm mb-2">{roomType.name}</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="bg-green-50 border border-green-200 rounded p-2">
                    <div className="font-medium text-green-800">MMT</div>
                    <div className="text-green-600">DLX-{roomType.id.slice(0, 4)}</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded p-2">
                    <div className="font-medium text-blue-800">Expedia</div>
                    <div className="text-blue-600">Room-{roomType.id.slice(0, 6)}</div>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded p-2">
                    <div className="font-medium text-purple-800">Airbnb</div>
                    <div className="text-purple-600">Type-{roomType.name.slice(0, 3).toUpperCase()}</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded p-2">
                    <div className="font-medium text-gray-800">Others</div>
                    <div className="text-gray-600">3 more...</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <Button variant="outline" size="sm" className="w-full">
            Configure All Mappings
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">System Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Calendar View
              </label>
              <Select value={calendarView} onValueChange={setCalendarView}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 Days</SelectItem>
                  <SelectItem value="14">14 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Currency
              </label>
              <Select defaultValue="inr">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inr">₹ Indian Rupee</SelectItem>
                  <SelectItem value="idr">Rp Indonesian Rupiah</SelectItem>
                  <SelectItem value="usd">$ US Dollar</SelectItem>
                  <SelectItem value="eur">€ Euro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Feb 2026 — don't flash the "No Properties Found" empty state while the
  // initial property list is still loading, and don't show it in
  // All Properties mode (where a null `selectedProperty` is valid).
  if (!propertiesLoaded) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="app-initial-loading">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <div className="text-sm">Loading your properties…</div>
        </div>
      </div>
    );
  }

  if (!allPropertiesMode && !selectedProperty) {
    return (
      <>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Card className="w-96">
            <CardContent className="p-8 text-center">
              <Hotel className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Properties Found</h3>
              <p className="text-gray-600 text-sm mb-4">
                Start by adding your first hotel/property to begin using the system.
              </p>
              <Button 
                className="bg-gradient-to-r from-orange-500 to-amber-600" 
                onClick={() => setShowPropertyModal(true)}
                data-testid="create-first-property-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Property
              </Button>
            </CardContent>
          </Card>
        </div>
        
        {/* Property Onboarding Modal - Also rendered when no properties exist */}
        <Dialog open={showPropertyModal} onOpenChange={setShowPropertyModal}>
          <HotelOnboardingForm
            onSubmit={handleOnboardingSubmit}
            onCancel={() => setShowPropertyModal(false)}
          />
        </Dialog>
        
        <Toaster position="top-right" />
      </>
    );
  }

  // Helper: Show sidebar expanded (with text labels) when either desktop or mobile sidebar is open
  const showSidebarExpanded = sidebarOpen || mobileSidebarOpen;

  return (
    <div className="app-container">
      {/* Left Sidebar - Fixed (MUST be above overlay in z-index) */}
      <div className={`sidebar ${sidebarOpen ? 'sidebar-open' : 'sidebar-hidden'} ${mobileSidebarOpen ? 'mobile-open' : ''} bg-white`}>
        {/* Logo Header */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
                <Hotel className="w-4 h-4 text-white" />
              </div>
              {showSidebarExpanded && (
                <div>
                  <h1 className="font-bold text-gray-800 text-sm">Channel Manager</h1>
                  <p className="text-xs text-gray-500">SaaS Platform</p>
                </div>
              )}
            </div>
            {/* Desktop toggle button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSidebar}
              className="p-1 hover:bg-gray-100 desktop-toggle"
              data-testid="sidebar-toggle"
            >
              {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </Button>
            {/* Mobile close button - X icon inside sidebar */}
            <Button
              variant="ghost"
              size="sm"
              onClick={closeMobileSidebar}
              className="p-1 hover:bg-gray-100 mobile-close"
              aria-label="Close sidebar"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 p-3">
          <div className="space-y-2">
            {isNavVisible('dashboard') && (
              <button
                onClick={() => handleTabChange('dashboard')}
                className={`w-full flex items-center ${showSidebarExpanded ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === 'dashboard' 
                    ? 'bg-orange-50 text-orange-600 border border-orange-200' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="nav-dashboard"
                title={!showSidebarExpanded ? 'Dashboard' : ''}
              >
                <LayoutDashboard className="w-5 h-5" />
                {showSidebarExpanded && <span className="font-medium">Dashboard</span>}
              </button>
            )}

            {isNavVisible('calendar') && (
              <button
                onClick={() => handleTabChange('calendar')}
                className={`w-full flex items-center ${showSidebarExpanded ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === 'calendar' 
                    ? 'bg-orange-50 text-orange-600 border border-orange-200' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="nav-calendar"
                title={!showSidebarExpanded ? 'Calendar' : ''}
              >
                <CalendarDays className="w-5 h-5" />
                {showSidebarExpanded && <span className="font-medium">Calendar</span>}
              </button>
            )}

            {isNavVisible('bookings') && (
              <button
                onClick={() => handleTabChange('bookings')}
                className={`w-full flex items-center ${showSidebarExpanded ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === 'bookings' 
                    ? 'bg-orange-50 text-orange-600 border border-orange-200' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="nav-bookings"
                title={!showSidebarExpanded ? 'Bookings' : ''}
              >
                <Users className="w-5 h-5" />
                {showSidebarExpanded && <span className="font-medium">Bookings</span>}
              </button>
            )}

            {/* Analytics & Ops — appears here (right after Bookings) so
                the portfolio-mode sidebar order is
                Dashboard → Bookings → Analytics & Ops → Listings → Settings.
                In single-property mode this tab is hidden by isNavVisible. */}
            {user?.role === 'admin' && isNavVisible('analytics-ops') && (
              <button
                onClick={() => handleTabChange('analytics-ops')}
                className={`w-full flex items-center ${showSidebarExpanded ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === 'analytics-ops'
                    ? 'bg-orange-50 text-orange-600 border border-orange-200'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="nav-analytics-ops"
                title={!showSidebarExpanded ? 'Analytics & Ops' : ''}
              >
                <TrendingUp className="w-5 h-5" />
                {showSidebarExpanded && <span className="font-medium">Analytics &amp; Ops</span>}
              </button>
            )}

            {user?.role === 'admin' && (
              <>
                {isNavVisible('analytics') && (
                  <button
                    onClick={() => handleTabChange('analytics')}
                  className={`w-full flex items-center ${showSidebarExpanded ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'analytics' 
                      ? 'bg-orange-50 text-orange-600 border border-orange-200' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid="nav-analytics"
                  title={!showSidebarExpanded ? 'Analytics' : ''}
                >
                  <BarChart3 className="w-5 h-5" />
                  {showSidebarExpanded && <span className="font-medium">Analytics</span>}
                </button>
                )}

                {isNavVisible('promotions') && (
                  <button
                    onClick={() => handleTabChange('promotions')}
                    className={`w-full flex items-center ${showSidebarExpanded ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === 'promotions' 
                        ? 'bg-orange-50 text-orange-600 border border-orange-200' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid="nav-promotions"
                    title={!showSidebarExpanded ? 'Promotions' : ''}
                  >
                    <Tag className="w-5 h-5" />
                    {showSidebarExpanded && <span className="font-medium">Promotions</span>}
                  </button>
                )}

                {isNavVisible('competitor') && (
                  <button
                    onClick={() => handleTabChange('competitor')}
                    className={`w-full flex items-center ${showSidebarExpanded ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === 'competitor' 
                        ? 'bg-orange-50 text-orange-600 border border-orange-200' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid="nav-competitor"
                    title={!showSidebarExpanded ? 'Competitor' : ''}
                  >
                    <TrendingUp className="w-5 h-5" />
                    {showSidebarExpanded && <span className="font-medium">Competitor</span>}
                  </button>
                )}
              </>
            )}

            {isNavVisible('property-info') && (
              <button
                onClick={() => handleTabChange('property-info')}
                className={`w-full flex items-center ${showSidebarExpanded ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === 'property-info' 
                    ? 'bg-orange-50 text-orange-600 border border-orange-200' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="nav-property-info"
                title={!showSidebarExpanded ? 'Property Info' : ''}
              >
                <Info className="w-5 h-5" />
                {showSidebarExpanded && <span className="font-medium">Property Info</span>}
              </button>
            )}

            {isNavVisible('listings') && (
              <button
                onClick={() => handleTabChange('listings')}
                className={`w-full flex items-center ${showSidebarExpanded ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === 'listings'
                    ? 'bg-orange-50 text-orange-600 border border-orange-200'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="nav-listings"
                title={!showSidebarExpanded ? 'Listings' : ''}
              >
                <Tag className="w-5 h-5" />
                {showSidebarExpanded && <span className="font-medium">Listings</span>}
              </button>
            )}


            {user?.role === 'admin' && (
              <>
                {isNavVisible('payments') && (
                  <button
                    onClick={() => handleTabChange('payments')}
                    className={`w-full flex items-center ${showSidebarExpanded ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === 'payments' 
                        ? 'bg-orange-50 text-orange-600 border border-orange-200' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid="nav-payments"
                    title={!showSidebarExpanded ? 'Payments' : ''}
                  >
                    <DollarSign className="w-5 h-5" />
                    {showSidebarExpanded && <span className="font-medium">Payments</span>}
                  </button>
                )}

                {isNavVisible('accounting') && (
                  <button
                    onClick={() => handleTabChange('accounting')}
                    className={`w-full flex items-center ${showSidebarExpanded ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === 'accounting' 
                        ? 'bg-orange-50 text-orange-600 border border-orange-200' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid="nav-accounting"
                    title={!showSidebarExpanded ? 'Accounting' : ''}
                  >
                    <FileText className="w-5 h-5" />
                    {showSidebarExpanded && <span className="font-medium">Accounting</span>}
                  </button>
                )}
              </>
            )}

            {isNavVisible('logs') && (
              <button
                onClick={() => handleTabChange('logs')}
                className={`w-full flex items-center ${showSidebarExpanded ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === 'logs' 
                    ? 'bg-orange-50 text-orange-600 border border-orange-200' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="nav-logs"
                title={!showSidebarExpanded ? 'Logs' : ''}
              >
                <FileText className="w-5 h-5" />
                {showSidebarExpanded && <span className="font-medium">Logs</span>}
              </button>
            )}

            {user?.role === 'admin' && isNavVisible('settings') && (
              <button
                onClick={() => handleTabChange('settings')}
                className={`w-full flex items-center ${showSidebarExpanded ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === 'settings' 
                    ? 'bg-orange-50 text-orange-600 border border-orange-200' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="nav-settings"
                title={!showSidebarExpanded ? 'Settings' : ''}
              >
                <Settings className="w-5 h-5" />
                {showSidebarExpanded && <span className="font-medium">Settings</span>}
              </button>
            )}

            {user?.role === 'admin' && isNavVisible('property-profile') && (
              <button
                onClick={() => handleTabChange('property-profile')}
                className={`w-full flex items-center ${showSidebarExpanded ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === 'property-profile'
                    ? 'bg-orange-50 text-orange-600 border border-orange-200'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="nav-property-profile"
                title={!showSidebarExpanded ? 'Property Profile' : ''}
              >
                <Building2 className="w-5 h-5" />
                {showSidebarExpanded && <span className="font-medium">Property Profile</span>}
              </button>
            )}

            {user?.role === 'admin' && isNavVisible('property-settings') && (
              <button
                onClick={() => handleTabChange('property-settings')}
                className={`w-full flex items-center ${showSidebarExpanded ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === 'property-settings'
                    ? 'bg-orange-50 text-orange-600 border border-orange-200'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="nav-property-settings"
                title={!showSidebarExpanded ? 'Property Settings' : ''}
              >
                <Building2 className="w-5 h-5" />
                {showSidebarExpanded && <span className="font-medium">Property Settings</span>}
              </button>
            )}

          </div>
        </nav>

        {/* User Info */}
        {showSidebarExpanded && (
          <div className="p-4 border-t">
            <div className="text-sm text-gray-600 mb-1">Welcome back,</div>
            <div className="font-medium text-gray-800 text-sm truncate">{user?.full_name}</div>
            <div className="text-xs text-gray-500 truncate">{user?.email}</div>
          </div>
        )}
      </div>

      {/* Mobile Hamburger Menu - Simple Menu Icon (Hidden when sidebar open) */}
      <button
        className={`mobile-hamburger ${mobileSidebarOpen ? 'sidebar-is-open' : ''}`}
        onClick={toggleMobileSidebar}
        aria-label="Open menu"
      >
        <Menu className="w-5 h-5 text-gray-700" />
      </button>

      {/* Overlay Backdrop - Rendered AFTER sidebar, click passes through to close */}
      <div 
        className={`mobile-overlay ${(mobileSidebarOpen || sidebarOpen) ? 'active' : ''}`}
        onClick={() => {
          if (mobileSidebarOpen) closeMobileSidebar();
          if (sidebarOpen) setSidebarOpen(false);
        }}
        aria-hidden="true"
      ></div>

      {/* Main Content Area */}
      <div className="main-content-wrapper">
        <div className="flex flex-col h-full">
        {/* Top Header */}
        <div className="bg-white border-b shadow-sm p-4">
          <div className="flex items-center justify-between mobile-navbar-header">
            <div className="flex items-center gap-4 navbar-title-section">
              {/* Desktop Hamburger Toggle */}
              <button
                className="desktop-hamburger-header p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={toggleSidebar}
                aria-label="Toggle sidebar"
              >
                <Menu className="w-6 h-6 text-gray-700" />
              </button>
              
              <div>
                <h2 className="text-xl font-semibold text-gray-800 capitalize">{activeTab}</h2>
                <p className="text-sm text-gray-600">
                  {activeTab === 'dashboard' && 'Overview of your properties and key metrics'}
                  {activeTab === 'calendar' && 'Manage rates, inventory, and availability'}
                  {activeTab === 'bookings' && 'Track and manage reservations'}
                  {activeTab === 'competitor' && 'Compare rates and inventory with competitors'}
                  {activeTab === 'property-info' && 'View and edit property information'}
                  {activeTab === 'listings' && 'Master OTA content — enter once, publish everywhere'}
                  {activeTab === 'payments' && 'Manage payments and invoices'}
                  {activeTab === 'accounting' && 'Financial records and accounting entries'}
                  {activeTab === 'promotions' && 'Marketing campaigns and offers'}
                  {activeTab === 'logs' && 'Activity logs and change history'}
                  {activeTab === 'settings' && 'System configuration and preferences'}
                  {activeTab === 'property-settings' && 'OTA mapping & credentials (MMT, Booking, Agoda, …)'}
                  {activeTab === 'analytics' && 'View performance and trends'}
                  {activeTab === 'analytics-ops' && 'Portfolio analytics & operational health (read-only)'}
                  {activeTab === 'property-profile' && 'Detailed profile for the selected property'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 navbar-actions-section">
              {/* Phase 1 redesign — global searchable PropertySelector.
                  Replaces the old dropdown; keeps the same `data-testid` so
                  existing selectors in tests continue to resolve. Renders
                  regardless of `properties.length` since it does its own
                  search + `All Properties` toggle. */}
              <PropertySelector
                selected={selectedProperty}
                onSelect={(p) => setSelectedProperty(p)}
                allPropertiesMode={allPropertiesMode}
                onToggleAllMode={setAllPropertiesMode}
              />

              <Button
                variant="outline"
                size="sm"
                onClick={handleExportOtaMappings}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="export-ota-mappings-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export to Excel
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkRateOpen(true)}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="bulk-rate-open-btn"
              >
                <Percent className="w-4 h-4 mr-2" />
                Bulk Rate Update
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={logout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Bulk Rate Update — full-screen modal launched from the navbar */}
        <BulkRateUpdateModal open={bulkRateOpen} onClose={() => setBulkRateOpen(false)} apiBase={API} />

        {/* Link Room Types — cascade prompt on Calendar rate edit */}
        <Dialog open={!!linkPrompt} onOpenChange={(o) => { if (!o && !linkPromptBusy) setLinkPrompt(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="link-cascade-prompt">
            <DialogHeader>
              <DialogTitle>Update linked room types?</DialogTitle>
              <DialogDescription>
                {linkPrompt && (() => {
                  const linkDoc = (roomTypeLinks || []).find(l => l.base_room_type_id === linkPrompt.roomTypeId);
                  const baseName = (calendarData?.room_types || []).find(rt => rt.room_type.id === linkPrompt.roomTypeId)?.room_type?.name || 'this room type';
                  const count = (linkDoc?.links || []).length;
                  return (
                    <>This room type (<strong>{baseName}</strong>) has <strong>{count}</strong> linked room type{count > 1 ? 's' : ''}.
                    You set <strong>{linkPrompt.ratePlanCode}</strong> occupancy <strong>{linkPrompt.occupancy}</strong> to <strong>₹{Number(linkPrompt.newRate).toLocaleString()}</strong> for <strong>{linkPrompt.date}</strong>.
                    Do you want to update the linked room types too (base rate + configured increment)?</>
                  );
                })()}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col sm:flex-row gap-2 justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                disabled={linkPromptBusy}
                onClick={() => handleLinkPromptChoice(false)}
                data-testid="link-cascade-this-only-btn"
              >
                Update this room type only
              </Button>
              <Button
                type="button"
                disabled={linkPromptBusy}
                className="bg-gradient-to-r from-orange-500 to-amber-600 text-white"
                onClick={() => handleLinkPromptChoice(true)}
                data-testid="link-cascade-linked-btn"
              >
                {linkPromptBusy ? 'Updating…' : 'Update linked room types also'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Content */}
        <div className="flex-1 p-6 overflow-auto">
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'calendar' && renderSimplifiedCalendar()}
          {activeTab === 'bookings' && renderEnhancedBookings()}
          {activeTab === 'competitor' && renderImprovedCompetitorTab()}
          {activeTab === 'property-info' && renderPropertyInfoTab()}
          {activeTab === 'listings' && renderListings()}
          {activeTab === 'payments' && renderPayments()}
          {activeTab === 'accounting' && renderAccounting()}
          {activeTab === 'analytics' && renderAnalytics()}
          {activeTab === 'promotions' && (selectedProperty?.id
            ? <YatraPromotions propertyId={selectedProperty.id} apiBase={API} />
            : renderPromotions())}
          {activeTab === 'logs' && renderLogs()}
          {activeTab === 'settings' && renderEnhancedSettings()}
          {activeTab === 'property-settings' && renderPropertySettings()}
          {activeTab === 'property-profile' && (
            <PropertyProfile propertyId={allPropertiesMode ? null : selectedProperty?.id} />
          )}
          {activeTab === 'analytics-ops' && (
            <AnalyticsOperations
              propertyId={allPropertiesMode ? null : selectedProperty?.id}
              allPropertiesMode={allPropertiesMode}
            />
          )}
        </div>
      </div>

      <Toaster position="top-right" />

      {/* Booking Detail Modal */}
      <Dialog open={showBookingDetailModal} onOpenChange={setShowBookingDetailModal}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Booking Details: {selectedBookingDetail?.booking_id}
            </DialogTitle>
          </DialogHeader>
          
          {selectedBookingDetail && (
            <div className="space-y-6">
              {/* Final Status Summary */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-sm text-gray-500">Final Status</div>
                  <Badge 
                    className={`text-lg ${
                      selectedBookingDetail.final_status === 'cancelled' 
                        ? 'bg-red-100 text-red-700' 
                        : selectedBookingDetail.final_status === 'amended'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {selectedBookingDetail.final_status?.toUpperCase()}
                  </Badge>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Total Records</div>
                  <div className="text-lg font-bold">{selectedBookingDetail.total_records}</div>
                </div>
                {selectedBookingDetail.has_amendments && (
                  <Badge variant="outline" className="bg-yellow-50">Has Amendments</Badge>
                )}
                {selectedBookingDetail.has_cancellation && (
                  <Badge variant="outline" className="bg-red-50">Has Cancellation</Badge>
                )}
              </div>
              
              {/* Field Changes (if amended) */}
              {selectedBookingDetail.field_changes && selectedBookingDetail.field_changes.length > 0 && (
                <Card className="border-yellow-200 bg-yellow-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-yellow-700">Field-Level Changes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedBookingDetail.field_changes.map((change, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <span className="font-medium capitalize">{change.field.replace('_', ' ')}:</span>
                          <span className="text-red-600 line-through">{change.original || 'N/A'}</span>
                          <span className="text-gray-400">→</span>
                          <span className="text-green-600 font-medium">{change.amended || 'N/A'}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Timeline of Records */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3">Booking History Timeline</h4>
                <div className="space-y-4">
                  {/* Confirmed Record */}
                  {selectedBookingDetail.confirmed && (
                    <Card className="border-green-200">
                      <CardHeader className="pb-2 bg-green-50">
                        <div className="flex items-center justify-between">
                          <Badge className="bg-green-100 text-green-700">CONFIRMED</Badge>
                          <span className="text-sm text-gray-500">
                            {formatBookingDate(selectedBookingDetail.confirmed.booked_date || selectedBookingDetail.confirmed.raw_data?.['Booked/Email Date'])}
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-3">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                          <div><span className="text-gray-500">Guest:</span> {selectedBookingDetail.confirmed.raw_data?.['Guest Name']}</div>
                          <div><span className="text-gray-500">Hotel:</span> {selectedBookingDetail.confirmed.raw_data?.['Hotel Name']}</div>
                          <div><span className="text-gray-500">Amount:</span> ₹{parseFloat(selectedBookingDetail.confirmed.raw_data?.['Amount/Net Rate'] || 0).toLocaleString()}</div>
                          <div><span className="text-gray-500">Check-In:</span> {formatBookingDate(selectedBookingDetail.confirmed.raw_data?.['Check-In'])}</div>
                          <div><span className="text-gray-500">Check-Out:</span> {formatBookingDate(selectedBookingDetail.confirmed.raw_data?.['Check-Out'])}</div>
                          <div><span className="text-gray-500">OTA:</span> {selectedBookingDetail.confirmed.raw_data?.OTA}</div>
                          <div><span className="text-gray-500">Room Type:</span> {selectedBookingDetail.confirmed.raw_data?.['Room Type'] || '—'}</div>
                        </div>
                        <BookingXmlDetails record={selectedBookingDetail.confirmed} testidPrefix="booking-detail-confirmed" />
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Amendment Records */}
                  {selectedBookingDetail.amendments && selectedBookingDetail.amendments.map((amendment, i) => (
                    <Card key={i} className="border-yellow-200">
                      <CardHeader className="pb-2 bg-yellow-50">
                        <div className="flex items-center justify-between">
                          <Badge className="bg-yellow-100 text-yellow-700">AMENDED</Badge>
                          <span className="text-sm text-gray-500">
                            {formatBookingDate(amendment.booked_date || amendment.raw_data?.['Booked/Email Date'])}
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-3">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                          <div><span className="text-gray-500">Guest:</span> {amendment.raw_data?.['Guest Name']}</div>
                          <div><span className="text-gray-500">Hotel:</span> {amendment.raw_data?.['Hotel Name']}</div>
                          <div><span className="text-gray-500">Amount:</span> ₹{parseFloat(amendment.raw_data?.['Amount/Net Rate'] || 0).toLocaleString()}</div>
                          <div><span className="text-gray-500">Check-In:</span> {formatBookingDate(amendment.raw_data?.['Check-In'])}</div>
                          <div><span className="text-gray-500">Check-Out:</span> {formatBookingDate(amendment.raw_data?.['Check-Out'])}</div>
                          <div><span className="text-gray-500">OTA:</span> {amendment.raw_data?.OTA}</div>
                          <div><span className="text-gray-500">Room Type:</span> {amendment.raw_data?.['Room Type'] || '—'}</div>
                        </div>
                        <BookingXmlDetails record={amendment} testidPrefix={`booking-detail-amendment-${i}`} />
                      </CardContent>
                    </Card>
                  ))}
                  
                  {/* Cancellation Record */}
                  {selectedBookingDetail.cancellation && (
                    <Card className="border-red-200">
                      <CardHeader className="pb-2 bg-red-50">
                        <div className="flex items-center justify-between">
                          <Badge className="bg-red-100 text-red-700">CANCELLED</Badge>
                          <span className="text-sm text-gray-500">
                            {formatBookingDate(selectedBookingDetail.cancellation.booked_date || selectedBookingDetail.cancellation.raw_data?.['Booked/Email Date'])}
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-3">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                          <div><span className="text-gray-500">Guest:</span> {selectedBookingDetail.cancellation.raw_data?.['Guest Name']}</div>
                          <div><span className="text-gray-500">Hotel:</span> {selectedBookingDetail.cancellation.raw_data?.['Hotel Name']}</div>
                          <div><span className="text-gray-500">Cancellation Amount:</span> ₹{parseFloat(selectedBookingDetail.cancellation.raw_data?.['Amount/Net Rate'] || 0).toLocaleString()}</div>
                          <div><span className="text-gray-500">Check-In:</span> {formatBookingDate(selectedBookingDetail.cancellation.raw_data?.['Check-In'])}</div>
                          <div><span className="text-gray-500">Check-Out:</span> {formatBookingDate(selectedBookingDetail.cancellation.raw_data?.['Check-Out'])}</div>
                          <div><span className="text-gray-500">OTA:</span> {selectedBookingDetail.cancellation.raw_data?.OTA}</div>
                          <div><span className="text-gray-500">Room Type:</span> {selectedBookingDetail.cancellation.raw_data?.['Room Type'] || '—'}</div>
                        </div>
                        <BookingXmlDetails record={selectedBookingDetail.cancellation} testidPrefix="booking-detail-cancellation" />
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => setShowBookingDetailModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Property Onboarding Modal */}
      <Dialog open={showPropertyModal} onOpenChange={setShowPropertyModal}>
        <HotelOnboardingForm
          onSubmit={handleOnboardingSubmit}
          onCancel={() => setShowPropertyModal(false)}
        />
      </Dialog>

      {/* Room Type Creation Modal */}
      <Dialog open={showRoomTypeModal} onOpenChange={setShowRoomTypeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Room Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateRoomType} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Room Type Name *
              </label>
              <Input
                name="name"
                value={roomTypeFormData.name}
                onChange={handleRoomTypeFormChange}
                placeholder="Deluxe Room"
                required
                data-testid="room-type-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <Input
                name="description"
                value={roomTypeFormData.description}
                onChange={handleRoomTypeFormChange}
                placeholder="Spacious room with city view"
                data-testid="room-type-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Occupancy *
                </label>
                <Input
                  name="max_occupancy"
                  type="number"
                  value={roomTypeFormData.max_occupancy}
                  onChange={handleRoomTypeFormChange}
                  placeholder="2"
                  min="1"
                  required
                  data-testid="room-type-occupancy-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Room Count *
                </label>
                <Input
                  name="room_count"
                  type="number"
                  value={roomTypeFormData.room_count}
                  onChange={handleRoomTypeFormChange}
                  placeholder="10"
                  min="1"
                  required
                  data-testid="room-type-count-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Base Rate (₹) *
              </label>
              <Input
                name="base_rate"
                type="number"
                value={roomTypeFormData.base_rate}
                onChange={handleRoomTypeFormChange}
                placeholder="5999"
                min="0"
                step="0.01"
                required
                data-testid="room-type-rate-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amenities (comma-separated)
              </label>
              <Input
                name="amenities"
                value={roomTypeFormData.amenities}
                onChange={handleRoomTypeFormChange}
                placeholder="WiFi, AC, TV, Mini Bar"
                data-testid="room-type-amenities-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowRoomTypeModal(false)}
                className="flex-1"
                data-testid="room-type-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700"
                data-testid="room-type-submit-btn"
              >
                Create Room Type
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Hotel Owner Modal */}
      <Dialog open={showCreateOwnerModal} onOpenChange={setShowCreateOwnerModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Login for Hotel Owner</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateHotelOwner} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hotel Name *
              </label>
              <Select 
                value={ownerFormData.hotelId} 
                onValueChange={(value) => {
                  const selectedProp = properties.find(p => p.id === value);
                  setOwnerFormData({
                    ...ownerFormData, 
                    hotelId: value,
                    hotelName: selectedProp?.name || ''
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a hotel" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((property) => (
                    <SelectItem key={property.id} value={property.id}>
                      {property.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <Input
                type="email"
                value={ownerFormData.email}
                onChange={(e) => setOwnerFormData({...ownerFormData, email: e.target.value})}
                placeholder="owner@hotel.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password *
              </label>
              <Input
                type="password"
                value={ownerFormData.password}
                onChange={(e) => setOwnerFormData({...ownerFormData, password: e.target.value})}
                placeholder="Enter password"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password *
              </label>
              <Input
                type="password"
                value={ownerFormData.confirmPassword}
                onChange={(e) => setOwnerFormData({...ownerFormData, confirmPassword: e.target.value})}
                placeholder="Confirm password"
                required
              />
            </div>

            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Copy and share these credentials (email + password) with the hotel owner after creation.
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateOwnerModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
              >
                Create Login
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rate Plan Manager Dialog */}
      <Dialog open={showRatePlanModal} onOpenChange={setShowRatePlanModal}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rate-plan-modal">
          <DialogHeader>
            <DialogTitle>Manage Rate Plans</DialogTitle>
            <DialogDescription>
              Attach rate plans (CP / EP / MAP / AP or custom) to room types of a property.
              Rates per occupancy can be tuned later in the Calendar tab.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* Property selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Property *</label>
              <Select
                value={ratePlanProperty?.id || ''}
                onValueChange={(value) => {
                  const p = properties.find(x => x.id === value);
                  setRatePlanProperty(p || null);
                  setRatePlanDrafts({});
                }}
              >
                <SelectTrigger data-testid="rate-plan-property-select">
                  <SelectValue placeholder="Select a property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Room type list with existing + draft rate plans */}
            {ratePlanProperty?.id && (
              <div className="space-y-4">
                {ratePlanGrouped.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500 bg-gray-50 rounded">
                    This property has no room types yet. Add a room type first.
                  </div>
                ) : (
                  ratePlanGrouped.map(rt => (
                    <Card key={rt.room_type_id} className="border-orange-100">
                      <CardHeader className="pb-2 bg-orange-50/40">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">{rt.room_type_name}</CardTitle>
                          <span className="text-xs text-gray-500">Max occupancy: {rt.max_occupancy}</span>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-3 space-y-2">
                        {/* Existing rate plans (editable base rate) */}
                        {(rt.rate_plans || []).map(rp => (
                          <div key={rp.id} className="flex items-center gap-2" data-testid={`existing-rate-plan-${rp.id}`}>
                            <div className="px-3 py-1 bg-orange-100 text-orange-800 rounded text-xs font-semibold w-16 text-center">
                              {rp.code}
                            </div>
                            <Input
                              type="number"
                              defaultValue={rp.base_rate}
                              className="flex-1"
                              onBlur={(e) => {
                                const v = parseFloat(e.target.value);
                                if (!isNaN(v) && v !== rp.base_rate) {
                                  updateRatePlanBaseRate(rp.id, v);
                                }
                              }}
                              data-testid={`rate-plan-rate-${rp.id}`}
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-800"
                              onClick={() => deleteRatePlan(rp.id)}
                              data-testid={`delete-rate-plan-${rp.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        {/* Draft rate plans (unsaved) */}
                        {(ratePlanDrafts[rt.room_type_id] || []).map((draft, idx) => (
                          <div key={`draft-${rt.room_type_id}-${idx}`} className="flex items-center gap-2 flex-wrap">
                            <Select
                              value={draft.code}
                              onValueChange={(v) => updateRatePlanDraft(rt.room_type_id, idx, 'code', v)}
                            >
                              <SelectTrigger className="w-28" data-testid={`draft-code-${rt.room_type_id}-${idx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {RATE_PLAN_CODE_OPTIONS.map(c => (
                                  <SelectItem key={c} value={c}>{labelForRatePlanCode(c)}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {draft.code === 'CUSTOM' && (
                              <Input
                                type="text"
                                placeholder="Custom Rate Plan Name *"
                                value={draft.custom_name || ''}
                                onChange={(e) => updateRatePlanDraft(rt.room_type_id, idx, 'custom_name', e.target.value)}
                                className="flex-1 min-w-[160px]"
                                data-testid={`draft-custom-name-${rt.room_type_id}-${idx}`}
                              />
                            )}
                            <Input
                              type="number"
                              placeholder="Base rate (₹)"
                              value={draft.base_rate}
                              onChange={(e) => updateRatePlanDraft(rt.room_type_id, idx, 'base_rate', e.target.value)}
                              className="flex-1 min-w-[120px]"
                              data-testid={`draft-rate-${rt.room_type_id}-${idx}`}
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-800"
                              onClick={() => removeRatePlanDraft(rt.room_type_id, idx)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => addRatePlanDraft(rt.room_type_id)}
                          data-testid={`add-rate-plan-row-${rt.room_type_id}`}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Rate Plan
                        </Button>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button variant="outline" onClick={() => setShowRatePlanModal(false)}>Close</Button>
              <Button
                onClick={saveRatePlanDrafts}
                disabled={ratePlanSaving}
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
                data-testid="save-rate-plans-btn"
              >
                {ratePlanSaving ? 'Saving…' : 'Save Drafts'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Audit Logs Modal */}
      <Dialog open={showAuditLogs} onOpenChange={setShowAuditLogs}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>System Audit Logs</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {auditLogs.length > 0 ? (
              <div className="space-y-2">
                {auditLogs.map((log) => (
                  <div key={log.id} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant={log.success ? 'default' : 'destructive'}>
                            {log.action}
                          </Badge>
                          <Badge variant="outline">
                            {log.entity_type}
                          </Badge>
                          <span className="text-sm text-gray-600">
                            by {log.user_email}
                          </span>
                        </div>
                        
                        <div className="text-sm text-gray-800 mb-2">
                          Entity ID: <code className="bg-gray-200 px-1 rounded">{log.entity_id}</code>
                        </div>
                        
                        {log.old_values && (
                          <div className="text-sm mb-1">
                            <span className="font-medium">Old Values:</span>
                            <pre className="bg-gray-200 p-2 rounded mt-1 text-xs overflow-x-auto">
                              {JSON.stringify(log.old_values, null, 2)}
                            </pre>
                          </div>
                        )}
                        
                        {log.new_values && (
                          <div className="text-sm mb-1">
                            <span className="font-medium">New Values:</span>
                            <pre className="bg-gray-200 p-2 rounded mt-1 text-xs overflow-x-auto">
                              {JSON.stringify(log.new_values, null, 2)}
                            </pre>
                          </div>
                        )}
                        
                        {log.ota_affected && log.ota_affected.length > 0 && (
                          <div className="text-sm mb-1">
                            <span className="font-medium">OTAs Affected:</span>
                            <div className="flex gap-1 mt-1">
                              {log.ota_affected.map((ota) => (
                                <Badge key={ota} variant="outline" className="text-xs">
                                  {ota.toUpperCase()}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {log.error_message && (
                          <div className="text-sm text-red-600">
                            <span className="font-medium">Error:</span> {log.error_message}
                          </div>
                        )}
                      </div>
                      
                      <div className="text-right text-sm text-gray-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                No audit logs available
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Actions Right Sidebar */}
      <div className={`fixed inset-y-0 right-0 w-96 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${showBulkSidebar ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-amber-600">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                {bulkActionType === 'rates' && 'Bulk Rates Update'}
                {bulkActionType === 'inventory' && 'Bulk Inventory Update'}
                {bulkActionType === 'restrictions' && 'Restrictions'}
              </h3>
              <button
                onClick={closeBulkSidebar}
                className="text-white hover:bg-white/20 rounded-lg p-1 transition-colors"
                data-testid="bulk-drawer-close-btn"
                aria-label="Close drawer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Sidebar Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            {/* Date Ranges (MMT CHM V3 supports multiple <DateRange> per push) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900 text-sm">Date Ranges *</h4>
                <button
                  type="button"
                  data-testid="bulk-add-date-range-btn"
                  onClick={() => setBulkFormData(prev => ({
                    ...prev,
                    dateRanges: [...(prev.dateRanges || []), { from_date: '', to_date: '' }],
                  }))}
                  className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                >
                  + Add Date Range
                </button>
              </div>
              <div className="space-y-2">
                {(bulkFormData.dateRanges || []).map((r, idx) => {
                  // Feb 28 2026 rev.3 — each date range keeps its own
                  // authoritative Days-of-Week map. The top-level `daysOfWeek`
                  // (below) still acts as a template applied to newly-created
                  // ranges, but once a range is created its DOW is independent.
                  const rangeDow = r.days_of_week || bulkFormData.daysOfWeek || {
                    sun: true, mon: true, tue: true, wed: true, thu: true, fri: true, sat: true,
                  };
                  const setRangeDow = (key, val) => setBulkFormData(prev => {
                    const next = [...(prev.dateRanges || [])];
                    const current = next[idx]?.days_of_week || prev.daysOfWeek || {
                      sun: true, mon: true, tue: true, wed: true, thu: true, fri: true, sat: true,
                    };
                    next[idx] = { ...next[idx], days_of_week: { ...current, [key]: val } };
                    return { ...prev, dateRanges: next };
                  });
                  return (
                  <div key={idx} className="border border-gray-100 rounded p-2 space-y-1.5" data-testid={`bulk-date-range-row-${idx}`}>
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                      <div>
                        <label className="block text-[11px] text-gray-600 mb-1">From</label>
                        <Input
                          type="date"
                          value={r.from_date || ''}
                          onChange={(e) => setBulkFormData(prev => {
                            const next = [...(prev.dateRanges || [])];
                            next[idx] = { ...next[idx], from_date: e.target.value };
                            const updates = idx === 0 ? { checkInDate: e.target.value } : {};
                            return { ...prev, dateRanges: next, ...updates };
                          })}
                          className="text-sm h-9"
                          data-testid={`bulk-range-from-${idx}`}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-gray-600 mb-1">To</label>
                        <Input
                          type="date"
                          value={r.to_date || ''}
                          onChange={(e) => setBulkFormData(prev => {
                            const next = [...(prev.dateRanges || [])];
                            next[idx] = { ...next[idx], to_date: e.target.value };
                            const updates = idx === 0 ? { checkOutDate: e.target.value } : {};
                            return { ...prev, dateRanges: next, ...updates };
                          })}
                          className="text-sm h-9"
                          data-testid={`bulk-range-to-${idx}`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setBulkFormData(prev => {
                          const next = (prev.dateRanges || []).filter((_, i) => i !== idx);
                          return { ...prev, dateRanges: next.length ? next : [{ from_date: '', to_date: '' }] };
                        })}
                        disabled={(bulkFormData.dateRanges || []).length <= 1}
                        className="h-9 w-9 flex items-center justify-center text-red-500 hover:bg-red-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Remove range"
                        data-testid={`bulk-range-remove-${idx}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {/* Per-range Days of Week picker — Feb 28 2026 rev.3. */}
                    <div className="grid grid-cols-7 gap-1" data-testid={`bulk-range-dow-${idx}`}>
                      {['sun','mon','tue','wed','thu','fri','sat'].map((k) => {
                        const on = rangeDow[k] !== false;
                        return (
                          <label
                            key={k}
                            className={`cursor-pointer text-center py-1 rounded border text-[10px] font-medium select-none transition-colors ${on ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                            data-testid={`bulk-range-${idx}-dow-${k}`}
                          >
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={on}
                              onChange={(e) => setRangeDow(k, e.target.checked)}
                            />
                            {k[0].toUpperCase()}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );})}
              </div>

              {/* Global Days of Week template — applied when a NEW date range is added */}
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-medium text-gray-700">Days of Week (template for new date ranges)</label>
                  <button
                    type="button"
                    onClick={() => setBulkFormData(prev => ({
                      ...prev,
                      daysOfWeek: { sun: true, mon: true, tue: true, wed: true, thu: true, fri: true, sat: true },
                    }))}
                    className="text-[11px] text-orange-600 hover:text-orange-700 font-medium"
                    data-testid="bulk-dow-reset-all"
                  >
                    Reset all
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {['sun','mon','tue','wed','thu','fri','sat'].map((k) => {
                    const on = (bulkFormData.daysOfWeek || {})[k] !== false;
                    return (
                      <label
                        key={k}
                        className={`cursor-pointer text-center py-1.5 rounded border text-[11px] font-medium select-none transition-colors ${on ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                        data-testid={`bulk-dow-${k}`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={on}
                          onChange={(e) => setBulkFormData(prev => ({
                            ...prev,
                            daysOfWeek: { ...(prev.daysOfWeek || {}), [k]: e.target.checked },
                          }))}
                        />
                        {k.charAt(0).toUpperCase() + k.slice(1)}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Room Types Multi-Select (hidden in rates mode — replaced by per-rate-plan flow below) */}
            {/* Feb 28 2026 rev.4 — multi-select checklist now available in
                BOTH 'inventory' / 'restrictions' / 'rate_inventory' AND
                'rates' actions, so multi-room bulk rate updates can span
                Deluxe + Superior in one submit. */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-medium text-gray-700">
                  Room Types * ({bulkFormData.roomTypeIds.length} selected)
                </label>
                <button
                  onClick={() => {
                    if (bulkFormData.roomTypeIds.length === roomTypes.length) {
                      setBulkFormData({...bulkFormData, roomTypeIds: []});
                    } else {
                      setBulkFormData({...bulkFormData, roomTypeIds: roomTypes.map(rt => rt.id)});
                    }
                  }}
                  className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                >
                  {bulkFormData.roomTypeIds.length === roomTypes.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="border rounded-lg p-2 max-h-48 overflow-y-auto space-y-1">
                {roomTypes.map((roomType) => (
                  <label key={roomType.id} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bulkFormData.roomTypeIds.includes(roomType.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setBulkFormData({...bulkFormData, roomTypeIds: [...bulkFormData.roomTypeIds, roomType.id]});
                        } else {
                          setBulkFormData({...bulkFormData, roomTypeIds: bulkFormData.roomTypeIds.filter(id => id !== roomType.id)});
                        }
                      }}
                      className="mr-3 rounded"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{roomType.name}</div>
                      <div className="text-xs text-gray-500">Max: {roomType.room_count} rooms</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Bulk Rates Fields — Rate Plan + Occupancies */}
            {(bulkActionType === 'rates' || bulkActionType === 'rate_inventory') && (() => {
              // Use the first selected room type from the multi-select for rate plan view
              // (rate plans are per room type; if multiple are selected, prompt to pick one)
              const selectedRoomTypeId = bulkRatePlanRoomTypeId || (bulkFormData.roomTypeIds && bulkFormData.roomTypeIds[0]) || '';
              const rpRoom = (ratePlanCalendar?.room_types || []).find(r => r.room_type_id === selectedRoomTypeId);
              const ratePlans = rpRoom?.rate_plans || [];

              // Room type options derived from currently loaded calendar
              const roomTypeOpts = (ratePlanCalendar?.room_types || []);

              return (
                <div className="space-y-3">
                  {/* Feb 28 2026 rev.4 — multi-room tabs.
                      When the user has selected multiple room types (via the
                      Rate+Inventory drawer's room checkboxes), show a compact
                      tab strip so they can switch context WITHOUT leaving the
                      drawer. Each tab's data (extras, rate plans, overrides)
                      is already keyed per-room in state, so switching just
                      changes `bulkRatePlanRoomTypeId`. */}
                  {(bulkFormData.roomTypeIds || []).length > 1 && (
                    <div className="flex flex-wrap gap-1 border-b border-orange-100 pb-2" data-testid="bulk-room-tabs">
                      {bulkFormData.roomTypeIds.map((rtId) => {
                        const rtOpt = roomTypeOpts.find(x => x.room_type_id === rtId);
                        const active = rtId === selectedRoomTypeId;
                        return (
                          <button
                            key={rtId}
                            type="button"
                            onClick={() => setBulkRatePlanRoomTypeId(rtId)}
                            data-testid={`bulk-room-tab-${rtId}`}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-orange-700 border-orange-200 hover:bg-orange-50'}`}
                          >
                            {rtOpt?.room_type_name || rtId}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Room Type *</label>
                    <Select
                      value={selectedRoomTypeId}
                      onValueChange={(v) => {
                        setBulkRatePlanRoomTypeId(v);
                        // View-context change only. Do NOT collapse the
                        // multi-room selection to a single value — the
                        // operator may have chosen several rooms via the
                        // pill row above and we must keep those intact.
                        // Only seed roomTypeIds when it is currently
                        // empty so the "Apply" button enable logic still
                        // has at least one room to work with.
                        setBulkFormData(prev => ({
                          ...prev,
                          roomTypeIds: (prev.roomTypeIds && prev.roomTypeIds.length)
                            ? prev.roomTypeIds
                            : [v],
                        }));
                      }}
                    >
                      <SelectTrigger className="text-sm" data-testid="bulk-rate-room-type-select">
                        <SelectValue placeholder="Select a room type" />
                      </SelectTrigger>
                      <SelectContent>
                        {roomTypeOpts.length === 0 ? (
                          <SelectItem value="__none__" disabled>No room types loaded</SelectItem>
                        ) : roomTypeOpts.map(rt => (
                          <SelectItem key={rt.room_type_id} value={rt.room_type_id}>
                            {rt.room_type_name} (max {rt.max_occupancy})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedRoomTypeId && ratePlans.length === 0 && (
                    <div className="p-3 text-xs text-gray-600 bg-orange-50 border border-orange-200 rounded">
                      No rate plans defined for this room type. Add via Settings → Add Rate Plan.
                    </div>
                  )}

                  {/* Extra-guest surcharges — per room type, applies to all rate plans of this room.
                      MMT CHM V3 cert steps 6/7/9: Adult + Child age-1 + Child age-2. These now
                      flow into the unified "Apply" footer below — no more dedicated save button. */}
                  {selectedRoomTypeId && (() => {
                    const staged = bulkExtraGuestInputs[selectedRoomTypeId] || {};
                    const rtObj = (roomTypes || []).find(x => x.id === selectedRoomTypeId) || {};
                    return (
                      <div className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-3 space-y-2" data-testid="bulk-extra-guest-panel">
                        <div className="text-xs font-semibold text-emerald-900">Additional guest rates for {rpRoom?.room_type_name || 'room'} <span className="font-normal text-emerald-700">(Room Type defaults — used unless a Rate Plan overrides below)</span></div>
                        <div className="grid grid-cols-4 gap-2">
                          <div>
                            <label className="block text-[11px] text-gray-700 mb-1">Extra Adult (₹)</label>
                            <Input type="number" min="0" step="0.01" className="h-8 text-sm"
                              placeholder={rtObj.extra_adult_rate != null ? String(rtObj.extra_adult_rate) : '0'}
                              value={staged.extra_adult_rate ?? ''}
                              onChange={(e) => setBulkExtraGuestInputs(prev => ({
                                ...prev, [selectedRoomTypeId]: { ...(prev[selectedRoomTypeId] || {}), extra_adult_rate: e.target.value },
                              }))}
                              data-testid="bulk-extra-adult-input" />
                          </div>
                          <div>
                            <label className="block text-[11px] text-gray-700 mb-1">Child (Age 1) ₹</label>
                            <Input type="number" min="0" step="0.01" className="h-8 text-sm"
                              placeholder={rtObj.extra_child_rate != null ? String(rtObj.extra_child_rate) : '0'}
                              value={staged.extra_child_rate_age1 ?? ''}
                              onChange={(e) => setBulkExtraGuestInputs(prev => ({
                                ...prev, [selectedRoomTypeId]: { ...(prev[selectedRoomTypeId] || {}), extra_child_rate_age1: e.target.value },
                              }))}
                              data-testid="bulk-extra-child-age1-input" />
                          </div>
                          <div>
                            <label className="block text-[11px] text-gray-700 mb-1">Child (Age 2) ₹</label>
                            <Input type="number" min="0" step="0.01" className="h-8 text-sm"
                              placeholder={rtObj.extra_child_rate_age2 != null ? String(rtObj.extra_child_rate_age2) : '0'}
                              value={staged.extra_child_rate_age2 ?? ''}
                              onChange={(e) => setBulkExtraGuestInputs(prev => ({
                                ...prev, [selectedRoomTypeId]: { ...(prev[selectedRoomTypeId] || {}), extra_child_rate_age2: e.target.value },
                              }))}
                              data-testid="bulk-extra-child-age2-input" />
                          </div>
                          <div>
                            <label className="block text-[11px] text-gray-700 mb-1">Child (Age 3) ₹</label>
                            <Input type="number" min="0" step="0.01" className="h-8 text-sm"
                              placeholder={rtObj.extra_child_rate_age3 != null ? String(rtObj.extra_child_rate_age3) : '—'}
                              value={staged.extra_child_rate_age3 ?? ''}
                              onChange={(e) => setBulkExtraGuestInputs(prev => ({
                                ...prev, [selectedRoomTypeId]: { ...(prev[selectedRoomTypeId] || {}), extra_child_rate_age3: e.target.value },
                              }))}
                              data-testid="bulk-extra-child-age3-input" />
                          </div>
                        </div>
                        <div className="text-[10px] text-emerald-800/80">
                          Sent to MMT as <span className="font-mono">&lt;AdditionalGuestRate&gt;</span> with ageRange 1 / 2 / 3 on Apply. Rate Plans may override per-plan below.
                        </div>
                        {/* Jun 2026 — Yatra Extend Inventory (Travelguru §5.5)
                            needs SEPARATE EA1/EA2/EA3 + EC1/EC2/EC3 guest
                            rates. Kept apart from the MMT/Agoda Extra Adult
                            above so those integrations are untouched. Sent as
                            yatra_extra_adult_rates / yatra_extra_child_rates on
                            the SAME Bulk Rates Apply. */}
                        <div className="border border-sky-200 bg-sky-50/50 rounded-md p-2.5 space-y-2" data-testid="bulk-yatra-guest-panel">
                          <div className="text-[11px] font-semibold text-sky-900">Yatra Additional Guest Rates <span className="font-normal text-sky-700">(Extend Inventory — EA1/EA2/EA3, EC1/EC2/EC3)</span></div>
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              ['yatra_ea1', 'Extra Adult 1 (EA1)'],
                              ['yatra_ea2', 'Extra Adult 2 (EA2)'],
                              ['yatra_ea3', 'Extra Adult 3 (EA3)'],
                              ['yatra_ec1', 'Child 1 (EC1)'],
                              ['yatra_ec2', 'Child 2 (EC2)'],
                              ['yatra_ec3', 'Child 3 (EC3)'],
                            ].map(([key, label]) => (
                              <div key={key}>
                                <label className="block text-[11px] text-gray-700 mb-1">{label} ₹</label>
                                <Input type="number" min="0" step="0.01" className="h-8 text-sm"
                                  placeholder="—"
                                  value={staged[key] ?? ''}
                                  onChange={(e) => setBulkExtraGuestInputs(prev => ({
                                    ...prev, [selectedRoomTypeId]: { ...(prev[selectedRoomTypeId] || {}), [key]: e.target.value },
                                  }))}
                                  data-testid={`bulk-${key.replace('_', '-')}-input`} />
                              </div>
                            ))}
                          </div>
                          <div className="text-[10px] text-sky-800/80">
                            Sent to Yatra as <span className="font-mono">&lt;AdditionalGuestAmount Type="EA1..EC3"&gt;</span> in the Extend Inventory push on Apply. Only filled values are sent.
                          </div>
                        </div>
                        {/* Feb 26 2026 — per-room "Set Rate Restrictions" trigger.
                            Opens a sub-drawer where the user configures Block/Unblock,
                            Min LOS, and Min Advance Booking cutoff per rate plan. */}
                        <button
                          type="button"
                          data-testid="bulk-open-restrictions-drawer"
                          onClick={() => {
                            const rp = Array.isArray(rpRoom?.rate_plans) ? rpRoom.rate_plans : [];
                            if (!selectedRoomTypeId) {
                              toast.error('Select a room type first');
                              return;
                            }
                            if (rp.length === 0) {
                              toast.error('No rate plans found for this room type — add one via Settings first');
                              return;
                            }
                            setRestrictionsDrawerRoomTypeId(selectedRoomTypeId);
                            // Seed draft from already-staged bulkRatePlanRestrictions.
                            const draft = {};
                            rp.forEach(p => {
                              if (!p?.id) return;
                              const s = (bulkRatePlanRestrictions || {})[p.id] || {};
                              draft[p.id] = {
                                closed:              s.closed,
                                min_los:             s.min_los ?? '',
                                min_advance_booking: s.min_advance_booking ?? '',
                              };
                            });
                            setRestrictionsDraft(draft);
                            setShowRestrictionsDrawer(true);
                          }}
                          className="w-full mt-2 flex items-center justify-center gap-1.5 py-1.5 rounded-md border border-emerald-300 text-emerald-800 hover:bg-emerald-100 text-xs font-medium"
                        >
                          <Settings className="w-3 h-3" /> Set Rate Restrictions
                        </button>
                        {/* Staged summary — quick glance of what will be submitted on Apply. */}
                        {(() => {
                          const staged = Object.entries(bulkRatePlanRestrictions || {})
                            .filter(([rpId, r]) => (rpRoom?.rate_plans || []).some(p => p.id === rpId)
                                && (r.closed !== undefined || r.min_los !== undefined || r.min_advance_booking !== undefined));
                          if (staged.length === 0) return null;
                          return (
                            <div className="text-[10px] text-emerald-900/90 space-y-0.5" data-testid="bulk-restrictions-summary">
                              <div className="font-semibold">Staged restrictions:</div>
                              {staged.map(([rpId, r]) => {
                                const p = (rpRoom?.rate_plans || []).find(x => x.id === rpId);
                                const bits = [];
                                if (r.closed !== undefined) bits.push(r.closed ? 'Blocked' : 'Unblocked');
                                if (r.min_los !== undefined && r.min_los !== '') bits.push(`MinLOS=${r.min_los}`);
                                if (r.min_advance_booking !== undefined && r.min_advance_booking !== '') bits.push(`MinAdvBook=${r.min_advance_booking}h`);
                                return <div key={rpId}>• {p?.code || rpId}: {bits.join(', ')}</div>;
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}

                  {ratePlans.map(rp => {
                    const expanded = bulkExpandedRatePlans.has(rp.id);
                    const inputs = bulkRatePlanInputs[rp.id] || {};
                    return (
                      <div key={rp.id} className="border border-orange-100 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => toggleBulkRatePlanExpanded(rp.id)}
                          className="w-full flex items-center justify-between px-3 py-2 bg-orange-50 hover:bg-orange-100 text-sm"
                          data-testid={`bulk-rate-plan-toggle-${rp.id}`}
                        >
                          <span className="flex items-center gap-2 font-medium text-orange-900">
                            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                            {rp.code}{rp.name ? ` — ${rp.name}` : ''}
                          </span>
                          <span className="text-xs text-gray-500">base ₹{(rp.base_rate || 0).toLocaleString()}</span>
                        </button>
                        {expanded && (
                          <div className="p-3 space-y-2 bg-white">
                            {Array.from({ length: rpRoom?.max_occupancy || 1 }).map((_, i) => {
                              const occ = i + 1;
                              return (
                                <div key={`occ-${rp.id}-${occ}`} className="flex items-center gap-2">
                                  <label className="text-xs text-gray-600 w-24">Occupancy {occ}</label>
                                  <Input
                                    type="number"
                                    placeholder={String(rp.base_rate || '')}
                                    value={inputs[occ] || ''}
                                    onChange={(e) => setBulkRatePlanInputs(prev => ({
                                      ...prev,
                                      [rp.id]: { ...(prev[rp.id] || {}), [occ]: e.target.value },
                                    }))}
                                    className="flex-1 text-sm h-8"
                                    data-testid={`bulk-rate-input-${rp.id}-${occ}`}
                                  />
                                </div>
                              );
                            })}
                            {/* Feb 28 2026 rev.3 — MMT V3 Scenario 14 support:
                                mark THIS rate plan to ride in a separate
                                <AvailRateUpdate locatorID="2"> block. Kept
                                intentionally minimal per user's non-refactor
                                request — one checkbox per plan. */}
                            <label className="flex items-center gap-2 mt-1 pt-2 border-t border-gray-100 text-[11px] text-gray-700 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={(bulkRatePlanLocatorGroups || {})[rp.id] === 2}
                                onChange={(e) => setBulkRatePlanLocatorGroups(prev => {
                                  const next = { ...(prev || {}) };
                                  if (e.target.checked) next[rp.id] = 2;
                                  else delete next[rp.id];
                                  return next;
                                })}
                                data-testid={`bulk-rate-locator2-${rp.id}`}
                              />
                              <span>Send as separate locator group (locatorID = 2)</span>
                            </label>
                            {/* Feb 28 2026 rev.4 — per-plan Additional Guest Rate override.
                                Unchecked = plan uses Room-Type defaults staged in the
                                emerald "Additional guest rates" panel above.
                                Checked = plan sends its own explicit values in
                                rate_plan_extras_overrides[] (backend already
                                supports this — MMT V3 CHM parity). */}
                            {(() => {
                              const ov = (bulkRatePlanExtrasOverrides || {})[rp.id] || null;
                              const enabled = !!ov;
                              return (
                                <div className="mt-1 pt-2 border-t border-gray-100 space-y-1.5">
                                  <label className="flex items-center gap-2 text-[11px] text-gray-700 cursor-pointer select-none">
                                    <input
                                      type="checkbox"
                                      checked={enabled}
                                      onChange={(e) => setBulkRatePlanExtrasOverrides(prev => {
                                        const next = { ...(prev || {}) };
                                        if (e.target.checked) next[rp.id] = ov || {};
                                        else delete next[rp.id];
                                        return next;
                                      })}
                                      data-testid={`bulk-rate-override-extras-${rp.id}`}
                                    />
                                    <span>Override Additional Guest Rates for this Rate Plan</span>
                                  </label>
                                  {enabled && (
                                    <div className="grid grid-cols-4 gap-2 pl-1">
                                      {[
                                        { key: 'adult_rate',       label: 'Adult'       },
                                        { key: 'child_rate_age1',  label: 'Child (1)'   },
                                        { key: 'child_rate_age2',  label: 'Child (2)'   },
                                        { key: 'child_rate_age3',  label: 'Child (3)'   },
                                      ].map(({ key, label }) => (
                                        <div key={key}>
                                          <label className="block text-[10px] text-gray-600 mb-0.5">{label} ₹</label>
                                          <Input type="number" min="0" step="0.01" className="h-7 text-xs"
                                            value={ov?.[key] ?? ''}
                                            onChange={(e) => setBulkRatePlanExtrasOverrides(prev => ({
                                              ...(prev || {}),
                                              [rp.id]: { ...((prev || {})[rp.id] || {}), [key]: e.target.value },
                                            }))}
                                            data-testid={`bulk-rate-override-${key}-${rp.id}`}
                                            placeholder="—"
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {(() => {
                              const s = mmtSyncStatus[rp.id];
                              if (!s) return null;
                              const bg = s.state === 'success' ? 'bg-green-50 border-green-300 text-green-800'
                                       : s.state === 'partial' ? 'bg-amber-50 border-amber-300 text-amber-800'
                                       : s.state === 'skipped' ? 'bg-gray-50 border-gray-300 text-gray-600'
                                       : s.state === 'syncing' ? 'bg-blue-50 border-blue-300 text-blue-800'
                                       :                          'bg-red-50 border-red-300 text-red-800';
                              const label = s.state === 'syncing' ? 'MMT: syncing…'
                                          : s.state === 'success' ? `MMT: ✓ synced (HTTP ${s.http_status ?? '—'})`
                                          : s.state === 'partial' ? `MMT: partial (${(s.errors || []).length} err, ${(s.warnings || []).length} warn)`
                                          : s.state === 'skipped' ? 'MMT: skipped (mapping missing)'
                                          :                          'MMT: failed';
                              const firstErr = (s.errors && s.errors[0]) || null;
                              return (
                                <div className={`mt-2 border rounded p-2 text-[11px] ${bg}`} data-testid={`mmt-sync-status-${rp.id}`}>
                                  <div className="font-medium">{label}</div>
                                  {firstErr && (firstErr.code || firstErr.description) && (
                                    <div className="mt-0.5 truncate" title={firstErr.description}>
                                      {firstErr.code ? <span className="font-mono">[{firstErr.code}]</span> : null} {firstErr.description || ''}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Bulk Inventory Fields — per-room inputs (uses room-type checkboxes above). */}
            {(bulkActionType === 'inventory' || bulkActionType === 'rate_inventory') && (() => {
              const propRoomTypes = (roomTypes || []).filter(rt => rt.property_id === selectedProperty?.id);
              const currentRoomTypeIds = bulkFormData.roomTypeIds || [];
              const inventoryEntries = bulkFormData.inventory || {};
              const setInventoryFor = (rtId, val) => setBulkFormData(prev => ({
                ...prev, inventory: { ...(prev.inventory || {}), [rtId]: val },
              }));
              const targeted = propRoomTypes.filter(rt => currentRoomTypeIds.includes(rt.id));
              return (
                <div className="space-y-3">
                  {targeted.length > 0 ? (
                    <div className="space-y-2" data-testid="bulk-inventory-per-room-inputs">
                      <div className="text-[11px] font-medium text-gray-600 mb-1">
                        Set new available-rooms count per selected room type
                      </div>
                      {targeted.map(rt => {
                        const cap = Number(rt.room_count ?? 0);
                        const raw = inventoryEntries[rt.id] ?? '';
                        const num = raw === '' ? null : Number(raw);
                        const overCap = num !== null && cap > 0 && num > cap;
                        const negative = num !== null && num < 0;
                        return (
                          <div key={rt.id} data-testid={`bulk-inventory-row-${rt.id}`}>
                            <div className="flex items-center gap-2">
                              <label className="text-xs text-gray-700 flex-1 truncate">
                                {rt.name} <span className="text-gray-400">(max {cap})</span>
                              </label>
                              <Input type="number" min="0" max={cap || undefined}
                                className={`w-24 h-8 text-sm ${overCap || negative ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                                value={raw}
                                placeholder="0"
                                onChange={(e) => setInventoryFor(rt.id, e.target.value)}
                                data-testid={`bulk-inventory-input-${rt.id}`} />
                            </div>
                            {overCap && (
                              <div className="text-[10px] text-red-600 mt-0.5" data-testid={`bulk-inventory-error-${rt.id}`}>
                                Exceeds max inventory for {rt.name} ({cap}). Reduce to ≤ {cap}.
                              </div>
                            )}
                            {negative && (
                              <div className="text-[10px] text-red-600 mt-0.5">Value cannot be negative.</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 border rounded p-3 bg-gray-50">
                      Select one or more room types above (or use "Select All") to enter inventory counts.
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Legacy single-input inventory block removed (Feb 23, 2026) — per-room
                inventory inputs above are the canonical UX. */}

            {/* Restrictions Fields */}
            {bulkActionType === 'restrictions' && (
              <div className="space-y-3">
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Restriction Type *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="restrictionType"
                      value="block"
                      checked={bulkFormData.restrictionType === 'block'}
                      onChange={(e) => setBulkFormData({...bulkFormData, restrictionType: e.target.value})}
                      className="mr-3"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Block Inventory</div>
                      <div className="text-xs text-gray-500">Mark selected dates as closed (no availability)</div>
                    </div>
                  </label>
                  <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="restrictionType"
                      value="unblock"
                      checked={bulkFormData.restrictionType === 'unblock'}
                      onChange={(e) => setBulkFormData({...bulkFormData, restrictionType: e.target.value})}
                      className="mr-3"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Unblock Inventory</div>
                      <div className="text-xs text-gray-500">Remove restrictions and open availability</div>
                    </div>
                  </label>
                </div>
                <div className={`rounded-lg p-3 ${bulkFormData.restrictionType === 'block' ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                  <div className="flex items-center gap-2">
                    {bulkFormData.restrictionType === 'block' ? (
                      <>
                        <div className="w-3 h-3 bg-red-500 rounded"></div>
                        <span className="font-medium text-red-800 text-xs">Calendar will show RED (blocked)</span>
                      </>
                    ) : (
                      <>
                        <div className="w-3 h-3 bg-green-500 rounded"></div>
                        <span className="font-medium text-green-800 text-xs">Calendar will show OPEN (available)</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={previewBulkAriXml}
                className="text-xs"
                data-testid="bulk-preview-xml-btn"
                title="Preview MMT ARI XML"
              >
                <FileText className="w-3 h-3 mr-1" /> Preview XML
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={closeBulkSidebar}
                className="flex-1"
                data-testid="bulk-cancel-btn"
              >
                Cancel
              </Button>
              {bulkActionType === 'rates' ? (
                <Button
                  type="button"
                  onClick={applyBulkRatesUnified}
                  disabled={
                    (bulkFormData.dateRanges || []).filter(r => r.from_date && r.to_date).length === 0
                    || !(bulkRatePlanRoomTypeId || (bulkFormData.roomTypeIds && bulkFormData.roomTypeIds[0]))
                  }
                  data-testid="bulk-apply-btn"
                  className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply
                </Button>
              ) : bulkActionType === 'rate_inventory' ? (
                <Button
                  type="button"
                  onClick={applyBulkRatesUnified}
                  disabled={
                    bulkFormData.roomTypeIds.length === 0
                    || (bulkFormData.dateRanges || []).filter(r => r.from_date && r.to_date).length === 0
                  }
                  data-testid="bulk-apply-btn"
                  className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleBulkSubmit}
                  disabled={
                    bulkFormData.roomTypeIds.length === 0
                    || (bulkFormData.dateRanges || []).filter(r => r.from_date && r.to_date).length === 0
                  }
                  data-testid="bulk-apply-btn"
                  className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply Changes
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Overlay for sidebar */}
      {showBulkSidebar && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 z-40"
          onClick={closeBulkSidebar}
        ></div>
      )}

      {/* Feb 28 2026 rev.3 — MMT V3 ARI XML preview modal. */}
      {previewXmlModal.open && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40" data-testid="preview-xml-modal">
          <div className="bg-white rounded-lg shadow-2xl w-[min(900px,95vw)] max-h-[90vh] flex flex-col">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-900">MMT ARI XML Preview</h3>
                <div className="text-xs text-gray-500">
                  Hotel Code: <span className="font-mono">{previewXmlModal.hotel_code || '—'}</span>
                  {previewXmlModal.locators.length > 0 && (
                    <> • Locators: <span className="font-mono">{previewXmlModal.locators.join(', ')}</span></>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPreviewXmlModal({ open: false, xml: '', hotel_code: '', warnings: [], locators: [] })}
                className="text-gray-400 hover:text-gray-600"
                data-testid="preview-xml-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {previewXmlModal.warnings.length > 0 && (
              <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900">
                <div className="font-semibold mb-0.5">Warnings</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {previewXmlModal.warnings.map((w, i) => (<li key={i}>{w}</li>))}
                </ul>
              </div>
            )}
            <div className="p-5 overflow-auto flex-1">
              <pre className="text-[11px] font-mono text-gray-800 bg-gray-50 border rounded p-3 whitespace-pre-wrap break-all" data-testid="preview-xml-content">
                {previewXmlModal.xml || '(no XML generated — check warnings)'}
              </pre>
            </div>
            <div className="px-5 py-3 border-t bg-gray-50 flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(previewXmlModal.xml || '');
                  toast.success('XML copied to clipboard');
                }}
                data-testid="preview-xml-copy"
              >Copy</Button>
              <Button
                type="button"
                onClick={() => setPreviewXmlModal({ open: false, xml: '', hotel_code: '', warnings: [], locators: [] })}
                data-testid="preview-xml-done"
              >Done</Button>
            </div>
          </div>
        </div>
      )}

      {/* Set Rate Restrictions sub-drawer (Feb 26 2026 — MMT Extranet parity).          Opens when the user clicks the ⚙️ button in the Bulk Rates drawer.
          Save merges into `bulkRatePlanRestrictions`; the main Apply picks
          them up and posts a `rate_plan_restrictions[]` array to the backend. */}
      {showRestrictionsDrawer && (() => {
        // ---- Safe lookup — every access is optional-chained so a missing
        //      room / rate plan can never crash React (white-screen bug).
        const rpRoom = (ratePlanCalendar?.room_types || [])
          .find(r => r?.room_type_id === restrictionsDrawerRoomTypeId);
        const plans = Array.isArray(rpRoom?.rate_plans) ? rpRoom.rate_plans : [];
        // Sentinel value "none" stands in for "no cutoff selected" — Radix
        // Select refuses value="" and crashes with a runtime error (that's
        // what caused the white screen in Feb-26 UAT).
        const CUTOFF_OPTIONS = [
          { value: 'none', label: '— No change —' },
          { value: '0',    label: '0 hrs (Same-day midnight)' },
          { value: '-6',   label: '6 hrs before check-in' },
          { value: '-12',  label: '12 hrs before check-in' },
          { value: '-24',  label: '24 hrs before check-in' },
          { value: '-48',  label: '48 hrs before check-in' },
        ];
        return (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-40 z-[60]" onClick={() => setShowRestrictionsDrawer(false)} />
            <div className="fixed inset-y-0 right-0 w-full sm:w-[440px] bg-white shadow-2xl z-[70] flex flex-col" data-testid="restrictions-drawer">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Set Rate Restrictions</h3>
                  <p className="text-xs text-gray-500">{rpRoom?.room_type_name || 'Room type'}</p>
                </div>
                <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowRestrictionsDrawer(false)}>
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                {plans.length === 0 && (
                  <div className="text-xs text-gray-500">No rate plans available for this room type.</div>
                )}
                {plans.map((p) => {
                  const d = restrictionsDraft[p.id] || {};
                  const upd = (patch) => setRestrictionsDraft(prev => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), ...patch } }));
                  // Feb 28 2026 rev.3 — tri-state helper for the 3 boolean
                  // restrictions (Closed, Closed-to-Arrival, Closed-to-Departure).
                  // Values: 'no_change' (undefined) | 'true' | 'false'.
                  const _triVal = (v) => v === true ? 'true' : v === false ? 'false' : 'no_change';
                  const _triApply = (key, sel) => upd({ [key]: sel === 'true' ? true : sel === 'false' ? false : undefined });
                  return (
                    <div key={p.id} className="border border-gray-200 rounded-lg p-3 space-y-2" data-testid={`restrictions-plan-${p.id}`}>
                      <div className="flex items-baseline justify-between">
                        <div className="text-sm font-semibold text-gray-800">{p.code}{p.name ? ` — ${p.name}` : ''}</div>
                      </div>

                      {/* Tri-state booleans — Feb 28 2026 rev.3 */}
                      <div className="grid grid-cols-3 gap-2 text-[11px]">
                        {[
                          { key: 'closed',                label: 'Closed',              testid: 'restr-closed' },
                          { key: 'closed_to_arrival',     label: 'Closed to Arrival',   testid: 'restr-cta' },
                          { key: 'closed_to_departure',   label: 'Closed to Departure', testid: 'restr-ctd' },
                        ].map(({ key, label, testid }) => (
                          <div key={key}>
                            <label className="block text-[10px] text-gray-600 mb-0.5">{label}</label>
                            <Select
                              value={_triVal(d[key])}
                              onValueChange={(v) => _triApply(key, v)}
                            >
                              <SelectTrigger className="h-8 text-xs" data-testid={`${testid}-${p.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="z-[100]" position="popper">
                                <SelectItem value="no_change">No Change</SelectItem>
                                <SelectItem value="true">True</SelectItem>
                                <SelectItem value="false">False</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>

                      {/* LOS Stay-Based */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] text-gray-600 mb-0.5">Min LOS (Stay-Based)</label>
                          <Input type="number" min="1" max="450" className="h-8 text-xs"
                            value={d.min_los_stay_based ?? d.min_los ?? ''}
                            onChange={(e) => upd({ min_los_stay_based: e.target.value })}
                            placeholder="—"
                            data-testid={`restr-min-los-stay-${p.id}`} />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-600 mb-0.5">Max LOS (Stay-Based)</label>
                          <Input type="number" min="1" max="450" className="h-8 text-xs"
                            value={d.max_los_stay_based ?? d.max_los ?? ''}
                            onChange={(e) => upd({ max_los_stay_based: e.target.value })}
                            placeholder="—"
                            data-testid={`restr-max-los-stay-${p.id}`} />
                        </div>
                      </div>
                      {/* LOS Arrival-Based */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] text-gray-600 mb-0.5">Min LOS (Arrival-Based)</label>
                          <Input type="number" min="1" max="450" className="h-8 text-xs"
                            value={d.min_los_arrival_based ?? ''}
                            onChange={(e) => upd({ min_los_arrival_based: e.target.value })}
                            placeholder="—"
                            data-testid={`restr-min-los-arr-${p.id}`} />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-600 mb-0.5">Max LOS (Arrival-Based)</label>
                          <Input type="number" min="1" max="450" className="h-8 text-xs"
                            value={d.max_los_arrival_based ?? ''}
                            onChange={(e) => upd({ max_los_arrival_based: e.target.value })}
                            placeholder="—"
                            data-testid={`restr-max-los-arr-${p.id}`} />
                        </div>
                      </div>
                      {/* Advance Booking Offsets */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] text-gray-600 mb-0.5">Min Advance Booking Offset</label>
                          <Input type="number" className="h-8 text-xs"
                            value={d.min_advance_booking_offset ?? d.min_advance_booking ?? ''}
                            onChange={(e) => upd({ min_advance_booking_offset: e.target.value })}
                            placeholder="—"
                            data-testid={`restr-min-abo-${p.id}`} />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-600 mb-0.5">Max Advance Booking Offset</label>
                          <Input type="number" className="h-8 text-xs"
                            value={d.max_advance_booking_offset ?? ''}
                            onChange={(e) => upd({ max_advance_booking_offset: e.target.value })}
                            placeholder="—"
                            data-testid={`restr-max-abo-${p.id}`} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowRestrictionsDrawer(false)} data-testid="restrictions-cancel">Cancel</Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white"
                  data-testid="restrictions-save"
                  onClick={() => {
                    // Merge draft → staged state, ignoring plans where every field is empty.
                    setBulkRatePlanRestrictions(prev => {
                      const next = { ...prev };
                      Object.entries(restrictionsDraft || {}).forEach(([rpId, r]) => {
                        const entry = {};
                        // Tri-state booleans — undefined = No Change → omit.
                        if (r.closed === true || r.closed === false) entry.closed = r.closed;
                        if (r.closed_to_arrival === true || r.closed_to_arrival === false)
                          entry.closed_to_arrival = r.closed_to_arrival;
                        if (r.closed_to_departure === true || r.closed_to_departure === false)
                          entry.closed_to_departure = r.closed_to_departure;
                        // Numerics — blank = No Change → omit.
                        const _num = (v) => {
                          if (v === '' || v == null) return undefined;
                          const n = parseInt(v, 10);
                          return Number.isFinite(n) ? n : undefined;
                        };
                        const numFields = [
                          'min_los_stay_based','max_los_stay_based',
                          'min_los_arrival_based','max_los_arrival_based',
                          'min_advance_booking_offset','max_advance_booking_offset',
                          // Legacy shorthand for backward compat with older
                          // drawer sessions still in memory:
                          'min_los','max_los',
                          'min_advance_booking','max_advance_booking',
                        ];
                        for (const k of numFields) {
                          const n = _num(r[k]);
                          if (n !== undefined) entry[k] = n;
                        }
                        // Validate min ≤ max pairs.
                        const pairs = [
                          ['min_los_stay_based','max_los_stay_based'],
                          ['min_los_arrival_based','max_los_arrival_based'],
                          ['min_advance_booking_offset','max_advance_booking_offset'],
                        ];
                        for (const [lo, hi] of pairs) {
                          if (entry[lo] != null && entry[hi] != null && entry[lo] > entry[hi]) {
                            toast.error(`${lo} must be ≤ ${hi} for ${rpId}`);
                            return;
                          }
                        }
                        if (Object.keys(entry).length > 0) {
                          next[rpId] = entry;
                        } else {
                          delete next[rpId];
                        }
                      });
                      return next;
                    });
                    toast.success('Restrictions staged — click Apply to submit');
                    setShowRestrictionsDrawer(false);
                  }}
                >
                  Save Restrictions
                </Button>
              </div>
            </div>
          </>
        );
      })()}

      {/* Competitor Upload Modal */}
      <Dialog open={showCompetitorUpload} onOpenChange={setShowCompetitorUpload} modal={false}>
        <DialogContent className="sm:max-w-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <DialogHeader>
              <DialogTitle>Upload Competitor Data</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hotel *
                </label>
                <Select 
                  value={competitorUploadData.selectedHotel}
                  onValueChange={(val) => setCompetitorUploadData(prev => ({ ...prev, selectedHotel: val }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Hotel" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((property) => (
                      <SelectItem key={property.id} value={property.id}>
                        {property.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Google Sheet URL *
                </label>
                <Input
                  type="url"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  value={competitorUploadData.sheetUrl}
                  onChange={(e) => setCompetitorUploadData(prev => ({ ...prev, sheetUrl: e.target.value }))}
                  data-testid="competitor-sheet-url"
                  className="w-full"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm text-blue-800">
                  <div className="font-medium mb-2">Required Sheet Format:</div>
                  <div className="font-mono text-xs bg-white p-2 rounded border">
                    Date | Rate | Competitor_Name<br/>
                    2025-01-15 | 5500 | Hotel ABC<br/>
                    2025-01-15 | 5800 | Hotel XYZ
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCompetitorUpload(false);
                    setCompetitorUploadData({ selectedHotel: '', sheetUrl: '' });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700"
                  onClick={handleImportCompetitorData}
                >
                  Connect & Import
                </Button>
              </div>
            </div>
          </motion.div>
        </DialogContent>
      </Dialog>

      {/* Create Booking Modal */}
      <Dialog open={showCreateBooking} onOpenChange={setShowCreateBooking}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{newBookingData._bookingId ? 'Edit Booking' : 'Create New Booking'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Booking Date *
                </label>
                <Input
                  type="date"
                  value={newBookingData.bookingDate}
                  onChange={(e) => setNewBookingData({...newBookingData, bookingDate: e.target.value})}
                  data-testid="new-booking-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Booking ID
                </label>
                <Input
                  type="text"
                  value={newBookingData.bookingId}
                  onChange={(e) => setNewBookingData({...newBookingData, bookingId: e.target.value})}
                  placeholder="Auto-generated if empty"
                  data-testid="new-booking-id"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Check-in Date *
                </label>
                <Input
                  type="date"
                  value={newBookingData.checkIn}
                  onChange={(e) => setNewBookingData({...newBookingData, checkIn: e.target.value})}
                  required
                  data-testid="new-booking-checkin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Check-out Date *
                </label>
                <Input
                  type="date"
                  value={newBookingData.checkOut}
                  onChange={(e) => setNewBookingData({...newBookingData, checkOut: e.target.value})}
                  required
                  data-testid="new-booking-checkout"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  OTA Name *
                </label>
                <Select 
                  value={newBookingData.otaName} 
                  onValueChange={(value) => setNewBookingData({...newBookingData, otaName: value})}
                >
                  <SelectTrigger data-testid="new-booking-ota">
                    <SelectValue placeholder="Select OTA" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mmt">MMT (MakeMyTrip)</SelectItem>
                    <SelectItem value="expedia">Expedia</SelectItem>
                    <SelectItem value="airbnb">Airbnb</SelectItem>
                    <SelectItem value="agoda">Agoda</SelectItem>
                    <SelectItem value="yatra">Yatra</SelectItem>
                    <SelectItem value="goibibo">Goibibo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rate (₹) *
                </label>
                <Input
                  type="number"
                  value={newBookingData.rate}
                  onChange={(e) => setNewBookingData({...newBookingData, rate: e.target.value})}
                  placeholder="5999"
                  min="0"
                  step="0.01"
                  required
                  data-testid="new-booking-rate"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Guest Name *
                </label>
                <Input
                  type="text"
                  value={newBookingData.guestName}
                  onChange={(e) => setNewBookingData({...newBookingData, guestName: e.target.value})}
                  placeholder="John Smith"
                  required
                  data-testid="new-booking-guest-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Guest Email
                </label>
                <Input
                  type="email"
                  value={newBookingData.guestEmail}
                  onChange={(e) => setNewBookingData({...newBookingData, guestEmail: e.target.value})}
                  placeholder="john@example.com"
                  data-testid="new-booking-guest-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Guest Phone
                </label>
                <Input
                  type="tel"
                  value={newBookingData.guestPhone}
                  onChange={(e) => setNewBookingData({...newBookingData, guestPhone: e.target.value})}
                  placeholder="+91-9876543210"
                  data-testid="new-booking-guest-phone"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Room Type *
                </label>
                <Select 
                  value={newBookingData.roomType} 
                  onValueChange={(value) => setNewBookingData({...newBookingData, roomType: value})}
                >
                  <SelectTrigger data-testid="new-booking-room-type">
                    <SelectValue placeholder="Select Room Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {roomTypes.map((roomType) => (
                      <SelectItem key={roomType.id} value={roomType.id}>
                        {roomType.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  No. of Rooms *
                </label>
                <Input
                  type="number"
                  value={newBookingData.numberOfRooms}
                  onChange={(e) => setNewBookingData({...newBookingData, numberOfRooms: e.target.value})}
                  min="1"
                  max="10"
                  required
                  data-testid="new-booking-rooms-count"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <Select 
                  value={newBookingData.status} 
                  onValueChange={(value) => setNewBookingData({...newBookingData, status: value})}
                >
                  <SelectTrigger data-testid="new-booking-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateBooking(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleCreateBooking}
                className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700"
                data-testid="save-booking-btn"
              >
                {newBookingData._bookingId ? 'Update Booking' : 'Save Booking'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Property Modal */}
      <Dialog open={showEditProperty} onOpenChange={setShowEditProperty}>
        <DialogContent className="sm:max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="edit-property-dialog">
          <DialogHeader>
            <DialogTitle>Edit Property Information</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Property Name *
                </label>
                <Input
                  value={editPropertyData.name || ''}
                  onChange={(e) => setEditPropertyData({...editPropertyData, name: e.target.value})}
                  data-testid="edit-property-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Rooms *
                </label>
                <Input
                  type="number"
                  value={editPropertyData.total_rooms ?? ''}
                  onChange={(e) => setEditPropertyData({...editPropertyData, total_rooms: e.target.value})}
                  min="1"
                  data-testid="edit-property-rooms"
                />
              </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address *
              </label>
              <Input
                value={editPropertyData.address || ''}
                onChange={(e) => setEditPropertyData({...editPropertyData, address: e.target.value})}
                data-testid="edit-property-address"
              />
            </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City *
                </label>
                <Input
                  value={editPropertyData.city || ''}
                  onChange={(e) => setEditPropertyData({...editPropertyData, city: e.target.value})}
                  data-testid="edit-property-city"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <Input
                  value={editPropertyData.country || ''}
                  onChange={(e) => setEditPropertyData({...editPropertyData, country: e.target.value})}
                  data-testid="edit-property-country"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  value={editPropertyData.email || ''}
                  onChange={(e) => setEditPropertyData({...editPropertyData, email: e.target.value})}
                  data-testid="edit-property-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone *
                </label>
                <Input
                  value={editPropertyData.phone || ''}
                  onChange={(e) => setEditPropertyData({...editPropertyData, phone: e.target.value})}
                  data-testid="edit-property-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GRB Agreement Date
                </label>
                <Input
                  type="date"
                  value={editPropertyData.grb_agreement_date || ''}
                  onChange={(e) => setEditPropertyData({...editPropertyData, grb_agreement_date: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GRB Setup Status
                </label>
                <Select
                  value={editPropertyData.grb_setup_status || 'Pending'}
                  onValueChange={(value) => setEditPropertyData({...editPropertyData, grb_setup_status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  OTA Live Date
                </label>
                <Input
                  type="date"
                  value={editPropertyData.ota_live_date || ''}
                  onChange={(e) => setEditPropertyData({...editPropertyData, ota_live_date: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Reservation Date
                </label>
                <Input
                  type="date"
                  value={editPropertyData.first_reservation_date || ''}
                  onChange={(e) => setEditPropertyData({...editPropertyData, first_reservation_date: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Salesperson Name
                </label>
                <Input
                  value={editPropertyData.salesperson_name || ''}
                  onChange={(e) => setEditPropertyData({...editPropertyData, salesperson_name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Property Status
                </label>
                <Select
                  value={editPropertyData.status || 'active'}
                  onValueChange={(value) => setEditPropertyData({...editPropertyData, status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ---------- Room Types editor (extends Edit Property modal) ---------- */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-800">Room Types</h4>
                <span className="text-[11px] text-gray-500">
                  Capacity, count &amp; base rate propagate to Settings / Calendar / Competitor
                </span>
              </div>
              {(roomTypes || []).filter(rt => rt.property_id === editPropertyData.id).length === 0 ? (
                <div className="text-xs text-gray-500 border rounded p-3 bg-gray-50">
                  No room types defined for this property yet. Add them via Settings → Room Types.
                </div>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto pr-1" data-testid="edit-property-room-types-list">
                  {(roomTypes || []).filter(rt => rt.property_id === editPropertyData.id).map(rt => {
                    const edits = (editPropertyData.room_type_edits || {})[rt.id] || {};
                    const getVal = (k, dflt) => edits[k] !== undefined ? edits[k] : (rt[k] ?? dflt);
                    const setVal = (k, v) => setEditPropertyData(prev => ({
                      ...prev,
                      room_type_edits: {
                        ...(prev.room_type_edits || {}),
                        [rt.id]: { ...(prev.room_type_edits || {})[rt.id], [k]: v },
                      },
                    }));
                    return (
                      <div key={rt.id} className="border border-orange-100 rounded-lg p-4 bg-orange-50/40" data-testid={`edit-room-type-row-${rt.id}`}>
                        <div className="text-sm font-semibold text-orange-900 mb-3">{rt.name}</div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Capacity</label>
                            <Input type="number" min="1" className="h-9 text-sm"
                              value={getVal('max_occupancy', '')}
                              onChange={(e) => setVal('max_occupancy', e.target.value)}
                              data-testid={`edit-rt-max-occ-${rt.id}`} />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Count</label>
                            <Input type="number" min="0" className="h-9 text-sm"
                              value={getVal('room_count', '')}
                              onChange={(e) => setVal('room_count', e.target.value)}
                              data-testid={`edit-rt-count-${rt.id}`} />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Base Rate</label>
                            <Input type="number" min="0" step="0.01" className="h-9 text-sm"
                              value={getVal('base_rate', '')}
                              onChange={(e) => setVal('base_rate', e.target.value)}
                              data-testid={`edit-rt-base-rate-${rt.id}`} />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Extra Adult</label>
                            <Input type="number" min="0" step="0.01" className="h-9 text-sm"
                              value={getVal('extra_adult_rate', '')}
                              onChange={(e) => setVal('extra_adult_rate', e.target.value)}
                              data-testid={`edit-rt-extra-adult-${rt.id}`} />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Child (Age 1)</label>
                            <Input type="number" min="0" step="0.01" className="h-9 text-sm"
                              value={getVal('extra_child_rate', '')}
                              onChange={(e) => setVal('extra_child_rate', e.target.value)}
                              data-testid={`edit-rt-extra-child-${rt.id}`} />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Child (Age 2)</label>
                            <Input type="number" min="0" step="0.01" className="h-9 text-sm"
                              value={getVal('extra_child_rate_age2', '')}
                              onChange={(e) => setVal('extra_child_rate_age2', e.target.value)}
                              data-testid={`edit-rt-extra-child-age2-${rt.id}`} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowEditProperty(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePropertyChanges}
                className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600"
                data-testid="save-property-btn"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Booking Modal */}
      {selectedBookingForView && (
        <Dialog open={!!selectedBookingForView} onOpenChange={() => setSelectedBookingForView(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Booking Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="font-medium text-gray-600">Booking ID:</label>
                  <div className="font-mono text-blue-600">{selectedBookingForView.ota_booking_id}</div>
                </div>
                <div>
                  <label className="font-medium text-gray-600">Status:</label>
                  <Badge className={selectedBookingForView.status === 'confirmed' ? 'bg-green-100 text-green-700' : ''}>
                    {selectedBookingForView.status}
                  </Badge>
                </div>
                <div>
                  <label className="font-medium text-gray-600">Guest Name:</label>
                  <div>{selectedBookingForView.guest_name}</div>
                </div>
                <div>
                  <label className="font-medium text-gray-600">Guest Email:</label>
                  <div>{selectedBookingForView.guest_email}</div>
                </div>
                <div>
                  <label className="font-medium text-gray-600">Guest Phone:</label>
                  <div>{selectedBookingForView.guest_phone}</div>
                </div>
                <div>
                  <label className="font-medium text-gray-600">OTA:</label>
                  <div className="flex items-center gap-2">
                    {getOTALogo(selectedBookingForView.ota_type)}
                    <span>{selectedBookingForView.ota_type ? selectedBookingForView.ota_type.toUpperCase() : 'N/A'}</span>
                  </div>
                </div>
                <div>
                  <label className="font-medium text-gray-600">Check-in:</label>
                  <div>{selectedBookingForView.check_in_date}</div>
                </div>
                <div>
                  <label className="font-medium text-gray-600">Check-out:</label>
                  <div>{selectedBookingForView.check_out_date}</div>
                </div>
                <div>
                  <label className="font-medium text-gray-600">Nights:</label>
                  <div>{selectedBookingForView.nights}</div>
                </div>
                <div>
                  <label className="font-medium text-gray-600">Rooms:</label>
                  <div>{selectedBookingForView.rooms_booked}</div>
                </div>
                <div className="col-span-2">
                  <label className="font-medium text-gray-600">Total Amount:</label>
                  <div className="text-lg font-bold text-green-600">₹{selectedBookingForView.total_amount.toLocaleString()}</div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Modal */}
      {/* Clear Storage Modal */}
      <Dialog open={showClearStorageModal} onOpenChange={setShowClearStorageModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Clear Storage</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select the data you want to clear. This action cannot be undone.
            </p>
            
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={clearStorageOptions.competitor_data}
                  onChange={(e) => setClearStorageOptions({
                    ...clearStorageOptions,
                    competitor_data: e.target.checked
                  })}
                  className="w-4 h-4 text-orange-600 rounded"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">Competitor Data</div>
                  <div className="text-xs text-gray-500">Remove all competitor rates and metadata</div>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={clearStorageOptions.audit_logs}
                  onChange={(e) => setClearStorageOptions({
                    ...clearStorageOptions,
                    audit_logs: e.target.checked
                  })}
                  className="w-4 h-4 text-orange-600 rounded"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">Old Audit Logs</div>
                  <div className="text-xs text-gray-500">Remove logs older than 90 days</div>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={clearStorageOptions.old_uploads}
                  onChange={(e) => setClearStorageOptions({
                    ...clearStorageOptions,
                    old_uploads: e.target.checked
                  })}
                  className="w-4 h-4 text-orange-600 rounded"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">Old Uploaded Files</div>
                  <div className="text-xs text-gray-500">Remove files older than 180 days</div>
                </div>
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowClearStorageModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleClearStorage}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Clear Selected
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add OTA API Modal */}
      <Dialog open={showAddOTAModal} onOpenChange={setShowAddOTAModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Connect New OTA API - Step {otaModalStep} of 2</DialogTitle>
          </DialogHeader>
          
          {otaModalStep === 1 ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">OTA Name *</label>
                <Input
                  placeholder="e.g., MakeMyTrip, Booking.com, Agoda"
                  value={otaFormData.ota_name}
                  onChange={(e) => setOtaFormData({...otaFormData, ota_name: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Authentication Type *</label>
                <Select
                  value={otaFormData.auth_type}
                  onValueChange={(value) => setOtaFormData({...otaFormData, auth_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="API Key">API Key</SelectItem>
                    <SelectItem value="Basic Auth">Basic Auth</SelectItem>
                    <SelectItem value="Bearer Token">Bearer Token</SelectItem>
                    <SelectItem value="OAuth">OAuth</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {otaFormData.auth_type === 'API Key' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">API Key *</label>
                  <Input
                    type="password"
                    placeholder="Enter API key"
                    value={otaFormData.api_key}
                    onChange={(e) => setOtaFormData({...otaFormData, api_key: e.target.value})}
                  />
                </div>
              )}

              {otaFormData.auth_type === 'Basic Auth' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Username *</label>
                    <Input
                      placeholder="Enter username"
                      value={otaFormData.username}
                      onChange={(e) => setOtaFormData({...otaFormData, username: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
                    <Input
                      type="password"
                      placeholder="Enter password"
                      value={otaFormData.password}
                      onChange={(e) => setOtaFormData({...otaFormData, password: e.target.value})}
                    />
                  </div>
                </>
              )}

              {otaFormData.auth_type === 'Bearer Token' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bearer Token *</label>
                  <Input
                    type="password"
                    placeholder="Enter bearer token"
                    value={otaFormData.bearer_token}
                    onChange={(e) => setOtaFormData({...otaFormData, bearer_token: e.target.value})}
                  />
                </div>
              )}

              {otaFormData.auth_type === 'OAuth' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">OAuth Token URL</label>
                  <Input
                    placeholder="https://auth.ota.com/token"
                    value={otaFormData.oauth_token_url}
                    onChange={(e) => setOtaFormData({...otaFormData, oauth_token_url: e.target.value})}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  className="w-full border border-gray-300 rounded-md p-2 text-sm"
                  rows="3"
                  placeholder="Brief description of this API integration"
                  value={otaFormData.description}
                  onChange={(e) => setOtaFormData({...otaFormData, description: e.target.value})}
                />
              </div>

              <Button
                onClick={() => setOtaModalStep(2)}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
              >
                Next: Add Endpoints
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-gray-700">Endpoints</div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addEndpointRow}
                  className="text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Endpoint
                </Button>
              </div>

              {otaFormData.endpoints.length === 0 && (
                <div className="text-center py-6 text-sm text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                  No endpoints added yet. Click "Add Endpoint" to create one.
                </div>
              )}

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {otaFormData.endpoints.map((endpoint, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-medium text-gray-700">Endpoint {index + 1}</div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeEndpointRow(index)}
                        className="text-red-600 hover:bg-red-50 h-6 w-6 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Endpoint Name *</label>
                        <Input
                          placeholder="e.g., ARI Update"
                          value={endpoint.endpoint_name}
                          onChange={(e) => updateEndpointField(index, 'endpoint_name', e.target.value)}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Category *</label>
                        <Select
                          value={endpoint.category}
                          onValueChange={(value) => updateEndpointField(index, 'category', value)}
                        >
                          <SelectTrigger className="text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ARI">ARI</SelectItem>
                            <SelectItem value="Booking">Booking</SelectItem>
                            <SelectItem value="Listing">Listing</SelectItem>
                            <SelectItem value="Notifications">Notifications</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Base URL *</label>
                      <Input
                        placeholder="https://api.ota.com/v1/endpoint"
                        value={endpoint.base_url}
                        onChange={(e) => updateEndpointField(index, 'base_url', e.target.value)}
                        className="text-sm"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Method *</label>
                        <Select
                          value={endpoint.method}
                          onValueChange={(value) => updateEndpointField(index, 'method', value)}
                        >
                          <SelectTrigger className="text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="GET">GET</SelectItem>
                            <SelectItem value="POST">POST</SelectItem>
                            <SelectItem value="PUT">PUT</SelectItem>
                            <SelectItem value="DELETE">DELETE</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center pt-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={endpoint.is_active}
                            onChange={(e) => updateEndpointField(index, 'is_active', e.target.checked)}
                            className="w-4 h-4 text-orange-600 rounded"
                          />
                          <span className="text-xs text-gray-700">Is Active</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                      <textarea
                        className="w-full border border-gray-300 rounded-md p-2 text-xs"
                        rows="2"
                        placeholder="Optional notes about this endpoint"
                        value={endpoint.notes}
                        onChange={(e) => updateEndpointField(index, 'notes', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setOtaModalStep(1)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmitOTA}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  Save API Configuration
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* OTA Detail View Modal */}
      <Dialog open={showOTADetailModal} onOpenChange={setShowOTADetailModal}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedOTA?.ota_name} - API Details</DialogTitle>
          </DialogHeader>
          
          {selectedOTA && (
            <div className="space-y-4">
              {/* OTA Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-600">Auth Type</div>
                    <div className="font-medium">{selectedOTA.auth_type}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Created By</div>
                    <div className="font-medium">{selectedOTA.created_by}</div>
                  </div>
                </div>
                {selectedOTA.description && (
                  <div className="pt-2 border-t">
                    <div className="text-gray-600 text-sm">Description</div>
                    <div className="text-sm">{selectedOTA.description}</div>
                  </div>
                )}
              </div>

              {/* Endpoints */}
              <div>
                <div className="text-sm font-medium text-gray-700 mb-3">
                  Endpoints ({selectedOTA.endpoints?.length || 0})
                </div>
                
                {selectedOTA.endpoints && selectedOTA.endpoints.length > 0 ? (
                  <div className="space-y-2">
                    {selectedOTA.endpoints.map((endpoint) => (
                      <div key={endpoint.id} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {endpoint.category}
                            </Badge>
                            <span className="font-medium text-sm">{endpoint.endpoint_name}</span>
                            {endpoint.is_active && (
                              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                            )}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleTestOTAConnection(selectedOTA.id, endpoint.id)}
                            disabled={testingEndpoint === endpoint.id}
                            className="text-xs"
                          >
                            {testingEndpoint === endpoint.id ? 'Testing...' : 'Test'}
                          </Button>
                        </div>
                        
                        <div className="text-xs text-gray-600 space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-700">{endpoint.method}</span>
                            <span className="flex-1 truncate">{endpoint.base_url}</span>
                          </div>
                          {endpoint.notes && (
                            <div className="text-gray-500">{endpoint.notes}</div>
                          )}
                        </div>

                        {testResults[endpoint.id] && (
                          <div className={`mt-2 p-2 rounded text-xs ${
                            testResults[endpoint.id].success 
                              ? 'bg-green-50 text-green-700 border border-green-200' 
                              : 'bg-red-50 text-red-700 border border-red-200'
                          }`}>
                            {testResults[endpoint.id].message || 'Test completed'}
                            {testResults[endpoint.id].status_code && (
                              <span className="ml-2 font-mono">
                                ({testResults[endpoint.id].status_code})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-sm text-gray-500">
                    No endpoints configured
                  </div>
                )}
              </div>

              {/* Linked Properties */}
              {selectedOTA.linked_properties && selectedOTA.linked_properties.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-3">
                    Linked Properties ({selectedOTA.linked_properties.length})
                  </div>
                  <div className="space-y-2">
                    {selectedOTA.linked_properties.map((prop) => (
                      <div key={prop.property_id} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-blue-900 text-sm">{prop.property_name}</div>
                            <div className="text-xs text-blue-700 mt-1 space-y-1">
                              <div>
                                <span className="font-medium">External ID:</span> {prop.external_property_id || 'N/A'}
                              </div>
                              <div>
                                <span className="font-medium">Status:</span>{' '}
                                <span className="inline-flex items-center">
                                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1"></span>
                                  Linked
                                </span>
                              </div>
                              <div>
                                {prop.mapped_endpoints.length} endpoint(s) mapped
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUnlinkProperty(prop.link_id, prop.property_name)}
                            className="text-xs text-red-600 hover:bg-red-50 border-red-200"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Unlink
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* See All Properties Modal — dependency-aware review + bulk delete */}
      <Dialog open={showAllPropertiesModal} onOpenChange={setShowAllPropertiesModal}>
        <DialogContent className="sm:max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="see-all-properties-modal">
          <DialogHeader>
            <DialogTitle>All Properties</DialogTitle>
          </DialogHeader>

          <Tabs value={allPropsTab} onValueChange={setAllPropsTab} className="w-full">
            <TabsList data-testid="all-props-tabs">
              <TabsTrigger value="properties" data-testid="all-props-tab-properties">Properties</TabsTrigger>
              <TabsTrigger value="mapping" data-testid="all-props-tab-mapping">Property Mapping</TabsTrigger>
            </TabsList>
            <TabsContent value="properties">
          {(() => {
            const rows = allPropertiesWithDeps;
            const q = propSearchQuery.trim().toLowerCase();
            const filtered = q
              ? rows.filter(p =>
                  (p.name || '').toLowerCase().includes(q) ||
                  (p.grb_id || '').toLowerCase().includes(q) ||
                  (p.city || '').toLowerCase().includes(q) ||
                  (p.id || '').toLowerCase().includes(q)
                )
              : rows;

            // Cluster likely duplicates by normalized (name, city) — UI-only
            const groupCounts = filtered.reduce((acc, p) => {
              acc[p.group_key] = (acc[p.group_key] || 0) + 1;
              return acc;
            }, {});
            const sorted = [...filtered].sort((a, b) => {
              const ac = groupCounts[a.group_key] || 0;
              const bc = groupCounts[b.group_key] || 0;
              if (ac !== bc) return bc - ac;
              if (a.group_key !== b.group_key) return (a.group_key || '').localeCompare(b.group_key || '');
              return (a.grb_id || '').localeCompare(b.grb_id || '');
            });

            const allIds = sorted.map(p => p.id);
            const allSelected = allIds.length > 0 && allIds.every(id => selectedPropertyIds.has(id));
            const someSelected = allIds.some(id => selectedPropertyIds.has(id));

            const toggle = (id) => {
              const next = new Set(selectedPropertyIds);
              if (next.has(id)) next.delete(id); else next.add(id);
              setSelectedPropertyIds(next);
            };
            const toggleAll = () => {
              if (allSelected) setSelectedPropertyIds(new Set());
              else setSelectedPropertyIds(new Set(allIds));
            };

            return (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-sm text-gray-600" data-testid="all-properties-summary">
                    Total: <span className="font-semibold">{rows.length}</span> properties
                    {q && <> · Showing <span className="font-semibold">{filtered.length}</span></>}
                    {selectedPropertyIds.size > 0 && (
                      <> · <span className="font-semibold text-red-600">{selectedPropertyIds.size} selected</span></>
                    )}
                  </div>
                  <div className="flex gap-2 items-center">
                    <input
                      data-testid="all-properties-search"
                      type="text"
                      placeholder="Search name, GRB ID, city, UUID..."
                      value={propSearchQuery}
                      onChange={(e) => setPropSearchQuery(e.target.value)}
                      className="border rounded-md px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <Button
                      data-testid="bulk-delete-selected-btn"
                      onClick={() => setShowBulkDeleteConfirm(true)}
                      disabled={selectedPropertyIds.size === 0 || bulkDeleteBusy}
                      className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete Selected ({selectedPropertyIds.size})
                    </Button>
                  </div>
                </div>

                {loadingAllProperties ? (
                  <div className="py-12 text-center text-gray-500" data-testid="all-properties-loading">Loading properties…</div>
                ) : sorted.length === 0 ? (
                  <div className="py-12 text-center text-gray-500" data-testid="all-properties-empty">
                    {q ? 'No properties match your search.' : 'No properties.'}
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-x-auto">
                    <table className="w-full text-sm" data-testid="all-properties-table">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="p-3 w-10 text-left">
                            <input
                              data-testid="all-properties-select-all"
                              type="checkbox"
                              checked={allSelected}
                              ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                              onChange={toggleAll}
                            />
                          </th>
                          <th className="text-left p-3 font-medium text-gray-700">Property</th>
                          <th className="text-left p-3 font-medium text-gray-700">GRB ID</th>
                          <th className="text-left p-3 font-medium text-gray-700">City</th>
                          <th className="text-right p-3 font-medium text-gray-700">Bookings</th>
                          <th className="text-right p-3 font-medium text-gray-700">No. of Room Types</th>
                          <th className="text-left p-3 font-medium text-gray-700">Dependencies</th>
                          <th className="text-center p-3 font-medium text-gray-700">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.map((p, idx) => {
                          const isDupGroup = (groupCounts[p.group_key] || 0) > 1;
                          const prev = idx > 0 ? sorted[idx - 1] : null;
                          const newGroup = !prev || prev.group_key !== p.group_key;
                          const d = p.dependencies || {};
                          return (
                            <tr
                              key={p.id}
                              data-testid={`property-row-${p.grb_id || p.id}`}
                              className={`border-t hover:bg-gray-50 ${newGroup && idx > 0 ? 'border-t-2 border-t-gray-300' : ''} ${isDupGroup ? 'bg-amber-50/40' : ''}`}
                            >
                              <td className="p-3">
                                <input
                                  data-testid={`property-checkbox-${p.grb_id || p.id}`}
                                  type="checkbox"
                                  checked={selectedPropertyIds.has(p.id)}
                                  onChange={() => toggle(p.id)}
                                />
                              </td>
                              <td className="p-3 font-medium">
                                {p.name}
                                {isDupGroup && (
                                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 uppercase tracking-wide">Possible duplicate</span>
                                )}
                              </td>
                              <td className="p-3 text-gray-600 font-mono text-xs">{p.grb_id || '-'}</td>
                              <td className="p-3 text-gray-600">{p.city || '-'}</td>
                              <td className="p-3 text-right font-semibold" data-testid={`property-bookings-${p.grb_id || p.id}`}>{d.bookings ?? 0}</td>
                              <td className="p-3 text-right font-semibold" data-testid={`property-roomtypes-${p.grb_id || p.id}`}>{d.rooms ?? 0}</td>
                              <td className="p-3">
                                <div className="flex flex-wrap gap-1">
                                  {d.mmt_mapping && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-testid={`dep-mmt-${p.grb_id || p.id}`}>MMT</span>}
                                  {d.other_ota_mappings > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">OTA×{d.other_ota_mappings}</span>}
                                  {d.rooms > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">Rooms×{d.rooms}</span>}
                                  {d.rate_plans > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">Rates×{d.rate_plans}</span>}
                                  {d.listings > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">Listings×{d.listings}</span>}
                                  {d.promotions > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">Promos×{d.promotions}</span>}
                                  {d.payments > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">Payments×{d.payments}</span>}
                                </div>
                              </td>
                              <td className="p-3 text-center">
                                <span className={`property-status-badge ${p.status || 'active'}`}>
                                  <span className={`property-status-indicator ${p.status || 'active'}`}></span>
                                  {(p.status || 'active').charAt(0).toUpperCase() + (p.status || 'active').slice(1)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {bulkDeleteResults && (
                  <div className="mt-2 border rounded-lg p-3 bg-gray-50" data-testid="bulk-delete-results">
                    <div className="text-sm font-semibold mb-2">
                      Last delete result — {bulkDeleteResults.deleted} deleted, {bulkDeleteResults.failed} failed
                    </div>
                    <ul className="text-xs space-y-0.5">
                      {(bulkDeleteResults.results || []).map((r, i) => (
                        <li key={i} className={r.success ? 'text-green-700' : 'text-red-700'}>
                          {r.success ? '✓' : '✗'} {r.name || r.property_id} {r.grb_id ? `(${r.grb_id})` : ''}{r.error ? ` — ${r.error}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })()}
            </TabsContent>
            <TabsContent value="mapping">
              <div className="space-y-5">
                <PropertyMappingTab
                  apiBase={API}
                  properties={allPropertiesWithDeps}
                  onMerged={reloadAllProperties}
                />
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Delete Property Confirmation */}
      {propertyToDelete && (
        <Dialog open={!!propertyToDelete} onOpenChange={() => setPropertyToDelete(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete Property</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to permanently delete <strong>{propertyToDelete.name}</strong>?
              </p>
              <p className="text-sm text-red-600">
                ⚠️ This will delete all associated data including room types, bookings, and availability. This action cannot be undone.
              </p>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setPropertyToDelete(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDeleteProperty(propertyToDelete.id)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete Property
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk Delete Confirmation — lists every selected property + warnings */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="bulk-delete-confirm-modal">
          <DialogHeader>
            <DialogTitle className="text-red-700">Confirm Bulk Delete</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              You are about to permanently delete <strong>{selectedPropertyIds.size}</strong> property record{selectedPropertyIds.size === 1 ? '' : 's'}:
            </p>
            <div className="border rounded-lg divide-y max-h-[45vh] overflow-y-auto">
              {allPropertiesWithDeps.filter(p => selectedPropertyIds.has(p.id)).map(p => {
                const d = p.dependencies || {};
                const anyDep = d.mmt_mapping || (d.other_ota_mappings || 0) > 0 || (d.rooms || 0) > 0 || (d.rate_plans || 0) > 0 || (d.listings || 0) > 0 || (d.promotions || 0) > 0 || (d.payments || 0) > 0 || (d.bookings || 0) > 0;
                return (
                  <div key={p.id} className="p-3 text-sm" data-testid={`bulk-delete-confirm-row-${p.grb_id || p.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-gray-500">{p.grb_id || '—'} · {p.city || '—'} · {d.bookings || 0} bookings</div>
                      </div>
                      {anyDep && (
                        <span className="text-[11px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 whitespace-nowrap">Has dependencies</span>
                      )}
                    </div>
                    {d.mmt_mapping && (
                      <div className="mt-2 text-xs bg-red-50 border border-red-200 rounded p-2 text-red-700" data-testid={`mmt-warning-${p.grb_id || p.id}`}>
                        ⚠ This property has an active <strong>MMT mapping</strong>. Deleting will remove the mapping record for this property only. MMT integration code, scheduler, and other properties are NOT affected.
                      </div>
                    )}
                    {((d.other_ota_mappings || 0) > 0 || (d.rooms || 0) > 0 || (d.rate_plans || 0) > 0 || (d.listings || 0) > 0 || (d.promotions || 0) > 0 || (d.payments || 0) > 0) && (
                      <div className="mt-2 text-xs text-gray-600 flex flex-wrap gap-x-3 gap-y-0.5">
                        {(d.other_ota_mappings || 0) > 0 && <span>Other OTA mappings: {d.other_ota_mappings}</span>}
                        {(d.rooms || 0) > 0 && <span>Rooms: {d.rooms}</span>}
                        {(d.rate_plans || 0) > 0 && <span>Rate plans: {d.rate_plans}</span>}
                        {((d.rate_plan_rates || 0) + (d.rate_plan_restrictions || 0) + (d.availability_records || 0)) > 0 && (
                          <span>Rate/inventory records: {(d.rate_plan_rates || 0) + (d.rate_plan_restrictions || 0) + (d.availability_records || 0)}</span>
                        )}
                        {(d.listings || 0) > 0 && <span>Listings: {d.listings}</span>}
                        {(d.promotions || 0) > 0 && <span>Promotions: {d.promotions}</span>}
                        {(d.payments || 0) > 0 && <span>Payment/API links: {d.payments}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500">
              Booking history (Google Sheets sync, MMT notifications, audit logs) is preserved. Only per-property configuration and mapping records are removed.
            </p>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowBulkDeleteConfirm(false)}
                disabled={bulkDeleteBusy}
                className="flex-1"
                data-testid="bulk-delete-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => { setShowBulkDeleteConfirm(false); await handleBulkDeleteProperties(); }}
                disabled={bulkDeleteBusy || selectedPropertyIds.size === 0}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="bulk-delete-confirm-btn"
              >
                {bulkDeleteBusy ? 'Deleting…' : `Delete ${selectedPropertyIds.size} Selected`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Analytics Upload Modal */}
      <Dialog open={showAnalyticsUploadModal} onOpenChange={setShowAnalyticsUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Analytics Excel</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Select OTA <span className="text-red-500">*</span>
              </label>
              <Select
                value={analyticsUploadOTA}
                onValueChange={setAnalyticsUploadOTA}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select OTA" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MMT">MakeMyTrip (MMT)</SelectItem>
                  <SelectItem value="Agoda">Agoda</SelectItem>
                  <SelectItem value="Airbnb">Airbnb</SelectItem>
                  <SelectItem value="Expedia">Expedia</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">
                Upload Excel File <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleAnalyticsUploadSubmit(file);
                  }
                }}
                className="mt-1 block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
              />
              <p className="mt-1 text-xs text-gray-500">
                Format: Excel with Competitor & GRB columns
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAnalyticsUploadModal(false);
                  setAnalyticsUploadOTA('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bookings Upload Modal */}
      <Dialog open={showBookingsUploadModal} onOpenChange={setShowBookingsUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Bookings</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Upload Excel File <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleBookingsUpload(file);
                  }
                }}
                className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
              />
              <p className="mt-2 text-xs text-gray-500">
                📋 Format: Booking ID, Guest Name, Booking Date, Check-in Date, Check-out Date, Nights, Room Type, Rate, Total Amount, Status
              </p>
              <p className="mt-1 text-xs text-blue-600">
                💡 Tip: Download template to see the correct format
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowBookingsUploadModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Promotion Modal */}
      <Dialog open={showCreatePromotionModal} onOpenChange={setShowCreatePromotionModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Promotion</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Applicable Property <span className="text-red-500">*</span>
              </label>
              <Select
                value={promotionFormData.property_id}
                onValueChange={(value) => setPromotionFormData({...promotionFormData, property_id: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(prop => (
                    <SelectItem key={prop.id} value={prop.id}>
                      {prop.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">
                Promotion Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={promotionFormData.promotion_name}
                onChange={(e) => setPromotionFormData({...promotionFormData, promotion_name: e.target.value})}
                placeholder="e.g., Summer Special"
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">
                Discount Percentage <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                min="0"
                max="100"
                value={promotionFormData.discount_percentage}
                onChange={(e) => setPromotionFormData({...promotionFormData, discount_percentage: e.target.value})}
                placeholder="e.g., 20"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={promotionFormData.start_date}
                  onChange={(e) => setPromotionFormData({...promotionFormData, start_date: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  End Date <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={promotionFormData.end_date}
                  onChange={(e) => setPromotionFormData({...promotionFormData, end_date: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">
                Room Type (optional)
              </label>
              <Select
                value={promotionFormData.applicable_room_types.join(',')}
                onValueChange={(value) => setPromotionFormData({
                  ...promotionFormData,
                  applicable_room_types: value ? value.split(',') : []
                })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select room types (leave empty for all)" />
                </SelectTrigger>
                <SelectContent>
                  {roomTypes.map(rt => (
                    <SelectItem key={rt.id} value={rt.id}>
                      {rt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                value={promotionFormData.description}
                onChange={(e) => setPromotionFormData({...promotionFormData, description: e.target.value})}
                placeholder="Enter promotion description (optional)"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                rows="3"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">
                Status
              </label>
              <Select
                value={promotionFormData.status}
                onValueChange={(value) => setPromotionFormData({...promotionFormData, status: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowCreatePromotionModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreatePromotion}
                className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600 text-white"
              >
                Save Promotion
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AGX Detail Modal */}
      <Dialog open={showAGXDetailModal} onOpenChange={setShowAGXDetailModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>AGX Analytics Details</DialogTitle>
          </DialogHeader>
          
          {selectedAGXForView && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Hotel ID</label>
                  <div className="text-sm text-gray-800 mt-1">{selectedAGXForView.hotel_id || 'N/A'}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Hotel Name</label>
                  <div className="text-sm text-gray-800 mt-1">{selectedAGXForView.property_name || 'N/A'}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">City</label>
                  <div className="text-sm text-gray-800 mt-1">{selectedAGXForView.city_name || 'N/A'}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">State</label>
                  <div className="text-sm text-gray-800 mt-1">{selectedAGXForView.state_name || 'N/A'}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Created Date</label>
                  <div className="text-sm text-gray-800 mt-1">{selectedAGXForView.created_date || 'N/A'}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Growth Program</label>
                  <div className="text-sm text-gray-800 mt-1">{selectedAGXForView.growth_program || 'N/A'}</div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Performance Metrics</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <label className="text-xs font-medium text-blue-600">Impression</label>
                    <div className="text-lg font-bold text-blue-800 mt-1">{selectedAGXForView.impression?.toLocaleString() || 0}</div>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <label className="text-xs font-medium text-green-600">Click</label>
                    <div className="text-lg font-bold text-green-800 mt-1">{selectedAGXForView.click?.toLocaleString() || 0}</div>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <label className="text-xs font-medium text-purple-600">Rank</label>
                    <div className="text-lg font-bold text-purple-800 mt-1">{selectedAGXForView.rank || 'N/A'}</div>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <label className="text-xs font-medium text-orange-600">Room Night</label>
                    <div className="text-lg font-bold text-orange-800 mt-1">{selectedAGXForView.room_night?.toLocaleString() || 0}</div>
                  </div>
                  <div className="bg-indigo-50 p-3 rounded-lg">
                    <label className="text-xs font-medium text-indigo-600">Booking</label>
                    <div className="text-lg font-bold text-indigo-800 mt-1">{selectedAGXForView.booking?.toLocaleString() || 0}</div>
                  </div>
                  <div className="bg-emerald-50 p-3 rounded-lg">
                    <label className="text-xs font-medium text-emerald-600">Revenue</label>
                    <div className="text-lg font-bold text-emerald-800 mt-1">₹{selectedAGXForView.revenue?.toLocaleString() || 0}</div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Cost Metrics</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-red-50 p-3 rounded-lg">
                    <label className="text-xs font-medium text-red-600">Total Cost</label>
                    <div className="text-lg font-bold text-red-800 mt-1">₹{selectedAGXForView.cost?.toLocaleString() || 0}</div>
                  </div>
                  <div className="bg-amber-50 p-3 rounded-lg">
                    <label className="text-xs font-medium text-amber-600">Cost Per Booking</label>
                    <div className="text-lg font-bold text-amber-800 mt-1">₹{selectedAGXForView.cost_per_bookings?.toLocaleString() || 0}</div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="text-xs text-gray-500">
                  <div>Uploaded by: {selectedAGXForView.uploaded_by || 'N/A'}</div>
                  <div>Uploaded at: {selectedAGXForView.uploaded_at ? new Date(selectedAGXForView.uploaded_at).toLocaleString() : 'N/A'}</div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Link Property to OTA Modal */}
      <Dialog open={showLinkPropertyModal} onOpenChange={setShowLinkPropertyModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Link Property to {linkPropertyFormData.ota_name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              Link a property from your Channel Manager to this OTA by providing the property details and external OTA property ID.
            </div>

            {/* Property Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Select Property <span className="text-red-500">*</span>
              </label>
              <Select 
                value={linkPropertyFormData.property_id}
                onValueChange={(value) => setLinkPropertyFormData(prev => ({
                  ...prev,
                  property_id: value
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a property..." />
                </SelectTrigger>
                <SelectContent>
                  {properties.filter(p => (p.status || 'active') === 'active').map((property) => (
                    <SelectItem key={property.id} value={property.id}>
                      {property.name} - {property.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Select the property from your Channel Manager
              </p>
            </div>

            {/* External Property ID */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                External OTA Property ID <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                placeholder="e.g., 12345 or PROP-ABC-123"
                value={linkPropertyFormData.external_property_id}
                onChange={(e) => setLinkPropertyFormData(prev => ({
                  ...prev,
                  external_property_id: e.target.value
                }))}
              />
              <p className="text-xs text-gray-500">
                Enter the property ID from {linkPropertyFormData.ota_name} (e.g., Agoda Hotel ID, Booking.com Property ID)
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium mb-1">Important:</div>
                  <ul className="space-y-1 text-blue-700">
                    <li>• External Property ID must be alphanumeric (hyphens and underscores allowed)</li>
                    <li>• Each property can only be linked once per OTA</li>
                    <li>• You can configure endpoint mappings later from the OTA detail view</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowLinkPropertyModal(false)}
                className="flex-1"
                disabled={isLinkingProperty}
              >
                Cancel
              </Button>
              <Button
                onClick={handleLinkProperty}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                disabled={isLinkingProperty}
              >
                {isLinkingProperty ? 'Linking...' : 'Link Property'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Delete Booking Confirmation */}
      {showDeleteConfirm && (
        <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete Booking</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-600">
                Are you sure you want to delete this booking? This action cannot be undone.
              </p>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDeleteBooking(showDeleteConfirm)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete-btn"
                >
                  Delete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      </div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Hotel className="w-6 h-6 text-white" />
          </div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/properties/:filter" element={
              <ProtectedRoute>
                <PropertiesPage />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}


export default App;
